import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
// Vietnamese and Simplified Chinese (2026-10-21) live in their own files: they were translated from `en` below in
// one pass, and a string added to en/ko later falls back to English in them until it is translated
// (scripts/i18n_langs.cjs lists what is missing and writes the files).
import vi from './i18n.vi';
import zh from './i18n.zh';

const resources = {
  en: {
    common: {
      notif: {
        title: 'Notifications',
        unreadCount: '{{count}} unread notifications',
        markAll: 'Mark all as read',
        markRead: 'Mark as read',
        loading: 'Loading…',
        empty: 'Nothing for you yet. When the portal mails you about your work, it shows here too.',
        open: 'Open the page this is about',
        by: 'by {{name}}',
        settings: 'Choose what rings',
        settingsHint: 'Pick the groups you want in the bell. Mails keep coming as they do; a group switched off is only hidden here, and shows again when you switch it back on.',
        save: 'Save',
        cancel: 'Back',
        saved: 'Saved',
        unreadIn: '{{count}} unread',
        g_approvals: 'Approvals & decisions',
        g_access: 'Access requests (new people)',
        g_tickets: 'Tickets',
        g_samples: 'Sample due dates',
        g_projects: 'Projects (SKUs added, moved, dropped)',
        g_cycletime: 'Cycle time checks',
        g_ceo: 'CEO Office',
        g_chat: 'Group chat (GCC)',
        g_tasks: 'System Project',
        g_other: 'Other',
        hidden: '{{count}} group(s) hidden',
      },
      common: {
        language: 'Language',
        theme: 'Theme',
        light: 'Light',
        dark: 'Dark',
        allRoles: 'All roles',
      },
      /* Collaboration Pricing Calculator. Its own namespace under `common` rather than loose keys:
         the screen is one self-contained tool and its wording is reviewed against the Costing
         Guideline, not against the rest of the portal's vocabulary. */
      /* Photos and the conversation on a quote — Max, 2026-09-14. Its own namespace because the
         band is a shared component, not part of the calculator's own vocabulary. */
      cpAtt: {
        photos: 'Photos',
        messages: 'Messages',
        you: 'You',
        pendingTag: 'sent when you save',
        noMessages: 'No messages yet.',
        placeholder: 'Write a note about this quote…',
        send: 'Send',
        // "Delete", not "Withdraw": since 2026-09-17 the row really goes, and a word that sounds
        // like taking something back would undersell what the button does.
        withdraw: 'Delete',
        deleteConfirm: 'Delete this message? It is removed for everyone and cannot be brought back.',
        saveFailed: 'The message could not be saved.',
        deleteFailed: 'The message could not be deleted.',
      },
      /* THE IN-LINE COST SHEET LIBRARY — Max, 2026-09-22. Its own namespace and not `pricing.*`:
         it is its own screen, and a key called pricing.title already means the calculator. */
      bench: {
        title: 'Inline Cost Sheets',
        sub: "The vendor cost sheets read into the Inline benchmark, kept so a quote can be measured against one without the file.",
        search: 'Search style, file, vendor, season…',
        empty: 'No cost sheet has been read yet. Use "Import cost sheet" above — what it reads is kept here and offered whenever a quote\'s Inline benchmark is filled.',
        noHit: 'Nothing here matches "{{q}}".',
        loading: 'Reading the sheet…',
        lines: '{{n}} line(s)',
        edit: 'Edit details',
        remove: 'Remove',
        save: 'Save',
        saved: 'Saved.',
        removed: 'Removed from the library.',
        editTitle: 'Season, vendor and note',
        editBody: 'The three things the workbook does not state. The costs cannot be edited — they are what the vendor said, and the buying cost beside them is what they were checked against.',
        season: 'Season',
        vendor: 'Vendor',
        note: 'Note',
        removeTitle: 'Remove this cost sheet?',
        removeBody: '{{file}} leaves the library and stops being offered when a benchmark is filled. Quotes already benchmarked against it keep their numbers and the line printed beside them.',
        colLine: 'Line',
        colVariant: 'Cost version',
        colStated: "Sheet's buying cost",
        import: 'Import cost sheet',
        readOk: '{{file}} — {{n}} line(s) read and kept.',
        readRefused: '{{n}} line(s) were refused: their columns do not add up to the buying cost printed beside them.',
      },
      /* CEO OFFICE — Young, 2026-09-14. Its own namespace: it is a section of its own and nothing
         here belongs to the pricing or development vocabularies. */
      ceo: {
        title: 'CEO Office',
        sub: 'Everything opened, tracked until it closes. Mail forwarded to ceo.office@ becomes a ticket; the counters are calculated from each ticket\'s own event log.',
        desk: 'Desk',
        bothDesks: 'Both',
        desk_business: 'Business',
        desk_executive: 'Executive',
        refresh: 'Refresh',
        newTicket: 'New ticket',
        newSub: 'A ticket is raised on one desk and stays there. Entity and internal contact can be left empty — most items start with no home.',
        create: 'Create',
        cancel: 'Cancel',
        close: 'Close',
        needTitle: 'The ticket needs a title.',
        fromWho: 'From',
        notSet: 'not set',
        noTickets: 'No tickets on this desk yet.',
        noDesk: 'You are not on a desk, so there is nothing to show. Ask Young to add you to Business or Executive.',
        filteredBy: 'Showing {{name}} only — {{n}} ticket(s).',
        clearFilter: 'Show all',
        cardOpen: 'In progress',
        cardOpenRule: 'not closed or dropped',
        cardOverdue: 'Past deadline',
        cardOverdueRule: 'still open, due date gone',
        cardUntouched: 'Untouched 3+ days',
        cardUntouchedRule: 'since the last contact event',
        cardWaiting: 'Waiting on CEO',
        cardWaitingRule: 'the status says so',
        cardNoEntity: 'Entity not set',
        cardNoEntityRule: 'no home yet',
        cardClosed: 'Closed this week',
        cardClosedRule: 'closed in the last 7 days',
        colTicket: 'Ticket',
        colTitle: 'Title / from',
        colDesk: 'Desk',
        colType: 'Type',
        colEntity: 'Entity',
        colContact: 'Internal contact',
        colOwner: 'Owner',
        colDue: 'Due',
        colStatus: 'Status',
        colLast: 'Last contact',
        colDday: 'D-day',
        logTitle: 'Event log',
        logContacted: 'We contacted them',
        logReply: 'They replied',
        ev_created: 'raised',
        ev_contacted: 'contacted',
        ev_reply: 'reply',
        ev_status: 'status',
        ev_field: 'edited',
        ev_closed: 'closed',
        ev_reopened: 'reopened',
        ev_note: 'note',
        closeTicket: 'Close ticket',
        reopenTicket: 'Reopen',
        closeTitle: 'Close {{id}}',
        reopenTitle: 'Reopen {{id}}',
        closeSub: 'It leaves IN PROGRESS and PAST DEADLINE and counts in CLOSED THIS WEEK. It stays in the list and in search, with its whole history, and can be reopened.',
        reopenSub: 'It goes back to the status it was closed from and counts as open again.',
        closeNote: 'Closing note (optional)',
        reopenNote: 'Why it is reopened (optional)',
        closeNotePh: 'e.g. Agreed on the call — nothing more needed.',
        reopenNotePh: 'e.g. They wrote back with a new request.',
        closedBy: 'Closed by {{who}} · {{when}}',
        statusClosedHint: 'Closed — press Reopen to change the status.',
        logNote: 'Add internal note',
        logNoteSub: 'Seen inside the portal by whoever can open this ticket, and mailed to nobody. It is not a contact and does not move Last contact. Ctrl+Enter records.',
        logNoteWhy: 'A note for the desk and the CEO. It moves no date and no counter.',
        notePh: 'What happened, what was decided, what comes next.',
        historyTitle: 'History',
        hist_all: 'All',
        hist_mail: 'Emails',
        hist_note: 'Notes',
        hist_change: 'Changes',
        historyEmpty: 'Nothing of this kind on the ticket yet.',
        hSent: 'Email sent',
        hReceived: 'Email received',
        hSentWhy: 'Sent from the CEO Office mailbox or by a member of a desk.',
        hReceivedWhy: 'Sent to the CEO Office from outside it.',
        hTo: 'To',
        hCc: 'Cc',
        hQuoted: 'Show the earlier messages quoted in this mail',
        hQuotedHide: 'Hide the quoted messages',
        hNote: 'Internal note',
        hCreated: 'Ticket raised',
        hCreatedMail: 'Ticket created from the CEO Office mailbox',
        hClosed: 'Closed (was {{from}})',
        hReopened: 'Reopened → {{to}}',
        hFile: 'File attached',
        historyScope: 'Emails appear here when they reach {{mailbox}} — sent to it, copied to it, or sent from it. A mail a desk member sends from their own mailbox without copying {{mailbox}} cannot be seen by the portal.',
        restricted: 'restricted — the other desk',
        otherDesk: 'This ticket belongs to the other desk. You can see that it exists; its history is not yours to read.',
        entityTitle: 'Entity CC addresses',
        entityFilled: '{{n}} of {{total}} set',
        entitySub: 'Entity and internal contact are what a ticket\'s outgoing mail copies. These are empty until Young sends the eight addresses — an address here receives real mail, so none is guessed.',
        entityPh: 'not set yet',
        fTitle: 'Title',
        fTitlePh: 'What was opened — the thing this ticket tracks',
        fFromPh: 'Who it came from, if anyone',
        fTypePh: 'Follow-up, Legal, Document…',
        newLater: 'Everything but the title can be left empty and filled in later — most items start with no home.',
        fLaterPh: 'Leave empty if not known yet',
        tabTickets: 'Tickets',
        allDesks: 'All',
        unassigned: 'Unassigned',
        choosedesk: 'Place on…',
        deskRule: 'A desk sees only its own tickets. The CEO sees both.',
        open: 'Open',
        fAllStatuses: 'All statuses',
        fAllTypes: 'All types',
        fAllEntities: 'All entities',
        fAllContacts: 'All internal contacts',
        fAllOwners: 'All owners',
        searchPh: 'Search title or sender…',
        searchLeadPh: 'Search counterparty…',
        ddayWhat: 'Counted from the Due date. + is days past it, - is days still to go.',
        ddayLegend: 'D-day counts from the Due date: +5 means five days past it, -5 means five days still to go. It is only counted while a ticket is open.',
        ev_file: 'file',
        fileUnreadable: 'That file could not be read.',
        files: 'Attachments',
        filesSub: 'What this ticket is waiting for, or what came with it. 10 MB each, {{n}} per ticket.',
        addFile: 'Add file',
        removeFile: 'Remove',
        removeFileSub: 'The file is deleted from the server. This cannot be undone.',
        uploading: 'Uploading…',
        logContactedSub: 'Where it went out and what you said. Both optional — the date is recorded either way. Ctrl+Enter records.',
        logReplySub: 'Where it came back and what they said. Both optional — the date is recorded either way. Ctrl+Enter records.',
        channel: 'How',
        channelPh: 'Email, Phone, Meeting…',
        contactNote: 'Note',
        contactNotePh: 'e.g. chased for the signed copy',
        replyNotePh: 'e.g. they need two more weeks',
        record: 'Record',
        colDwell: 'Days at status',
        nDays: '{{n}}d',
        atStageN: '{{n}}d at this stage',
        worstN: 'worst {{n}}d',
        lateShape: 'Where the late ones are',
        lateShapeSub: 'one queue stuck, or all of them',
        nothingLate: 'Nothing is past its date.',
        howLong: 'How long this desk takes',
        howLongSub: 'from the event log — nothing is typed in',
        toFirstAnswer: 'Raised \u2192 first answer',
        toClose: 'Raised \u2192 closed',
        neverAnswered: 'Open and never answered',
        medianOf: 'median of {{n}}',
        notEnough: 'not enough yet \u2014 {{n}} so far',
        loading: 'Loading\u2026',
        noHistory: 'Nothing recorded yet.',
        reset: 'Reset',
        resetN: 'Reset ({{n}})',
        sortedBy: 'sorted by {{col}}',
        showingN: 'Showing {{n}} of {{total}} —',
        leadContactHint: 'Records the date — Last contact and "no reply" both count from it.',
        exportExcel: 'Export to Excel',
        contactHint: 'Records the date — Last contact and UNTOUCHED 3+ DAYS both count from it.',
        logContactedWhy: 'We reached out. Stamps today on this ticket and restarts the untouched count.',
        logReplyWhy: 'They answered. Stamps today as well — kept separate because being chased and being answered are different facts.',
        tab_tickets: 'Tickets',
        tab_leads: 'Leads',
        tab_daily: 'Daily',
        sub_tickets: 'Everything opened, tracked until it closes. Mail forwarded to ceo.office@ becomes a ticket; the counters are calculated from each ticket\'s own event log.',
        sub_leads: 'New account pipeline. The CEO sets where we look and on what terms; the desk makes contact and checks the answer against the criteria.',
        sub_daily: 'Four blocks each morning. The screen and the automated mail show the same thing.',
        newLead: 'New lead',
        newLeadSub: 'Leads sit on the business desk. A lead can name the ticket it belongs to, or be opened before one exists.',
        needCounterparty: 'The lead needs a counterparty.',
        counterpartyPh: 'Who we are talking to',
        sectorPh: 'Distribution, Retail, Online…',
        leadTicketPh: 'Pick the ticket this lead belongs to, if there is one',
        countryPh: 'Thailand, Vietnam…',
        criteriaPh: 'Against the CEO\'s terms…',
        noLeads: 'No leads yet.',
        noReply: 'no reply {{n}}d',
        colLead: 'Lead',
        colCounterparty: 'Counterparty',
        colCountry: 'Country',
        colSector: 'Sector',
        colStage: 'Stage',
        colCriteria: 'Criteria',
        colNext: 'Next contact',
        leadsNote: 'Leads sit on the business desk only. The CEO sets which markets and on what terms; the desk makes contact, gets an answer, and checks it against the criteria.',
        stageRule_Contacted: 'first mail sent',
        stageRule_Replied: 'counterparty responded',
        stageRule_Checking_criteria: 'against the CEO\'s terms',
        stageRule_With_CEO: 'passed, awaiting decision',
        stageRule_Closed: 'won or below criteria',
        dOpened: 'Opened since yesterday',
        dMoved: 'Moved',
        dOverdue: 'Past deadline',
        dWaiting: 'Waiting on the CEO',
        nItems: '{{n}} item(s)',
        reachFirst: 'reach out first',
        nothingHere: 'Nothing.',
        dailyNote: 'Sent at 08:30 KL each weekday. Each desk gets its own mail covering its own tickets; the CEO gets both. The screen and the mail show the same thing.',
      },
      pricing: {
        title: 'Collab Pricing Calculator',
        subtitle: 'Cost, T1, MSRP and royalty on one screen — costing in USD',
        modeGeneral: 'General Collab · OEM / ODM',
        modeGeneralSub: 'No royalty · Markup Base → T1 → MSRP',
        modeLicensed: 'Licensed Collab (Royalty)',
        modeLicensedSub: 'Global MSRP → local price → royalty',
        padClose: 'Close',
        padNeedInstruction: 'Write the terms in the Instruction box below, then press this again.',
        padOptionN: 'Option {{n}}',
        padInlineRrp: 'Inline retail price',
        padPremium: 'Collab premium',
        padTargetMsrp: 'Target MSRP',
        padOwnRetailRef: 'inline, for reference',
        padPartnerNa: 'n/a \u2014 inline is not sold to partners',
        padGapOwn: 'Own retail: collab keeps {{c}}% against inline {{i}}%',
        padGapPartnerBelow: 'Partner wholesale: below cost by {{amt}} once the extra is in',
        padGapPartnerOk: 'Partner wholesale: {{pct}}% after the extra',
        padToB7: '\u2192 Block 7: who pays this {{amt}} a unit on the partner side?',
        padSkuN: 'SKU {{n}}',
        padB7NoteMany: 'One decision for the whole quote: {{n}} SKUs, {{e}} of extra over the whole order. Across A, B and C the MSRP and the partner unit prices stay the same \u2014 only who carries that extra.',
        padFrameModel: 'Frame model',
        padB1: '1 \u00b7 The order',
        padKindOrder: 'what these figures were quoted against',
        padLeadDays: '{{d}} days \u2192 short-lead {{r}}',
        padWhyShow: 'why they differ ({{n}})',
        padWhyHide: 'hide',
        padNoReason: 'no reason given',
        padMoqNote: 'The MOQ surcharge alone is {{amt}} \u2014 {{pct}}% of the extra at this quantity.',
        padB2: '2 \u00b7 Factory cost',
        padB3: '3 \u00b7 Frame cost',
        padB4: '4 \u00b7 Extra cost',
        padB5: '5 \u00b7 Total cost',
        padB6: '6 \u00b7 Price and margin',
        padB7: '7 \u00b7 Who carries the extra \u2014 {{amt}} over the whole order of {{n}} EA',
        padB7Kind: 'decision \u00b7 one click \u00b7 net profit after O.H',
        padB7KindCalc: 'read-only \u00b7 the cards the approver decides on',
        method: 'Pricing method',
        methodCbd: 'CBD cost-plus (HEVN Costing)',
        methodGuideline: 'Costing guideline (O.H 15% + tier ratio)',
        methodCbdHint: 'Factory price from the CBD, HPAC overhead and T2 markup set in Settings, prices rounded up to the dollar.',
        methodGuidelineHint: 'Markup Base, the fixed 15% overhead and the T1 / T2 ratios typed on this quote.',
        cbdSub: 'As the Calculation sheet: HPAC overhead {{oh}}% of Base + Frame, T2 = (Base + Frame + O.H) × {{x}}. Surcharges pass through at cost; tick one if HPAC absorbs it.',
        cbdBase: 'Base price',
        cbdBaseSub: 'Skin + CMT + Vendor OH & profit, plus handling and local transport',
        cbdFrame: 'Frame',
        cbdBaseFrame: 'Base + Frame',
        cbdOh: 'HPAC overhead ({{pct}}%)',
        cbdOhSub: 'rounded to the dollar',
        cbdMoq: 'Production MOQ surcharge',
        cbdSl: 'Short lead-time surcharge',
        cbdAbsorb: 'HPAC absorbs',
        cbdAbsorbed: '(absorbed by HPAC)',
        cbdT1: 'T1 · HPAC → Helinox entity',
        cbdT1Sub: 'Base + Frame + O.H + surcharges, rounded up',
        cbdT2: 'T2 · HPAC → partner',
        cbdT2Unit: 'T2 partner price / unit',
        cbdT2Sub: '(Base + Frame + O.H) × {{x}} + surcharges, rounded up',
        cbdFactory: 'Vendor cost',
        cbdFactorySub: 'Base + Frame + both surcharges — what the factory charges',
        cbdGp: 'GP vs vendor cost',
        cbdMargin: 'Margin % (on T2)',
        cbdExtraSub: 'On the CBD method the handling fee, transport, additional material and FF CMT sit inside the Base price; only the MOQ and short-lead surcharges are charged on top.',
        cbdMsrpNote: 'Target MSRP {{msrp}} is {{x}} × T2.',
        cbdSetMarkup: 'T2 markup',
        cbdOrderValue: 'Order value at T2',
        cbdOrderValueRule: 'T2 × quantity, added up over every SKU',
        cbdFactoryAmt: 'Total vendor cost',
        cbdFactoryAmtRule: 'Vendor cost × quantity — the vendor charges both surcharges whoever carries them',
        cbdAbsorbedAmt: 'Surcharges HPAC absorbs',
        cbdAbsorbedRule: 'The surcharges left out of T2 on this card, over the order',
        cbdGpRule: 'Order value at T2 − total vendor cost',
        cbdMarginRule: 'GP ÷ order value at T2',
        cbdCard_A: 'Partner pays both surcharges',
        cbdCard_B: 'HPAC absorbs both surcharges',
        cbdCard_HM: 'HPAC absorbs the MOQ surcharge',
        cbdCard_HL: 'HPAC absorbs the short-lead surcharge',
        cbdCardWho_A: 'MOQ and short-lead surcharges are added to T1 and T2.',
        cbdCardWho_B: 'Both surcharges are left out of T1 and T2; HPAC pays the factory for them.',
        cbdCardWho_HM: 'The MOQ surcharge is left out of T1 and T2; the short-lead surcharge is charged.',
        cbdCardWho_HL: 'The short-lead surcharge is left out of T1 and T2; the MOQ surcharge is charged.',
        padPerUnitHead: 'Per unit',
        cbdOfferBox: 'The offer — whole quote',
        padLiveDraft: 'Not submitted yet — this is the quote as it stands now in the calculator, not a frozen copy.',
        cbdSlYesHint: 'Short Lead Time = Yes: {{pct}}% of the vendor CMT (labour), as the Offer Sheet charges it.',
        cbdSlNoHint: 'Short Lead Time = No: no surcharge.',
        cbdPartner: 'Partner',
        cbdSeason: 'Season',
        cbdTerm: 'Price term',
        cbdValidFrom: 'Valid from',
        cbdValidTo: 'Valid to',
        cbdColor: 'Colour',
        cbdSlFlag: 'Short lead time',
        cbdSlAuto: 'Auto from the lead time ({{rate}})',
        cbdSlYes: 'Yes — 50% of CMT',
        cbdSlNo: 'No',
        cbdKeyPoints: 'Key points for decision',
        cbdKpMargin: 'Weighted margin (on T2) {{pct}}% on total T2 sales {{rev}} (GP vs vendor cost {{gp}}).',
        cbdKpLowest: 'Lowest margin: {{name}} at {{pct}}%.',
        cbdKpT0: 'Vendor cost = Base + Frame + MOQ surcharge + short lead-time surcharge.',
        cbdStripTitle: 'Price, profit and margin',
        cbdStripWhole: 'The whole quote',
        cbdStripAsCard: 'The whole quote, priced as: {{card}}',
        cbdStripTry: 'Try a margin',
        cbdStripTryHint: 'Selling price = vendor cost ÷ (1 − margin), rounded up to the dollar. Trying a margin does not change the quote.',
        cbdStripClear: 'Clear',
        cbdStripNow: 'As quoted',
        cbdStripAt: 'At {{pct}}% margin',
        cbdStripPrice: 'Selling price (T2)',
        cbdStripProfit: 'Profit / unit',
        cbdStripMargin: 'Margin',
        cbdStripInline: 'Inline retail',
        cbdStripTotal: 'Order',
        cbdStripQty: 'Qty (EA)',
        paS1: '1. Pricing options vs inline — USD/pc',
        paS1Kind: 'Options 1–2 prepared by Sales/Costing · last option: YL enters the Gross Margin %',
        paOption: 'Option', paOptionN: 'Option {{n}}', paItem: 'Item name / Color', paStyle: 'Style code', paQty: 'Qty (pcs)',
        paCost: 'Buying cost', paOh: 'HPAC overhead', paT1: 'T1 price (HPAC → other Helinox)', paInlineT1: 'Inline T1 price',
        paT1vs: 'T1 vs inline T1', paInlineMsrp: 'Inline MSRP', paPartnerMsrp: 'Partner MSRP (ref)', paGmIn: 'Gross margin % (YL input)',
        paT2: 'T2 price', paGp: 'Gross profit /pc', paGm: 'Gross margin % (on T2)', paNet: 'Net margin % (after HPAC overhead)',
        paNetTot: 'Net margin % (total)', paSales: 'Total sales (USD)', paGpTot: 'Total gross profit (USD)', paOfMsrp: 'T2 % of partner MSRP (ref)',
        paCheck: 'T2 ≥ T1 check', paApprove: 'Approve (YL ✔)', paBelow: 'Below T1', paOptTotal: 'Option total',
        paOpt1: 'Partner pays surcharges', paOpt1Sub: 'standard formula, surcharges passed through',
        paOpt2: 'HPAC absorbs surcharges', paOpt2Sub: 'standard formula',
        paOptGm: "YL's decision", paOptGmSub: 'Gross Margin % on T2 — type it here',
        paOptOSub: 'costing prepared by Sales/Costing, priced with the partner paying the surcharges',
        paTick: 'Approve this', paApproved: 'Approved option',
        paApproveOne: 'choose ONE option', paGmAll: 'one margin for every item', paGmEach: 'a margin per item', paGmToAll: 'same for all', paYlOnly: 'YL chooses one option here, in Pricing Approval', paSwitch: 'Choose this instead',
        paNote1: 'Buying Cost = price Helinox pays the factory incl. MOQ + short lead-time surcharges. T1 vs Inline T1 = % difference vs the equivalent inline style.',
        paNote2: 'Gross Margin % = (T2 − Buying Cost) ÷ T2 (margin on selling price, not markup). Net Margin % = (T2 − Buying Cost − HPAC Overhead) ÷ T2. YL enters Gross Margin % for Option {{n}}; all results update immediately.',
        paNote3: '{{partner}} MSRP (ref) = Inline MSRP × {{pct}}%, reference only. "Below T1" = T2 lower than the collab T1. Tick ✔ in the Approve column for ONE option only.',
        paNoInlineT1: 'No inline T1 price on this quote yet — Sales/Costing fill it in the calculator (Inline T1 price) so T1 can be read against the inline style.',
        paGrpShow: 'Show the inline columns (Inline T1, T1 vs inline, Inline MSRP, Partner MSRP)', paGrpHide: 'Fold the inline columns', paPartnerWord: 'Partner', paHProject: 'Project', paHPartner: 'Partner (T2 customer)', paHSeason: 'Season', paHFactory: 'Factory', paHPrepared: 'Prepared by', paHReviewed: 'Reviewed by', paHPriceTerm: 'Price term', paHCurrency: 'Currency', paHLead: 'Total lead time (days)', paHApprover: 'Approver', paApprovedBy: 'Approved by', paApprovalDate: 'Approval date', paYlComments: 'YL comments', paYlCommentsPh: 'Optional — saved with the option you tick',
        paS2: '2. Margin sensitivity — T2 and profit at different Gross Margin %', paS2Kind: 'T2 = Buying cost ÷ (1 − margin), rounded up',
        paS3: '3. Approval', paApprovedOpt: 'Approved option', paApprovedT2: 'Approved T2 — {{name}} (USD/pc)', paNoneYet: 'Not approved yet — tick ONE option above',
        paExport: 'Export Price Approval (Excel)', paExportHint: "This quote on Linda's Price Approval template, with its formulas — YL can still type a margin in the file.",
        inlineT1: 'Inline T1 price', inlineT1Hint: 'T1 of the equivalent inline style — the approval sheet reads the collab T1 against it',
        styleCode: 'Style code', cbdFactoryName: 'Factory',

        cbdStripVsInline: '% of inline retail',
        cbdKpInline: 'Selling price (T2) is {{pct}}% of the inline retail price.',
        cbdKpInlineSome: '({{n}} SKU without an inline retail price.)',
        cbdStripUse: 'Put these prices in my instruction',
        cbdStripUseHint: 'They go into the Instruction box below; approve on them with the terms card.',
        cbdStripInstr: 'Price at {{pct}}% margin on T2 (vendor cost ÷ (1 − margin), rounded up): {{list}}. Order {{rev}}, profit {{gp}}.',
        cbdKpLead: 'Lead time {{days}} days (PO {{po}} → ex-factory {{xf}}).',
        cbdKpAbsorbs: 'HPAC absorbs: {{what}}.',
        cbdKpAbsorbsNone: 'HPAC absorbs: none — all surcharges charged to the partner.',
        cbdOffer: 'Partner Offer (Excel)',
        cbdOfferTitle: 'The offer to the partner: item, colour, quantity and the T2 price only — no cost data',
        cbdOfferNeedSave: 'Save the quote first',
        cbdOfferNeedApproval: 'Released once the quote is approved',
        cbdOfferNeedCard: 'Released once a card is chosen',
        cbdOfferTerms: 'Other terms were chosen — write the offer from the instruction',
        cbdB7: 'Who carries the surcharges — {{amt}} over the whole order of {{n}} EA',
        cbdB7Note: 'The factory charges the MOQ and short-lead surcharges whoever carries them. A to D are the four answers of the Calculation sheet; an option above is one more card, priced with the partner paying both.',
        padOrderHead: 'Order ({{qty}} EA)',
        rule_cbd_oh_pct: 'CBD · HPAC overhead %',
        rule_cbd_t2_markup_pct: 'CBD · T2 markup % (30 = × 1.3)',
        padB7NoteCalc: 'A, B and C sit on every quote. Add an option above for any other way of splitting the extra \u2014 it becomes one more card here and on the approver\u2019s screen.',
        padB7Note: 'Applies to the partner wholesale channel only. Across A, B and C the MSRP {{m}} and the partner unit price {{p}} stay the same \u2014 only the {{e}} of extra per unit moves.',
        padKindCompare: 'compare',
        padKindSolo: 'collab only \u00b7 no comparison',
        padThisQuote: 'this quote',
        padCompareOnly: 'comparison only',
        padInlineShort: 'Inline',
        padSameWord: 'same',
        padNoBench: 'no benchmark',
        padNoneWord: 'none',
        padFactory: 'Factory cost',
        padFrame: 'Frame cost',
        padFactoryN: 'Factory cost (2)',
        padFrameN: 'Frame cost (3)',
        padExtraN: 'Extra cost (4)',
        padExtraPerUnit: 'Extra cost per unit',
        padBaseFrom: '= factory {{f}} + frame {{r}}',
        padBaseSub: 'factory + frame',
        padPctOfTotal: '{{n}}% of total',
        padMultBase: 'Base only: collab is {{x}}\u00d7 inline',
        padMultAll: 'With extra: {{x}}\u00d7 inline',
        padLead: 'The base is close. The extra is the problem.',
        padOwnRetailSub: 'HINC and others sell at MSRP',
        padPartnerWs: 'Partner wholesale',
        padPartnerWsSub: 'supplied at {{p}}% of MSRP',
        padCallout: 'Own retail carries the extra and still keeps {{pct}}%. Partner wholesale cannot \u2014 it goes below cost. Block 7 decides who pays the extra on the partner side.',
        padMsrp: 'MSRP',
        padPartnerUnit: 'Partner unit price',
        padExtraBilledUnit: '+ extra billed',
        padPerUnit: '/unit',
        padExtraAbsorbed: 'Extra absorbed by Helinox',
        padGrossSub: '= O.H + net profit',
        padWhoA: 'Extra billed as a separate line at net cost. No margin on it.',
        padWhoB: 'Extra booked as HPAC marketing cost. Partner sees only the unit price.',
        padWhoC: 'Half billed to the partner, half absorbed by Helinox.',
        padCeoDecision: 'CEO decision',
        padCeoDecisionSub: 'Recorded with the approval. The requester finishes the quote on these terms \u2014 no re-submission needed.',
        padDrowFinal: 'Final terms',
        padDrowRecorded: 'Recorded',
        padOpenCalc: 'Open in the calculator',
        approverView: 'What the approver will see',
        approverViewSub: 'The same two blocks the Pricing Approval screen draws, from the same figures. Read-only — the numbers are typed above.',
        padSubmittedBy: 'submitted by',
        padNoSnapshot: 'This quote was submitted before the figures were frozen, so there is nothing recorded to show. Open it in the calculator to see it as it stands now.',
        padFrozen: 'The figures below are the ones submitted for approval.',
        padEdited: 'The quote has been edited since it was sent.',
        padLine: 'Line',
        padCollab: 'Collab',
        padInline: 'Inline',
        padGap: 'Gap',
        padBase: 'Base cost',
        padExtra: 'Extra cost',
        padExtraNote: 'does not occur in inline production',
        padTotal: 'Total cost per unit',
        padOrder: 'Order total \u00b7 {{qty}} EA',
        padPriceMargin: 'Price and margin',
        padOwnRetail: 'Own retail',
        padPartner: 'Partner wholesale \u00b7 {{pct}}% of MSRP',
        padWeSell: 'We sell at',
        padMarginPre: 'Margin before extra',
        padMarginPost: 'Margin after extra',
        padWhoCarries: 'Who carries the extra \u2014 {{total}} on {{qty}} EA',
        padWhoSub: 'The partner wholesale channel only. MSRP {{msrp}} and the partner unit price {{unit}} stay the same in every option \u2014 only the extra moves.',
        padOptA: 'A \u00b7 Partner pays the extra',
        padOptB: 'B \u00b7 Helinox absorbs it',
        padOptC: 'C \u00b7 Split 50 / 50',
        fxBilledRule: 'the extra \u00d7 the share the partner carries',
        fxRevenueRule: 'each SKU\u2019s quantity \u00d7 its MSRP, added up over the order',
        fxAbsorbedRule: 'the extra \u2212 what the partner is billed',
        fxGrossRule: 'partner revenue + extra billed \u2212 total cost',
        fxOhRule: 'base cost on the order \u00d7 the O.H rate',
        fxNetRule: 'gross margin \u2212 Helinox O.H',
        fxMarginRule: 'net profit \u00f7 partner revenue',
        padGate: 'Approve this quote?',
        padGateSub: 'It is at {{status}}. You decide SKU by SKU in the next step, and a note comes with it.',
        padGateApprove: 'Approve',
        padGateReject: 'Send back',
        padGateCancel: 'Leave it',
        padOtherCostings: 'This quote also carries {{n}} other costing(s)',
        padOtherCostingsSub: 'The same quote costed another way, built in the calculator. Each is priced at A so it can be read against card A \u2014 choosing one means that costing with the partner carrying the extra.',
        padRefAt: 'Priced at A \u00b7 partner pays the extra',
        padSettings: 'Settings these figures were worked out with',
        padSetPremium: 'Collab premium',
        padSetPartner: 'Partner margin',
        padSetSplitC: 'Split ratio (card C)',
        padSetOh: 'Helinox O.H',
        padSetOhVal: '{{pct}}% of base cost',
        padSetCase: 'Case',
        padOptTerms: 'Other terms',
        padOptTermsSub: 'None of the cards above? Write the terms in the Instruction box below. The requester finalises the quote from that text.',
        padSrcPortal: 'Portal default',
        padSrcQuote: 'Added by the requester',
        padSrcApprover: 'You write the terms',
        padSumHead: 'You are approving {{n}} of the {{all}} SKUs on this quote',
        padSumCard: 'Who carries the extra',
        padSumQty: 'Quantity approved',
        padSumNoCard: 'No card has been chosen yet, so this approval settles the SKUs and leaves the extra unallocated.',
        padSumNothing: 'Every SKU is crossed off. Use Send back for the whole quote instead.',
        padSumUnread: '{{n}} of them you have not opened yet.',
        padSkuUnseen: 'not opened yet',
        padSkuCut: 'refused',
        padSkuSeen: 'You have opened {{n}} of the {{all}} SKUs on this quote',
        padSkuAllSeen: 'You have opened all {{n}} SKUs',
        padSkuCutN: '{{n}} refused earlier',
        padCardScope: 'These figures cover the {{n}} SKUs still in, not all {{all}} on the quote.',
        padCardGone: '\u2014 this option is no longer on the quote',
        padBilled: 'Extra billed',
        padRevenue: 'Sales revenue (QTY \u00d7 MSRP)',
        padAllIn: 'Partner pays all-in',
        padGross: 'Gross margin on the order',
        padOh: 'Helinox O.H \u00b7 {{pct}}% of base cost',
        padNetProfit: 'Helinox net profit on the order',
        padNetMargin: 'Net margin \u00b7 % of partner price',
        padSelect: 'Select {{key}}',
        padDecision: 'Decision on the record',
        padChosen: 'Who carries the extra',
        padUndecided: 'Not decided yet',
        padInstruction: 'Instruction',
        padInstructionPh: 'e.g. Go with A, but round the partner unit price to $60 and bill the extra as one lump sum.',
        padSaveInstruction: 'Save the instruction',
        padDecisionSaved: 'The decision is on the record.',
        padMemo: 'Sales OP final memo',
        padMemoSub: 'The final agreed price and terms, after reading the decision above. It can be written once and cannot be edited afterwards.',
        padMemoPh: 'Final price and terms as agreed\u2026',
        padMemoWaitShort: 'Not recorded yet',
        padMemoWait: 'Nothing has been decided on this quote yet.',
        padMemoSave: 'Record the memo',
        padMemoSaved: 'The memo is recorded.',
        modeRules: 'Pricing rules',
        modeRulesSub: 'Admin \u00b7 the constants every quote is priced on',
        rulesTitle: 'Pricing rules',
        rulesSub: 'A change applies to quotes created AFTER it is saved. Quotes already raised keep the rule they were priced under, so nothing already quoted moves.',
        ruleName: 'Rule',
        ruleNow: 'In force',
        ruleNew: 'New value',
        ruleWhy: 'Reason',
        ruleWhyPh: 'Why is this changing?',
        ruleSave: 'Save',
        ruleSaved: 'Saved. Quotes created from now on use the new rule.',
        ruleHistory: 'Every change, newest first',
        ruleValue: 'Value',
        ruleBy: 'Changed by',
        ruleFrom: 'In force from',
        rule_partner_ratio_pct: 'Partner price',
        ruleHint_partner_ratio_pct: 'The partner price as a share of MSRP.',
        rule_tier_oh_pct: 'Helinox O.H',
        ruleHint_tier_oh_pct: 'The overhead half of a tier ratio. Seeds a new quote; a saved one keeps its own.',
        rule_frame_profit_pct: 'Frame profit',
        ruleHint_frame_profit_pct: 'Frame cost \u00d7 this rate, unless the frame is furnished free.',
        modeSaved: 'Saved projects',
        modeSavedSub: 'Browse · load · export to Excel',
        modeOpa: 'OPA / SCT',
        modeOpaSub: 'Operation analysis · standard cycle time',
        projectPlaceholder: 'Project name — e.g. 26FW MICRO KICKBOARD x Chair One (re)',
        template: 'Import template',
        importExcel: 'Import Excel',
        importBom: 'Import vendor BOM',
        bomRead: 'Read as a {{vendor}} BOM — {{n}} SKU from: {{names}}',
        bomFailed: 'This BOM could not be read',
        bomLookedFor: 'The portal knows two vendor formats and looked for:',
        bomSheets: 'Sheets in this file: {{list}}',
        dismiss: 'dismiss',
        imported: 'Loaded {{n}} SKU from the file — check the numbers, then Save project.',
        save: 'Save project',
        saveChanges: 'Save changes',
        newProject: 'New project',
        exportExcel: 'Export Excel',
        savedAs: 'Saved as {{id}} — the screen is ready for the next one.',
        loaded: 'Loaded {{id}} — Save will update it, then clear the screen.',
        needName: 'Give the project a name first.',
        needSave: 'Save the project first — the export reads what was saved.',
        deleteAsk: 'Delete {{id}}? It leaves the list; the costing itself is kept.',
        sku: 'SKU',
        addSku: 'Add SKU',
        duplicate: 'Duplicate',
        del: 'Delete',
        inputs: 'Inputs',
        collapse: 'Collapse',
        expand: 'Expand',
        results: 'Results',
        skuName: 'SKU / item name',
        skuNameHint: 'A name for this item within the project.',
        qty: 'Order quantity (EA)',
        qtyHint: 'Drives the MOQ surcharge and the HPAC debit.',
        poDate: 'PO issue date',
        poDateHint: 'Start of lead time.',
        xfDate: 'Requested XF date',
        xfDateHint: 'XF − PO = lead time.',
        moqLabel: 'MOQ',
        moqNone: '500 pcs or more → no surcharge',
        moqApplied: '{{qty}} pcs → {{rate}} of {{of}}',
        moqQtyPcs: '{{qty}} pcs →',
        moqOfBase: 'of {{of}}',
        leadLabel: 'Lead time',
        leadNone: 'enter both dates to calculate',
        leadDays: '{{days}} days → short-lead {{rate}}',
        leadWarn: '{{days}} days is under 60 — the guideline does not price this. 50% is applied as a placeholder and the surcharge has to be negotiated.',
        baseTitle: 'Markup base cost',
        baseSub: 'The margin is built on these, the FM fee, and nothing else.',
        skin: 'Skin + Case + Packaging',
        skinHint: 'Fabric skin, case and packaging combined.',
        cmt: 'Vendor CMT',
        cmtHint: 'Vendor sewing labour (Cut · Make · Trim).',
        ohp: 'Vendor OH & Profit',
        ohpHint: "The vendor's overhead and margin.",
        frame: 'Frame cost',
        frameHint: 'Pick a model to fill this from the frame price list.',
        framePick: 'Pick a frame model…',
        frameSupply: 'Frame supply',
        supplySell: 'Sold to the vendor',
        supplySellHint: 'Frame profit + Handling $0.50 + freight (p.19)',
        supplyFoc: 'Furnished free (FOC)',
        supplyFocHint: 'FM fee $0.04 only (p.13, p.20)',
        matSupply: 'Material supply',
        matVendor: 'The vendor buys it',
        matVendorHint: 'Fabric, case and packaging are the vendor\u2019s own purchase.',
        matFf: 'Helinox furnishes it (FF)',
        matFfHint: 'The material costs the vendor nothing, so it charges an FF CMT fee instead.',
        frameProfitRate: 'Frame profit rate',
        excludedTitle: 'Excluded items',
        excludedSub: 'Costs that do not occur in inline production. They sit inside the T1 margin base and are also recovered from the partner as an HPAC marketing debit; T2 is priced without them. Typing in a field overrides the rule for that field only.',
        autoToggle: 'Auto from the guideline',
        autoTag: 'auto',
        resetOverride: 'manual — reset',
        fprofit: 'Frame profit',
        fprofitHint: 'Frame cost x profit rate. Zero when the frame is furnished free.',
        handling: 'Handling',
        handlingHint: '$0.50 when Helinox sells the frame to the vendor (p.19).',
        fmfee: 'FM fee',
        fmfeeHint: '$0.04 when the frame is furnished free (p.13, p.20). Part of the base cost.',
        transport: 'Transport',
        transportHint: 'Actual freight — never guessed.',
        moqCharge: 'MOQ surcharge',
        moqChargeHint: 'Markup Base x MOQ rate (p.15).',
        addMat: 'Additional material',
        addMatHint: 'Anything the order needs that the base does not cover.',
        ffcmt: 'FF CMT fee',
        ffcmtHint: 'The vendor\u2019s labour on material Helinox furnishes. Added after the surcharge.',
        ffcmtOff: 'Only when Helinox furnishes the material \u2014 see Material supply.',
        shortLead: 'Short-lead surcharge',
        shortLeadHint: 'Vendor CMT x (1 + MOQ rate) x short-lead rate (p.21).',
        moqBaseLabel: 'MOQ surcharge is charged on',
        moqBaseHint: 'Untick what this vendor adds after the surcharge instead of inside it.',
        part_skin: 'Skin + Case + Packaging',
        part_cmt: 'Vendor CMT',
        part_ohp: 'OH & Profit',
        part_frame: 'Frame',
        part_fmfee: 'FM fee',
        pricingTitle: 'Pricing',
        stepProfit: 'Profit / MSRP simulator',
        stepTarget: 'Target MSRP',
        /* THE TIER LADDER MOVED UP ONE ON 2026-09-14 (Max): what was T0 is now T1, what was T1 is
           now T2. Only the LABELS moved. The stored keys are still `t0Ratio` and `t1Ratio`, because
           they sit inside the payload JSON of every quote ever saved and renaming them would break
           the seven that exist plus every revision copied from one. Read `t0Ratio` as "the first
           tier" and `t1Ratio` as "the second" and the code is consistent again. */
        t1Ratio: 'HPAC profit external (T2 ratio)',
        t1RatioHint: 'Margin ON T2, not a markup on the base. Required for Case A.',
        t0Ratio: 'HPAC profit internal (T1 ratio)',
        t0RatioHint: 'The Helinox entity\u2019s tier. Required for Case B.',
        options: 'Options',
        optionsNote: 'Each option is this quote with one or two answers changed — it follows every figure above it.',
        optionAdd: 'Add an option',
        optionN: 'Option {{n}}',
        optAsQuoted: 'The quote',
        optAsQuotedSub: 'the figures entered above',
        optNoCond: 'nothing changed yet',
        optSameAsQuote: 'Same as the quote — change a value on the left and these figures move.',
        optEdit: 'Conditions',
        optCancel: 'Cancel',
        optDone: 'Done',
        optRemove: 'Remove',
        optCondNote: 'Change any value here. Anything left as it is follows the quote.',
        optQuoteReadOnly: 'The quote\u2019s own answers, for checking an option against. Change them in the blocks above.',
        optClose: 'Close',
        optOrderProfitT1: 'Profit on the order at T1',
        optOrderProfitT2: 'Profit on the order at T2',
        optQty: 'Order quantity',
        optBasis: 'Margin taken on',
        optT1Profit: 'T1 profit',
        optT2Ratio: 'T2 ratio',
        optCbdMargin: 'Margin on T2',
        optT2Profit: 'T2 profit',
        optPremium: 'Collab premium',
        optShortLead: 'Short-lead surcharge',
        benchTitle: 'Inline benchmark',
        benchTag: 'comparison only',
        benchLine: 'Cost line',
        benchThis: 'This quote',
        benchInline: 'Inline product',
        benchDiff: 'Difference',
        vsInline: 'vs inline retail',
        linesApplied: 'Extra cost 1 lines this option charges',
        qtyWord: 'order quantity',
        targetHint: 'Inline retail price × (1 + collab premium)',
        lineDropped: 'not charged',
        inclTitle: 'In the figure the margin is taken on',
        exclTitle: 'Outside it — recovered as an HPAC marketing debit',
        inclBoth: 'also inside the margin base, so it is recovered twice',
        inclTotal: 'margin taken on',
        exclTotal: 'debited to the partner',
        quotedIn: 'margin on',
        quotedOut: 'outside the margin',
        estIsCheck: 'A cross-check, not the quote’s MSRP — nothing downstream reads it. The MSRP this quote carries is in block 6.',
        msrpIsQuote: 'the quote’s MSRP',
        factoryWord: 'factory',
        frameWord: 'frame',
        marginAtQty: 'Margin at {{n}} EA',
        gapWord: 'Gap',
        collabCol: 'Collab',
        benchCol: 'Inline',
        benchTotal: 'Factory cost',
        benchWhy: 'Why the two differ',
        benchWhyHint: 'What the collaboration adds or removes against the inline version — the sentence the approver reads beside the gap. Nothing in this step changes the quote.',
        ohPart: 'O.H',
        ohPartFixed: 'O.H (fixed)',
        frameModelLabel: 'Frame model',
        benchLinkedTo: 'Linked to',
        benchNoModel: 'no frame model yet',
        benchFollowsFrame: 'follows the frame model chosen in block 3',
        benchFromCollab: 'from Collab',
        benchFrameHint: 'The frame the inline product carries, at its own list price.',
        benchModelHint: 'Follows block 3 — the same part, so the gap is the price and not the design.',
        benchSame: 'same',
        benchBase: 'base',
        blkExtra: 'extra',
        fxBuying: 'What a unit costs before any margin',
        fxBuyingRule: 'Base is factory + frame — the figure the tier ratios are taken on. The extra cost is added AFTER the margin and is never marked up. The two together are the buying cost.',
        fxBenchBase: 'Where the inline base comes from',
        fxBenchBaseRule: 'The inline product\u2019s own factory cost plus the frame it carries, at its own list price. Nothing is added after it — the inline side has no extra cost set.',
        benchFrameLabel: 'Frame cost',
        benchImport: 'Fill from a cost sheet',
        benchImportHint: 'Fill this column from a cost sheet already filed on Inline Cost Sheets.',
        benchSrc: 'From',
        benchPickTitle: 'Which line is this quote measured against?',
        benchPickBody: '{{file}} prices {{n}} line(s). Pick the one this quote is measured against — the figure on each row is the buying cost the sheet itself states.',
        benchVariant: 'Cost version',
        benchApply: 'Fill the benchmark',
        benchRefused: '{{n}} line(s) could not be read, because the columns do not add up to the buying cost printed beside them:',
        benchLibBody: 'Search the cost sheets filed on Inline Cost Sheets by style, SKU, colorway or file.',
        benchSearch: 'Search style, SKU, colorway, file…',
        benchNewFile: 'Read a new file…',
        benchBackToLib: 'Back to the library',
        benchLibEmpty: 'No cost sheet has been filed yet. They are read in on Sales Operation → Inline Cost Sheets.',
        benchNoHit: 'Nothing in the library matches "{{q}}".',
        benchLineCol: 'Line',
        benchDiff: 'Difference',
        benchWhyCol: 'Explanation',
        benchWhyPh: 'Why the two differ',
        benchNoGap: 'No gap — nothing to explain',
        benchAllLevel: 'Every line matches the inline product — nothing to explain.',
        benchNotFilled: 'Fill the Inline benchmark to see where the two differ.',
        benchElsePh: 'Anything else…',
        benchBomSub: 'BOM difference between the collab price and the inline price.',
        stepExtra2: 'Extra cost 2',
        stepExtra2Sub: '(Cost comparison)',
        addMatWhat: 'What it is…',
        addLine: 'Add a line',
        removeLine: 'Remove this line',
        ohLine: 'Helinox O.H',
        profitLine: 'Helinox profit',
        extraLine: 'Extra cost',
        extraFromBlock: 'from block 4',
        inclExtra: 'O.H + profit + extra cost — what the partner pays',
        fxOpen: 'How {{what}} is worked out',
        fxFactory: 'Factory cost',
        fxFrame: 'Frame cost',
        fxExtra: 'Extra cost 1',
        t2Divide: '{{basis}} ÷ (1 − {{r}}%)',
        t2ThisQuote: 'this quote',
        t2Both: 'The same ratio on each of the two costing conventions. The quote carries the one marked — an option is where that choice is made.',
        t2BasisFromOpt: 'set per option',
        fxT2: 'How T2 is priced',
        fxT2Rule: 'A margin taken OUT of the price, not added on top: the basis divided by one minus the T2 ratio. It is the convention every saved quote carries — the build-up used for T1 gives a different number.',
        fxExtraAfter: 'added after the margin, never marked up',
        fxT1Base: 'Factory cost + Frame cost + Extra cost 1',
        t1BaseLine: 'Factory cost + Frame cost + Extra cost 1',
        optExtraOff: 'Extra cost 1 lines applied',
        optExtraOffLbl: 'no',
        optExtraOffNone: 'all of them',
        optExtraOffSome: '{{n}} dropped',
        t1Sum: 'base + O.H + profit',
        fxT1On: 'taken on {{n}}',
        fxOh: 'Helinox O.H',
        fxProfit: 'Helinox profit',
        fxTier: 'Tier price',
        fxMsrp: 'Target MSRP',
        fxLead: 'Lead time',
        fxBenchProfit: 'Profit on the inline product',
        fxMoq: 'MOQ surcharge',
        fxSl: 'Short-lead surcharge',
        fxFprofit: 'Frame profit',
        byOption: 'The same quote, option by option',
        optPick: 'What this option changes:',
        optName: 'Option name',
        optNamePh: 'Option {{n}} — type a name people will recognise',
        fxBenchRule: "The inline product's own cost, at the collab's T1 profit rate — there is no separate rate for the benchmark.",
        fxBenchBase: 'Inline cost (skin + CMT + OH & profit + frame)',
        optDropCond: 'Take this out of the option',
        tierSubAny: "O.H + profit + extra cost, at that column's own rates",
        tierSubOwn: 'O.H + profit + extra cost',
        fxTimesQty: 'Per unit × the order quantity — the whole order, not one unit.',
        fxDebitRule: 'Every excluded item, billed to the partner on top of the tier price.',
        fxDebitInPrice: 'Nothing is debited — on this convention it is already inside the price.',
        profitSubOwn: 'the O.H and the profit this tier earns, per unit',
        ohShareAny: 'of which O.H',
        profitShareAny: 'of which profit',
        optSameShort: 'same as the quote',
        revenueSubAny: 'the tier price × the order quantity — the whole order, not one unit',
        totalProfitSubAny: 'profit per unit × the order quantity — revenue less the cost of the order',
        fxExtraRule: 'Every excluded item, added together.',
        fxMoqRule: 'The MOQ base times the rate the order quantity falls on.',
        fxMoqBase: 'MOQ base',
        fxSlRule: 'Vendor CMT times the short-lead rate the lead time falls on.',
        extraInBase: 'inside the base',
        unit: 'unit',
        totalWord: 'Total',
        estMsrp: 'Estimated MSRP: from',
        estTo: 'to',
        estTimes: 'times →',
        targetMsrp: 'Target MSRP',
        targetMsrpHint: 'Inline retail price × (1 + premium).',
        msrpOverT1: 'MSRP ÷ T1',
        msrpOverT1Hint: 'the multiple this lands on',
        yTarget: "Y's target",
        yTargetHint: 'A target set by ratio to the inline price. Optional.',
        collabVsInline: 'Collab vs inline',
        blockResult: 'Result / Options',
        blockDetail: 'How the figures are built',
        optConditions: 'Conditions',
        colCollab: 'Collab',
        colInline: 'Inline benchmark',
        profitPart: 'Profit',
        ohShare: 'of which O.H ({{pct}})',
        profitShare: 'of which profit ({{pct}})',
        dealCase: 'Case',
        dealCaseHint: 'Which collaboration model this is — it decides which tier ratio is required.',
        dealCasePick: 'Select a case…',
        dealCaseBoth: 'Both',
        dealCaseA: 'Case A: HPAC Direct Collaboration',
        dealCaseB: 'Case B: Subsidiary-led Collaboration',
        dealCasePath_both: 'Factory → HPAC → partner   ·   Factory → HPAC → Helinox subsidiary → its market',
        dealCasePath_a: 'Factory → HPAC → collaboration partner',
        dealCasePath_b: 'Factory → HPAC → Helinox subsidiary → its own market',
        dealCasePathLbl: 'Who sells to whom',
        dealCaseWhat_both: 'Both routes are being priced: HPAC buys and sells on, and a Helinox subsidiary also sells in its own market. Both tier ratios are required.',
        dealCaseWhat_a: 'HPAC buys the goods from the factory and sells them straight to the collaboration partner. No Helinox subsidiary stands in between, so only the HPAC tier is priced.',
        dealCaseWhat_b: 'A Helinox subsidiary — Helinox Inc., Helinox Japan and so on — buys from HPAC and sells in its own market. That second step is what the subsidiary tier prices.',
        needPhoto: 'Add at least one photo before saving.',
        needCase: '{{sku}}: choose a Case.',
        needT1: '{{sku}}: T1 ratio is required for this case.',
        needT2: '{{sku}}: T2 ratio is required for this case.',
        needInlineRrp: '{{sku}}: Inline Retail price is required.',
        needPremium: '{{sku}}: Collab premium is required.',
        marginBasis: 'Margin taken on',
        marginBasisBase: 'Markup Base (guideline)',
        marginBasisBuying: 'Total buying cost (quote sheet)',
        marginBasisBaseHint: 'Excluded items are outside T2\u2019s basis \u2014 they are recovered as an HPAC debit instead.',
        marginBasisBuyingHint: 'Everything is marked up, so nothing is debited separately.',
        partnerRatio: 'Partner ratio',
        partnerRatioHint: 'Partner price as a share of MSRP.',
        inlineRrp: 'Inline Retail price',
        productPage: 'Product page',
        folderPlaceholder: 'Folder — e.g. 27SS',
        folderNone: 'Unfiled',
        newFolder: 'New folder',
        newFolderPlaceholder: 'Folder name',
        renameFolder: 'Rename',
        renameFolderTitle: 'Rename {{name}}',
        deleteFolderTitle: 'Delete the folder {{name}}?',
        deleteFolderBody: 'The quotes in it are kept — they go back to Unfiled.',
        dragHint: 'Drag a row onto a folder to file it.',
        approvePricing: 'Approve pricing',
        inlineRrpHint: 'Retail price of the comparable in-line product.',
        premium: 'Collab premium',
        premiumHint: 'What the collaboration adds, as a % of the inline RRP.',
        markupBase: 'Markup Base',
        markupBaseSub: 'The cost the margin is built on',
        composition: 'Vendor FOB composition',
        pricingResults: 'Pricing results',
        buyingCost: 'Total buying cost',
        buyingCostSub: 'Markup Base + excluded items',
        hpacUnit: 'HPAC marketing cost',
        hpacUnitSub: 'per unit',
        hpacTotal: 'HPAC marketing cost',
        hpacTotalSub: 'total debit · per unit × order qty',
        t1: 'T2 (Partner brand)',
        t1Sub: 'Markup Base ÷ (1 − T2 ratio)',
        colStatus: 'Status',
        stDraft: 'Draft',
        colNo: 'No.',
        countAll_one: '{{count}} quote',
        countAll_other: '{{count}} quotes',
        countOf: '{{n}} of {{total}}',
        allTeams: 'All teams',
        approvalTitle: 'Pricing Approval',
        approvalSub: 'Two levels: the sale manager, then the CEO. A quote here is work waiting on somebody.',
        colTeam: 'Sales OP',
        colTrail: 'Trail',
        colSubmittedBy: 'Submitted by',
        colNote: 'Note',
        colCreatedBy: 'Created by',
        colCreatedAt: 'Created at',
        colModifiedBy: 'Modified by',
        colModifiedAt: 'Modified at',
        selected: '{{n}} selected',
        approveSelected: 'Approve selected',
        clearSelection: 'Clear',
        submitTitle: 'Submit {{id}}',
        submitBody: 'Add a note for {{who}} — why this price, or what changed. Optional.',
        submitPh: 'e.g. frame cost updated after the vendor quote on 9 Sept',
        recall: 'Recall',
        recallTitle: 'Recall {{id}}',
        recallBody: 'This takes the quote out of {{who}}\u2019s queue and back to Draft, so you can edit it and send it again. They are emailed that it is no longer waiting for them.',
        recallPh: 'Why you are recalling it (optional)',
        noteFrom: 'Note',
        noteManager: 'Manager',
        noteFinal: 'Final approver',
        gateBy: 'submitted by {{who}}',
        openMissing: '{{id}} is not in this list — it may already be approved.',
        duplicate: 'Duplicate',
        deleted: '{{id}} deleted.',
        deleteTitle: 'Delete this quote?',
        deleteBody: '{{id}} will be removed. Only a draft or a sent-back quote can be deleted.',
        confirmDelete: 'Delete',
        stagePending: 'Pending',
        stagePendingHint: 'With the person who wrote it — new drafts, and anything sent back. The status on the row says which.',
        stageManager: 'Sale manager — approval',
        stageManagerHint: 'Submitted, waiting for the Sales OP team manager.',
        stageFinal: 'CEO — approval',
        stageFinalHint: 'The manager has approved it. Waiting for the final decision.',
        // The fourth tab is not a stage of the gate — it is what came out of it.
        stageDone: 'Approved',
        stageDoneHint: 'Approved and finished. Nothing here is waiting on anybody.',
        stageApproval: 'approval',
        approvedElsewhere: 'Approved quotes are finished work — they are in Saved projects, with Duplicate.',
        queueMine: 'Waiting for you',
        queueMineHint: 'Your decision. Approving sends it on; sending it back needs a reason.',
        queueBack: 'Sent back',
        queueBackHint: 'Change what was asked for, then submit again.',
        queueDraft: 'Not submitted yet',
        queueDraftHint: 'Drafts nobody has been asked about.',
        queueWaiting: 'In flight elsewhere',
        queueWaitingHint: 'Waiting on somebody else — here so "where is my quote" does not need asking.',
        queueDone: 'Approved',
        queueDoneHint: 'Final. Duplicate makes a new draft that says what it came from.',
        queueEmpty: 'Nothing here.',
        trailSubmitted: '{{who}} · {{on}}',
        trailManager: 'Sale manager: {{who}} · {{on}}',
        trailFinal: 'CEO: {{who}} · {{on}}',
        trailNone: 'not submitted',
        resubmit: 'Resubmit',
        gate: 'Approval',
        goApproval: 'Open the Pricing Approval screen',
        reload: 'Reload',
        loading: 'Loading…',
        stPendingmanager: 'Waiting: manager',
        stPendingCEO: 'Waiting: CEO',
        stApproved: 'Approved',
        stRejected: 'Reject',
        sentBackMgr: 'rejected by the sale manager',
        sentBackCeo: 'rejected by the CEO',
        submit: 'Submit',
        reject: 'Send back',
        revise: 'Duplicate',
        view: 'View',
        rejectTitle: 'Send {{id}} back',
        rejectBody: 'Say what has to change. The person it goes back to only has this to work with.',
        nowStatus: 'Done — the quote is now {{status}}. {{to}}',
        revisedInto: 'Copied into {{id}} as a new draft.',
        /* WHICH ENTITY EACH TIER IS — Christine, 2026-09-17: "Please change to T1(Helinox
           Entity), T2(Partner brand)". The tiers, the keys and the formulas are untouched; these
           two strings say whose price each one is, and they were naming the wrong parties. */
        // The five numbered steps and the statement — the layout tabled on 2026-09-17.
        // The lead time's own line: the subtraction it came from, then the band it lands in.
        leadWorked: 'XF − PO = {{days}} days → short-lead {{rate}}',
        stepOrder: 'The order',
        stepFactory: 'Factory cost',
        stepFrame: 'Frame cost',
        stepOthers: 'Extra cost 1',
        days: 'days',
        unitCost: 'Cost of one unit',
        share: 'Share',
        bothConventions: 'The same quote on both conventions',
        whatTheyPay: 'What the entities and the partner pay',
        marginTakenOn: 'Margin is taken on',
        profitAtT1: 'Profit at T1',
        profitAtT2: 'Profit at T2',
        profitSub: 'the tier price less what the margin was taken on, per unit',
        totalProfitAtT1: 'Total profit at T1',
        totalProfitAtT2: 'Total profit at T2',
        totalProfitSub: 'profit per unit × {{qty}} EA — revenue less the cost of the order',
        revenueAtT1: 'Revenue at T1',
        revenueAtT2: 'Revenue at T2',
        revenueSub: 'the tier price × {{qty}} EA — the whole order, not one unit',
        gapNote: 'The two conventions are {{gap}} apart on {{tier}}. On the guideline the others cost of {{unit}} a unit is billed to the partner separately — {{debit}} over {{qty}} EA; on the quote sheet it is already inside the price. Same money, two places, which is why the quote has to say which one it is on.',
        basisLine: 'Submitted on {{name}} — the highlighted column is what the approver is asked to accept.',
        t0: 'T1 (Helinox Entity)',
        t0Sub: 'Markup Base \u00f7 (1 \u2212 T1 ratio)',
        t0SubBuying: 'Total buying cost \u00f7 (1 \u2212 T1 ratio)',
        t1SubBuying: 'Total buying cost \u00f7 (1 \u2212 T2 ratio)',
        marginOnBuying: 'Total buying cost',
        hpacTotalInPrice: 'Already inside the price on this margin basis',
        msrp: 'MSRP',
        msrpSub: 'Inline Retail price × (1 + premium %)',
        partnerPrice: 'Partner price',
        partnerPriceSub: 'MSRP × partner ratio',
        allSkus: 'All SKUs',
        globalInputs: 'Global inputs',
        licensedSub: "The licensed model prices from the partner's shelf price down. A royalty is paid on what they sell it for, so the number that matters is the margin left after it.",
        globalMsrp: 'Global MSRP (USD)',
        t1Fob: 'T1 FOB',
        logistics: 'Logistics %',
        logisticsHint: 'Added to T1 FOB to reach the landed cost.',
        wholesale: 'Wholesale %',
        wholesaleHint: 'Wholesale price as a share of local SRP.',
        wsRoyalty: 'WS royalty %',
        wsRoyaltyHint: 'Royalty on the wholesale price.',
        dtcRoyalty: 'DTC royalty %',
        dtcRoyaltyHint: 'Royalty on the retail price.',
        landed: 'Landed cost',
        landedSub: 'T1 FOB × (1 + logistics %)',
        entities: 'Entities',
        addEntity: 'Add entity',
        entity: 'Entity',
        fx: 'FX / USD',
        localSrp: 'Local SRP',
        resultsByEntity: 'Results by entity',
        resultsByEntitySub: 'Margins are shown pre and post royalty. The gap between them is what the licence costs.',
        srpUsd: 'SRP (USD)',
        wholesaleCol: 'Wholesale',
        wsMargin: 'WS margin',
        dtcMargin: 'DTC margin',
        prePost: 'pre → post',
        fobMult: 'FOB ×',
        addEntityFirst: 'Add an entity',
        colProject: 'Project',
        colName: 'Name',
        openQuote: 'Open quote {{id}}',
        colMode: 'Mode',
        colSkus: 'SKUs',
        colSavedBy: 'Saved by',
        colSaved: 'Saved',
        general: 'General',
        licensed: 'Licensed',
        nothingSaved: 'Nothing saved yet',
        skuField: 'SKU',
        skuFieldHint: 'A single item to calculate.',
        globalMsrpHint: 'One global retail price (USD). Local SRPs are converted from it.',
        t1FobHint: 'Entity transfer price after the vendor negotiation, before logistics.',
        royaltyNote: 'Royalty is not a cost. It is not what it takes to make the product — it is a brand fee paid out of sales after selling, so it is a separate line below revenue rather than something stacked into cost. It also does not apply to intercompany T1 transfers (HPAC → each entity): it arises only when the product leaves to the outside, to a wholesaler or a consumer.',
        burdenNote: 'When wholesale is 50% of SRP, a wholesale royalty of 11% is the same real burden as a DTC royalty of 5.5%. Change the wholesale ratio and that equivalence moves, so review both rates together.',
        autoFillSrp: 'Auto-fill local SRP',
        autoFillHint: 'Global MSRP × each FX rate.',
        resetEntities: 'Reset',
        wholesalePrice: 'Wholesale price',
        dtcRoyaltyAmt: 'DTC royalty',
        wsRoyaltyAmt: 'royalty',
        wsMarginLabel: 'Wholesale margin',
        dtcMarginLabel: 'DTC margin',
        pre: 'pre',
        lowMargin: 'low margin',
        fobBadge: 'FOB',
        deleteTitle: 'Delete this project?',
        deleteBody: '{{id}} leaves the saved list. The costing itself is kept and an administrator can bring it back.',
        cancel: 'Cancel',
        confirmDelete: 'Delete',
        colAction: 'Action',
        colBuying: 'Buying cost',
        colT1: 'T2',
        edit: 'Edit',
        clearFilters: 'Clear sort & filters',
        discardTitle: 'Discard this costing?',
        discardBody: 'What is on screen has not been saved. Discarding clears it and starts a blank project.',
        keepEditing: 'Keep editing',
        discard: 'Discard',
      },
      ask: {
        launch: 'Ask Catchy',
        role: 'AI productivity assistant',
        note: 'Chats are used to make Catchy smarter.',
        hello: "Hi, I'm Catchy. Ask me about projects, SKUs and their stages, samples, tickets, OPA / SCT, NFC tokens and sales — whatever your screens let you see. You can also paste a screenshot.",
        s1: 'Which samples are due this week?',
        s2: 'Which tickets are overdue?',
        s3: 'How many Team 2 SKUs are Delayed?',
        hideSugs: 'Hide suggested questions', showSugs: 'Show suggested questions', otherSugs: 'Other questions',
        placeholder: 'Ask, or paste a screenshot…', send: 'Send', attach: 'Add a screenshot',
        screenshot: 'Screenshot', screenshotReady: 'Screenshot attached', remove: 'Remove',
        lookingAt: 'Looking at:', lookingAtHint: 'Catchy takes “this” to mean the item open on this screen', dropContext: 'Ask without it',
        save: 'Save the chat', newChat: 'New chat', expand: 'Expand', shrink: 'Make smaller', close: 'Close',
        you: 'You',
        leftToday_one: '{{count}} question left today', leftToday_other: '{{count}} questions left today',
        noneLeft: 'No questions left today. Ask again after midnight.',
      },
      /* A partner company's own sample track (DAC VN, Jay 2026-10-17) — 51_PartnerSamples.jsx. Everything
         else on that screen reads the Sample Tracker's own st.* strings; these are only the places where the
         partner's name stands in for "HEVN", or where the project SKU is gone. */
      ps: {
        titleFor: '{{org}} Sample Tracker',
        partner: 'Partner',
        loading: 'Loading…',
        fills: '{{org}} fills',
        /* Jay, 2026-10-19: "Requires Buyer/DAC KR review" / "DAC VN internal review only". The reviewer is
           the company's own setting (00_PORTAL_external_org.reviewer_label). */
        requiresReview: 'Requires {{reviewer}} review',
        reviewerDefault: 'designer',
        reviewerNote: '{{reviewer}} review: record the Review Date and the outcome in Round Outcome below.',
        reviewDateHint: 'Date {{reviewer}} gave the review',
        internalOnly: '{{org}} internal review only',
        internalNote: 'Use Review Date and Remarks for the {{org}} internal review record. This round is not counted as designer workload or pending feedback.',
        internalReview: '{{org}} Internal Review',
        location: 'Location',
        allLocations: 'All Locations',
        locationPh: 'Select or type a location...',
        createTitle: 'New sample — type its style / item',
        createSub: 'A new style gets the next Base ID ({{prefix}}-00001, {{prefix}}-00002…) and needs a photo. A style that already exists adds this round to its Base ID.',
        styleTypedHint: 'Typed here — the Base ID is given from the style name',
        pickPh: 'e.g. {{prefix}}-00001-P1 or {{prefix}}-00001...',
        pickNone: 'No sample matches. To start a new one, use + Create Sample.',
        pickCompany: 'Pick the company this sample is for',
        allCompanies: 'All companies',
        empty: 'No {{org}} sample yet. Press + Create Sample to make the first one.',
        tourCreateB: 'Type the style / item name and add a photo. A new style gets the next Base ID; a style that already exists takes the round under its own Base ID. Then fill in the Sample Plan.',
        tourFiltersB: 'By status, developer or location, or type into the search box. Clear puts everything back, including the funnels on the column headers.',
        manual: 'User manual',
        manualTitle: 'Opens this tracker\'s manual in a new tab — Tiếng Việt, English, 한국어',
        tourTabsT: 'Two ways to read it',
        tourTabsB: 'All Samples lists every Base ID with its rounds. Sample-level Summary gives one line per sample, with its rounds R1 to R5 side by side.',
      },
      cr: {
        requestTitle: 'Courier Requests',
        requestSub: 'Before any shipment on a Helinox courier account (FedEx, DHL, UPS …), send it here first. The portal checks the cost against the contract; the approver decides before dispatch. Not for ordinary ocean or air freight.',
        approvalTitle: 'Courier Approval',
        approvalSub: 'Courier shipments waiting for a decision before dispatch, checked against HINC\'s FedEx and HPAC\'s DHL contract rates. Approver: {{who}}.',
        new: 'New request',
        newTitle: 'New courier request',
        close: 'Close',
        cancel: 'Cancel',
        send: 'Send for approval',
        pick: 'Choose…',
        loading: 'Loading…',
        clear: 'Clear',
        sendNote: 'Send for approval goes to the approver, {{who}}, who approves or turns it down here. You and the people in Copy get the mail too.',
        b_requester: 'Requester',
        b_carrier: 'Courier and the account billed',
        b_route: 'From and to',
        b_weight: 'Weight and boxes',
        b_cost: 'Estimated cost',
        b_goods: 'The goods',
        requester: 'Requested by',
        department: 'Department',
        ccEmails: 'Copy (the account-holding entity)',
        ccHint: 'Young\'s rule 1: the account-holding entity is copied. @helinox.com addresses only, separated by commas.',
        carrier: 'Courier',
        carrierOther: 'Courier name',
        service: 'Service level',
        accountHolder: 'Helinox account billed',
        accountHint: 'The entity whose courier account pays.',
        accountNo: 'Account number',
        from: 'From (origin)',
        to: 'To (destination)',
        shipper: 'Shipper',
        receiver: 'Receiver',
        country: 'Country',
        city: 'City',
        address: 'Address',
        zipHint: 'Include the ZIP / postal code.',
        toIndividual: 'Delivered to an individual\'s address (a colleague\'s home, a customer\'s site)',
        importer: 'Consignee and importer of record',
        importerHint: 'Young\'s rule 5: the Helinox entity, with the individual as care-of — never the individual alone.',
        shipmentTo: 'Shipment to',
        to_internal: 'A Helinox entity or team',
        to_direct: 'A customer or third party (direct)',
        toDirect: 'Sent directly to a customer or third party (not to a Helinox entity)',
        to_customer: 'A customer (direct)',
        to_partner: 'A partner or third party (direct)',
        freightPayer: 'Who bears the freight',
        payerHint: 'Young\'s rule 6: say who pays, before booking.',
        incoterm: 'Shipping term (Incoterm)',
        cartons: 'Cartons',
        actualKg: 'Actual (gross) weight, kg',
        dimKg: 'Dimensional weight, kg',
        dimPh: 'All cartons together',
        useBoxTitle: 'Work the dimensional weight out from one carton (L × W × H ÷ 5,000) × the cartons',
        boxSize: 'Carton size, cm (L × W × H)',
        useBox: 'Use',
        estCost: 'Estimated freight cost',
        currency: 'Currency',
        duties: 'Estimated duties / taxes',
        dutiesDdp: 'DDP: Helinox pays them — enter the estimate (0 if none).',
        bulkyNote: 'Bulky freight (dimensional weight above actual, or over 100 kg): express is not the default. Add at least one forwarder quote to compare before booking (Young\'s rule 2). Poles, tents, frames and furniture are bulky by definition.',
        fwdName: 'Forwarder',
        fwdCost: 'Forwarder quote',
        goodsFor: 'Who the goods are for',
        goodsForHint: 'The person, team or customer the goods go to.',
        contents: 'Contents',
        purpose: 'Purpose',
        notes: 'Notes',
        checkTitle: 'Cost check',
        checkWhat: 'As you fill in the form, the portal compares your estimated cost with HINC\'s FedEx contract rate.',
        checkNeed: 'Still needed: {{list}}.',
        need_courier: 'courier', need_dest: 'destination country', need_weight: 'actual weight', need_cost: 'estimated cost', need_currency: 'currency',
        chargeable: 'Chargeable weight',
        bulky: 'BULKY',
        quoted: 'Your estimate',
        usdPerKg: 'Per kg',
        diff: 'Against the contract rate',
        fxWeek: 'Exchange rate (NetSuite)',
        v_reasonable: 'Reasonable',
        v_review: 'Review recommended',
        v_high: 'Potentially high',
        v_nocontract: 'No contract rate',
        v_manual: 'Check by hand',
        v_undefined: '—',
        s_waiting: 'Waiting',
        s_approved: 'Approved',
        s_rejected: 'Rejected',
        s_withdrawn: 'Withdrawn',
        tab_waiting: 'Waiting for decision',
        tab_approved: 'Approved',
        tab_rejected: 'Rejected',
        tab_all: 'All',
        tab_rates: 'Rates',
        empty_waiting: 'Nothing is waiting for a decision.',
        empty_approved: 'Nothing approved yet.',
        empty_rejected: 'Nothing has been rejected.',
        empty_all: 'No courier requests yet.',
        emptyMine: 'You have not sent a courier request yet.',
        countRows: '{{n}} of {{total}}',
        c_no: 'Request',
        c_created: 'Sent',
        c_requester: 'Requester',
        c_entity: 'Entity',
        c_route: 'Route',
        c_carrier: 'Courier',
        c_originCountry: 'From country',
        c_originCity: 'From city',
        c_destCountry: 'To country',
        c_destCity: 'To city',
        c_service: 'Service',
        c_account: 'Account',
        c_kg: 'Chargeable kg',
        c_cost: 'Estimate',
        c_check: 'Check',
        c_status: 'Status',
        c_decidedBy: 'Decided by',
        c_decidedAt: 'Decided',
        f_route: 'Route',
        f_carrier: 'Courier and service',
        f_account: 'Account billed',
        f_weight: 'Weight and boxes',
        f_cost: 'Estimated freight',
        f_fwd: 'Forwarder quote',
        f_goodsFor: 'Who the goods are for',
        f_purpose: 'Purpose',
        f_contents: 'Contents',
        f_shipper: 'Shipper',
        f_receiver: 'Receiver',
        f_importer: 'Importer of record',
        f_direct: 'Direct shipment (who bears the freight)',
        f_incoterm: 'Shipping term',
        ddpShort: 'Helinox pays the duties and taxes',
        f_requester: 'Requested by',
        f_cc: 'Copied',
        f_notes: 'Notes',
        actualShort: 'actual',
        dimShort: 'dimensional',
        chargeableShort: 'chargeable',
        cartonsShort: 'carton(s)',
        dutiesShort: 'duties/taxes',
        raised: 'Sent {{at}} by {{by}}.',
        approvedBy: 'Approved by {{by}} · {{at}}',
        rejectedBy: 'Rejected by {{by}} · {{at}}',
        withdrawnAt: 'Withdrawn {{at}}.',
        rule4: 'If the carrier\'s quote at booking comes back above this estimate, stop before dispatch and say so (Young\'s rule 4).',
        approve: 'Approve',
        reject: 'Reject',
        withdraw: 'Withdraw',
        withdrawAsk: 'Withdraw {{no}}? The approver is told there is nothing to decide.',
        didApprove: '{{no}} approved — the requester is told.',
        didReject: '{{no}} rejected — the requester is told why.',
        didWithdraw: '{{no}} withdrawn.',
        // Draft, and the list's Action column (Max, 2026-10-19).
        s_draft: 'Draft', notSent: 'Not sent', saveDraft: 'Save draft', c_createdBy: 'Created by', c_createdAt: 'Created at',
        checkSource: 'Calculated from HINC\'s FedEx contract rate file — not an AI review.', noContract: 'no contract price on this lane',
        contractBase: 'Contract rate before fuel', h_shipment: 'Shipment', h_source: 'Where the figures come from', h_findings: 'What the check found',
        editTitle: 'Edit {{no}}', copyTitle: 'New courier request — copied from {{no}}',
        savedDraft: '{{no}} saved as a draft — send it when it is ready.', didDelete: '{{no}} deleted.',
        didDeleteMailed: '{{no}} deleted — the approver was told there is nothing to decide.',
        deleteAsk: 'Delete {{no}}? This cannot be undone.',
        deleteAskWaiting: 'Delete {{no}}? It is with the approver, who will be told there is nothing to decide. This cannot be undone.',
        saveWaiting: 'Save',
        changedByYou: '{{no}} changed — the requester is told what changed. It is still waiting for your decision.',
        changedBy: 'Last changed by {{by}} · {{at}}.',
        why_notWaiting: 'Only a request waiting for a decision',
        c_managerNote: 'Manager note',
        c_action: 'Action', a_view: 'View', a_edit: 'Edit', a_submit: 'Submit', a_copy: 'Duplicate', a_delete: 'Delete', a_withdraw: 'Withdraw',
        why_waiting: 'With the approver — withdraw it first to change it',
        why_approved: 'Approved — duplicate it for another shipment',
        why_sent: 'Already with the approver', why_done: 'Already decided',
        why_record: 'Approved — kept as the record of the approval', why_notMine: 'Not your request',
        sent: '{{no}} sent for approval.',
        sentMailed: '{{no}} sent for approval — the approver is told.',
        billed: 'Billed weight',
        lane: 'Contract lane',
        dir_export: 'Export from Korea',
        dir_import: 'Import to Korea',
        'dir_sg-export': 'Export from Singapore',
        'dir_sg-import': 'Import to Singapore',
        dhlFreightOnly: 'DHL card: freight only — fuel, duties and DHL charges extra',
        estSurcharge: 'Estimated surcharge cost', estSurchargeHint: 'Fuel, remote area and other surcharges on top of the freight — the contract rates price the freight only',
        dhlSettingsTitle: 'HPAC DHL (Singapore) — the card prices freight only',
        dhlFuelPct: 'DHL fuel surcharge %',
        dhlRemarkLabel: 'Remark shown under every DHL comparison',
        ratesTitleDhl: 'HPAC DHL Express contract rates (SGD)',
        ratesNoteDhl: 'DHL Express Worldwide, from HPAC\'s DHL Express Singapore rate card. Up to 30 kg the price is for the whole shipment, by the half kilo; from 30.1 kg it is a price per kg, by the whole kilo. Freight only: fuel surcharge, duties, taxes and DHL charges are extra. Documents-only prices and third-country DHL are not loaded. Next year\'s card (.pdf) goes in with Upload the same way.',
        contract: 'Contract rate',
        withFuel: 'With fuel surcharge {{pct}}%',
        prioRate: 'Priority: the Economy rate × {{f}}', econRate: 'Economy rate, as in the file',
        economyBase: 'Economy rate in the file', h_remark: 'Remark',
        priorityFactor: 'Priority = Economy rate ×', remarkLabel: 'Remark shown under every contract comparison',
        rateFile: 'Rate file',
        depPick: 'Choose or type a department',
        depNone: 'No match for “{{name}}”', depAddTo: '+ Add “{{name}}” to the department list',
        depAdded: '"{{name}}" added to the list.',
        rateVersion: 'Contract rates from {{file}} · loaded by {{by}} · {{at}}',
        rateNone: 'No contract rates loaded yet.',
        settingsTitle: 'How the check judges a quote',
        settingsNote: 'The rate file is FedEx’s Economy rate; a Priority service costs it times the factor. The contract carries no fuel surcharge, so that stays 0 unless it changes. A quote within "Reasonable up to" of the contract rate is reasonable, up to "Review up to" wants a look, above it is potentially high.',
        fuelPct: 'Fuel surcharge %',
        okPct: 'Reasonable up to (%)',
        reviewPct: 'Review up to (%)',
        saveSettings: 'Save',
        settingsSaved: 'Saved.',
        ratesTitle: 'HINC FedEx contract rates (KRW)',
        ratesNote: 'Up to 20.5 kg the price is for the whole shipment, by the half kilo; from 21 kg it is a price per kg, by the whole kilo. Next year’s file goes in with Upload: it is read first, then replaces this list.',
        upload: 'Upload a rate file (FedEx .xlsx / DHL .pdf)',
        previewCounts: 'Export: {{el}} columns, {{ec}} destinations. Import: {{il}} region codes, {{ic}} countries. {{rows}} prices.',
        replaceRates: 'Replace the rates',
        ratesLoaded: 'Rates loaded from {{file}}.',
        weightCol: 'Weight',
        perKgShort: '(per kg)',
        laneNoCountry: 'No country mapped to this column',
        countriesTitle: 'Countries in each column',
      },
      spl: {
        title: 'Sales Planning', loading: 'Loading…', cycle: 'Order cycle', season: 'Season', next: 'Next', orderDue: 'Order', exf: 'Ex-fty',
        tab_format: 'Order format', tab_sheets: 'Order sheets', tab_stock: 'Stock', tab_production: 'Production', tab_overview: 'Overview', tab_setup: 'Setup',
        fmtNone: 'not uploaded', fmtCount: '{{n}} SKU', sheetsCount: '{{done}}/{{n}} done', stockServed: '{{n}} pcs from stock', prodCount: '{{set}}/{{n}} factory set',
        st_open: 'Open', st_closed: 'Closed', closedNote: 'This order is closed — reopen it on Order Format (Edit › Status) to change it.',
        newOrder: 'Create order', newOrderTitle: 'Create an order', open: 'Open', openFormat: "Open this order's format", delOrder: 'Delete order',
        col_id: 'ID', view: 'View', viewOrder: 'View this order — read only', editOrder: 'Edit this order and its format',
        modeView: 'View only', modeEdit: 'Editing', formatHead: 'Order format · {{n}} SKU',
        col_file: 'Order format file', fileBy: '{{by}} · {{at}}', fileNone: 'No order format file yet.',
        col_format: 'SKUs in format', col_sheets: 'Sheets done', col_qty: 'Ordered (pcs)', col_createdBy: 'Created by', col_createdAt: 'Created at', col_action: 'Action',
        ordersCount: '{{n}} of {{total}} orders', clearFilters: 'Clear filters',
        noOrderYetMgr: 'No order yet — press Create order and import its order format.', noOrderYet: 'No order yet — the Sales Planning manager creates it on Order Format.',
        fmtSource: 'Order format', src_file: 'Import the Master order format (.xlsx)', src_copy: 'Copy the format of an earlier order', src_later: 'Add it later',
        copyOption: '{{date}} · ex-fty {{exf}} · {{n}} SKUs', datesFirst: 'Fill in Order due and Ex-factory — the file is read as soon as both are there.',
        pvFile: 'Sheet "{{sheet}}": {{rows}} SKUs become this order\'s format.', pvCopy: '{{n}} SKUs will be copied from the order of {{date}}.',
        orderCreated: 'Order {{date}} created — {{n}} SKUs in its format.', orderCreatedNoFmt: 'Order {{date}} created — import its order format when it is ready.',
        orderDeleted: 'Order {{date}} deleted.', delAsk: 'Delete the order due {{date}} (ex-factory {{exf}}) and the {{n}} SKUs of its format? Nobody has worked on it yet.',
        formatOf: 'Order format · {{date}} ({{season}}) · Ex-fty {{exf}}', seasonAuto: 'Season {{s}} — from the order due date (October to September).',
        seasonAutoTip: 'Worked out from the order due date: a season runs October to September.',
        sh_none: 'Not started', sh_open: 'In progress', sh_done: 'Done', sh_checked: 'Checked',
        st_done_done: '{{cust}}: marked done — the Sales Planning manager can check it now.', st_check_done: '{{cust}}: checked.', st_reopen_done: '{{cust}}: reopened.',
        cancel: 'Cancel', close: 'Close', save: 'Save', saveN: 'Save ({{n}})', saved: 'Saved.', savedCells: 'Saved {{n}} change(s).',
        edit: 'Edit', remove: 'Remove', removed: '{{sap}} removed from this cycle.', read: 'Read the file', write: 'Write', writeN: 'Write {{n}} change(s)',
        search: 'SAP code, product, colour…', allCategories: 'All categories', allLineups: 'All lineups', noRows: 'Nothing matches.', noOrders: 'No order in this cycle yet.',
        sap: 'SAP code', product: 'Product', qty: "Order Q'ty", amountUsd: 'Amount (USD)', total: 'Total', buyer: 'Buyer', pic: 'PIC', status: 'Status',
        fmtEmpty: 'No order format for this cycle yet.',
        fmtEmptyHint: "Upload the month's Master order format (.xlsx). Its SKUs and prices become this cycle's list — the order sheets offer exactly these. A SKU can be added or corrected at any time.",
        fmtWaiting: 'The Sales Planning manager uploads it.', upload: 'Upload the order format', uploadNew: 'Upload a new version', copyFrom: 'Copy the format of {{cycle}}',
        addSku: 'Add a SKU', fmtDownload: 'Download the format', fmtDownloadTip: "This cycle's order format as an .xlsx in the Master order format's layout — edit it and upload it back.", portalOnly: 'Kept by the Portal — the Master order format has no such column; carried from the last cycle that had the SKU.', fmtNote: '{{n}} of {{total}} SKUs · prices in USD',
        fmtUploadTitle: 'Upload the Master order format',
        fmtUploadBody: "The file is read first and written only when you press Write. SKUs already in this cycle take the file's attributes and prices; a SKU the file does not list is kept unless you tick the box below.",
        fmtSummary: 'Sheet "{{sheet}}": {{rows}} SKUs — {{added}} new, {{changed}} changed, {{missing}} in this cycle but not in the file.',
        fmtAdded: 'New', fmtChanged: 'Changed', fmtMissing: 'Not in the file', fmtUsed: 'ticked or ordered, kept',
        fmtRemove: 'Also remove the {{n}} not in the file that nobody has ticked or ordered', fmtNoChange: "The file matches this cycle's format.",
        fmtDone: 'Order format written: {{added}} new, {{changed}} changed, {{removed}} removed.', fmtCopied: '{{n}} SKUs copied.',
        skuNew: 'Add a SKU', skuEdit: 'Edit {{sap}}', skuRemoveAsk: "Remove {{sap}} ({{product}}) from this cycle's order format?",
        noFormatYet: 'Upload the order format first — every sheet offers its SKUs.',
        priceCol: 'Priced at', pf_price_t1: 'T1 FOB', pf_price_t2: 'T2 FOB', pf_price_hema_dt: 'HEMA DT price', pf_null: '—',
        ticked: 'SKUs ticked', ordered: 'Ordered', updated: 'Last saved', myBuyers: 'Only my buyers', allStatus: 'All statuses',
        sheetsNote: '{{done}} of {{n}} sheets done', sapAll: 'SAP upload (all buyers)', sapOne: 'SAP SO upload for this buyer',
        sheetTitle: 'order sheet {{date}}', customerRef: 'Customer Reference (buyer PO#)', showTicked: 'Ticked ({{n}})', showAll: 'All SKUs ({{n}})',
        tick: 'Tick', tickShown: 'Tick all shown', untickShown: 'Untick all shown', fromLast: 'Same SKUs as last cycle',
        fromLastDone: '{{n}} SKUs ticked, as in {{date}}.', nothingTicked: 'Nothing ticked yet — tick the SKUs this buyer may order (All SKUs), or start from last cycle.',
        tickedN: '{{n}} ticked', exportForm: 'Form for the buyer', exportTip: "The order format with the ticked SKUs and this buyer's price only — the buyer fills Order Q'ty.",
        tickFirst: 'Tick the SKUs this buyer may order first', saveFirst: 'Save your changes first', importForm: "Import the buyer's form",
        markDone: 'Done', markChecked: 'Mark as checked', reopen: 'Reopen', doneBy: 'done by {{who}}, {{at}}', checkedBy: 'checked by {{who}}, {{at}}',
        unsaved: '{{n}} unsaved change(s).', keepEditing: 'Keep editing', discard: 'Discard',
        sheetLocked: 'This sheet has been handed over — the Sales Planning manager can still change or reopen it.',
        notYours: "This buyer's PIC is {{pic}} — only they or the Sales Planning manager fill this sheet.",
        flagCycle: 'Order format: {{rule}} — not open this cycle', flagPrice: 'No price for this buyer',
        formTitle: "{{cust}} — import the buyer's form",
        formBody: "The form you sent, filled by the buyer (.xlsx): its SAP Code and Order Q'ty columns are read. Every SKU on the form takes its quantity — a blank is nothing ordered — and a SKU not on the form is left as it is. Nothing is written until you press Write.",
        formSummary: 'Sheet "{{sheet}}": {{rows}} SKU rows, {{total}} pcs ordered — {{n}} SKU(s) change.',
        formUnknown: "Not in this cycle's order format (not imported): {{list}}", formDup: 'Listed twice (the first is used): {{list}}',
        formNoChange: 'The form says what the sheet already holds.', formWritten: '{{cust}}: {{n}} SKU(s) written from the form.', was: 'Now', now: 'On the form',
        stockNote: 'Enter the three stock pools, then allocate: buyers are served in the stock priority (Setup › Buyers), each from KR Domestic, then EUSU, then KR SOP.',
        notAllDone: '{{n}} sheet(s) are not done yet — allocate again once they are in.', countOver: '{{n}} buyer line(s) get more from stock than they ordered.',
        pool_kr_dom: 'KR Domestic', pool_eusu: 'EUSU (SOP)', pool_kr_sop: 'KR (SOP)', avail: 'Available', fromStock: 'From stock', left: 'Left',
        allocation: 'Served to', onlyActive: 'Only SKUs with an order or stock', stockTpl: 'Stock file', stockImport: 'Import a stock file', allocate: 'Allocate stock',
        allocateTitle: 'Allocate stock', allocateGo: 'Allocate',
        allocateBody: 'Every SKU with stock is handed out again: buyers in the order below, each taking from KR Domestic, then EUSU, then KR SOP, as much as it ordered and the pool still holds. A figure corrected by hand on those SKUs is replaced.',
        allocateOrder: 'Order of service (Setup › Buyers › Stock priority):', allocated: 'Stock allocated on {{skus}} SKU(s): {{pcs}} pcs from stock.',
        allocEdit: 'Allocation of {{sap}}', allocTotal: 'Served {{served}} of {{avail}} available',
        stockImportBody: 'The SKUs in the file take its three figures (a blank is no stock); SKUs it does not list are left as they are. Download the stock file for the layout.',
        stockSummary: 'Sheet "{{sheet}}": {{rows}} rows — {{n}} SKU(s) change.', stockWritten: 'Stock written for {{n}} SKU(s).',
        prodNote: 'Required = order − stock served, per buyer, added up. Give each SKU its factory and any overbuild; the cards are the planned pieces per factory.',
        required: 'Required', overbuild: 'Overbuild', planned: 'Planned', factory: 'Factory', comment: 'Comment', needsOnly: 'Only SKUs to produce',
        allFactories: 'All factories', unassigned: 'Unassigned', byBuyer: 'By buyer',
        m_order: 'Orders', m_stock: 'From stock', m_prod: 'Production', total_order: 'Total orders', total_stock: 'Total from stock', total_prod: 'Required',
        mNote_order: 'Amber: ordered on a SKU not open this cycle, or with no price for that buyer.', mNote_stock: 'Red: more from stock than the buyer ordered.',
        mNote_prod: 'Order − from stock, per buyer.',
        c_orders: 'Total orders', c_stock: 'From stock', c_prod: 'Required production', c_ob: 'Overbuild', c_planned: 'Planned production', c_amount: 'Order amount',
        customerReport: 'Customer report — by product category',
        priceRule: "Amount = Q'ty × the buyer's price: T2 buyers at the T2 FOB, HEMA distributors at the HEMA DT price, everyone else at the T1 FOB.",
        sub_customers: 'Buyers', sub_cycles: 'Schedule', addCustomer: 'Add a buyer', active: 'Active', discontinued: 'Discontinued',
        custNote: "PIC = the Sale OP staff who fills the buyer's sheet. Pricing and entity decide the price column; Order book column makes it a buyer in the sheets; Stock priority is the order stock is served in.",
        cycleNote: 'The order & ex-factory schedule (Monthly order program 2026). A closed cycle cannot be changed.',
        etas: 'ETA KR/JP/SIN · AUS · EU · USA', etaLine: '{{a}} · {{au}} · {{eu}} · {{us}}',
        edit_customer: 'Edit buyer', edit_customer_new: 'Add a buyer', edit_cycle: 'Edit order {{date}}',
        f_customer_key: 'Buyer', f_pic_user_id: 'PIC (Sale OP)', f_country: 'Country', f_pricing: 'Pricing', f_entity: 'Helinox entity', f_status: 'Status',
        f_sold_to: 'Sold-To (SAP)', f_ship_to: 'Ship-To (SAP)', f_grid_order: 'Order book column', f_alloc_priority: 'Stock priority', f_remark: 'Remark',
        f_sap_code: 'New SKU# (SAP Code)', f_product: 'Product', f_skin_color: 'Skin Color', f_frame_color: 'Frame Color', f_selling_sku: 'Selling SKU#', f_upc: 'UPC',
        f_line_category: 'Category', f_lineup: 'Lineup', f_order_cycle: 'Order cycle', f_noos: 'NOOS=Never Out of Stock (Evergreen SKU)', f_product_category: 'Product category',
        f_ex_factory_loc: 'Ex-Factory location', f_carton_qty: "Q'ty in Carton", f_hts_usa: 'USA HTS Code', f_hts_eu: 'Europe HTS Code', f_ctn_size: 'CTN Size (mm) /Type',
        f_frame_category: 'Frame Category', f_price_t1: 'T1 FOB for Global Entity & Asia Customers', f_price_hema_dt: 'T1 FOB only for HEMA DTs', f_price_t2: 'T2 FOB for non-distributors',
        f_season: 'Season', f_fc_due: 'FC due', f_order_due: 'Order due', f_ex_factory: 'Ex-factory', f_items_note: 'Items', f_eta_asia: 'ETA KR/JP/SIN',
        f_eta_aus: 'ETA Australia', f_eta_eu: 'ETA Europe', f_eta_usa: 'ETA USA',
      },
      nav: {
        aiPowered: 'Summaries and answers in this portal are written by Claude, Anthropic\'s AI',
        report: 'Report',
        salesReport: 'Sales Report',
        saleOperation: 'Other tools',
        development: 'Development',
        salesOperation: 'Sales Operation',
        production: 'Production',
        productSalesDashboard: 'Product Sales Dashboard',
        salesReportDashboard: 'Business Performance',
        marketingDashboard: 'Marketing Dashboard',
        dataExplorer: 'Data Explorer',
        salesDataLoad: 'Sales Data Update',
        inventoryReport: 'Global Inventory Report',
        poleInventory: 'Pole Inventory Close',
        eusuStock: 'EUSU Non-Selling Stock',
        inventoryDashboard: 'Inventory Dashboard',
        poleDashboard: 'Pole Inventory Dashboard',
        demandForecastDashboard: 'Demand Forecast Dashboard',
        autoJobs: 'Sales Op. Python',
        dashboard: 'Dashboard',
        projectTracker: 'Project Tracker',
        documents: 'E-Docs',
        documentLibrary: 'Library',
        sampleTracker: 'Sample Tracker',
        partnerSamples: 'DAC Sample Tracker',
        ticketStation: 'Ticket Station',
        userManual: 'User Manual',
        projectApproval: 'Project Approval',
        nfcTokenPortal: 'NFC Token Portal',
        pricingCalculator: 'Collab Pricing Calculator',
        ceoOffice: 'CEO Office',
        gtm: 'GTM',
        finance: 'Finance',
        cashSetup: 'Set up',
        cashImport: 'Submit',
        cashApproval: 'Approval',
        courierRequests: 'Courier Requests',
        courierApproval: 'Courier Approval',
        courier: 'Courier',
        collabPricing: 'Collab Pricing',
        // Each person's own menu order (Jay, 2026-10-21).
        arrangeOpen: 'Arrange menu',
        arrangeTitle: 'Arrange your menu',
        arrangeHint: 'Drag a folder or an entry, or use the arrows. Only you see this order.',
        arrangeUp: 'Move {{name}} up',
        arrangeDown: 'Move {{name}} down',
        arrangeDone: 'Done',
        arrangeCancel: 'Cancel',
        arrangeReset: 'Reset',
        arrangeResetHint: "Put the portal's own order back",
        arrangeIsDefault: "This is already the portal's own order",
        arrangeFailed: 'Could not save the menu order',
        arrangeUnfold: 'Show the entries in {{name}}',
        arrangeFold: 'Hide the entries in {{name}}',
        hevnAdmin: 'HEVN Admin',
        driverLog: "Driver's Log",
        salesPlanning: 'Sales Planning',
        splFormat: 'Order Format', splOrders: 'Order Sheets', splStock: 'Stock Allocation', splProduction: 'Factory Allocation', splOverview: 'Order Overview', splSetup: 'Planning Setup',
        cashWeekly: 'Weekly Cash Report',
        cashPlan: 'Monthly Cash Plan',
        cashMonthEnd: 'Month-End Reconcile',
        cashReport: 'Cash Report',
        /* The screen's own title, in the menu too — Max, 2026-10-08. "Programmes" and
           "Approval" read as two of anything once the group is folded or scrolled past. */
        gtmProgrammes: 'GTM Programmes',
        gtmApproval: 'GTM Approval',
        gtmTracker: 'GTM Tracker',
        ceoFolder: 'CEO',
        benchmarkLibrary: 'Inline Cost Sheets',
        pricingApproval: 'Pricing Approval',
        costing: 'Costing',
        opaSct: 'OPA / SCT',
        sctSummary: 'SCT Summary',
      },
      /* GTM programmes. The SEVEN QUESTIONS themselves are not here: they are the document's own
         words and come back from the server with the programme, so the screen and the stored
         sections cannot drift into calling question 3 two different things. */
      /* CASH REPORT — SET UP (2026-10-08, Tuan Nguyen). Field labels are Tuan's own headings. */
      cash: {
        title: 'Set up',
        sub: 'The lists every cash report reads: entities, bank accounts, weeks, exchange rates, expense categories and who prepares, reviews and approves. A change takes effect only once the Group CFO approves it.',
        loading: 'Loading…',
        tab_entity: 'Entity', tab_account: 'Account', tab_user: 'User', tab_period: 'Period', tab_fx: 'Exchange rates',
        tab_expense: 'Expense Category', tab_account_type: 'Type of Account', tab_role: 'Role', tab_notice: 'Notice',
        tab_flow: 'Flow', tab_changes: 'Pending changes', tab_history: 'History', tab_setting: 'Settings', tab_currency: 'Currency',
        f_code: 'Code', f_name: 'Name', f_tax_code: 'Tax Code / ID Code', f_legal_address: 'Legal Address', f_country: 'Country',
        f_ho_address: 'HO Address', f_acc_mail: 'Admin / Accounting Mail', f_min_balance_sgd: 'Minimum Balance (SGD)', f_status: 'Status',
        f_seq: 'Order', f_account_id: 'Account ID', f_entity_code: 'Entity', f_account_no: 'Account no.', f_currency: 'Currency',
        f_bank_name: 'Bank name', f_account_type: 'Type of Account', f_restricted: 'Restricted', f_fixed_deposit: 'Fixed Deposit',
        f_remarks: 'Remarks', f_user_code: 'Code', f_mail: 'Mail', f_role_key: 'Role', f_year: 'Year', f_week: 'Week',
        f_week_ending: 'Week Ending (Friday)', f_submit_deadline: 'Submit Deadline', f_category_type: 'Type', f_describe: 'Describe',
        f_type_name: 'Type', f_default_restricted: 'Default: Restricted', f_default_fd: 'Default: Fixed Deposit',
        f_action: 'Action', f_right_scope: 'Right', f_restriction: 'Restriction (segregation of duties)',
        f_notice_id: 'Notice ID', f_header: 'Header', f_content: 'Content', f_report: 'Report', f_send_to_role: 'Send to (Role)',
        f_send_day: 'Send Day', f_send_time: 'Send Time', f_setting_value: 'Value', f_rate: 'Rate', f_week_code: 'Week',
        f_trigger_key: 'Trigger', tab_log: 'Notice log',
        trg_report_open: 'Weekly report open (scheduled)', trg_due_soon: 'Deadline reminder (scheduled)', trg_missing: 'Missing submission (scheduled)',
        trg_plan_update: '13-week plan update (scheduled)', trg_month_open: 'Month-end reconcile open (scheduled)',
        trg_upload_errors: 'Upload has errors', trg_submitted: 'Sent for review', trg_reviewed: 'Reviewed', trg_approved: 'Approved by Group CFO',
        trg_closed: 'Closed by CEO', trg_returned: 'Sent back', trg_below_min: 'Below minimum balance', trg_setup_submitted: 'Set up sent for approval',
        mailHeld: 'Mail held', mailLive: 'Mail live',
        mailHeldLine: 'Cash Report mail is HELD while the module is being built: every notice below was built and logged, and none was sent to anybody.',
        mailLiveLine: 'Cash Report mail is live: the notices below were sent.',
        logRun: 'Run scheduled notices now', logRunAt: 'Run as at this moment', logAtWhy: 'Leave empty for now, or pick a moment to see what would go then (only while mail is held). Server time.',
        logRan_one: 'Ran as at {{at}}: {{count}} notice built.', logRan_other: 'Ran as at {{at}}: {{count}} notices built.',
        logEmpty: 'No Cash Report mail has been built yet.', logWhen: 'When', logStatus: 'Status', logWhat: 'Notice', logFor: 'Entity · period',
        logTo: 'To', logSubject: 'Subject', log_held: 'Held', log_sent: 'Sent', log_failed: 'Failed', log_claimed: 'Sending…', log_tester: 'Sent to the tester',
        testAs: 'Test as', testOff: 'Off — my own roles',
        testOnNote: 'Testing as {{role}}: every step taken is recorded as "(test as {{role}})". Available only while Cash mail is held.',
        testOffNote: 'While building: play the Preparer or the Reviewer to walk a report through its two steps.',
        logSim: 'simulated', logSimWhy: 'Built by Run as at a chosen moment. It does not use up that day\'s real notice.',
        s_base_currency: 'Base currency', s_rate_source: 'Rate source', s_rate_date: 'Rate date',
        state_clean: 'In force', state_draft: 'Draft — not sent', state_submitted: 'Waiting for Group CFO',
        bandClean: 'Everything on these tabs is approved and in force.',
        bandEmpty: 'Nothing has been approved yet. Import the Set up workbook to start, then send it to the Group CFO.',
        bandCounts: '{{insert}} new · {{update}} changed · {{delete}} removed — not in force until the Group CFO approves',
        sentBy: 'sent by {{by}} on {{at}}',
        returned: 'Sent back by {{by}} on {{at}}:',
        bootstrap: 'The User list has no approved rows yet, so you are acting as the Cash Report Admin because you are a portal admin. This ends once the first list is approved.',
        provisional: 'Nothing is approved yet, so you see this with the role the proposed User list gives you ({{roles}}). It becomes final once the Group CFO approves the list.',
        import: 'Import Excel', send: 'Send to Group CFO', approve: 'Approve',
        notePh: 'Note for the Group CFO (optional)',
        fixFirst: 'Rows with a Data Check error have to be fixed before sending.',
        sent: 'Sent to the Group CFO. Nothing changes until it is approved.',
        approved: 'Approved — the changes are in force now.',
        rejected: 'Sent back to the admin with your reason.',
        imported: 'Imported: {{insert}} new, {{update}} changed, {{same}} already the same. They are in the draft, not in force yet.',
        sumErrors_one: '{{count}} row with a Data Check error', sumErrors_other: '{{count}} rows with a Data Check error',
        sumWarns_one: '{{count}} row with a warning', sumWarns_other: '{{count}} rows with a warning',
        onlyIssues: 'Only rows with a Data Check message', onlyChanged: 'Only changed rows',
        rows_one: '{{count}} row', rows_other: '{{count}} rows',
        search: 'Search…', clear: 'Clear', addRow: 'Add a row', save: 'Save', cancel: 'Cancel', edit: 'Edit',
        remove: 'Remove', undo: 'Undo this change', was: 'Approved value', choose: 'Choose…', auto: 'issued on save',
        autoHint: 'The code of a new row is issued by the portal (Entity-01, EC-0001, N-001…) and never reused.',
        dataCheck: 'Data Check', chkOk: 'OK', chkError: 'Error', chkWarn: 'Warning',
        op_insert: 'New', op_update: 'Changed', op_delete: 'Removing',
        noRows: 'No rows.', yes: 'Yes', no: 'No',
        week: 'Week', allWeeks: 'All weeks', year: 'Year', addYear: 'Add every week of', addWeeks: 'Add weeks', weeksAdded: '{{count}} weeks of {{year}} added to the draft.',
        fxHint: 'Rate = units of the base currency per 1 unit of the currency, so SGD amount = foreign amount × rate. One rate per week; Template 1 reads its own week.',
        newCurrency: 'e.g. CHF', addCurrency: 'Add currency', editRates: 'Edit rates', saveRates: 'Save {{count}} cells',
        fxSync: 'Sync from NetSuite', fxSyncTitle: "Read every week's Friday rate from NetSuite. These go in straight away, without the Group CFO; a rate typed here is left alone.",
        fxLastSync: 'Last rate from NetSuite: {{when}}', fxNeverSynced: 'Not synced from NetSuite yet',
        fxSynced: 'NetSuite: {{written}} rates written, {{unchanged}} already up to date, over {{weeks}} weeks.',
        fxKeptTyped: '{{n}} rates typed in Set up were left alone ({{list}}).',
        fxMissing: 'NetSuite has no rate for {{n}} ({{list}}).',
        fxFromNetsuite: 'From NetSuite — the rate of {{day}}', fxBase: 'Base currency', fxTyped: 'Typed in Set up and approved — the NetSuite sync leaves it alone',
        flowReport: 'Report', step: 'Step {{n}}',
        rep_weekly: 'Weekly Cash Report', rep_13w: '13 Weeks Cash Plan', rep_month: 'Month-End Reconcile', rep_setup: 'Set up',
        flowPrep: 'Preparer: prepare', flowReview: 'Reviewer: review & agree', flowCfo: 'Group CFO: approve', flowCeo: 'CEO: approve & close',
        flowAdmin: 'Admin: do the Set up',
        flowNote: 'All three reports follow this flow (Finance › Cash Report: Weekly Cash Report, 13 Weeks Cash Plan, Month-End Reconcile) — the Reviewer\'s agreement is the last step, and the report shows in the Cash Report once it is given. The Notice tab says what is sent, to whom and when — a Send Day and Time for the scheduled ones, Event for the ones a step sets off — and the Notice log shows every mail built.',
        noChanges: 'No pending changes.', list: 'List', code: 'Code', change: 'Change', what: 'What changes', by: 'By',
        noHistory: 'No approved changes yet.',
        histLine: '{{n}} changes · sent by {{by}} {{at}} · approved by {{cfo}} {{on}}',
        histBuild: '{{n}} changes · applied for the build by {{by}} {{on}} — NOT approved by the Group CFO',
      },
      /* CASH REPORT — THE WEEKLY REPORT (phase 2, 2026-10-08). Column names are Tuan's own. */
      cashr: {
        title: 'Cash Report',
        reviewedOnly: 'Only reports their Reviewer has agreed show figures here.',
      },
      cashw: {
        h_balance: "Balance ({{ccy}})",
        h_balanceOrig: "Balance (account currency)",
        h_amountIn: "Amount ({{ccy}})",
        baseLabel: "Show in",
        baseWhy: "Every amount is converted from SGD at the week's rate in Set up (NetSuite, Friday); an account's own balance stays in its own currency.",
        baseRate: "Shown at the week's rate in Set up: 1 {{ccy}} = {{rate}} SGD.",
        noBaseRate: "No {{ccy}} rate for this week in Set up yet — shown in SGD.",
        balanceWhy: "Show the cash by bank account",
        balanceNote: "Balance = the cash the banks hold at the end of the week. Available = Balance − Restricted − Fixed deposit. An account's own balance is in its currency, as on the statement; every other amount is converted at the week's rate in Set up (NetSuite). Change is over the entities that reported both weeks.",
        tot_balance: "Balance",
        title: 'Cash Report · Weekly',
        sub: 'Each entity imports Template 1 for the week on the Submit screen; the report then goes Preparer → Reviewer, and shows here once the Reviewer has agreed. Every figure here is in SGD and worked out by the portal from what was entered.',
        prevWeek: 'Previous week', nextWeek: 'Next week',
        weekLine: 'Week ending {{end}} · report due {{due}}',
        closedWeek: 'Closed',
        noRates: 'No exchange rate for this week in Set up yet — it arrives from NetSuite once the Friday\'s rate is out. Until then the figures cannot be shown in SGD and a report cannot be sent.',
        h_entity: 'Entity', h_opening: 'Opening', h_inflow: 'Inflow', h_outflow: 'Outflow', h_closing: 'Closing',
        h_restricted: 'Restricted', h_fd: 'Fixed Deposit', h_available: 'Available', h_change: 'Change with previous',
        h_belowMin: 'Below minimum balance', h_status: 'Status',
        h_no: 'No.', h_category: 'Category', h_memo: 'Memo', h_amount: 'Amount in SGD',
        h_account: 'Account', h_ccy: 'Currency', h_closingStmt: 'Closing (statement)', h_diff: 'Difference', h_rate: 'Rate to SGD',
        h_closingSgd: 'Closing in SGD', h_files: 'Files', h_kind: 'Kind', h_other: 'Other entity', h_ref: 'Reference',
        groupNote: 'Without the entities\' transactions with each other',
        groupCount: '{{n}} of {{of}} entities reviewed',
        unmatched: 'Intercompany not netted: {{amt}} SGD',
        unmatchedWhy: 'What the entities report receiving from each other and paying each other does not add up to zero — one side has not reported it yet, or it is in transit. Closing is the cash the banks hold, so it is not Opening + Inflow − Outflow by exactly this amount.',
        nothingYet: 'No entity has uploaded this week yet.',
        yearTitle: 'Every week of {{year}}', yearPick: 'Year', h_weekEntity: 'Entity / Account', h_bank: 'Bank', h_acctNo: 'Account No.', h_acctType: 'Type of account', h_acctFlag: 'Restricted / Fixed deposit',
        weekTitle: 'Week {{code}} · ending {{end}}',
        weekOpenWhy: 'Open or close this week\'s entities', weekPickWhy: 'Make this the week on screen — its charts, Top 3 and reports',
        weekNothing: 'Nothing reported', yearLoading: 'Loading the rest of the year…',
        yearNote: 'One GROUP row per week of the year; press ▸ to open a week\'s entities, an entity\'s ▸ for its accounts, and a week code to make it the week on screen.',
        origClosing: 'Closing in {{ccy}}: {{amt}}',
        inCcy: 'In {{ccy}}',
        origWhy: 'In {{ccy}}, the account\'s own currency, as on the bank statement.',
        notUploaded: 'Not uploaded yet.',
        notReviewed: 'Not reviewed yet — its figures show once the Reviewer agrees.',
        expandWhy: 'Show the cash by bank account',
        errorsN_one: '{{count}} Data Check error', errorsN_other: '{{count}} Data Check errors',
        below: 'Below', belowWhy: 'Available cash is below the minimum balance of {{min}} SGD set for this entity.',
        noMinWhy: 'No minimum balance is set for this entity in Set up.',
        interGroup: 'Intercompany transactions', interGroupWhy: 'With other Helinox entities — left out of the GROUP row.',
        restrictedTag: 'Restricted', fdTag: 'Fixed deposit', noTxn: 'No transaction',
        sgdNote: 'SGD = the account\'s own currency × the week\'s rate in Set up (NetSuite). An account\'s closing in its own currency is printed on its row. Inflow and Outflow leave out intra-entity transfers; Available = Closing − Restricted − Fixed Deposit.',
        topTitle: 'Top 3', top_spend: 'Top 3 key spendings this week', top_payable: 'Top 3 payable next week', noTops: 'No entity has reported this week yet',
        expandAll: 'Expand all', collapseAll: 'Collapse all',
        st_none: 'Not uploaded', st_open: 'Prepared — not sent', st_returned: 'Sent back', st_submitted: 'With the Reviewer',
        st_reviewed: 'Agreed', st_approved: 'Approved', st_closed: 'Closed',
        by_submitted: 'sent by {{by}} {{at}}', by_reviewed: 'reviewed by {{by}} {{at}}', by_approved: 'approved by {{by}} {{at}}',
        by_closed: 'closed by {{by}} {{at}}', by_returned: 'sent back by {{by}} {{at}}',
        weekEnding: 'week ending {{d}}',
        step_prep: 'Preparer', step_review: 'Reviewer', step_cfo: 'Group CFO', step_ceo: 'CEO',
        notYet: 'Not yet',
        returnedBy: 'Sent back by {{by}} on {{at}}:',
        weekClosed: 'This week is closed in Set up — its report can be read, not uploaded.',
        weekClosedReturned: 'This week is closed in Set up, but a report sent back can still be corrected and sent again.',
        selfBlocked: 'You uploaded this version or took an earlier step on this report, so “{{step}}” is somebody else\'s to take.',
        tooBig: 'A file can be at most 3.5 MB. Save a bank statement as a smaller PDF, or attach it in parts.',
        changeOver_one: 'Over the {{count}} entity that reported both weeks', changeOver_other: 'Over the {{count}} entities that reported both weeks',
        template: 'Download template', upload: 'Import file', uploadAgain: 'Import again',
        notePh: 'Note for the Reviewer (optional)', submit: 'Send to the Reviewer',
        fixFirst: 'Fix the Data Check errors before sending.',
        btn_review: 'Review & agree', btn_approve: 'Approve', btn_close: 'Approve & close', sendBack: 'Send back',
        reopen: 'Reopen for correction', reopenWhy: 'Agreed. Reopening sends it back to the Preparer with your reason; its figures leave the Cash Report until it is agreed again.',
        done_submit: 'Sent to the Reviewer.', done_review: 'Reviewed and agreed — it now shows in the Cash Report.',
        done_approve: 'Approved — now with the CEO.', done_close: 'Approved and closed.', done_return: 'Sent back to the Preparer with your reason.',
        mailHeld: '(No mail was sent: Cash Report mail is held while the module is being built.)',
        uploadedOk: 'Version {{v}} imported.',
        replacedOk: 'Version {{v}} imported — it passed the Data Check and replaces version {{old}} with the Reviewer.',
        correctWhileReview: 'With the Reviewer (version {{v}}). You can still correct it, below or by importing a corrected file: once saved, if its Data Check passes, it replaces the version the Reviewer is reading.',
        uploadedErrors_one: 'Version {{v}} uploaded — {{count}} Data Check error to fix before it can be sent.',
        uploadedErrors_other: 'Version {{v}} uploaded — {{count}} Data Check errors to fix before it can be sent.',
        nothingUploaded: 'Nothing entered yet.',
        sumErrors_one: '{{count}} Data Check error', sumErrors_other: '{{count}} Data Check errors',
        sumWarns_one: '{{count}} warning', sumWarns_other: '{{count}} warnings',
        versionLine: 'Showing version {{v}}',
        accountsTitle: 'Accounts', transfersTitle: 'Transfers', filesTitle: 'Bank statements and screenshots',
        versionsTitle: 'Versions — imported or entered', historyTitle: 'History and comments', current: 'In the flow',
        rateFrom_setup: 'Rate from Set up', rateFrom_base: 'Base currency',
        tot_opening: 'Opening', tot_inflow: 'Inflow', tot_outflow: 'Outflow', tot_closing: 'Closing', tot_restricted: 'Restricted',
        tot_fixed_deposit: 'Fixed deposit', tot_available: 'Available',
        tr_internal: 'Intra-entity', tr_group: 'Intercompany',
        kind_statement: 'Statement', kind_balance: 'Balance screenshot', kind_other: 'Other',
        wholeEntity: 'Whole entity', attach: 'Attach a file', attached: 'File attached.', removeFile: 'Remove this file',
        noFiles: 'No files attached.', commentPh: 'Write a comment…', comment: 'Comment', noEvents: 'Nothing has happened yet.',
        waitingYou: 'Waiting for you',
        waitingOpen: 'Open it in Approval to decide',
        ev_upload: 'imported version {{v}} from a file', ev_entry: 'saved version {{v}}, corrected on the Portal',
        changesAgainst: 'Changed against version {{v}} — {{n}}', noChange: 'No figure changed against version {{v}}.', moreChanges: 'Show {{n}} more', fewerChanges: 'Show fewer', changesCut: '…and {{n}} more.', ev_discard: 'cancelled version {{v}}', cancelled: 'Cancelled', ev_submit: 'sent version {{v}} to the Reviewer', ev_review: 'reviewed and agreed',
        ev_approve: 'approved it', ev_close: 'approved and closed it', ev_return: 'sent it back', ev_comment: 'commented',
        ev_file: 'attached a file', ev_unfile: 'removed a file',
      },
      /* CASH REPORT — the 13 Weeks Cash Plan (Template 2) and the Month-End Reconcile (Template 3). */
      cashp: {
        title: 'Cash Report · 13 Weeks Cash Plan',
        sub: 'Each entity plans thirteen weeks ahead in SGD (Template 2), and the plan is set against what its Weekly Cash Reports then say. A fresh plan every week; the same two steps as the weekly report.',
        startsLine: 'Plan starting {{week}} (week ending {{end}}), running to {{last}}',
        h_week: 'Week No.', h_end: 'Week Ending', h_planOpen: 'Opening (previous closing)', h_planIn: 'Plan Inflow', h_planOut: 'Plan Outflow',
        h_planClose: 'Plan Closing (Expected)', h_actIn: 'Actual Inflow', h_actOut: 'Actual Outflow', h_actClose: 'Actual Closing',
        h_variance: 'Variance vs Plan (Closing)', h_weekly: 'Weekly report', h_reported: 'Reported', h_comment: 'Comment',
        h_planClose13: 'Plan closing, week 13',
        h_planOpen1: 'Opening, week 1', h_planIn13: 'Plan inflow, 13 weeks', h_planOut13: 'Plan outflow, 13 weeks',
        noReviewed: 'No reviewed plan yet',
        reportedOf: '{{n}} reported',
        noPlan: 'No entity has uploaded a plan starting this week yet.',
        openWhy: 'Open or close its plan for the whole year', groupPlanned: 'Reviewed plans: {{n}} of {{of}} entities',
        listHint: 'Where each entity\'s plan for this week stands. The chart and the table above follow the pick.',
        pickLabel: 'Show', pickGroup: 'GROUP (every entity)', pickGroupWhy: 'every entity, added up',
        filtersTitle: 'Filter the plan', filterEntities: 'Entities',
        filtersHint: 'Pick GROUP, one entity, or several (they add up); then the year. The chart and the 53-week table below follow both.',
        oneYear: 'The plans cover one year so far',
        weeksShown: '{{count}} of {{of}} weeks', h_planFrom: 'From plan', planFromWhy: 'Read from the plan starting {{week}} — the latest plan that covers this week',
        plannedBy_one: '{{count}} entity', plannedBy_other: '{{count}} entities', yearOf: 'every week of {{year}}',
        yearNote: 'Every week of the year. A week reads from the latest plan that covers it — a week gone by from the plan filed for it, a week to come from the newest forecast — and "From plan" names that plan.',
        groupTitle_one: 'GROUP — {{count}} entity planned', groupTitle_other: 'GROUP — the sum of {{n}} entities that planned this week',
        groupNote: 'The GROUP sums the entities that planned this week. An actual counts once every one of them has reported that week, so a variance never sets some entities\' actuals against all of their plans.',
        entityNote: 'Actuals are the Weekly Cash Report\'s own figures in SGD (inflow and outflow without intra-entity transfers, closing = the banks\' closings). Variance = actual closing − plan closing.',
        panelHeading: 'Plan starting {{week}}', opening: 'Opening cash (SGD)', closing13: 'Plan closing, week 13 (SGD)',
      },
      cashm: {
        title: 'Cash Report · Month-End Reconcile',
        sub: 'Each entity reconciles every account at month end (Template 3): the closing on the monthly bank statement against the week-end closing of its Weekly Cash Report (the last week-end on or before the month end), and what moved between the two dates. Reconcile = Variance − Inflow + Outflow, and it must be 0. The same two steps as the weekly report.',
        monthLine: '{{month}} · month end {{end}}', open: 'Open for reconcile', notEnded: 'Not ended yet',
        notEndedLine: 'This month has not ended yet — its reconcile opens once it has.',
        ruleHead: 'Week-end: {{week}} ({{end}}).',
        h_reconciled: 'Accounts reconciled', h_totalSgd: 'Month-end closing (SGD)',
        h_monthEnd: 'Month-end Closing', h_weekEnd: 'Week-end Closing ({{week}})', h_variance: 'Variance', netOff: 'Net-off', statusGroup: 'Status', h_netIn: 'Net-off inflow',
        h_netOut: 'Net-off outflow', h_reconcile: 'Reconcile',
        accountsTitle: 'Accounts', reconciled: 'Accounts reconciled', totalSgd: 'Month-end closing (SGD)',
        unconvertedWhy: 'Accounts with no Set up rate for the week-end are left out of the SGD total.',
        monthEndOn: 'month end {{d}}',
        st_submitted: 'Prepared', st_reviewed: 'Reviewed', st_approved: 'Approved', st_closed: 'Closed',
        unconverted: '{{n}} without a rate', allReconciled: 'All {{n}} reconciled',
        reportNote: 'Each entity\'s line carries its month-end closing in SGD and who prepared and reviewed its reconcile; its accounts follow, in their own currency. Press an entity to open its reconcile.',
      },
      /* The three templates as screens — the INPUT, typed here or imported. */
      cashq: {
        title: 'Approval',
        sub: 'Reports the Preparers have sent, across the Weekly Cash Report, the 13 Weeks Cash Plan and the Month-End Reconcile. Pick one to read it, then agree to it or send it back. Once the Reviewer agrees, the report shows in the Cash Report. A report sent back stays in In progress until it is sent again.',
        tab_waiting: 'Waiting for you', tab_progress: 'In progress', tab_closed: 'Agreed',
        rows_one: '{{count}} report', rows_other: '{{count}} reports', clear: 'Clear sort and filters',
        colReport: 'Report', colEntity: 'Entity', colPeriod: 'Period', colStatus: 'Status',
        colSentBy: 'Sent by', colSentAt: 'Sent on', colUpdated: 'Last change',
        empty_waiting: 'Nothing is waiting for your decision.', empty_progress: 'No report is on its way through the steps or back with its Preparer.',
        empty_closed: 'No report has been agreed yet.',
        notListed: 'That report is not in your queue — it may not have been sent yet, or it is not one you can see.',
      },
      cashi: {
        title: 'Submit',
        sub: 'Download each template for your entity, fill it in, import it, check it and send it to the Reviewer.',
        entity: 'Entity',
        yoursOne: 'Your entity, from the User list in Set up.',
        yoursMany: 'You are the Preparer of {{n}} entities — pick the one you are entering.',
        notPreparer: 'You are not the Preparer of any entity on the Cash Report User list, so you can read and download here but not import. Preparers are named in Set up › User.',
        noEntity: 'No entity to show.',
        checkPassed: 'Data Check passed',
        saveOrCancel: 'Save or cancel your changes first, then import.',
        openReport: 'Open the {{report}}',
        pointer: 'This report is imported and sent on the Submit screen ({{tpl}}).',
        pointerReturned: 'Sent back to the Preparer — it is corrected and sent again on the Submit screen.',
        goImport: 'Open Submit',
        pointerDecide: 'This report is waiting for your decision — agree to it or send it back in Approval.',
        nothingYet: 'Nothing entered yet — the entity\'s Preparer imports {{tpl}} on the Submit screen.',
      },
      cashc: {
        byEntity: 'Cash by entity ({{ccy}})', byEntitySub: 'Balance split into Available, Restricted and Fixed deposit — {{n}} of {{of}} entities reviewed this week.',
        trend: 'GROUP cash, last 12 weeks ({{ccy}})', trendSub: 'Balance and Available of the entities that reported each week — a gap is a week nobody reported.',
        reported: '{{n}} of {{of}} reported', noneYet: 'No entity has reported yet.', noTrend: 'No week in this range has a report yet.',
        planTitle: 'The plan — {{who}} (SGD)', planSub: 'Plan inflow up, plan outflow down; the dashed line is the plan closing, the solid one the actual closing from the Weekly Cash Report.',
        monthCash: 'Month-end cash by entity (SGD)', monthCashSub: '{{n}} of {{of}} entities have reported this month.',
        noRateAll: 'Shown in SGD once the exchange rates for {{week}} are in Set up — none of the reported accounts can be converted yet.',
        noRateSome: 'Not drawn — no exchange rate for {{week}} yet: {{list}}.',
        recon: 'Accounts reconciled', reconSub: 'Per entity: accounts whose Reconcile is 0, and accounts with a difference.',
        reconciled: 'Reconciled', difference: 'With a difference', accounts_one: '{{count}} account', accounts_other: '{{count}} accounts',
      },
      cashf: {
        inputTitle: 'Input — {{tpl}}', enterHere: 'Enter on the Portal', editHere: 'Edit on the Portal',
        inputClosed: 'Only the entity\'s Preparer imports this report, while it is open or sent back.',
        field1: 'Field 1 — Entity actual cash flow (SGD)', field2: 'Field 2 — Entity account: sum of all transactions',
        field3: 'Field 3 — Intra-entity transfers (between the entity\'s own accounts)',
        field4: 'Field 4 — Intercompany transactions (with another Helinox entity)',
        field5: 'Field 5 — Top 3 key spendings this week', field6: 'Field 6 — Top 3 payable next week',
        f1Entity: 'Entity', f1Overall: 'Overall', f1Group: 'Intercompany transactions',
        f1NoRate: 'Field 1 is worked out in SGD once every account\'s currency has this week\'s rate in Set up › Exchange rates.',
        h_rate: 'Exchange Rate', h_noTxn: 'No Transaction confirm', h_restrictedAcct: 'Restricted Account', h_fdAcct: 'Fixed Deposit',
        h_upStatement: 'Upload Statement', h_upBalance: 'Upload Balance screenshot', h_entity: 'Entity',
        h_no: 'No.', h_category: 'Category', h_memo: 'Memo', h_amountSgd: 'Amount in SGD',
        fromSetup: 'From the account type in Set up', rateFromSetup: 'This week\'s rate from Set up',
        rateMissing: 'No {{ccy}} rate for this week in Set up › Exchange rates (synced from NetSuite) — it cannot be typed here', closingCalc: 'Closing = Opening + Inflow − Outflow',
        closingFiled: 'The imported file gave a Closing of {{v}} — Opening + Inflow − Outflow comes to the figure shown. Saving here keeps the worked-out figure.',
        closingDiff: 'Opening + Inflow − Outflow differs from this Closing by {{d}}',
        memoPh: 'What it is — as long as it needs', f4None: 'No intercompany transaction this week.',
        rateOpt: 'Rate to SGD (optional)', noTxn: 'No transaction', check: 'Check',
        ok: 'OK', diff: 'Diff {{d}}', notNumber: 'Not a number', noTxnAmounts: 'No transaction, but amounts entered',
        upStatement: 'Statement', upBalance: 'Balance',
        addRow: 'Add a row', removeRow: 'Remove this row', save: 'Save as a new version', cancel: 'Cancel editing', cancelImport: 'Cancel import',
        cancelBack: 'Takes version {{v}} out and puts the report back on version {{back}}.', cancelEmpty: 'Takes version {{v}} out — the report goes back to empty, ready to import again.',
        discardedBack: 'Version {{v}} cancelled — back on version {{back}}.', discardedEmpty: 'Version {{v}} cancelled — the report is empty again.', noChanges: 'No changes yet — edit a figure, or import a file.',
        saveHint: 'Saving makes a new version and runs the Data Check again.',
        payrollRule: 'Payroll is ONE total. Never enter employee names or individual salaries.',
        savedOk: 'Version {{v}} saved.', savedReplaced: 'Version {{v}} saved — it passed the Data Check and replaces version {{old}} with the Reviewer.',
        savedErrors_one: 'Version {{v}} saved — {{count}} Data Check error to fix before it can be sent.',
        savedErrors_other: 'Version {{v}} saved — {{count}} Data Check errors to fix before it can be sent.',
        openingFrom: 'The first week\'s previous closing is your Weekly Cash Report\'s closing for {{week}} — correct it only if it is wrong.',
        prepareDate: 'Prepare Date', entityGroup: 'Entity / Group', entityLabel: 'Entity', currency: 'Currency', currencySgd: 'SGD — every figure in this template',
        planCloseExp: 'Plan Closing (Expected)', prevWeek: 'previous week', thisWeek: 'this week',
        openingNone: 'No Weekly Cash Report for the week before — type the opening cash.',
      },
      gtm: {
        title: 'GTM Programmes',
        subtitle: 'Write a programme against the seven questions and send it for review. The approver decides on GTM › Approval.',
        newProgramme: 'New programme',
        createTitle: 'Start a programme',
        nameLabel: 'Title',
        namePlaceholder: 'e.g. Sunset Chair (re) Home · Book Reading Edition',
        create: 'Create',
        cancel: 'Cancel',
        back: 'All programmes',
        none: 'No programmes yet.',
        openDecisions: 'Decisions needed',
        openVerifies: 'To verify',
        regionsWaiting: 'Regions awaiting reply',
        committed: 'Units committed',
        ofTarget: 'of {{n}}',
        editProgramme: 'Edit programme',
        headline: 'Headline',
        openItem: 'Open item',
        openItems: 'Decisions, work and checks',
        noItems: 'Nothing on this question yet.',
        lineTitle: 'What is it',
        lineDetail: 'Detail',
        inThisProgrammeHint: 'Why it is in, or why it is not — the sentence the table prints beside it.',
        discardAsk: 'Discard what you typed?',
        needed: 'Still needed: {{fields}}',
        editTable: 'Edit the table',
        editTableWhy: 'Unlock the table, change as many rows as you like, then save it in one press',
        editTableHint: 'Type freely — nothing is written until you save, and it saves in one go: either all of it lands or nothing changes.',
        saveTable: 'Save the table',
        saveTableN: 'Save the table ({{n}})',
        layerHead: 'What moves above the sales channels',
        stage: 'Stage',
        role: 'Role',
        condition: 'Condition',
        addStage: 'Add a stage',
        orderHead: 'Order',
        orderLabel: 'The order these happen in',
        step: 'Step',
        stepDetail: 'What it is',
        addStep: 'Add a step',
        savedN: 'Saved {{n}} change(s).',
        savedNothing: 'Nothing had changed.',
        regionsHint: 'The regions this programme covers, in priority order — drag a row, or use the arrows, to change it.',
        sheetLocked: 'Press Edit to change anything on this sheet.',
        sheetOpen: 'The sheet is open — type freely. Nothing is written until you press Save. Moving a row saves as you do it.',
        acrossProgramme: 'Across the whole programme',
        qOpenCount: '{{n}} still open',
        /* Q5's comparison, Q6's three sections and the photographs — added 2026-10-07. */
        removeYes: 'Remove',
        /* REVIEW AND APPROVAL — Max, 2026-10-08. The four states read as sentences about the
           programme rather than as system words: "Not sent yet" says what has not happened,
           where "Draft" says what the row is called. */
        waitingOnYou: 'Waiting for your decision',
        rev_draft: 'Not sent for review',
        rev_in_review: 'With the approver',
        rev_approved: 'Approved',
        rev_rejected: 'Rejected',
        approver: 'Approver',
        sentForReview: 'Sent for review',
        approvedOn: 'Approved',
        rejectedOn: 'Rejected',
        sendForReview: 'Send for review',
        reviewAgain: 'Send for review again',
        /* THE LIST AND THE BAND SPEAK PROJECT APPROVAL'S WORDS — Max, 2026-10-08: "tôi muốn nó
           sẽ có design gần giống với phía project tracker và project approval". "Decide" was a
           word only this screen used for the button Project Approval calls Approve, and it opens
           the SAME dialog with the same Approve / Reject switch inside it. */
        approve: 'Approve',
        resubmit: 'Resubmit',
        sent: 'Sent',
        view: 'View',
        kpiAll: 'All programmes',
        kpiMine: 'My programmes',
        scopeMine: 'My programmes',
        scopeAll: 'All programmes',
        noneMine: 'You have no programmes yet — none you created, sent or own.',
        showAll_one: 'Show the {{count}} programme',
        showAll_other: 'Show all {{count}} programmes',
        backApproval: 'GTM Approval',
        approvalTitle: 'GTM Approval',
        approvalSub: 'Programmes sent to you for a decision. Drafts stay with their authors and never appear here.',
        approvalSubAdmin: 'Programmes sent for a decision. As an admin you see every approver\'s queue; drafts never appear here.',
        tabWaiting: 'Waiting for decision',
        tabApproved: 'Approved',
        tabRejected: 'Rejected',
        emptyQueue_in_review: 'Nothing is waiting for your decision.',
        emptyQueue_approved: 'Nothing approved yet.',
        emptyQueue_rejected: 'Nothing rejected.',
        colSentBy: 'Sent by',
        colSentOn: 'Sent on',
        colDecidedOn: 'Decided on',
        colCreatedBy: 'Created by',
        colCreatedAt: 'Created at',
        colUpdatedBy: 'Updated by',
        colUpdatedAt: 'Updated at',
        colManagerNote: 'Manager note',
        colSkus: 'Project SKUs',
        skuHead: 'Linked Project SKUs',
        skuHint: 'The Project SKUs launched under this programme. The same link is the GTM field on each SKU in Project Tracker.',
        skuNone: 'No Project SKU linked yet.',
        skuId: 'Project SKU ID',
        skuName: 'SKU name',
        skuGroup: 'Project group',
        skuStatus: 'Status',
        skuPh: 'Select or type a Project SKU, e.g. 9048 or SKU-09048',
        skuAdd: 'Link SKU',
        skuUnlink: 'Unlink this SKU',
        deleteLockedSent: 'Sent for review — it cannot be deleted while the approver holds it.',
        deleteLockedApproved: 'Approved — an approved programme cannot be deleted.',
        countAll_one: '{{count}} programme',
        countAll_other: '{{count}} programmes',
        countOf: '{{n}} of {{total}} programmes',
        searchPh: 'Search code, title, owner…',
        clear: 'Clear',
        noMatch: 'No programme matches these filters.',
        loading: 'Loading…',
        colNo: 'No.',
        colCode: 'GTM ID',
        colTitle: 'Programme',
        colStatus: 'Status',
        colOwner: 'Owner',
        colApprover: 'Approver',
        colLaunch: 'Launch',
        colDecisions: 'Decisions',
        colVerify: 'To verify',
        colRegions: 'Regions',
        colAction: 'Action',
        reviewDone_submit: 'Sent for review.',
        reviewDone_approve: 'Approved.',
        reviewDone_reject: 'Rejected.',
        coverEyebrow: 'GTM Programme',
        printedOn: 'Printed',
        coverStatus: 'Review',
        coverDecisionNote: 'Decision note',
        exportPdf: 'Export PDF',
        delete: 'Delete',
        deleteProgramme: 'Delete this programme',
        deleteBody: 'This removes {{n}} rows and {{photos}} photos under it.',
        deleteWarn: 'It cannot be undone.',
        exporting: 'Exporting…',
        pdf: 'PDF',
        addStop: 'Add a stop condition',
        stopKind_manual: 'Watched by hand',
        stopKind_commit_min: 'Regional commits below a figure',
        stopKind_anchor_consent: 'The lead region\'s person does not agree',
        stopKindAsk: 'What should the portal read?',
        deleteYes: 'Delete it',
        measureHead: 'How success is measured',
        measureHint: 'What each one is, and what counts as good.',
        measureItem: 'Measure',
        measureWhat: 'What it is, and how it is judged',
        measurePh: 'Defence, edition, inline…',
        timelineHead: 'Timeline · Owners',
        timelineHint: 'How this programme gets run, and who holds each step.',
        timelineStep: 'Step',
        timelineStage: 'Stage',
        timelineWho: 'What happens',
        timelinePh: 'Assign owner, decisions, launch…',
        timelineWhen: 'Expected',
        timelineNote: 'Who this goes to',
        stopWhere: 'Where it stands',
        stopThreshold: 'Threshold',
        removeDoc: 'Remove anyway',
        removeDocWhy: 'This line came from the masterplan. Re-running the import brings it back.',
        keepIt: 'Keep it',
        specHead: 'Items and content',
        specHint: 'What must exist before launch: each item and its content. Add a line for anything else.',
        specItem: 'Item',
        specItemPh: 'Frame, pocket, price…',
        specA: 'Inline',
        specB: 'Edition',
        photosHead: 'Photos',
        photosHint: 'Pictures this programme refers to. A caption says what each one is pointing at.',
        discoverHead: 'How people find it',
        discoverHint: 'What happens before the launch, on the day, and after it.',
        discoverPh: 'Content, seeding, invites…',
        noLines: 'Nothing here yet.',
        collapseAll: 'Collapse all',
        expandAll: 'Expand all',
        fieldTitle: 'Title',
        stopHead: 'Stop conditions (proposed)',
        stopNone: 'No stop conditions are set.',
        tripped: 'Stop triggered',
        holding: 'Holding',
        addLine: 'Add a line',
        lineKind: 'Kind',
        persona: 'Persona',
        channel: 'Channel',
        inThisProgramme: 'In this programme',
        remarks: 'Remarks', select: 'Select', selectHint: 'Tick a row, or pick it in the Add box below, to select it. The order of the selected rows is the priority — drag a row, or use the arrows, to change it.',
        regionsCount: 'Regions in this programme', specContent: 'Content', timelineOwner: 'Owner',
        discoverItem: 'Item', discoverDetail: 'What happens',
        trkTitle: 'GTM Tracker', trkSub: 'The approved programmes and how far each has got, step by step on its Timeline · Owners. Mark a step In progress or Done as the work moves; a step past its expected date and not done is flagged Overdue.',
        trkApproved: 'Approved programmes', trkStepsDone: 'Steps done', trkOverdue: 'Overdue', trkOnlyOverdue: 'Show only programmes with an overdue step',
        trkSearch: 'Search programme, step or owner…', trkToday: 'Today: {{day}}', trkNone: 'No programme has been approved yet.',
        trkApprovedBy: 'Approved by', trkStepsOf: '{{done}} of {{total}} steps done', trkNoSteps: 'No timeline steps yet',
        trkOverdueN_one: '{{count}} overdue', trkOverdueN_other: '{{count}} overdue', trkNext: 'Next', trkAllDone: 'Every step is done',
        trkStatus: 'Status', trkDoneOn: 'Done on', trkNoStepsHint: 'This programme has no timeline steps yet — add them under Timeline · Owners on the programme.',
        st_not_started: 'Not started', st_in_progress: 'In progress', st_done: 'Done',
        trkProgress: 'Progress', trkNextDue: 'Next step due', trkApprovedOn: 'Approved',
        listNew: 'New: it will be added to the list for next time.', listAllIn: 'Everything on the list is already in this programme. Type a new one.', listDup: 'Already selected in this programme.', listHave: 'Already in this programme.', listPick: 'Pick from the list, or type a new one',
        addPersona: 'Add a persona',
        addChannel: 'Add a channel',
        removeRow: 'Remove',
        removeAsk: 'Remove "{{name}}"? This cannot be undone.',
        add: 'Add',
        noteOnTable: 'Note on this table',
        noteOnTableHint: 'Anything the table above cannot say — a caveat, or what it leaves out.',
        moveUp: 'Move up',
        moveDown: 'Move down',
        targetAudience: 'Selected personas — in priority order',
        notATarget: 'Not selected',
        channelsInUse: 'Selected channels — in priority order',
        channelsOut: 'Not selected',
        chStatus_green: 'Company-wide channel status: healthy',
        chStatus_amber: 'Company-wide channel status: watch',
        chStatus_red: 'Company-wide channel status: weak',
        notYet: 'Not yet answerable',
        unmeasured: 'Watched by hand',
        stopNote: 'Whoever runs the programme writes where each condition stands.',
        question: 'Question {{n}}',
        emptySection: 'Not answered yet.',
        oneChange: 'One section changed',
        nChanges: '{{count}} sections changed',
        discard: 'Discard',
        edit: 'Edit',
        save: 'Save',
        decision: 'Decision needed',
        verify: 'To verify',
        addDecision: 'Add a decision',
        addVerify: 'Add something to verify',
        todo: 'To do',
        addTodo: 'Add something to do',
        /* The row is a FILTER over the seven questions, so its label names the dimension it cuts
           on. "The work" named the contents and left the reader to guess what the pills did. */
        phaseFilter: 'Launch stage',
        allPhases: 'Every stage',
        phaseHintOne: 'Showing {{phase}} only — {{hidden}} of the {{all}} open items are hidden. Press it again, or Every stage, to bring them back.',
        phaseWhy_all: 'Everything in the plan that is tied to a moment',
        phaseWhy_before: 'Q5 — what must exist before the work starts, and the decisions still open',
        phaseWhy_pre: 'Q6 — PRE-LAUNCH: before the launch day',
        phaseWhy_launch: 'Q6 — LAUNCH: on the day itself',
        phaseWhy_post: 'Q6 — POST-LAUNCH: after it is out',
        workNone: 'Nothing listed yet.',
        phase_before: 'Before start',
        phase_pre: 'Pre-launch',
        phase_launch: 'Launch',
        phase_post: 'Post-launch',
        noPhase: 'No phase',
        owner: 'Owner',
        noOwner: 'No owner',
        due: 'Due',
        noDue: 'No date',
        answer: 'Answer',
        answered: 'Answered',
        dropped: 'Dropped',
        open: 'Open',
        remove: 'Remove',
        regionsHead: 'Regions',
        region: 'Region',
        person: 'Person',
        replyStatus: 'Reply',
        consent: 'Consent',
        commitQty: 'Commit',
        replyDue: 'Reply by',
        anchor: 'Anchor',
        note: 'Note',
        addRegion: 'Add a region',
        metricsHead: 'How success is measured',
        metric: 'Metric',
        baseline: 'Baseline',
        baselineWindow: 'Window',
        live: 'Since',
        takeBaseline: 'Take baseline',
        retakeBaseline: 'Take again',
        baselineTaken: 'Taken {{when}}',
        baselineNotTaken: 'Not taken',
        manualMetric: 'Typed in by hand',
        baselineWhy: 'Frozen on the day it is taken. A Shopify order can be refunded months later, so a figure that re-derived itself would move under the comparison it anchors.',
        units: 'units',
        sourceDoc: 'Source document',
        status: 'Status',
        launchTarget: 'Launch',
        notSet: 'Not set',
        saved: 'Saved',
        saveFailed: 'Could not save',
      },
      account: {
        profile: 'Profile',
        listUsers: 'List Users',
        createUser: 'Create User',
        accessRequests: 'Access requests',
        rolesPermissions: 'Roles & Permissions',
        portalInsights: 'Portal Insights',
        masterData: 'Master data',
        activityLog: 'Activity Log',
        logout: 'Logout',
      },
      quote: {
        label: 'Quote of the day',
      },
      doc: {
        title: 'Library',
        subtitle: 'Company documents — read them here, or download a copy that names its version. The portal always has the current one.',
        /* Folders — Jay, 2026-09-14. A folder is where a document is filed; the code stays its
           permanent identity, so moving one breaks no link and no mail. */
        folders: 'Folders',
        allDocs: 'All documents',
        unfiled: 'Unfiled',
        newFolder: 'New folder',
        newFolderIn: 'New folder in {{name}}',
        folderName: 'Folder name',
        renameFolder: 'Rename',
        deleteFolder: 'Remove',
        deleteFolderAsk: 'Remove the folder "{{name}}"? Nothing inside it is deleted.',
        folderField: 'Folder',
        folderNone: 'Unfiled',
        folderSaved: 'Folder saved.',
        search: 'Search title or code…',
        upload: 'Upload',
        refresh: 'Refresh',
        none: 'No document has been shared with you yet.',
        noMatch: 'Nothing matches that search.',
        unread: 'unread',
        acknowledged: 'read',
        code: 'Code',
        docTitle: 'Title',
        version: 'Version',
        updated: 'Updated',
        status: 'Status',
        changeNote: 'What changed',
        uploadedBy: 'Uploaded by',
        // The author is the document's; Uploaded by stays the person who pressed the button.
        author: 'Author',
        authorNone: '— not stated —',
        versionAuthor: 'Author of this version', authorSame: '— same as the version before —', byAuthor: 'by {{name}}',
        authorHint: 'Who wrote it. Not the same as who uploads it.',
        newDoc: 'New document',
        newVersion: 'Upload New version',
        addingVersionTo: 'Adding a new version to',
        findDocPh: 'Type a code or a title — HB-0001, handbook…',
        findDocNone: 'No document matches "{{q}}".',
        currentlyAt: 'currently {{version}} — the new one is added, the old stays readable in the history below',
        kind: 'Kind',
        codeAuto: 'Issued by the Portal',
        summary: 'Short description',
        everyone: 'Everyone who can open the library may read it',
        narrowHint: 'Otherwise only you and other library admins can read it until you share it.',
        file: 'File',
        fileKinds: 'HTML, PDF, PNG, JPEG, Word, PowerPoint and Excel (.xlsx) are all read here. The download is always the original file.',
        noPreview: 'No browser can show this kind of file in the page. It is stored here with its version, and downloads with the name it was uploaded under.',
        download: 'Download',
        imgBroken: 'This picture will not open — the file is damaged or in a format the browser cannot read. It is still stored here, and downloads with the name it was uploaded under.',
        // The document names its own version and this box shows what it said — Max to Jay,
        // 2026-09-17. Empty means the Portal keeps counting from the last upload.
        versionLabel: 'Version',
        versionAuto: 'Left empty: the Portal continues from the last one',
        versionFound: 'Read from the document itself: {{version}}',
        versionNotFound: 'No version found on the document\'s cover — leave it empty for v1.0, or type it.',
        changeNoteLabel: 'What changed (one line)',
        uploading: 'Uploading…',
        allDocuments: 'All documents',
        history: 'Version history',
        latest: 'latest',
        view: 'Open',
        readingOld: 'You are reading an older version —',
        backToLatest: 'back to the latest',
        iHaveRead: 'I have read this',
        recording: 'Recording…',
        youAcknowledged: 'You confirmed you read {{version}}',
        noVersion: 'No version has been uploaded yet.',
        readers: 'Who has read it',
        readersCount: '{{opened}} of {{notified}} opened · {{acked}} confirmed',
        notified: 'Notified',
        opened: 'Opened',
        confirmed: 'Confirmed',
        nobody: 'Nobody has been notified yet.',
        share: 'Share',
        shareLink: 'Copy link',
        linkCopied: 'Link copied',
        shareByEmail: 'Send by email',
        emails: 'Email addresses',
        emailsHint: 'Separate several with a comma or a space.',
        groups: 'Groups',
        person: 'Recipient',
        groupsNone: 'Choose groups…',
        groupsClear: 'Clear',
        addGroup: 'Add a group',
        addGroupName: 'Group name',
        addGroupEmail: 'Group email address',
        addGroupSave: 'Add to the list',
        addGroupHint: 'Everyone sharing a document will see this group from now on.',
        groupAdded: '"{{name}}" is in the list.',
        groupRemoved: '"{{name}}" was removed from the list.',
        removeGroupConfirm: 'Remove "{{name}}" from everybody\u2019s group list?',
        sharers: 'Who may share',
        sharersIntro: 'You uploaded {{code}}, so it is yours to send. Anybody you name here can send it too.',
        sharersIntroAdmin: '{{owner}} uploaded {{code}}. You can send it as an administrator, and so can anybody named here.',
        sharersNone: 'Nobody else. Only {{owner}} and the administrators can send this document.',
        sharersAdd: 'Add somebody',
        sharersPick: 'Choose people…',
        sharersFind: 'Type a name or an address…',
        sharersNoMatch: 'Nobody matches that.',
        sharersAddN: 'Give sharing ({{count}})',
        sharersSaved: 'Saved who may share.',
        sharersUploader: 'Uploader',
        cannotShare: 'Only {{owner}} and the administrators can send this document.',
        sendNote: 'Note (optional — defaults to the change note)',
        send: 'Send notification',
        sending: 'Sending…',
        sentTo: 'Sent to {{count}} recipient(s).',
        remove: 'Remove',
        edit: 'Edit',
        save: 'Save',
        saving: 'Saving…',
        savedMsg: 'Saved.',
        noteOfVersion: 'The change note belongs to {{version}}, the version on the list right now.',
        comments: 'Comments',
        noComments: 'No comment yet. The first question is usually one somebody else also has.',
        commentPlaceholder: 'Ask about this document, or answer somebody…',
        commentHint: 'Everyone who can open this document sees your comment. Ctrl+Enter to post.',
        post: 'Post',
        posting: 'Posting…',
        commentGone: 'This comment was deleted.',
        deleteComment: 'Delete',
        deleteCommentTitle: 'Delete this comment?',
        deleteCommentBody: 'It disappears for everybody. The place it held in the conversation stays, marked as deleted.',
        removeTitle: 'Remove this document?',
        removeConfirm: 'It leaves everybody\'s list and the link stops opening. The read record is kept, and the code is never given to another document.',
        removedMsg: '{{code}} was removed from the library.',
        cancel: 'Cancel',
        removed: 'Removed.',
      },
      home: {
        morning: 'Good morning',
        afternoon: 'Good afternoon',
        evening: 'Good evening',
        myWork: 'My work — {{role}}',
        notLinkedToEmployee: 'This portal account is not linked to an employee record yet, so no work can be matched to you. Ask an admin to link it.',
        notLinkedToSalesRep: 'This account is not linked to a Sales Rep yet, so your projects cannot be matched to you. Ask an admin to link it.',
        targetLaunch: 'Target launching month',
        // Acknowledging a rejected project on the home page — Jay Jung, 2026-09-14.
        ack: 'Got it',
        ackTitle: 'Confirm you have seen this rejection — it will stop appearing on this card',
        lateThreshold: 'Late after (days)',
        saved: 'Saved',
        lateThresholdRange: '1-365',
        save: 'Save',
        ackUndo: 'Put back',
        seenCount: '{{n}} acknowledged — show',
        viewAs: 'Viewing',
        allOf: 'Everyone — {{role}}',
        viewAll: 'Everyone',
        repNoAccount: 'not linked to an account',
        openThisList: 'Open this list in Sample Tracker',
        andMore: '+{{n}} more',
        workload: {
            title: 'Workload by week',
            sub: 'One snapshot every Monday — what each PIC was carrying that morning',
            rounds: 'Active rounds',
            projects: 'Active projects',
            pic: 'PIC',
            change: 'vs last week',
            total: 'Total',
            reconstructed: 'This week was not counted on that Monday morning — it was filled in afterwards.',
            cellRounds: 'In production {{prod}} · pending outcome {{pending}} · RM waiting {{wait}}',
            note: 'Active rounds = In Production + Pending Outcome. A week marked * was not counted on that Monday — it was filled in afterwards.',
        },
        noModules: 'No modules have been granted to your account yet. Ask an administrator for access.',
      },
      tc: {
        colNo: 'No.',
        colAction: 'Action',
        stateActive: 'Active',
        stateInactive: 'Inactive',
        title: 'Ticket Codes',
        subtitle: 'The master list behind the ticket code dropdown. {{n}} codes.',
        readOnly: '— read-only for your role.',
        newCode: 'New code',
        exportExcel: 'Export Excel',
        searchPh: 'Search code, name, phase, team…',
        showInactive: 'Show inactive',
        clearTools: 'Clear sort & filter',
        countOf: '{{n}} of {{total}}',
        loading: 'Loading…',
        saving: 'Saving…',
        btnEdit: 'Edit',
        btnView: 'View',
        btnViewHint: 'View this code (read only)',
        btnClose: 'Close',
        formView: 'Ticket code {{code}}',
        viewOnly: 'View only',
        btnSave: 'Save',
        btnCancel: 'Cancel',
        formNew: 'New ticket code',
        formEdit: 'Edit {{code}}',
        fCode: 'Code',
        fTeam: 'Team',
        fCase: 'Case',
        fCategory: 'Category',
        fName: 'Ticket Name',
        fPhase: 'Phase',
        fAssignedTo: 'Assigned To',
        fGroupEmail: 'Group Email',
        fNote: 'Note',
        fActive: 'Active — an inactive code stays on the tickets that already use it, but is no longer offered on the New Ticket form',
        phCase: 'A / B / blank',
        phName: 'Sample development request',
        phTeamSearch: 'Search team…',
        everyCategory: 'Every category',
        created: '{{code}} created.',
        saved: '{{code}} saved.',
        nothingToExport: 'Nothing to export',
        preparingExport: 'Preparing Excel export…',
        exported: 'Exported {{n}} codes',
        exportFailed: 'Export failed',
        exportInclInactive: 'Including inactive codes',
        exportActiveOnly: 'Active codes only',
      },
      pa: {
        title: 'Project Approval',
        subtitle: 'A new project stays Pending until it is approved here. Tickets cannot be issued on a project that is not approved.',
        tabPending: 'Pending',
        tabApproved: 'Approved',
        tabRejected: 'Rejected',
        tabSettings: 'Settings',
        loading: 'Loading…',
        saving: 'Saving…',
        working: 'Working…',
        countAll: '{{n}} projects',
        countOf: '{{n}} of {{total}} projects',
        chartPick: 'Chart: {{label}} · {{n}} projects',
        chartPickClear: 'Show every project again',
        allTeams: 'All teams',
        scopeHint: "Which team's projects to show",
        clearTools: 'Clear sort & filter',
        searchPh: 'Search project, SKU, team, owner…',
        rejectedChip: 'Rejected · {{n}} SKU',
        rejectedChipHint: '{{n}} SKU sent back by the approver — fix them and press Resubmit.',
        selected: '{{n}} selected',
        decideSelected: 'Decide selected',
        clearSelection: 'Clear selection',
        emptyFiltered: 'No project matches the search or the column filters.',
        emptyPending: 'Nothing waiting for approval.',
        emptyTab: 'No {{tab}} projects.',
        colNo: 'No.',
        colProjectId: 'Project Group ID',
        colProjectName: 'Project Name',
        colTeam: 'Team',
        colManager: 'Manager',
        colOwner: 'Owner',
        colSalesRep: 'Sales Rep.',
        colCategory: 'Category',
        colSeason: 'Season',
        colPartner: 'Partner / Line',
        colTargetLaunch: 'Target Launch',
        colSkus: 'SKUs',
        colRequestedBy: 'Requested by',
        colRequestedAt: 'Requested at',
        colManagerNote: 'Manager note',
        colApprovedBy: 'Approved by',
        colApprovedAt: 'Approved at',
        colApprovedOpinion: 'Approved Opinion',
        colRejectedBy: 'Rejected by',
        colRejectedAt: 'Rejected at',
        colReason: 'Reason',
        colAction: 'Action',
        btnView: 'View',
        btnApprove: 'Approve',
        btnEdit: 'Edit',
        btnResubmit: 'Resubmit',
        btnCancel: 'Cancel',
        btnClose: 'Close',
        btnSaveChanges: 'Save changes',
        btnApproveN: 'Approve {{n}}',
        btnRejectN: 'Reject {{n}}',
        notYourTeam: 'This project belongs to another team — only its project owner, that team\u2019s manager or an admin can change it.',
        dlgResubmitTitle: 'Resubmit {{id}}',
        dlgDecisionOne: 'Decision on {{id}}',
        dlgDecisionBulk: 'Decision on {{n}} projects',
        dlgStatus: 'Approval Status',
        dlgAgree: 'Agree',
        dlgReject: 'Reject',
        skuToggle: 'Show the {{n}} SKU in this project',
        skuNone: 'This project has no SKU yet.',
        skuId: 'SKU',
        skuItem: 'Item',
        skuCategory: 'Category',
        skuQty: 'Qty',
        skuPrice: 'Target',
        skuExf: 'EXF',
        skuPic: 'Developer / Designer',
        skuStatus: 'Stage',
        skuTba: 'TBA',
        skuState: 'Approval',
        skuNote: 'Approver note',
        skuSt_unsent: 'Not sent yet',
        skuSt_waiting: 'Waiting',
        skuSt_approved: 'Approved',
        skuSt_rejected: 'Not approved',
        skuPicked: '{{n}} SKU selected',
        skuWithApprover: 'Already sent — this one is the approver\u2019s to decide.',
        skuNotYours: 'Not sent yet — the requesting team sends this one.',
        skuDoneAlready: 'Already decided.',
        skuNoneYours: 'Nothing here is waiting on you.',
        submitPickFirst: 'Tick the SKUs to send, or tick the project to send all of them.',
        skuNotRefused: 'Not refused — there is nothing here to send back.',
        resubmitSome: 'Resubmit {{n}}',
        dlgBackHead: 'What changed on each SKU ({{n}} going back)',
        dlgBackWas: 'Not approved',
        dlgBackWhy: 'What changed on this SKU',
        skuMgrNote: 'Manager note',
        submitSome: 'Submit {{n}}',
        submitSomeHint: 'Send only the {{n}} SKU ticked below; the rest stay where they are.',
        approveSome: 'Decide {{n}}',
        approveSomeHint: 'Decide only the {{n}} SKU ticked below.',
        dlgSkuHead: 'Decide each SKU ({{n}} waiting)',
        dlgAllApprove: 'Approve all',
        dlgAllReject: 'Reject all',
        dlgSkuWhy: 'Why this SKU is not approved',
        dlgSkuDefault: 'Every SKU here is approved unless you mark it ✕.',
        dlgSkuYes: 'Approve',
        dlgSkuNo: 'Not approved',
        dlgSkuUndo: 'Put this SKU back to approved',
        dlgWasApproved: 'approved earlier',
        dlgWasRejected: 'rejected earlier',
        btnApproveSkus: 'Approve {{n}} SKU',
        btnRejectSkus: 'Reject {{n}} SKU',
        btnPartial: 'Approve {{yes}}, reject {{no}}',
        stPartial: 'Partially approved',
        dlgUnchangedTitle: 'Nothing has been edited since the rejection.',
        dlgUnchangedBody: 'Either fix what the approver asked for (Edit on the row), or explain below why it should be approved as it stands — a note is required to send it back unchanged.',
        dlgManagerOpinion: 'Manager Opinion',
        dlgApproverOpinion: 'Approver Opinion',
        dlgPhReject: 'Why is this project not approved? The team sees this.',
        dlgPhResubmitRequired: 'Required — why should this be approved without changes?',
        dlgPhResubmit: 'What changed since the rejection? (optional)',
        dlgPhOptional: 'Enter content (optional)',
        secProject: 'Project Information',
        secSku: 'SKU Information',
        fTeam: 'Team',
        fOwner: 'Project Owner (Sales OP)',
        fCategory: 'Category',
        fSeason: 'Season',
        fPartner: 'Partner / Line',
        fKeyword: 'Keyword',
        fTargetLaunch: 'Target Launching (month)',
        fCase: 'Case',
        fEntity: 'Entity',
        selectTeam: 'Select team…',
        selectOwner: 'Select owner…',
        pickTeamFirst: 'Pick a team first',
        noMember: 'No member on this team yet — set it in List Users',
        addSku: '+ Add SKU',
        noSkuYet: 'No SKU yet — that is fine. SKUs can be added at any time.',
        skuSaveHint: 'New SKUs are created when you save. Deleting an existing SKU happens immediately, not on save.',
        fillRequired: 'Fill in the required fields first:',
        couldNotSave: 'Could not save:',
        rejectedBy: 'Rejected by {{who}}:',
        managerNoteLabel: 'Manager note:',
        submit: 'Submit',
        submitSelected: 'Submit selected to approver',
        readyToSubmit: '{{n}} of {{total}} ready to submit',
        nothingToSubmit: 'Nothing ticked that can be submitted — already sent, or another team.',
        sent: 'Sent',
        submittedOn: 'Submitted to the approver {{when}}',
        remind: 'Remind',
        remindHint: 'Sent {{when}} · {{n}} SKU still waiting on the approver. Send a reminder.',
        remindSent: 'Reminder sent to {{to}} — {{n}} SKU still waiting',
        lockedSubmitted: 'Already submitted — the approver is holding it, and the mail he received still shows the values as they were sent.',
        // Deleting a whole project. Two phrasings per count rather than "SKU(s)" — the screen is
        // read by people deciding whether to throw work away, so it should read like a sentence.
        drop: 'Delete',
        dropTitle: 'Delete this project',
        dropSubmitted: 'Already submitted — it cannot be deleted.',
        dropHead: 'Delete {{id}}?',
        dropChecking: 'Checking what belongs to this project…',
        dropWarnOne: '1 SKU will be removed. Sample rounds are kept — they stay in Sample Tracker without a project. This cannot be undone.',
        dropWarnMany: '{{n}} SKUs will be removed. Sample rounds are kept — they stay in Sample Tracker without a project. This cannot be undone.',
        dropTicketsOne: '1 ticket on this project will be cancelled, and the receiving team told:',
        dropTicketsMany: '{{n}} tickets on this project will be cancelled, and the receiving team told:',
        dropEmailed: 'already emailed',
        dropReason: 'Why is it being cancelled? (goes in the mail)',
        dropConfirm: 'Delete project',
        dropBusy: 'Deleting…',
        approverNoteLabel: 'Approver note:',
        setTitle: 'Approval email',
        setNote: 'Where the “project needs approval” mail goes. Change it here — no deploy needed.',
        setApprover: 'Approver (To)',
        setCc: 'CC (comma separated)',
        setGate: 'Approval gate is on (new projects start Pending and tickets are blocked)',
        setSave: 'Save approval settings',
        setSaved: 'Approval settings saved.',
      },
      tour: {
        title: 'Screen guide',
        ciTabsT: 'One tab per template',
        ciTabsB: 'Template 1 is the Weekly Cash Report, Template 2 the 13 Weeks Cash Plan, Template 3 the Month-End Reconcile. Pick the one you are submitting.',
        ciPeriodT: 'Your entity and the period',
        ciPeriodB: 'The entity follows you: it comes from your Preparer row on the Cash Report User list, and a dropdown appears only if you prepare more than one. The arrows move to the week (or month) before or after; the line beside them says when the report is due.',
        ciStepsT: 'Where this report stands',
        ciStepsB: 'Preparer → Reviewer. A step is lit once it has been done; once the Reviewer agrees, the report\'s figures show in the Cash Report.',
        ciTemplateT: 'Download the template',
        ciTemplateB: 'The Excel template for this entity and period, with its accounts already written in. Fill it in and import it — figures are not typed on the Portal.',
        ciImportT: 'Import the file',
        ciImportB: 'Import the filled template. It becomes a version: the Data Check runs and its figures show below. A wrong file can be cancelled and the corrected one imported again.', ciFormT: 'Correct it here', ciFormB: 'Once the template is imported, its figures open here. Correct a figure and Save: it becomes the next version, through the same Data Check — no need to import the file again.',
        ciCheckT: 'The Data Check',
        ciCheckB: 'Every version is checked. An error must be fixed before the report can be sent; a warning goes along for the Reviewer to read.',
        ciSendT: 'Send it to the Reviewer',
        ciSendB: 'Add a note if the Reviewer should know something, then send. A sent report cannot be changed; if it is sent back, you correct it here and send it again.',
        ciFilesT: 'Bank statements and screenshots',
        ciFilesB: 'Attach each account\'s statement or balance screenshot. Everyone who reviews or approves the report sees them.',
        ciHistoryT: 'History and comments',
        ciHistoryB: 'Every import, entry, send and decision is listed here with who did it and when. Write a comment to ask or answer a question about this report.',
        ciTestAsT: 'Test as',
        ciTestAsB: 'For testing only: while Cash mail is held, play each role to try the whole flow. Every step taken is recorded as "test as …".',
        cqTabsT: 'Three tabs',
        cqTabsB: 'Waiting for you holds the reports waiting on your review, oldest first. In progress holds the ones with another Reviewer, and the ones sent back to the Preparer with the reason. Agreed holds the reports the Reviewer has agreed — those are the ones the Cash Report shows.',
        cqListT: 'Every report sent, in one list',
        cqListB: 'Weekly Cash Report, 13 Weeks Cash Plan and Month-End Reconcile, every entity and period. Each header sorts and filters like Excel. Click a row to open that report below; click it again to close it.',
        cqDecideT: 'Your decision',
        cqDecideB: 'Review & agree is the last step: once you agree, the report\'s figures show in the Cash Report. The button opens a box where you agree, or send the report back to the Preparer with a reason. An agreed report can be reopened here for correction, with a reason. A report sent back waits under In progress until it is sent again.',
        cqCheckT: 'The Data Check',
        cqCheckB: 'Errors would have stopped the Preparer sending, so a sent report has none. Warnings went along for you to read before you decide.',
        cqBodyT: 'What was imported',
        cqBodyB: 'The figures exactly as the Preparer imported them from the template — Fields 1 to 6 of the weekly report, the 13 weeks of the plan, or each account\'s reconcile. Nothing here can be changed; a wrong figure is sent back.',
        cqFilesT: 'Bank statements and screenshots',
        cqFilesB: 'What the Preparer attached for each account. Open them to check the figures against the bank.',
        cqHistoryT: 'History and comments',
        cqHistoryB: 'Who imported, sent and decided, and when. Write a comment to ask the Preparer a question without sending the report back.',
        // Courier Requests / Courier Approval (Max, 2026-10-19).
        crNewT: 'Start a request',
        crNewB: 'Who the goods are for, the courier and the Helinox account billed, from and to, the weight, your estimated cost and the shipping term. The cost check beside the form compares your estimate with HINC\'s FedEx contract rate as you type. Save draft keeps a half-filled form; Send for approval sends it to the approver.',
        crListT: 'Your requests',
        crListB: 'One row per request. The first five columns stay put while the rest scroll sideways. Click a row to read the request as the form it was sent as.',
        crCheckT: 'The cost check',
        crCheckB: 'Your estimate against the contract rate: Reasonable (up to 15% above), Review (up to 35%) or Potentially high. A lane or courier the contract does not cover is checked by hand.',
        crStatusT: 'Where it stands',
        crStatusB: 'Draft (not sent), Waiting (with the approver), Approved, Rejected or Withdrawn.',
        crNoteT: 'Manager note',
        crNoteB: 'What the approver wrote with the decision. Click it to read all of it.',
        crActT: 'What you can do',
        crActB: 'View · Edit (a draft, a rejected or withdrawn request, or one still waiting — saving it tells the approver what changed) · Submit a draft · Duplicate into a new request · Delete anything not approved. A greyed-out button says why on its hover.',
        crqTabsT: 'Three tabs',
        crqTabsB: 'Waiting for decision is your queue; Approved and Rejected keep what was decided.',
        crqListT: 'The requests',
        crqListB: 'Who asked, from which entity, the route and the courier. The first five columns stay put. Click a row to read the request with its cost check.',
        crqNoteT: 'Manager note',
        crqNoteB: 'What you wrote with the decision; the requester sees it on their list too.',
        crqActT: 'View · Edit · Approve',
        crqActB: 'Edit corrects a request that is waiting — it stays waiting and the requester is mailed what changed. Approve opens the decision; Reject is inside it and needs a reason.',
        crqRatesT: 'Rates',
        crqRatesB: 'The FedEx contract rate file the cost check reads, its upload, the fuel surcharge and the two thresholds.',
        crTabsT: 'Three reports',
        crTabsB: 'The Weekly Cash Report, the 13 Weeks Cash Plan and the Month-End Reconcile. Only figures the Reviewer has agreed are shown here; a report still with its Preparer or its Reviewer is a line saying where it is.',
        crPeriodT: 'The period',
        crPeriodB: 'Pick a week (or a month on the Month-End tab); the arrows move to the one before or after. The line beside it says when the report is due.',
        crChartsT: 'The charts',
        crChartsWB: 'Cash by entity, split into Available, Restricted and Fixed deposit, and the GROUP\'s cash over the last 12 weeks. Hover a bar or a point for its figures.',
        crChartsPB: 'Plan inflow up, plan outflow down; the dashed line is the plan closing, the solid one the actual closing from the Weekly Cash Report.',
        crChartsMB: 'Month-end cash by entity, and how many accounts reconcile and how many carry a difference.',
        crWeekT: 'GROUP, then each entity',
        crWeekB: 'The first row is the GROUP; each entity follows in SGD. Click an entity\'s row to open its intercompany row and its accounts — the Bank, Account No., Type of account and Restricted / Fixed deposit columns appear then, and each account is shown in its own currency, as uploaded.',
        crExpandT: 'Open or fold every entity',
        crExpandB: 'Expand all opens every entity into its accounts here and its Top 3 below; Collapse all folds them again.',
        crTopT: 'Top 3',
        crTopB: 'Each entity\'s three key spendings this week and three payables next week, in SGD. An entity that has not uploaded is listed too, so nobody is missed.',
        crFiltersT: 'The two filters',
        crFiltersB: 'Entities: GROUP, one entity, or several ticked together (they add up). Year: which year of weeks to show. The chart and the table below follow both.',
        crPlanTableT: 'Every week of the year',
        crPlanTableB: 'One row per week. A week gone by reads from the plan filed for it, a week to come from the newest plan; the From plan column names it. Actuals come from the Weekly Cash Report once it is agreed. The week on screen is lit, and every header sorts and filters like Excel.',
        crPlanListT: 'Who has planned this week',
        crPlanListB: 'Each entity\'s plan for the week on screen: its plan closing at week 13 and where the plan stands.',
        crMonthT: 'The reconcile',
        crMonthB: 'Each entity\'s line carries its month-end closing in SGD and who prepared and reviewed it; its accounts follow, in their own currency. Reconcile must be 0 on every account.',
        crWaitingT: 'Waiting for you',
        crWaitingB: 'A report whose next step is yours carries this tag. Click it to open the report in Approval, where you agree to it or send it back.',
        step: 'Step {{n}} of {{of}}',
        next: 'Next',
        back: 'Back',
        done: 'Done',
        skip: 'Skip',
        stCreateT: 'Start a new sample here',
        stCreateB: 'Pick the project SKU the sample is for. Only SKUs that do not have one yet are listed, because a SKU carries exactly one sample. You no longer need to go to Project Tracker for this.',
        stRoundT: 'Add a round to a sample that exists',
        stRoundB: 'P1, P2, P3 and so on. Type the Sample ID, the Base ID or the style name to find the sample first — everything else opens once you have picked it.',
        stTrialT: 'A sample with no project behind it',
        stTrialB: 'The exception, for work that has not become a project SKU yet. It gets a TSMP number instead of an SMP one.',
        stCardsT: 'The cards are the filter',
        stCardsB: 'Click one and the table below shows only those. Click it again to clear. The top row counts samples, the row under it counts rounds.',
        stFiltersT: 'Narrow it further',
        stFiltersB: 'By developer, designer or R&D location, or type into the search box. Clear puts everything back, including the funnels on the column headers.',
        stRowT: 'A sample opens into its rounds',
        stRowB: 'Click the arrow on any row to see every round of that Base ID. Expand All opens them at once.',
        stExportT: 'Take it to Excel',
        stExportB: 'Exports exactly what the table is showing, filters and all.',
      },
      st: {
        tabAll: 'All Samples',
        tabSummary: 'Sample-level Summary',
        // One list, three pills over it. Both is the default — it is the state in which the tab's
        // count and the table under it agree.
        tagTrial: 'Trial',
        pillProject: 'Project Samples (SMP)',
        pillTrial: 'Trial Samples (TSMP)',
        pillBoth: 'Both',
        pillCtrlHint: 'Ctrl / \u2318 + click to show this one together with the other',
        hintTrial: 'One-off samples with no project SKU behind them. Their Base ID is TSMP, not SMP.',
        titleTracker: 'Sample Tracker',
        titleSummary: 'Sample-level Summary',
        hintTracker: 'Click a card to update the table - click a sample row to expand rounds',
        hintSummary: 'One row per Base ID / product with latest schedule and round status',
        groupSample: 'Sample / Base ID',
        groupRounds: 'Rounds / Workflow',
        totalSamples: 'Total Samples',
        totalSamplesSub: 'All status sample records',
        inProgressSamples: 'In Progress Samples',
        confirmedSamples: 'Confirmed Samples',
        reworkSamples: 'Rework Samples',
        droppedSamples: 'Dropped Samples',
        onHoldSamples: 'On Hold Samples',
        totalRounds: 'Total Rounds',
        totalRoundsSub: 'All status round records',
        inProgressRounds: 'In Progress Rounds',
        confirmedRounds: 'Confirmed Rounds',
        reworkRounds: 'Rework Rounds',
        droppedRounds: 'Dropped Rounds',
        onHoldRounds: 'On Hold Rounds',
        pendingOutcome: 'Pending Outcome',
        pendingOutcomeSub: 'Finish date, no outcome',
        rmWaiting: 'RM Waiting',
        rmWaitingSub: 'Material not ready',
        inProduction: 'In Production',
        inProductionSub: 'RM ready, not finished',
        allRounds: 'All Rounds',
        allDevelopers: 'All Developers',
        allDesigners: 'All Designers',
        allLocations: 'All R&D Locations',
        allStatus: 'All Status',
        searchPlaceholder: 'Search ID or item...',
        clear: 'Clear',
        newRound: '+ New Round',
        csButton: '+ Create Sample',
        tourButton: 'Guide',
        tourReopen: 'Walk me through this screen',
        csTitle: 'Create a sample',
        csIntro: 'Pick the project SKU this sample is for. Only SKUs without one are listed — a SKU carries exactly one sample.',
        csSearch: 'Search project, SKU code or style…',
        csCount: '{{skus}} SKU in {{projects}} project(s)',
        csNone: 'Every live SKU already has a sample.',
        csNoMatch: 'Nothing here matches that.',
        csMore: 'and {{n}} more — narrow the search to see them',
        // The exception route: a sample with no project behind it. Its Base ID is TSMP, not SMP.
        trButton: '+ Trial Sample (Non-Project)',
        trTitleNew: 'New Trial Sample',
        trAddNewSample: 'Add New Sample',
        trTitle: 'Trial sample — no project',
        trSub: 'For a one-off with no project SKU behind it. The Base ID is issued as TSMP, not SMP.',
        trStylePh: 'Type the style / item name',
        trStyleHint: 'Typed above — a trial sample has no project SKU to take it from',
        expandAll: 'Expand All',
        collapseAll: 'Collapse All',
        exportExcel: 'Export Excel',
        backToBase: 'Back to Base ID view',
        colBaseId: 'Base ID',
        colStyleItem: 'Style / Item',
        colRound: 'Round',
        colSeason: 'Season',
        colLocation: 'R&D Location',
        colDeveloper: 'Developer',
        colDesigner: 'Designer',
        colProjectId: 'Project SKU ID',
        colRequestDate: 'Request Date',
        colDevelopmentDue: 'Development Due',
        colRmReady: 'RM Ready',
        colRoundDue: 'Round Due',
        colSampleFinish: 'Sample Finish Date',
        colReceived: 'Received',
        colComment: 'Comment',
        colQty: 'Qty',
        colRounds: 'Rounds',
        colStatus: 'Status',
        colDday: 'D-day',
        colDdayHint: 'D-DAY = today − Development Due. A positive value means the sample is overdue by that many days; a negative value means that many days remain.',
        colDaysWaiting: 'Days Waiting',
        goToProject: 'Open this SKU in Project Tracker',
        colRemarks: 'Remarks',
        colActions: 'Actions',
        colRework: 'Rework',
        ptNo: 'No.',
        ptId: 'ID',
        ptProjectItem: 'Project / Item',
        ptNotYours: 'Only the project owner, the team manager or an admin can change this project.',
        ptNotYourSku: 'Only the developer this SKU is assigned to, the project owner or the team manager can change it.',
        ptDeleteNotYours: 'Only the project owner, the team manager or an admin can delete a SKU.',
        ptCompletedLocked: 'This SKU is Complete and Production has been notified — it can no longer be edited or deleted.',
        ptTicketNotYours: 'Only the project owner, the team manager or an admin can raise tickets on this project.',
        ptTeam: 'Team',
        ptOwner: 'Project Owner',
        ptCategory: 'Category',
        ptCase: 'Case',
        ptEntity: 'Entity',
        ptItemCategory: 'Item Category',
        ptFrameColor: 'Frame Color',
        ptQty: 'QTY',
        ptAssigned: 'Assigned',
        ptAllocation: 'Allocation',
        ptStatus: 'Status',
        ptSalesRep: 'Sales Rep',
        ptWhere: 'Project Status',
        ptWhereComplete: 'Completed',
        ptWhereDropped: 'Dropped',
        ptWhereActive: 'Active',
        ptAiHead: 'Claude summary',
        ptAiWriting: 'Summarising…',
        ptStart: 'Start',
        ptExfDate: 'EXF Date',
        ptTargetPrice: 'Target Price (USD)',
        ptLaunchDate: 'Launching Date',
        ptWeeksLeft: 'Weeks Left',
        ptProgress: 'Progress',
        ptRemark: 'Remark',
        ptSampleId: 'Sample ID',
        ptAction: 'Action',
        ptTitle: 'Project Tracker',
        ptHint: 'Manage and track all development projects',
        ptGroups: 'Project Groups',
        ptProjectComplete: 'Completed Projects',
        ptProjectCompleteSub: 'every SKU Complete (or Dropped)',
        ptProjectDropped: 'Dropped Projects',
        ptProjectDroppedSub: 'every SKU is Dropped',
        ptProjectActive: 'Active Projects',
        ptProjectActiveSub: 'still has an unfinished SKU',
        ptSkus: 'Project SKUs',
        ptComplete: 'SKU Complete',
        ptDrop: 'SKU Dropped',
        ptActive: 'SKU Active',
        ptPriority: 'SKU Priority',
        ptViewProject: 'View this project (read only)',
        ptViewSku: 'View this SKU (read only)',
        ptViewTag: 'View only',
        rmTitleView: 'Sample round',
        viewRound: 'View this round (read only)',
        viewBase: 'View this Base ID (read only)',
        tkView: 'View this ticket (read only)',
        tkTitleView: 'Ticket',
        viewClose: 'Close',
        ptViewHint: 'Read only — nothing here can be changed or saved.',
        ptViewClose: 'Close',
        ptOnTrack: 'SKU On Track',
        ptConcern: 'SKU Concern',
        ptDelayed: 'SKU Delayed',
        ptOnHold: 'SKU On Hold',
        ptShipFinished: 'SKU Ship Finished',
        ptAllTeams: 'All Teams',
        ptAllOwners: 'All Owners',
        ptByMember: 'By member',
        ptByMemberTitle: 'Workload by Project Owner',
        ptByMemberHint: 'Click a row to filter the tracker to that owner. Counts follow the filters above.',
        ptOverdueExf: 'Overdue EXF',
        ptAllCategories: 'All Categories',
        ptAllAssignees: 'All Assignees',
        ptAllAllocation: 'All Allocation',
        ptSearch: 'Search project / item...',
        ptExpandAll: 'Expand all',
        ptCollapseAll: 'Collapse all',
        ptColumns: 'Columns',
        ptReport: 'Report',
        ptNewProject: '+ New Project',
        ptExpand: 'Expand',
        ptCollapse: 'Collapse',
        ptNoProjects: 'No projects found.',
        // Process stage column headers. Shared by the project grid, the column picker and the three stage
        // grids inside the project forms, so they live here rather than beside stageColumns.
        stgLdSoff: 'L/D S/OFF',
        stgProto: 'Proto',
        stgCounter: 'Counter',
        stgCosting: 'Costing',
        stgHandOffMeeting: 'Hand-off Meeting',
        stgPpsPpm: 'PPS/PPM',
        stgProduction: 'Production',
        stgFinalInspection: 'Final Inspection',
        stgExfShipment: 'Shipment Status',
        stgPayment: 'Payment',
        stgPoStatus: 'PO Status',
        // New / Edit Project form
        pfNewProject: 'New Project',
        pfProjectInfo: 'Project Information',
        pfProjectInfoHint: 'Basic details, schedule, status, stages, and linked samples',
        pfCategory: 'Category',
        pfSelectCategory: 'Select category...',
        dgpTitle: 'Delete project {{id}}?',
        dgpChecking: 'Checking what belongs to this project…',
        dgpSkus: '{{n}} SKU(s) will be removed.',
        dgpRounds: 'Sample rounds are NOT deleted — they stay in Sample Tracker without a project link.',
        dgpTickets: '{{n}} ticket(s) will be cancelled, and the teams holding them will be told.',
        dgpUndo: 'This cannot be undone from the portal.',
        dgpConfirm: 'Delete project',
        dt1Title: 'Delete ticket {{id}}?',
        dt1Emailed: 'This ticket was already emailed to {{to}}. Deleting it cancels the request, and they will be told — with the reason below.',
        dt1NotEmailed: 'This ticket was never emailed, so nobody outside the portal knows about it. Deleting it notifies no one.',
        dt1ReasonHint: 'This line is what the receiving team reads to know why their request disappeared.',
        dt1Confirm: 'Delete ticket',
        dt1ConfirmMail: 'Delete and notify',
        dtTitle: 'Delete {{id}} — it has tickets',
        dtLead: 'These {{n}} ticket(s) belong to this project. Deleting it cancels them, and the teams that received them will be told:',
        dtNeverEmailed: 'never emailed',
        dtReason: 'Why is it being deleted?',
        dtReasonPh: 'e.g. the partner cancelled the collaboration',
        dtReasonHint: 'This line goes into the cancellation email — it is what the receiving team reads to know why their request disappeared.',
        dtConfirm: 'Delete and cancel the tickets',
        gmSkuOnlyHint: 'Your team may edit the SKU section only — the project details above belong to Sales OP.',
        pfReadOnlyHere: 'Read-only here — edit it from Edit Project Group',
        bmHint: 'What belongs to the Base ID itself, not to one round: its photos, and the style name every round of it repeats ({{rounds}} round(s) here).',
        bmStyleHint: 'Renaming applies to every round of this Base ID in one write.',
        bmRename: 'Rename style',
        rmBasePhotos: 'Sample photos (Base ID)',
        rmPhotoRequired: 'Required',
        rmPhotoNewBase: 'This style is new, so saving creates a new Base ID — it needs at least one photo of the sample. The photo belongs to the Base ID, so later rounds will not ask again.',
        rmPhotoExistingBase: 'This style already exists as {{baseId}}, which has its own photos. Nothing to add here.',
        rmPhotoNoStyle: 'Type the Style / Item first. A new style creates a new Base ID, which needs a photo.',
        pfPurpose: 'Project Purpose',
        pfPurposePh: 'why we are doing it, and where it will be sold.',
        pfPurposeHint: 'Sales OP Manager must check with the Sales rep. The CEO reads this line to decide go / no-go.',
        pfNote: 'Note',
        pfTeam: 'Team',
        pfOwner: 'Project Owner (Sales OP)',
        pfHandover: 'You are handing this project to {{who}}. Once you save, you will not be able to edit it any more \u2014 only {{who}}, {{mgr}} or an admin can.',
        pfHandoverMgr: 'the team manager',
        pfYear: 'Year',
        pfYearPick: 'Select year…',
        pfSeasonParts: 'Season',
        pfSeasonMax2: 'Optional — pick up to two',
        pfSeasonCode: 'Project code: {{code}}',
        seasonSpring: 'Spring',
        seasonSummer: 'Summer',
        seasonFall: 'Fall',
        seasonWinter: 'Winter',
        pfSeason: 'Season',
        pfPartner: 'Partner / Line',
        pfKeyword: 'Keyword',
        pfTargetLaunch: 'Target Launching (month)',
        pfNameHint: 'Built by the system. The PRJ-ID stays the permanent key; this is what people read.',
        pfSelectTeam: 'Select team…',
        pfSelectOwner: 'Select owner…',
        pfPickTeamFirst: 'Pick a team first',
        gmTitle: 'Edit Project Group',
        gmSaveGroup: 'Save Group',
        gmSaveTickets: 'Save Tickets',
        pfProjectName: 'Project Name',
        pfProjectNamePh: 'e.g. 25 PARTICULIER',
        pfCase: 'Case',
        pfSelectCase: 'Select case...',
        pfEntity: 'Entity',
        pfSelectEntity: 'Select entity...',
        pfSkuInfo: 'SKU Information',
        pfAddSku: '+ Add SKU',
        // Pulling approved SKUs in from another project (MoveSkusIn.jsx, 2026-10-12, Tina / Jay).
        mvBtn: 'Move SKUs from a project',
        mvBtnTitle: 'Bring approved SKUs over from another project of this team — they keep their approval',
        mvNeedTeam: 'Choose the project\u2019s team first',
        mvNotManager: 'Only an admin or the manager of this team can move SKUs',
        mvHead: 'Move approved SKUs into this project',
        mvIntro: 'Pick a project of the same team and tick its approved SKUs. They move when you save this form, keeping their SKU ID, their approval, their samples and stages — nothing goes back to the CEO. Tickets already raised stay on the project they came from. The developer, designer and project owners get a mail.',
        mvSource: 'Take them from',
        mvSearch: 'Search a project or a SKU',
        mvSearchPh: 'PRJ number, project name, SKU number or item name…',
        mvNoMatch: 'Nothing matches — try a shorter word.',
        mvPicked: '{{n}} ticked',
        mvChoose: 'Choose a project…',
        mvLoading: 'Loading…',
        mvApprovedN: '{{n}} approved',
        mvNone: 'No other project of this team has an approved SKU.',
        mvAllTaken: 'Every approved SKU of this project is already picked.',
        mvAll: 'Tick them all',
        mvColItem: 'Item', mvColApproved: 'Approved', mvColDeveloper: 'Developer', mvColDesigner: 'Designer',
        mvCancel: 'Cancel',
        mvAdd: 'Add {{n}} SKU(s)',
        mvListHead: 'Moving in on Save — {{n}} SKU(s)',
        mvFrom: 'from {{id}}',
        mvApprovedBy: 'approved by {{who}}',
        mvRemove: 'Do not move this one',
        mvSaveHint: 'Nothing has moved yet: these SKUs move into this project when you save the form.',
        mvTeamChanged: 'These SKUs come from another team\u2019s project than the one chosen above — remove them or switch the team back.',
        pfSkuHint: 'Each SKU is saved as its own PRJ item under one PG project. Expand a SKU to edit its information, initial stages, and linked sample.',
        pfUnassigned: 'Unassigned',
        pfNoCategory: 'No category',
        pfQty: 'QTY',
        pfStageChip: 'stage',
        pfRemoveSku: 'Remove SKU',
        pfDuplicateSku: 'Duplicate this SKU',
        pfApprovalNoteHead: 'Saved as Pending Approval — not sent to the approver yet.',
        pfApprovalNoteBody: 'Open Project Approval and press Submit to send the request. Tickets can be issued once it is approved.',
        pfDeveloper: 'Developer',
        pfDesigner: 'Designer',
        pfNoDesigner: 'No designer',
        ptDesigner: 'Designer',
        ptAllSalesReps: 'All Sales Reps',
        ptAllDesigners: 'All Designers',
        pfAssignedTo: 'Assigned To',
        pfAllocation: 'Allocation',
        pfProductCategory: 'Product Category',
        pfFrameColor: 'Frame Color',
        pfFrameColorPh: 'Search or add a colour…',
        pfItemName: 'Item / SKU Name',
        pfItemNamePh: 'e.g. Backpack 17L',
        pfEstQty: 'Estimate QTY',
        pfStartDate: 'Start Date',
        pfExfShipDate: 'EXF Ship Date',
        pfTargetPrice: 'Target Price (USD)',
        pfLaunchDate: 'Launching Date',
        pfProjectStatus: 'Project Status',
        pfRemark: 'Remark',
        pfInitialStages: 'Initial Process Stages',
        pfPctComplete: 'complete',
        pfStageHint: 'Pick a status from the list or type your own — use dd/mm/yyyy if you enter a date. Leaving a box empty saves it as Pending.',
        pfRepeatOrder: 'Repeat order',
        pfRepeatOrderHint: 'A style already developed, ordered again — no proto or counter sample, no L/D sign-off or hand-off meeting, so these four are set to Complete.',
        pfStagePh: 'Pending or dd/mm/yyyy',
        pfStageAuto: 'Auto — from the linked sample',
        pfLinkSample: 'Link Sample (optional)',
        pfLinkSampleHint: 'One or more sample/base IDs, separated by commas. Choosing a sample links it to this PRJ item.',
        pfLinkSamplePh: 'Select or type one Sample/Base ID',
        pfGtm: 'GTM programme (optional)',
        pfGtmHint: 'The GTM programme this SKU is launched under. It also shows on the GTM sheet',
        pfGtmPh: 'Select or type a GTM code, e.g. GTM-26-001',
        pfTickets: 'Tickets',
        pfTicketsAdded: '{{count}} ticket(s) added',
        pfNoTicketYet: 'No ticket added yet',
        pfAllReceivingTeams: 'All Receiving Teams',
        pfAddTicket: '＋ Add Ticket…',
        pfTicketHint: 'Only the codes matching this project’s Team, Case and Category are offered, grouped by phase. Pick a receiving team, then “＋ Add Ticket”. New tickets are created when you save the group.',
        pfClearTickets: 'Clear Tickets',
        pfPhotosOnSku: 'Photos are attached on each SKU above — open a SKU and use Add photos.',
        pfTicketEmpty: 'No tickets added yet — choose tickets from “＋ Add Ticket”.',
        pfCancel: 'Cancel',
        pfSaving: 'Saving...',
        pfAddProject: 'Add Project',
        pfDiscardTitle: 'Discard this project?',
        pfDiscardBody: 'What you have entered has not been saved yet. Close the form and it is gone.',
        pfDiscardKeep: 'Keep editing',
        pfDiscardConfirm: 'Discard',
        dgEditTitle: 'Discard your changes?',
        dgEditBody: 'What you changed here has not been saved yet. Close the form and the changes are gone.',
        pfSaveChanges: 'Save Changes',
        pfViewSample: 'View linked sample',
        pfSampleBtn: 'Sample',
        pfNewSku: 'New SKU',
        pfEditPrefix: 'Edit',
        pfGroupLevelHint: 'Group-level details — edit them from the project group',
        pfNewTag: 'New',
        pfSaveAddSku: 'Add SKU',
        thSortFilter: 'Sort and filter',
        thSortAZ: 'Sort A to Z',
        thSortOldNew: 'Sort Oldest to Newest',
        thSortZA: 'Sort Z to A',
        thSortNewOld: 'Sort Newest to Oldest',
        thSearch: 'Search...',
        thSelectAll: '(Select All)',
        thNoMatches: 'No matches',
        thOk: 'OK',
        thCancel: 'Cancel',
        thBlanks: '(Blanks)',
        thFiltered: 'Filtered — {{count}} value(s) kept',
        pgSharedHint: 'Basic details shared by every SKU in this group',
        pgPrjItems: 'PRJ item(s)',
        pgSkuHint: 'Each SKU is its own PRJ item under this PG project. Click a SKU to edit its information, stages, and linked sample.',
        pgNoSku: 'No SKU in this group yet.',
        pgRemoveSku: 'Remove this SKU from the group',
        // New / Edit Round modal
        rmTitleNew: 'New Round',
        rmTitleNewSample: 'New Sample',
        rmTitleEdit: 'Edit Existing Round',
        rmTitleAdvance: 'Next Round (required by Advance)',
        tkFiles: 'Attached files',
        tfPick: 'Click to attach a file',
        tfHint: 'PDF, Excel, CSV, Word, images — up to {{max}} MB each, sent with the ticket email',
        tfUploading: 'Uploading…',
        tfRemove: 'Remove this file',
        tfTotal: '{{n}} file(s), {{size}} — these go out as attachments on the ticket email',
        tfSaveFirst: 'This ticket cannot take files.',
        tfDraftNote: 'they attach when you save the ticket',
        tfTooBig: '{{name}} is {{mb}} MB; the limit is {{max}} MB per file.',
        rmPickSample: 'Select an existing sample to add a round to',
        rmPickSampleSub: '— type Sample ID, Base ID or Style name',
        rmPickPh: 'e.g. SMP-2601-P1 or SMP-2601...',
        rmPickRequired: 'Pick the sample this round belongs to — a round is always added to an existing sample.',
        rmPickFirst: 'Start here. Type the Sample ID — the rest of the form opens once a sample is selected.',
        rmStyleFromSample: 'Comes from the sample you pick above',
        rmStyleFromSku: 'Comes from the project SKU this sample is for',
        rmStylePlaceholder: 'Pick a sample above',
        rmSalesTeam: 'Sales team',
        rmProjectOwner: 'Project Owner',
        rmProjectName: 'Project',
        rmNoLinkedSku: 'Not linked to a SKU',
        rmClear: 'Clear',
        rmAddingTo: 'Adding round to',
        rmChange: 'Change',
        rmAdvanceNote: 'is being set to Advance, which means development moves to the next round. Fill in that round and save — both are saved together.',
        rmAdvanceNoteSub: 'If you cancel, the Advance outcome is not saved either and the round stays as it was.',
        rmDupWarn: 'Possible duplicate: {{sampleId}} already has round "{{round}}" for this style (outcome: {{outcome}}).',
        rmDupOpen: 'open',
        rmOwnership: 'Field ownership:',
        rmHevnFills: 'HEVN fills',
        rmHincFills: 'HINC Design fills',
        rmBothTeams: 'Both teams',
        rmAutoGen: 'Auto-generated',
        rmMandatory: '= Mandatory field',
        rmSampleIdent: 'Sample Identification',
        rmStyleItem: 'Style / Item',
        rmStyleHint: 'Pick existing or type new — Base ID auto-assigns',
        rmStylePh: 'e.g. Chair Zero LT',
        rmBaseId: 'Base ID',
        rmAutoAssigned: 'Auto-assigned',
        rmSampleId: 'Sample ID',
        rmSampleIdHint: 'Base ID + Round + Option (auto)',
        rmRound: 'Round',
        rmOption: 'Option',
        rmOptionHint: 'Only when construction differs',
        rmOptionPh: 'Fabric / Mesh (blank for colour variants)',
        rmCategory: 'Category',
        rmSeason: 'Season',
        rmDevelopmentDue: 'Development Due',
        rmDevDueHint: 'Mandatory — yellow in Sheet',
        rmTargetPrice: 'Target Price (USD)',
        rmLinkedSku: 'Linked Project SKU',
        rmOptional: 'Optional',
        rmLinkedSkuHint: 'Link this sample to a Project SKU',
        rmSearchProject: 'Search project...',
        rmReviewType: 'Review Type',
        rmHevn: 'HEVN',
        rmRequiresDesigner: 'Requires designer review',
        rmInternalOnly: 'HEVN internal review only',
        rmInternalNote: 'Use Review Date and Remarks for the HEVN internal review record. This round is not counted as designer workload or pending feedback.',
        rmDesignerNote: 'Designer review keeps the current HINC review and outcome flow.',
        rmInternalReview: 'HEVN Internal Review',
        rmRequestDate: 'Request Date',
        rmInternalReqHint: 'Same field as Request Date in HINC Design Review — this is the internal review date',
        rmRemarks: 'Remarks',
        rmInternalRemarksPh: 'Any notes for this internal review...',
        // "Sample Plan", not "HEVN Production" — Jay, 2026-10-19 ("sounds weird"), the DAC form too.
        rmSamplePlan: 'Sample Plan',
        rmDeveloper: 'Developer',
        rmDeveloperPh: 'Select or type name...',
        rmEitherName: 'A Developer or a Designer — either one.',
        rmLocation: 'R&D Location',
        rmRmReady: 'RM Ready Date',
        rmRmReadyHint: 'Check Expected if this is a planned date; uncheck once materials/spec are ready.',
        pfNeedOwner: 'This project has no Project Owner yet, so a ticket cannot be raised on it — the email would go out with "Project Owner: -" and the receiving team would not know who to go back to. Open the project with the pencil on its row, set Project Owner, save, then come back here.',
        pfNeedOwnerShort: 'Set the Project Owner on the project first',
        rmMaterials: 'Material list',
        rmMaterialsHint: 'Every column here belongs to the SKU and is edited on the SKU — one list, not a copy, so a material added there shows up in every round. RM ready date is the exception: that one belongs to this round, and is saved with it.',
        rmMaterialsNoSku: 'No material list yet — the list belongs to a project SKU, and this round is not linked to one. Fill in Linked Project SKU above and its materials appear here.',
        rmExpectedDate: 'Expected date',
        rmSampleFinish: 'Sample Finish Date',
        rmQty: 'QTY',
        rmActualCost: 'Actual Cost (USD)',
        rmAutoCalc: 'Auto-calculated (read-only):',
        rmMakeDays: 'Make Days',
        rmShipDays: 'Ship Days',
        rmRoundCycle: 'Round Cycle',
        rmDday: 'D-day',
        rmHincReview: 'HINC Design Review',
        rmHincDesigner: 'HINC Designer',
        rmHincReqHint: 'Date HINC requested this sample',
        rmRoundDue: 'Round Due',
        rmRoundDueHint: 'HINC\'s target for sample completion',
        rmReceivedDate: 'Received Date',
        rmReceivedHint: 'Date HINC received the sample',
        rmReviewDate: 'Review Date (Comment Date)',
        rmReviewDateHint: 'Date HINC provided feedback',
        rmRoundOutcome: 'Round Outcome',
        rmOutcomeHint: 'Set after weekly comment meeting',
        rmNotSetYet: '— not set yet —',
        rmOutcomePick: 'Select an outcome to see what it means.',
        rmReworkTitle: 'Rework — reason required',
        rmReworkReason: 'Rework Reason',
        rmSelectAll: '(select all that apply)',
        rmRoundRemarksPh: 'Any notes for this round...',
        rmSaving: 'Saving...',
        rmSaveChanges: 'Save Changes',
        rmSaveAdvance: 'Save Advance + New Round',
        rmAddNewRound: 'Add New Round',
        rmCancel: 'Cancel',
        rmCancelAdvance: 'Cancel (discard Advance too)',
        // Ticket Station + ticket forms
        tkTitle: 'Ticket Station',
        tkHint: 'Track project tickets, owner follow-up, due dates, and status updates.',
        tkAll: 'All Tickets',
        // The MANAGER's three, renamed on 2026-09-17 (Jay: "use term OPEN not Pending even from
        // Ticket Station", and the three Dones on one screen). The receiving team's own answers —
        // Not started / In Progress / Done / Cancelled — are untouched: see src/ticketStatus.js.
        tkDone: 'Closed',
        tkCancelled: 'Cancelled',
        tkPending: 'Open',
        tkOverdue: 'Overdue',
        tkAllRequesters: 'All Requesters',
        tkAllTeams: 'All Receiving Teams',
        tkAllSopTeams: 'All Sales OP Teams',
        tkAllPriority: 'All Priority',
        tkSearch: 'Search ticket / project...',
        tkNewTicket: '+ New Ticket',
        tkTabTickets: 'Ticket Station',
        tkManageCodes: 'Ticket Codes',
        tkColTicketId: 'Ticket ID',
        tkColTicket: 'Ticket',
        tkColProject: 'Project',
        tkColProjectName: 'Project Name',
        tkColTeam: 'Receiving Team',
        tkColRequester: 'Requester',
        tkColType: 'Type',
        tkColPriority: 'Priority',
        tkColDue: 'Due',
        tkColCreatedBy: 'Created by',
        tkColCreatedAt: 'Created at',
        tkColUpdatedBy: 'Modified by',
        tkColUpdatedAt: 'Modified at',
        tkColNote: 'Note',
        tkColAssigneeReply: 'Assignee reply',
        tkLinkNotSent: 'Send the ticket first — nobody on the receiving team knows it exists yet, so there is nobody to answer through this link.',
        /* Excel only — the grid packs each pair into one cell, but a spreadsheet is sorted and
           filtered per column, so the export splits them. */
        tkColDescription: 'Description',
        tkColAssigneeNote: 'Assignee note',
        tkColEmailSent: 'Email sent',
        tkExportEmpty: 'Nothing to export — no ticket matches the current filters.',
        tkManagerStatus: 'Manager Status',
        tkRemind: 'Remind',
        tkRemindHint: 'Email the receiving team that this ticket is still open',
        /* One slot, two jobs — Max, 2026-09-14. A ticket whose mail has never gone out cannot
           sensibly be reminded about, so the button offers the first send instead. */
        tkSend: 'Send',
        tkSendHint: 'This ticket has never been emailed — send the request to the receiving team',
        tkSendDone: 'Ticket {{id}} emailed to {{to}}.',
        tkSendFailed: 'Send failed: {{err}}',
        tkDdayHint: 'D-DAY = today − Due date. A positive value means the ticket is overdue by that many days. Once the ticket is Done or Cancelled it freezes at the day it closed.',
        tkNotYourTeam: 'Only the person who raised this ticket, the project owner, that team\u2019s manager or an admin can change it.',
        tkExpandAll: 'Expand all',
        tkCollapseAll: 'Collapse all',
        tkTicketsSingular: 'ticket',
        tkTicketsPlural: 'tickets',
        tkNoProject: '(no project)',
        tkEdit: 'Edit ticket',
        tkDuplicate: 'Duplicate ticket',
        tkDelete: 'Delete ticket',
        tkOpenLink: 'Open public status link',
        tkLink: 'Link',
        tkNoTickets: 'No tickets found.',
        tkTitleNew: 'New Ticket',
        tkTitleEdit: 'Edit Ticket',
        tkTitleDup: 'Duplicate Ticket',
        tkAuto: 'Auto',
        tkProjectGroup: 'Project Group',
        tkSearchGroup: 'Search project group...',
        tkAutoFromGroup: 'Auto from Project Group',
        tkSelectTeam: 'Select receiving team...',
        tkTicketCode: 'Ticket Code',
        tkSelectCode: 'Select agreed ticket type...',
        tkTicketName: 'Ticket Name',
        tkSelectTypeFirst: 'Select a ticket type',
        tkDescription: 'Description',
        tkDescriptionPh: 'Ticket detail / expected output',
        tkRequesterPh: 'Auto by login user',
        tkGroupEmail: 'Group Email',
        tkGroupEmailPh: 'Auto by receiving team',
        tkDueDate: 'Due Date',
        tkSentAt: 'Emailed on',
        tkAttachment: 'Attachment Link',
        tkAttachmentPh: 'Paste Drive/file link',
        tkNotePh: 'Internal note or latest update',
        tkSendAfterSave: 'Send email after saving',
        tkSent: 'Sent',
        tkNotSent: 'Not sent',
        tkEmailSubject: 'Email Subject',
        tkSystemGenerated: 'system generated',
        tkEmailCc: 'Email CC',
        tkEmailMessage: 'Email Message',
        tkReadOnly: 'read only',
        tkEmailMessagePh: 'Email message',
        tkGeneratedNote: 'Email subject and message are generated automatically and cannot be edited.',
        tkSendEmail: 'Send email',
        tkDeleteTicket: 'Delete Ticket',
        tkSaveTicket: 'Save Ticket',
        tkNoDueDate: 'No due date',
        tkRemoveTicket: 'Remove ticket',
        tkOptionalNote: 'Optional note',
        tkWillSend: 'Will send on save',
        tkQueued: 'Queued to send',
        tkQueuedCancelTitle: 'Queued — click to cancel',
        tkSendBlocked: 'Fill in the required fields above first — the email cannot be sent without them',
        tkSendTitle: 'The ticket is created on save, so the email is sent right after',
      },
      // Toolbar of the note editor (RichText.jsx). Shared, so it sits outside the screen namespaces.
      rt: {
        bold: 'Bold (Ctrl+B)',
        italic: 'Italic (Ctrl+I)',
        underline: 'Underline (Ctrl+U)',
        strike: 'Strikethrough',
        colour: 'Text colour',
        bullets: 'Bulleted list',
        numbers: 'Numbered list',
        clear: 'Clear formatting',
        cDefault: 'Default',
        cRed: 'Red',
        cOrange: 'Orange',
        cGreen: 'Green',
        cBlue: 'Blue',
        cPurple: 'Purple',
        cGrey: 'Grey',
      },
      nfc: {
        stDraft: 'NOT SUBMITTED',
        stWaiting: 'WAITING APPROVAL',
        stApproved: 'APPROVED',
        stRejected: 'REJECTED',
        replOk: 'OK',
        replNotFound: 'Not found',
        replReplaced: 'Already replaced',
        replDuplicate: 'Duplicate',
        notSignedIn: '(not signed in)',
        role: 'Role',
        manager: 'Manager',
        coordinator: 'Coordinator',
        unassigned: 'unassigned',
        loading: 'Loading…',
        noAccessTitle: 'No access',
        noAccessBody: 'has not been assigned a Coordinator/Manager role for the NFC portal. Contact Special Project to be added.',
        accountWord: 'Account',
        createTitle: 'Create a token issuance request',
        tabIssuance: 'Token issuance',
        tabReplacement: 'Replace defective NFC',
        labelType: 'Label Type',
        labelNfc: 'NFC Label',
        labelQr: 'QR Heat-transfer Label',
        projectName: 'Project Name',
        batchName: 'Batch Name',
        qty: 'Quantity (QTY)',
        qtyPh: 'Required unless importing items',
        style: 'Style / Product',
        stylePh: 'e.g. Chair One (re)',
        color: 'Color',
        vendor: 'Vendor',
        vendorPh: 'e.g. Metric / TheSang',
        code: 'Code',
        codePh: 'e.g. 10001555',
        importTitle: 'Import multiple items (optional)',
        importReplTitle: 'Import defective NFC tokens',
        downloadTemplate: 'Download CSV template',
        chooseCsv: 'Choose CSV',
        noFile: 'No file selected',
        importSummary: 'Loaded {{count}} item(s), total QTY {{qty}}. Review and edit before submitting.',
        importInvalid: 'Some lines are invalid (need Project + Batch + Qty).',
        importEmpty: 'No import file loaded — leave empty to use the single-item fields above.',
        replSummary: 'Loaded {{count}} defective token(s). Review before submitting.',
        replChecking: 'Checking against ledger…',
        warning: 'Warning:',
        oldToken: 'Old Token / NFC URL',
        reason: 'Reason',
        notes: 'Notes',
        status: 'Status',
        checking: 'Checking…',
        notChecked: 'Not checked',
        replEmpty: 'No defective tokens loaded — download the template, then upload a CSV to begin.',
        optional: 'Optional',
        saving: 'Saving…',
        save: 'Save',
        cancel: 'Cancel',
        myRequests: 'My requests',
        reload: 'Reload',
        colId: 'ID',
        colProject: 'Project',
        colBatch: 'Batch',
        colQty: 'Qty',
        colItems: 'Items',
        colLabel: 'Label',
        colVendor: 'Vendor',
        colCode: 'Code',
        colStatus: 'Status',
        colUpdated: 'Updated',
        colFile: 'File',
        colAction: 'Action',
        colOld: 'Old',
        colNew: 'New',
        colOutput: 'Output',
        colRequestedBy: 'Requested by',
        colEmail: 'Email',
        colAddedBy: 'Added by',
        downloadOrder: 'Download order file',
        viewDetail: 'View detail',
        edit: 'Edit',
        submitApproval: 'Submit for approval',
        delete: 'Delete',
        reject: 'Reject',
        noRequests: 'No requests yet.',
        reviewTitle: 'Request review',
        accessTitle: 'Manage portal access',
        accessHint: 'Grant or revoke Coordinator / Manager access to this NFC portal. Changes take effect the next time the user loads the portal.',
        grantAccess: 'Grant access',
        noUsers: 'No users assigned yet.',
        detailTitle: 'Request detail',
        lblNotes: 'NOTES',
        lblOutputFile: 'OUTPUT FILE',
        openOutput: 'Open output',
        lblDecisionNote: 'DECISION NOTE',
        replItems: 'Replacement items',
        items: 'Items',
        close: 'Close',
        genTitle: 'Generating tokens',
        genLead: 'is being approved. Please keep this tab open and',
        genLeadBold: 'do not click Approve again',
        requestWord: 'Request',
        working: 'Working...',
        rejectTitle: 'Reject request',
        rejectReason: 'Reason (sent to requester)',
        rejectReasonPh: 'Why is this rejected?',
        rejectReasonReq: 'Please enter a rejection reason.',
        rejectBtn: 'Reject request',
        deleteTitle: 'Delete request',
        deleteBody: 'Delete request {{id}}? Only Draft or Rejected requests can be deleted.',
        nReplacements: '{{count}} replacement(s)',
        nItems: '{{count}} item(s)',
        single: 'Single',
        nCodes: '{{count}} code(s)',
        flashCleared: 'Request form cleared.',
        flashSubmitted: 'Request {{id}} submitted for approval.',
        flashDraft: 'Request {{id}} saved as draft.',
        flashLoadedEdit: 'Loaded {{id}} for editing.',
        flashDeleted: 'Request {{id}} deleted.',
        flashApproved: 'Request {{id}} approved — {{qty}} tokens generated.',
        flashGranted: 'Granted {{role}} for {{email}}.',
        flashRevoked: 'Revoked {{role}} for {{email}}.',
        flashEmailReq: 'Please enter an email.',
        flashLoadedFile: 'Loaded {{name}}.',
        flashRejected: 'Request {{id}} rejected.',
        progStarting: 'Starting approval...',
        progCompleted: 'Completed',
        progGenerated: '{{qty}} tokens generated',
        progSavedImport: 'Saved import loaded',
      },
      dd: {
        title: 'Dashboard',
        scopeNote: 'Project samples only — trial samples (TSMP) are left out of every figure on this page. The Sample Tracker lists both.',
        loading: 'Loading dashboard…',
        retry: 'Retry',
        noData: 'No data',
        totalRounds: 'Total Rounds',
        acrossSamples: 'across {{count}} samples',
        avgRoundsSample: 'Avg Rounds / Sample',
        higherSpinning: 'higher = more spinning',
        reworkRatio: 'Rework Ratio',
        mainKpi: 'main KPI',
        pastDue: 'Past Development Due',
        pastDueSub: '{{unit}} with D-day ≥ 0',
        reworkByRoom: 'Rework rate by dev room',
        reworkAttribution: 'Rework attribution (bidirectional)',
        hevnCaused: 'HEVN (caused)',
        hincCaused: 'HINC (caused)',
        external: 'External',
        hevnRatio: 'HEVN-attributed ratio',
        stuckTime: 'Stuck time proxy',
        avgCommentDiffers: "Avg comment days — 'differs' rounds",
        avgCommentAdvance: 'Avg comment days — Advance rounds',
        avgCommentAll: 'Avg comment days — all',
        spinning: 'Spinning samples (4+ rounds)',
        sampleSpeed: 'Sample Speed',
        sampleSpeedNote: 'RM Ready Date = the date when fabric/materials/spec are ready and the sample room can start making the sample. Sample Speed counts from RM Ready Date to Sample Finish Date; rounds without an actual ready date, or with an expected ready date in the future, stay in RM Waiting and are excluded.',
        avgMakeHevn: 'Avg Make Days (HEVN R&D)',
        avgMakeTs: 'Avg Make Days (TS)',
        avgMakeMs: 'Avg Make Days (MS)',
        avgMakeTotal: 'Avg Make Days (Total)',
        byDeveloper: 'By developer — full breakdown',
        colDeveloper: 'Developer',
        colSamples: 'Samples',
        colRounds: 'Rounds',
        colRework: 'Rework',
        colReworkRate: 'Rework Rate',
        colAvgRounds: 'Avg Rounds',
        colAvgMakeDays: 'Avg Make Days',
        colPending: 'Pending',
      },
    },
  },
  ko: {
    common: {
      notif: {
        title: '알림',
        unreadCount: '읽지 않은 알림 {{count}}개',
        markAll: '모두 읽음으로 표시',
        markRead: '읽음으로 표시',
        loading: '불러오는 중…',
        empty: '아직 알림이 없습니다. 포털이 업무 관련 메일을 보내면 여기에도 표시됩니다.',
        open: '관련 화면 열기',
        by: '{{name}}',
        settings: '알림 받을 항목 선택',
        settingsHint: '벨에 표시할 그룹을 고르세요. 메일은 지금처럼 계속 발송됩니다. 끈 그룹은 여기서만 숨겨지고, 다시 켜면 다시 보입니다.',
        save: '저장',
        cancel: '뒤로',
        saved: '저장됨',
        unreadIn: '읽지 않음 {{count}}',
        g_approvals: '승인 및 결정',
        g_access: '접근 요청 (신규 인원)',
        g_tickets: '티켓',
        g_samples: '샘플 마감일',
        g_projects: '프로젝트 (SKU 추가·이동·드롭)',
        g_cycletime: '사이클 타임 점검',
        g_ceo: 'CEO Office',
        g_chat: '그룹 채팅 (GCC)',
        g_tasks: '시스템 프로젝트',
        g_other: '기타',
        hidden: '{{count}}개 그룹 숨김',
      },
      common: {
        language: '언어',
        theme: '테마',
        light: '라이트',
        dark: '다크',
        allRoles: '전체 역할',
      },
      cpAtt: {
        photos: '사진',
        messages: '메시지',
        you: '나',
        pendingTag: '저장할 때 전송됩니다',
        noMessages: '아직 메시지가 없습니다.',
        placeholder: '이 견적에 대한 메모를 남기세요…',
        send: '보내기',
        withdraw: '삭제',
        deleteConfirm: '이 메시지를 삭제할까요? 모두에게서 사라지며 되돌릴 수 없습니다.',
        saveFailed: '메시지를 저장하지 못했습니다.',
        deleteFailed: '메시지를 삭제하지 못했습니다.',
      },
      bench: {
        title: '인라인 원가표',
        sub: 'Inline benchmark에 읽어 온 벤더 원가표입니다. 파일 없이도 견적을 비교할 수 있도록 보관합니다.',
        search: '스타일, 파일, 벤더, 시즌 검색…',
        empty: '아직 읽어 온 원가표가 없습니다. 위의 "원가표 불러오기"를 쓰세요 — 읽은 내용은 여기에 보관되고, 견적의 Inline benchmark를 채울 때 제시됩니다.',
        noHit: '"{{q}}"와 일치하는 항목이 없습니다.',
        loading: '원가표를 읽는 중…',
        lines: '{{n}}개 행',
        edit: '정보 수정',
        remove: '삭제',
        save: '저장',
        saved: '저장했습니다.',
        removed: '보관함에서 삭제했습니다.',
        editTitle: '시즌 · 벤더 · 메모',
        editBody: '워크북에 적혀 있지 않은 세 가지입니다. 원가는 수정할 수 없습니다 — 벤더가 제시한 값이고, 옆의 buying cost가 그 검증 기준입니다.',
        season: '시즌',
        vendor: '벤더',
        note: '메모',
        removeTitle: '이 원가표를 삭제할까요?',
        removeBody: '{{file}}은(는) 보관함에서 빠지고 benchmark를 채울 때 더 이상 제시되지 않습니다. 이미 이 원가표로 비교한 견적은 숫자와 그 옆에 적힌 행을 그대로 유지합니다.',
        colLine: '행',
        colVariant: '원가 버전',
        colStated: '원가표의 buying cost',
        import: '원가표 불러오기',
        readOk: '{{file}} — {{n}}개 행을 읽어 보관했습니다.',
        readRefused: '{{n}}개 행은 거부되었습니다. 항목 합계가 옆에 적힌 buying cost와 맞지 않습니다.',
      },
      ceo: {
        title: 'CEO Office',
        sub: '열린 모든 건을 종료될 때까지 추적합니다. 카운터는 각 티켓의 이벤트 로그에서 계산됩니다.',
        desk: '데스크',
        bothDesks: '전체',
        desk_business: 'Business',
        desk_executive: 'Executive',
        refresh: '새로고침',
        newTicket: '새 티켓',
        newSub: '티켓은 한 데스크에서 생성되어 그대로 유지됩니다. 법인과 내부 담당자는 비워 둘 수 있습니다 — 대부분의 건은 소속이 정해지지 않은 상태로 시작합니다.',
        create: '생성',
        cancel: '취소',
        close: '닫기',
        needTitle: '티켓 제목이 필요합니다.',
        fromWho: '발신',
        notSet: '미지정',
        noTickets: '이 데스크에는 아직 티켓이 없습니다.',
        noDesk: '소속된 데스크가 없어 표시할 항목이 없습니다. Business 또는 Executive 추가를 Young에게 요청하세요.',
        filteredBy: '{{name}}만 표시 중 — {{n}}건.',
        clearFilter: '전체 보기',
        cardOpen: '진행 중',
        cardOpenRule: '종료·중단되지 않음',
        cardOverdue: '기한 초과',
        cardOverdueRule: '아직 열려 있고 기한이 지남',
        cardUntouched: '3일 이상 미접촉',
        cardUntouchedRule: '마지막 접촉 이벤트 기준',
        cardWaiting: 'CEO 대기',
        cardWaitingRule: '상태가 그렇게 표시됨',
        cardNoEntity: '법인 미지정',
        cardNoEntityRule: '아직 소속 없음',
        cardClosed: '이번 주 종료',
        cardClosedRule: '최근 7일 내 종료',
        colTicket: '티켓',
        colTitle: '제목 / 발신',
        colDesk: '데스크',
        colType: '유형',
        colEntity: '법인',
        colContact: '내부 담당자',
        colOwner: '담당',
        colDue: '기한',
        colStatus: '상태',
        colLast: '최근 접촉',
        colDday: 'D-day',
        logTitle: '이벤트 로그',
        logContacted: '우리가 연락함',
        logReply: '상대가 회신함',
        ev_created: '생성',
        ev_contacted: '연락',
        ev_reply: '회신',
        ev_status: '상태',
        ev_field: '수정',
        ev_closed: '종료',
        ev_reopened: '다시 열림',
        ev_note: '메모',
        closeTicket: '티켓 종료',
        reopenTicket: '다시 열기',
        closeTitle: '{{id}} 종료',
        reopenTitle: '{{id}} 다시 열기',
        closeSub: '진행 중·기한 경과에서 빠지고 이번 주 종료에 집계됩니다. 목록과 검색에는 전체 이력과 함께 남으며, 다시 열 수 있습니다.',
        reopenSub: '종료 직전의 상태로 돌아가며 다시 진행 중으로 집계됩니다.',
        closeNote: '종료 메모 (선택)',
        reopenNote: '다시 여는 이유 (선택)',
        closeNotePh: '예: 통화로 합의 — 추가 조치 없음.',
        reopenNotePh: '예: 상대가 새 요청으로 회신함.',
        closedBy: '{{who}} 종료 · {{when}}',
        statusClosedHint: '종료됨 — 상태를 바꾸려면 다시 열기를 누르세요.',
        logNote: '내부 메모 추가',
        logNoteSub: '이 티켓을 열 수 있는 사람만 포털에서 보며, 메일로 발송되지 않습니다. 연락으로 집계되지 않고 최근 접촉도 바뀌지 않습니다. Ctrl+Enter로 기록.',
        logNoteWhy: '데스크와 CEO를 위한 메모입니다. 날짜나 집계를 바꾸지 않습니다.',
        notePh: '무슨 일이 있었는지, 무엇을 정했는지, 다음 할 일.',
        historyTitle: '이력',
        hist_all: '전체',
        hist_mail: '메일',
        hist_note: '메모',
        hist_change: '변경',
        historyEmpty: '이 티켓에는 아직 해당 항목이 없습니다.',
        hSent: '보낸 메일',
        hReceived: '받은 메일',
        hSentWhy: 'CEO Office 메일함 또는 데스크 구성원이 보낸 메일.',
        hReceivedWhy: '외부에서 CEO Office로 온 메일.',
        hTo: '받는 사람',
        hCc: '참조',
        hQuoted: '이 메일에 인용된 이전 메일 보기',
        hQuotedHide: '인용된 메일 숨기기',
        hNote: '내부 메모',
        hCreated: '티켓 생성',
        hCreatedMail: 'CEO Office 메일함에서 티켓 생성',
        hClosed: '종료 (이전 상태: {{from}})',
        hReopened: '다시 열림 → {{to}}',
        hFile: '파일 첨부',
        historyScope: '{{mailbox}}에 도착한 메일만 여기에 표시됩니다 — 이 주소로 오거나, 참조되었거나, 이 주소에서 보낸 메일. 데스크 구성원이 자신의 메일함에서 {{mailbox}}를 참조하지 않고 보낸 메일은 포털이 볼 수 없습니다.',
        restricted: '비공개 — 다른 데스크',
        otherDesk: '다른 데스크의 티켓입니다. 존재는 보이지만 이력은 열람할 수 없습니다.',
        entityTitle: '법인 CC 주소',
        entityFilled: '{{total}}개 중 {{n}}개 설정됨',
        entitySub: '법인과 내부 담당자는 티켓 발신 메일의 CC 대상입니다. Young이 8개 주소를 보내줄 때까지 비어 있습니다 — 여기 적힌 주소로 실제 메일이 가므로 추측해서 넣지 않습니다.',
        entityPh: '미설정',
        fTitle: '제목',
        fTitlePh: '무엇이 열렸는지 — 이 티켓이 추적할 사안',
        fFromPh: '발신자 (있는 경우)',
        fTypePh: 'Follow-up, Legal, Document…',
        newLater: '제목 외에는 비워 두고 나중에 채워도 됩니다 — 대부분의 건은 소속이 정해지지 않은 상태로 시작합니다.',
        fLaterPh: '아직 모르면 비워 두세요',
        tabTickets: '티켓',
        allDesks: '전체',
        unassigned: '미배정',
        choosedesk: '데스크 선택…',
        deskRule: '각 데스크는 자기 티켓만 봅니다. CEO는 양쪽 모두 봅니다.',
        open: '열기',
        fAllStatuses: '전체 상태',
        fAllTypes: '전체 유형',
        fAllEntities: '전체 법인',
        fAllContacts: '전체 내부 담당자',
        fAllOwners: '전체 담당',
        searchPh: '제목 또는 발신자 검색…',
        searchLeadPh: '거래처명 검색…',
        ddayWhat: '마감일(Due) 기준. +는 지난 일수, -는 남은 일수입니다.',
        ddayLegend: 'D-day는 마감일(Due) 기준입니다: +5는 5일 지남, -5는 5일 남음. 열려 있는 티켓만 계산합니다.',
        ev_file: '파일',
        fileUnreadable: '파일을 읽을 수 없습니다.',
        files: '첨부파일',
        filesSub: '이 티켓이 기다리는 자료, 또는 함께 온 자료. 개당 10 MB, 티켓당 {{n}}개.',
        addFile: '파일 추가',
        removeFile: '삭제',
        removeFileSub: '파일이 서버에서 삭제됩니다. 되돌릴 수 없습니다.',
        uploading: '업로드 중…',
        logContactedSub: '어디로 보냈고 무슨 내용인지. 둘 다 선택 사항 — 날짜는 어느 쪽이든 기록됩니다. Ctrl+Enter로 기록.',
        logReplySub: '어디로 회신이 왔고 무슨 내용인지. 둘 다 선택 사항 — 날짜는 어느 쪽이든 기록됩니다. Ctrl+Enter로 기록.',
        channel: '경로',
        channelPh: '이메일, 전화, 미팅…',
        contactNote: '메모',
        contactNotePh: '예: 서명본 재요청',
        replyNotePh: '예: 2주 더 필요하다고 함',
        record: '기록',
        colDwell: '현 상태 경과일',
        nDays: '{{n}}일',
        atStageN: '이 단계에서 {{n}}일',
        worstN: '최장 {{n}}일',
        lateShape: '지연이 어디에 몰려 있나',
        lateShapeSub: '한 곳만 막힌 것인지, 전체가 밀린 것인지',
        nothingLate: '기한을 넘긴 건이 없습니다.',
        howLong: '이 데스크의 소요 시간',
        howLongSub: '이벤트 로그 기준 — 직접 입력한 값 없음',
        toFirstAnswer: '접수 \u2192 첫 응답',
        toClose: '접수 \u2192 종료',
        neverAnswered: '열려 있고 아직 응답 없음',
        medianOf: '{{n}}건 중앙값',
        notEnough: '아직 부족 \u2014 현재 {{n}}건',
        loading: '불러오는 중\u2026',
        noHistory: '아직 기록이 없습니다.',
        reset: '초기화',
        resetN: '초기화 ({{n}})',
        sortedBy: '{{col}} 기준 정렬',
        showingN: '{{total}}건 중 {{n}}건 표시 —',
        leadContactHint: '날짜를 기록합니다 — 최근 접촉과 "무응답"이 모두 이 값을 기준으로 셉니다.',
        exportExcel: 'Excel 내보내기',
        contactHint: '날짜를 기록합니다 — 최근 접촉과 3일 이상 미접촉이 모두 이 값을 기준으로 셉니다.',
        logContactedWhy: '우리가 연락했습니다. 오늘 날짜를 기록하고 미접촉 일수를 다시 셉니다.',
        logReplyWhy: '상대가 회신했습니다. 마찬가지로 오늘 날짜를 기록하며, 연락한 것과 답을 받은 것은 다른 사실이므로 따로 둡니다.',
        tab_tickets: '티켓',
        tab_leads: '리드',
        tab_daily: '데일리',
        sub_tickets: '열린 모든 건을 종료될 때까지 추적합니다. ceo.office@로 전달된 메일은 티켓이 되며, 카운터는 각 티켓의 이벤트 로그에서 계산됩니다.',
        sub_leads: '신규 거래처 파이프라인. CEO가 시장과 조건을 정하고, 데스크가 접촉해 답변을 기준과 대조합니다.',
        sub_daily: '매일 아침 네 개의 블록. 화면과 자동 메일은 같은 내용을 보여줍니다.',
        newLead: '새 리드',
        newLeadSub: '리드는 비즈니스 데스크에 속합니다. 해당 티켓을 지정하거나, 티켓이 생기기 전에 먼저 열 수 있습니다.',
        needCounterparty: '상대처 이름이 필요합니다.',
        counterpartyPh: '누구와 이야기하고 있는지',
        sectorPh: 'Distribution, Retail, Online…',
        leadTicketPh: '이 리드가 속한 티켓을 고르세요 (있는 경우)',
        countryPh: 'Thailand, Vietnam…',
        criteriaPh: 'CEO의 조건 대비…',
        noLeads: '아직 리드가 없습니다.',
        noReply: '{{n}}일째 무응답',
        colLead: '리드',
        colCounterparty: '상대처',
        colCountry: '국가',
        colSector: '분야',
        colStage: '단계',
        colCriteria: '기준',
        colNext: '다음 접촉',
        leadsNote: '리드는 비즈니스 데스크에만 있습니다. CEO가 어떤 시장을 어떤 조건으로 볼지 정하고, 데스크가 접촉해 답을 받아 기준과 대조합니다.',
        stageRule_Contacted: '첫 메일 발송',
        stageRule_Replied: '상대처 회신',
        stageRule_Checking_criteria: 'CEO 조건 대비 확인',
        stageRule_With_CEO: '통과, 결정 대기',
        stageRule_Closed: '성사 또는 기준 미달',
        dOpened: '어제 이후 신규',
        dMoved: '변동',
        dOverdue: '기한 초과',
        dWaiting: 'CEO 대기',
        nItems: '{{n}}건',
        reachFirst: '먼저 연락할 것',
        nothingHere: '없음.',
        dailyNote: '평일 매일 08:30 KL 발송. 각 데스크는 자기 티켓에 대한 메일을 받고, CEO는 양쪽 모두 받습니다. 화면과 메일은 같은 내용입니다.',
      },
      pricing: {
        title: '콜라보 가격 계산기',
        subtitle: '원가 · T1 · MSRP · 로열티를 한 화면에서 — 코스팅 단위 USD',
        modeGeneral: '일반 콜라보 · OEM / ODM',
        modeGeneralSub: '로열티 없음 · Markup Base → T1 → MSRP',
        modeLicensed: '라이선스 콜라보 (로열티)',
        modeLicensedSub: '글로벌 MSRP → 현지 가격 → 로열티',
        padClose: '\ub2eb\uae30',
        padNeedInstruction: '\uc544\ub798 Instruction \uce78\uc5d0 \uc870\uac74\uc744 \uc801\uc740 \ub4a4 \ub2e4\uc2dc \ub20c\ub7ec\uc8fc\uc138\uc694.',
        padOptionN: '\uc635\uc158 {{n}}',
        padInlineRrp: '\uc778\ub77c\uc778 \uc18c\ube44\uc790\uac00',
        padPremium: '\ucf5c\ub77c\ubcf4 \ud504\ub9ac\ubbf8\uc5c4',
        padTargetMsrp: '\ubaa9\ud45c MSRP',
        padOwnRetailRef: '\uc778\ub77c\uc778 \u00b7 \ucc38\uace0\uc6a9',
        padPartnerNa: '\ud574\ub2f9 \uc5c6\uc74c \u2014 \uc778\ub77c\uc778\uc740 \ud30c\ud2b8\ub108\uc5d0 \uacf5\uae09\ud558\uc9c0 \uc54a\uc74c',
        padGapOwn: '\uc790\uccb4 \ub9ac\ud14c\uc77c: \ucf5c\ub77c\ubcf4 {{c}}% \ub300 \uc778\ub77c\uc778 {{i}}%',
        padGapPartnerBelow: '\ud30c\ud2b8\ub108 \ub3c4\ub9e4: Extra\ub97c \ub123\uc73c\uba74 \uc6d0\uac00\ubcf4\ub2e4 {{amt}} \uc544\ub798',
        padGapPartnerOk: '\ud30c\ud2b8\ub108 \ub3c4\ub9e4: Extra \ud6c4 {{pct}}%',
        padToB7: '\u2192 \ube14\ub85d 7: \ud30c\ud2b8\ub108 \ucabd \ub2e8\uc704\ub2f9 {{amt}}\ub294 \ub204\uac00 \ubd80\ub2f4\ud558\ub294\uac00?',
        padSkuN: 'SKU {{n}}',
        padB7NoteMany: '\uacac\uc801 \uc804\uccb4\uc5d0 \ub300\ud55c \ud558\ub098\uc758 \uacb0\uc815\uc785\ub2c8\ub2e4. SKU {{n}}\uac1c, \uc8fc\ubb38 \uc804\uccb4 Extra \ud569\uacc4 {{e}}. A, B, C \uc5d0\uc11c\ub294 MSRP\uc640 \ud30c\ud2b8\ub108 \ub2e8\uac00\uac00 \uadf8\ub300\ub85c\uc774\uace0, \uadf8 Extra\ub97c \ub204\uac00 \ubd80\ub2f4\ud558\ub294\uc9c0\ub9cc \ub2ec\ub77c\uc9d1\ub2c8\ub2e4.',
        padFrameModel: '\ud504\ub808\uc784 \ubaa8\ub378',
        padB1: '1 \u00b7 \uc8fc\ubb38 \uc870\uac74',
        padKindOrder: '\uc774 \uc22b\uc790\ub4e4\uc774 \uc0b0\ucd9c\ub41c \uc870\uac74',
        padLeadDays: '{{d}}\uc77c \u2192 \uc20f\ub9ac\ub4dc {{r}}',
        padWhyShow: '\ucc28\uc774 \uc774\uc720 \ubcf4\uae30 ({{n}})',
        padWhyHide: '\uc811\uae30',
        padNoReason: '\uc0ac\uc720 \ubbf8\uae30\uc7ac',
        padMoqNote: 'MOQ \uc11c\ucc28\uc9c0\ub9cc {{amt}}, \uc774 \uc218\ub7c9\uc5d0\uc11c Extra\uc758 {{pct}}%\uc785\ub2c8\ub2e4.',
        padB2: '2 \u00b7 \uacf5\uc7a5 \uc0ac\uc785\uac00',
        padB3: '3 \u00b7 \ud504\ub808\uc784 \ube44\uc6a9',
        padB4: '4 \u00b7 \uc778\ub77c\uc778 \uc0dd\uc0b0\uc5d0\uc11c\ub294 \ubc1c\uc0dd\ud558\uc9c0 \uc54a\ub294 \uae08\uc561',
        padB5: '5 \u00b7 \ucd1d\uc6d0\uac00',
        padB6: '6 \u00b7 \uac00\uaca9\uacfc \ub9c8\uc9c4 \u2014 \ub450 \uac00\uc9c0 \ud310\ub9e4 \uacbd\ub85c',
        padB7: '7 \u00b7 \uc8fc\ubb38 \uc804\uccb4({{n}} EA) Extra {{amt}}\ub97c \ub204\uac00 \ubd80\ub2f4\ud558\ub294\uac00 \u2014 \uacb0\uc815\uc740 \ud558\ub098',
        padB7Kind: '\uacb0\uc815 \u00b7 \ud55c \ubc88 \ud074\ub9ad \u00b7 O.H \ucc28\uac10 \ud6c4 \uc21c\ub9c8\uc9c4',
        padB7KindCalc: '\uc77d\uae30 \uc804\uc6a9 \u00b7 \uc2b9\uc778\uc790\uac00 \uc120\ud0dd\ud558\uac8c \ub420 \uce74\ub4dc',
        method: '가격 산정 방식',
        methodCbd: 'CBD 원가 가산 (HEVN Costing)',
        methodGuideline: '원가 가이드라인 (O.H 15% + 티어 비율)',
        methodCbdHint: 'CBD의 공장 가격에 설정의 HPAC 간접비와 T2 마크업을 더하고 달러 단위로 올림합니다.',
        methodGuidelineHint: '마크업 기준가, 고정 15% 간접비, 이 견적의 T1 / T2 비율.',
        cbdSub: 'Calculation 시트와 동일: HPAC 간접비 = (Base + Frame)의 {{oh}}%, T2 = (Base + Frame + O.H) × {{x}}. 할증은 원가 그대로 부과되며, HPAC가 부담하면 체크하세요.',
        cbdBase: 'Base 가격',
        cbdBaseSub: 'Skin + CMT + 벤더 OH & 이익 + 핸들링 + 현지 운송',
        cbdFrame: '프레임',
        cbdBaseFrame: 'Base + 프레임',
        cbdOh: 'HPAC 간접비 ({{pct}}%)',
        cbdOhSub: '달러 단위 반올림',
        cbdMoq: '생산 MOQ 할증',
        cbdSl: '단납기 할증',
        cbdAbsorb: 'HPAC 부담',
        cbdAbsorbed: '(HPAC 부담)',
        cbdT1: 'T1 · HPAC → 헬리녹스 법인',
        cbdT1Sub: 'Base + 프레임 + O.H + 할증, 올림',
        cbdT2: 'T2 · HPAC → 파트너',
        cbdT2Unit: 'T2 파트너 단가',
        cbdT2Sub: '(Base + 프레임 + O.H) × {{x}} + 할증, 올림',
        cbdFactory: '벤더 원가',
        cbdFactorySub: 'Base + 프레임 + 두 할증 — 공장 청구액',
        cbdGp: '벤더 원가 대비 GP',
        cbdMargin: '마진 % (T2 기준)',
        cbdExtraSub: 'CBD 방식에서는 핸들링, 운송, 추가 자재, FF CMT가 Base 가격에 포함되며, MOQ와 단납기 할증만 별도로 부과됩니다.',
        cbdMsrpNote: '목표 MSRP {{msrp}}는 T2의 {{x}}배입니다.',
        cbdSetMarkup: 'T2 마크업',
        cbdOrderValue: 'T2 기준 주문 금액',
        cbdOrderValueRule: 'T2 × 수량, 모든 SKU 합계',
        cbdFactoryAmt: '벤더 원가 합계',
        cbdFactoryAmtRule: '벤더 원가 × 수량 — 할증은 누가 부담하든 벤더가 청구',
        cbdAbsorbedAmt: 'HPAC 부담 할증',
        cbdAbsorbedRule: '이 카드에서 T2에서 빠진 할증, 주문 전체',
        cbdGpRule: 'T2 기준 주문 금액 − 벤더 원가 합계',
        cbdMarginRule: 'GP ÷ T2 기준 주문 금액',
        cbdCard_A: '파트너가 두 할증 부담',
        cbdCard_B: 'HPAC가 두 할증 부담',
        cbdCard_HM: 'HPAC가 MOQ 할증 부담',
        cbdCard_HL: 'HPAC가 단납기 할증 부담',
        cbdCardWho_A: 'MOQ와 단납기 할증이 T1과 T2에 더해집니다.',
        cbdCardWho_B: '두 할증 모두 T1과 T2에서 빠지고, HPAC가 공장에 지불합니다.',
        cbdCardWho_HM: 'MOQ 할증은 T1과 T2에서 빠지고, 단납기 할증은 부과됩니다.',
        cbdCardWho_HL: '단납기 할증은 T1과 T2에서 빠지고, MOQ 할증은 부과됩니다.',
        padPerUnitHead: '단가',
        cbdOfferBox: '오퍼 — 견적 전체',
        padLiveDraft: '아직 제출되지 않음 — 고정된 사본이 아니라 계산기에 있는 현재 견적입니다.',
        cbdSlYesHint: '단납기 = Yes: Offer Sheet와 같이 벤더 CMT(인건비)의 {{pct}}%.',
        cbdSlNoHint: '단납기 = No: 할증 없음.',
        cbdPartner: '파트너',
        cbdSeason: '시즌',
        cbdTerm: '가격 조건',
        cbdValidFrom: '유효 시작일',
        cbdValidTo: '유효 종료일',
        cbdColor: '컬러',
        cbdSlFlag: '단납기',
        cbdSlAuto: '리드타임으로 자동 ({{rate}})',
        cbdSlYes: 'Yes — 인건비의 50%',
        cbdSlNo: 'No',
        cbdKeyPoints: '의사결정 핵심 사항',
        cbdKpMargin: 'T2 총 매출 {{rev}} 기준 가중 마진 {{pct}}% (벤더 원가 대비 GP {{gp}}).',
        cbdKpLowest: '최저 마진: {{name}} {{pct}}%.',
        cbdKpT0: '벤더 원가 = Base + 프레임 + MOQ 할증 + 단납기 할증.',
        cbdStripTitle: '판매가, 이익, 마진',
        cbdStripWhole: '견적 전체',
        cbdStripAsCard: '견적 전체 — 기준: {{card}}',
        cbdStripTry: '마진 시뮬레이션',
        cbdStripTryHint: '판매가 = 벤더 원가 ÷ (1 − 마진), 달러 단위 올림. 시뮬레이션은 견적을 변경하지 않습니다.',
        cbdStripClear: '지우기',
        cbdStripNow: '견적 기준',
        cbdStripAt: '마진 {{pct}}% 기준',
        cbdStripPrice: '판매가 (T2)',
        cbdStripProfit: '개당 이익',
        cbdStripMargin: '마진',
        cbdStripInline: '인라인 소비자가',
        cbdStripTotal: '주문 합계',
        cbdStripQty: '수량 (EA)',
        paS1: '1. 인라인 대비 가격 옵션 — USD/개',
        paS1Kind: '옵션 1–2는 영업/원가팀 작성 · 마지막 옵션: YL이 매출총이익률 입력',
        paOption: '옵션', paOptionN: '옵션 {{n}}', paItem: '품명 / 컬러', paStyle: '스타일 코드', paQty: '수량 (개)',
        paCost: '매입 원가', paOh: 'HPAC 오버헤드', paT1: 'T1 가격 (HPAC → 타 헬리녹스)', paInlineT1: '인라인 T1 가격',
        paT1vs: 'T1 vs 인라인 T1', paInlineMsrp: '인라인 소비자가', paPartnerMsrp: '파트너 소비자가 (참고)', paGmIn: '매출총이익률 (YL 입력)',
        paT2: 'T2 가격', paGp: '개당 매출총이익', paGm: '매출총이익률 (T2 기준)', paNet: '순이익률 (HPAC 오버헤드 차감)',
        paNetTot: '순이익률 (합계)', paSales: '총 매출 (USD)', paGpTot: '총 매출총이익 (USD)', paOfMsrp: '파트너 소비자가 대비 T2 (참고)',
        paCheck: 'T2 ≥ T1 확인', paApprove: '승인 (YL ✔)', paBelow: 'T1 미만', paOptTotal: '옵션 합계',
        paOpt1: '파트너가 할증 부담', paOpt1Sub: '표준 공식, 할증 전가',
        paOpt2: 'HPAC가 할증 부담', paOpt2Sub: '표준 공식',
        paOptGm: 'YL 결정', paOptGmSub: 'T2 기준 매출총이익률 — 여기에 입력',
        paOptOSub: '영업/원가팀이 작성한 원가, 파트너가 할증 부담 기준',
        paTick: '이 옵션 승인', paApproved: '승인된 옵션',
        paApproveOne: '옵션 하나만 선택', paGmAll: '모든 품목에 같은 이익률', paGmEach: '품목별 이익률', paGmToAll: '모두 같게', paYlOnly: 'YL이 가격 승인 화면에서 옵션 하나를 선택합니다', paSwitch: '이 옵션으로 변경',
        paNote1: '매입 원가 = 헬리녹스가 공장에 지불하는 가격 (MOQ + 단납기 할증 포함). T1 vs 인라인 T1 = 동등 인라인 스타일 대비 차이(%).',
        paNote2: '매출총이익률 = (T2 − 매입 원가) ÷ T2 (판매가 기준 마진, 마크업 아님). 순이익률 = (T2 − 매입 원가 − HPAC 오버헤드) ÷ T2. YL이 옵션 {{n}}의 매출총이익률을 입력하면 모든 결과가 바로 갱신됩니다.',
        paNote3: '{{partner}} 소비자가 (참고) = 인라인 소비자가 × {{pct}}%, 참고용. "T1 미만" = T2가 콜라보 T1보다 낮음. 승인 열에서 옵션 하나에만 ✔ 하세요.',
        paNoInlineT1: '이 견적에 인라인 T1 가격이 아직 없습니다 — 계산기의 "인라인 T1 가격"에 입력하면 T1을 인라인과 비교할 수 있습니다.',
        paGrpShow: '인라인 열 보기 (인라인 T1, T1 vs 인라인, 인라인 소비자가, 파트너 소비자가)', paGrpHide: '인라인 열 접기', paPartnerWord: '파트너', paHProject: '프로젝트', paHPartner: '파트너 (T2 고객)', paHSeason: '시즌', paHFactory: '공장', paHPrepared: '작성', paHReviewed: '검토', paHPriceTerm: '가격 조건', paHCurrency: '통화', paHLead: '총 리드타임 (일)', paHApprover: '승인자', paApprovedBy: '승인자', paApprovalDate: '승인일', paYlComments: 'YL 코멘트', paYlCommentsPh: '선택 사항 — 체크한 옵션과 함께 저장됩니다',
        paS2: '2. 마진 민감도 — 매출총이익률별 T2와 이익', paS2Kind: 'T2 = 매입 원가 ÷ (1 − 이익률), 올림',
        paS3: '3. 승인', paApprovedOpt: '승인 옵션', paApprovedT2: '승인 T2 — {{name}} (USD/개)', paNoneYet: '아직 승인 전 — 위에서 옵션 하나를 선택하세요',
        paExport: '가격 승인서 내보내기 (엑셀)', paExportHint: '이 견적을 린다의 가격 승인 템플릿(수식 포함)으로 — 파일에서도 YL이 이익률을 입력할 수 있습니다.',
        inlineT1: '인라인 T1 가격', inlineT1Hint: '동등 인라인 스타일의 T1 — 승인서에서 콜라보 T1과 비교합니다',
        styleCode: '스타일 코드', cbdFactoryName: '공장',

        cbdStripVsInline: '인라인 소비자가 대비',
        cbdKpInline: '판매가(T2)는 인라인 소비자가의 {{pct}}%입니다.',
        cbdKpInlineSome: '(인라인 소비자가 없는 SKU {{n}}개)',
        cbdStripUse: '이 가격을 지시사항에 넣기',
        cbdStripUseHint: '아래 지시사항 칸에 들어갑니다. 조건 카드로 승인하세요.',
        cbdStripInstr: 'T2 마진 {{pct}}% 기준 가격 (벤더 원가 ÷ (1 − 마진), 올림): {{list}}. 주문 {{rev}}, 이익 {{gp}}.',
        cbdKpLead: '리드타임 {{days}}일 (PO {{po}} → 출고 {{xf}}).',
        cbdKpAbsorbs: 'HPAC 부담: {{what}}.',
        cbdKpAbsorbsNone: 'HPAC 부담: 없음 — 모든 할증을 파트너에게 청구.',
        cbdOffer: '파트너 오퍼 (Excel)',
        cbdOfferTitle: '파트너에게 보내는 오퍼: 품목, 컬러, 수량, T2 가격만 — 원가 없음',
        cbdOfferNeedSave: '먼저 견적을 저장하세요',
        cbdOfferNeedApproval: '견적 승인 후 발행됩니다',
        cbdOfferNeedCard: '카드 선택 후 발행됩니다',
        cbdOfferTerms: '기타 조건이 선택되었습니다 — 지시 내용에 따라 오퍼를 작성하세요',
        cbdB7: '할증 부담 주체 — 주문 전체 {{n}} EA에 대해 {{amt}}',
        cbdB7Note: 'MOQ와 단납기 할증은 누가 부담하든 공장이 청구합니다. A–D는 Calculation 시트의 네 가지 경우이며, 위의 옵션은 파트너가 두 할증을 부담하는 기준으로 계산된 카드가 하나 더 추가됩니다.',
        padOrderHead: '주문 ({{qty}} EA)',
        rule_cbd_oh_pct: 'CBD · HPAC 간접비 %',
        rule_cbd_t2_markup_pct: 'CBD · T2 마크업 % (30 = × 1.3)',
        padB7NoteCalc: 'A, B, C\ub294 \ubaa8\ub4e0 \uacac\uc801\uc5d0 \uae30\ubcf8\uc73c\ub85c \uc788\uc2b5\ub2c8\ub2e4. \ub2e4\ub978 \ubc29\uc2dd\uc73c\ub85c Extra\ub97c \ub098\ub204\ub824\uba74 \uc704\uc5d0\uc11c \uc635\uc158\uc744 \ucd94\uac00\ud558\uc138\uc694 \u2014 \uc5ec\uae30\uc640 \uc2b9\uc778 \ud654\uba74\uc5d0 \uce74\ub4dc\uac00 \ud558\ub098 \ub354 \uc0dd\uae41\ub2c8\ub2e4.',
        padB7Note: '\ud30c\ud2b8\ub108 \ub3c4\ub9e4 \uacbd\ub85c\uc5d0\ub9cc \ud574\ub2f9\ud569\ub2c8\ub2e4. A, B, C \uc5d0\uc11c\ub294 MSRP {{m}}\uc640 \ud30c\ud2b8\ub108 \ub2e8\uac00 {{p}}\uac00 \ub3d9\uc77c\ud558\uba70, \ub2e8\uc704\ub2f9 Extra {{e}}\ub9cc \uc6c0\uc9c1\uc785\ub2c8\ub2e4.',
        padKindCompare: '\ube44\uad50',
        padKindSolo: '\ucf5c\ub77c\ubcf4 \uc804\uc6a9 \u00b7 \ube44\uad50 \uc5c6\uc74c',
        padThisQuote: '\uc774 \uacac\uc801',
        padCompareOnly: '\ube44\uad50\uc6a9',
        padInlineShort: '\uc778\ub77c\uc778',
        padSameWord: '\ub3d9\uc77c',
        padNoBench: '\uae30\uc900 \uc5c6\uc74c',
        padNoneWord: '\uc5c6\uc74c',
        padFactory: '\uacf5\uc7a5 \uc0ac\uc785\uac00',
        padFrame: '\ud504\ub808\uc784 \ube44\uc6a9',
        padFactoryN: '\uacf5\uc7a5 \uc0ac\uc785\uac00 (2)',
        padFrameN: '\ud504\ub808\uc784 \ube44\uc6a9 (3)',
        padExtraN: 'Extra \ube44\uc6a9 (4)',
        padExtraPerUnit: '\ub2e8\uc704\ub2f9 Extra \ube44\uc6a9',
        padBaseFrom: '= \uacf5\uc7a5 {{f}} + \ud504\ub808\uc784 {{r}}',
        padBaseSub: '\uacf5\uc7a5 + \ud504\ub808\uc784',
        padPctOfTotal: '\ucd1d\uc6d0\uac00\uc758 {{n}}%',
        padMultBase: '\uae30\ubcf8 \uc6d0\uac00\ub9cc: \ucf5c\ub77c\ubcf4\uac00 \uc778\ub77c\uc778\uc758 {{x}}\ubc30',
        padMultAll: 'Extra \ud3ec\ud568: \uc778\ub77c\uc778\uc758 {{x}}\ubc30',
        padLead: '\uae30\ubcf8 \uc6d0\uac00\ub294 \ube44\uc2b7\ud569\ub2c8\ub2e4. \ubb38\uc81c\ub294 Extra\uc785\ub2c8\ub2e4.',
        padOwnRetailSub: 'HINC \ub4f1\uc774 MSRP\ub85c \ud310\ub9e4',
        padPartnerWs: '\ud30c\ud2b8\ub108 \ub3c4\ub9e4',
        padPartnerWsSub: 'MSRP\uc758 {{p}}%\ub85c \uacf5\uae09',
        padCallout: '\uc790\uccb4 \ub9ac\ud14c\uc77c\uc740 Extra\ub97c \ubd80\ub2f4\ud558\uace0\ub3c4 {{pct}}%\ub97c \ub0a8\uae41\ub2c8\ub2e4. \ud30c\ud2b8\ub108 \ub3c4\ub9e4\ub294 \uadf8\ub7f4 \uc218 \uc5c6\uc5b4 \uc6d0\uac00 \uc544\ub798\ub85c \ub0b4\ub824\uac11\ub2c8\ub2e4. \ud30c\ud2b8\ub108 \ucabd Extra\ub97c \ub204\uac00 \ubd80\ub2f4\ud560\uc9c0\ub294 \ube14\ub85d 7\uc5d0\uc11c \uc815\ud569\ub2c8\ub2e4.',
        padMsrp: 'MSRP',
        padPartnerUnit: '\ud30c\ud2b8\ub108 \ub2e8\uac00',
        padExtraBilledUnit: '+ \uccad\uad6c Extra',
        padPerUnit: '/\ub2e8\uc704',
        padExtraAbsorbed: 'Helinox \ud761\uc218 Extra',
        padGrossSub: '= O.H + \uc21c\ub9c8\uc9c4',
        padWhoA: 'Extra\ub97c \uc6d0\uac00 \uadf8\ub300\ub85c \ubcc4\ub3c4 \ud56d\ubaa9\uc73c\ub85c \uccad\uad6c\ud569\ub2c8\ub2e4. \ub9c8\uc9c4\uc740 \ubd99\uc774\uc9c0 \uc54a\uc2b5\ub2c8\ub2e4.',
        padWhoB: 'Extra\ub97c HPAC \ub9c8\ucf00\ud305 \ube44\uc6a9\uc73c\ub85c \ucc98\ub9ac\ud569\ub2c8\ub2e4. \ud30c\ud2b8\ub108\ub294 \ub2e8\uac00\ub9cc \ubd05\ub2c8\ub2e4.',
        padWhoC: 'Extra\uc758 \uc808\ubc18\uc740 \ud30c\ud2b8\ub108\uc5d0\uac8c \uccad\uad6c\ud558\uace0 \uc808\ubc18\uc740 Helinox\uac00 \ud761\uc218\ud569\ub2c8\ub2e4.',
        padCeoDecision: '\ub300\ud45c \uacb0\uc815',
        padCeoDecisionSub: '\uc2b9\uc778\uacfc \ud568\uaed8 \uae30\ub85d\ub429\ub2c8\ub2e4. \uae30\uc548\uc790\ub294 \uc774 \ub0b4\uc6a9\ub300\ub85c \ub9c8\uac10\ud558\uba70, \uc7ac\uc0c1\uc2e0\uc740 \uc5c6\uc2b5\ub2c8\ub2e4.',
        padDrowFinal: '\ucd5c\uc885 \uc870\uac74',
        padDrowRecorded: '\uae30\ub85d',
        padOpenCalc: '\uacc4\uc0b0\uae30\uc5d0\uc11c \uc5f4\uae30',
        approverView: '\uc2b9\uc778\uc790\uac00 \ubcf4\uac8c \ub420 \ud654\uba74',
        approverViewSub: '\uac00\uaca9 \uc2b9\uc778 \ud654\uba74\uacfc \ub3d9\uc77c\ud55c \ub450 \ube14\ub85d\uc744 \ub3d9\uc77c\ud55c \uc218\uce58\ub85c \ud45c\uc2dc\ud569\ub2c8\ub2e4. \uc77d\uae30 \uc804\uc6a9 \u2014 \uc22b\uc790\ub294 \uc704\uc5d0\uc11c \uc785\ub825\ud569\ub2c8\ub2e4.',
        padSubmittedBy: '\uc0c1\uc2e0\uc790',
        padNoSnapshot: '\uc774 \uacac\uc801\uc740 \uc22b\uc790 \uace0\uc815 \uae30\ub2a5 \uc774\uc804\uc5d0 \uc0c1\uc2e0\ub418\uc5b4 \uae30\ub85d\ub41c \ub0b4\uc6a9\uc774 \uc5c6\uc2b5\ub2c8\ub2e4. \uacc4\uc0b0\uae30\uc5d0\uc11c \ud604\uc7ac \uc0c1\ud0dc\ub97c \ud655\uc778\ud558\uc138\uc694.',
        padFrozen: '\uc544\ub798 \uc22b\uc790\ub294 \uc2b9\uc778 \uc694\uccad \ub2f9\uc2dc \uc0c1\uc2e0\ub41c \uac12\uc785\ub2c8\ub2e4.',
        padEdited: '\uc0c1\uc2e0 \uc774\ud6c4 \uacac\uc801\uc774 \uc218\uc815\ub418\uc5c8\uc2b5\ub2c8\ub2e4.',
        padLine: '\ud56d\ubaa9',
        padCollab: '\ucf5c\ub77c\ubcf4',
        padInline: '\uc778\ub77c\uc778',
        padGap: '\ucc28\uc774',
        padBase: '\uae30\ubcf8 \uc6d0\uac00',
        padExtra: 'Extra \ube44\uc6a9',
        padExtraNote: '\uc778\ub77c\uc778 \uc0dd\uc0b0\uc5d0\uc11c\ub294 \ubc1c\uc0dd\ud558\uc9c0 \uc54a\ub294 \uae08\uc561',
        padTotal: '\ub2e8\uc704\ub2f9 \ucd1d\uc6d0\uac00',
        padOrder: '\uc8fc\ubb38 \ud569\uacc4 \u00b7 {{qty}} EA',
        padPriceMargin: '\uac00\uaca9\uacfc \ub9c8\uc9c4',
        padOwnRetail: '\uc790\uccb4 \ub9ac\ud14c\uc77c',
        padPartner: '\ud30c\ud2b8\ub108 \ub3c4\ub9e4 \u00b7 MSRP\uc758 {{pct}}%',
        padWeSell: '\ud310\ub9e4\uac00',
        padMarginPre: 'Extra \uc804 \ub9c8\uc9c4',
        padMarginPost: 'Extra \ud6c4 \ub9c8\uc9c4',
        padWhoCarries: '\ucd94\uac00 \ube44\uc6a9\uc744 \ub204\uac00 \ubd80\ub2f4\ud558\ub294\uac00 \u2014 {{qty}} EA \uae30\uc900 {{total}}',
        padWhoSub: '\ud30c\ud2b8\ub108 \ub3c4\ub9e4 \ucc44\ub110\uc5d0\ub9cc \uc801\uc6a9\ub429\ub2c8\ub2e4. MSRP {{msrp}}\uc640 \ud30c\ud2b8\ub108 \ub2e8\uac00 {{unit}}\ub294 \ubaa8\ub4e0 \uc548\uc5d0\uc11c \ub3d9\uc77c\ud558\uba70, \ucd94\uac00 \ube44\uc6a9\ub9cc \ub2ec\ub77c\uc9d1\ub2c8\ub2e4.',
        padOptA: 'A \u00b7 \ud30c\ud2b8\ub108 \ubd80\ub2f4',
        padOptB: 'B \u00b7 Helinox \ud761\uc218',
        padOptC: 'C \u00b7 \ubc18\ubc18',
        fxBilledRule: 'Extra \u00d7 \ud30c\ud2b8\ub108 \ubd80\ub2f4 \ube44\uc728',
        fxRevenueRule: 'SKU\ubcc4 \uc218\ub7c9 \u00d7 MSRP\ub97c \uc8fc\ubb38 \uc804\uccb4\ub85c \ud569\uc0b0',
        fxAbsorbedRule: 'Extra \u2212 \ud30c\ud2b8\ub108\uc5d0\uac8c \uccad\uad6c\ud55c \uae08\uc561',
        fxGrossRule: '\ud30c\ud2b8\ub108 \ub9e4\ucd9c + \uccad\uad6c Extra \u2212 \ucd1d\uc6d0\uac00',
        fxOhRule: '\uc8fc\ubb38 \uae30\uc900 \uc6d0\uac00 \u00d7 O.H \uc694\uc728',
        fxNetRule: '\ub9e4\ucd9c\ucd1d\ub9c8\uc9c4 \u2212 Helinox O.H',
        fxMarginRule: '\uc21c\ub9c8\uc9c4 \u00f7 \ud30c\ud2b8\ub108 \ub9e4\ucd9c',
        padGate: '\uc774 \uacac\uc801\uc744 \uc2b9\uc778\ud558\uc2dc\uaca0\uc2b5\ub2c8\uae4c?',
        padGateSub: '현재 {{status}} 상태입니다. 다음 단계에서 SKU별로 결정하고 메모도 함께 남깁니다.',
        padGateApprove: '\uc2b9\uc778',
        padGateReject: '\ub418\ub3cc\ub9ac\uae30',
        padGateCancel: '\ub098\uc911\uc5d0',
        padOtherCostings: '\uc774 \uacac\uc801\uc5d0\ub294 \ub2e4\ub978 \uc6d0\uac00 \uc0b0\uc815 {{n}}\uac1c\uac00 \ub354 \uc788\uc2b5\ub2c8\ub2e4',
        padOtherCostingsSub: '\uacc4\uc0b0\uae30\uc5d0\uc11c \ub2e4\ub978 \ubc29\uc2dd\uc73c\ub85c \uc0b0\uc815\ud55c \uac19\uc740 \uacac\uc801\uc785\ub2c8\ub2e4. A \uce74\ub4dc\uc640 \ube44\uad50\ud560 \uc218 \uc788\ub3c4\ub85d \uac01\uac01 A \uae30\uc900\uc73c\ub85c \uacc4\uc0b0\ub418\uc5b4 \uc788\uc73c\uba70, \uc120\ud0dd\ud558\uba74 \ud574\ub2f9 \uc0b0\uc815 + \ud30c\ud2b8\ub108 \ubd80\ub2f4\uc774 \ub429\ub2c8\ub2e4.',
        padRefAt: 'A \uae30\uc900 \u00b7 \ud30c\ud2b8\ub108 \ubd80\ub2f4',
        padSettings: '\uc774 \uc22b\uc790\ub4e4\uc774 \uacc4\uc0b0\ub41c \uae30\uc900',
        padSetPremium: 'Collab premium',
        padSetPartner: '\ud30c\ud2b8\ub108 \ub9c8\uc9c4',
        padSetSplitC: '\ubd84\ub2f4 \ube44\uc728 (C \uce74\ub4dc)',
        padSetOh: 'Helinox O.H',
        padSetOhVal: '\uc6d0\uac00\uc758 {{pct}}%',
        padSetCase: 'Case',
        padOptTerms: '\uae30\ud0c0 \uc870\uac74',
        padOptTermsSub: '\uc704 \uce74\ub4dc \uc911 \ub9de\ub294 \uac83\uc774 \uc5c6\uc73c\uba74 \uc544\ub798 Instruction \uce78\uc5d0 \uc6d0\ud558\ub294 \uc870\uac74\uc744 \uc801\uc5b4\uc8fc\uc138\uc694. \uae30\uc548\uc790\uac00 \uadf8 \ub0b4\uc6a9\ub300\ub85c \ub9c8\uac10\ud569\ub2c8\ub2e4.',
        padSrcPortal: '포털 기본',
        padSrcQuote: '기안자 추가',
        padSrcApprover: '승인자가 작성',
        padSumHead: '이 견적의 {{all}}개 SKU 중 {{n}}개를 승인합니다',
        padSumCard: '추가 비용 부담',
        padSumQty: '승인 수량',
        padSumNoCard: '아직 카드를 선택하지 않았습니다. 이 승인은 SKU만 정리하고 추가 비용은 미정으로 남깁니다.',
        padSumNothing: '모든 SKU가 제외되었습니다. 견적 전체를 반려하려면 Send back을 써주세요.',
        padSumUnread: '그중 {{n}}개는 아직 열어보지 않았습니다.',
        padSkuUnseen: '미확인',
        padSkuCut: '제외됨',
        padSkuSeen: '이 견적의 {{all}}개 SKU 중 {{n}}개를 열어봤습니다',
        padSkuAllSeen: '{{n}}개 SKU를 모두 열어봤습니다',
        padSkuCutN: '{{n}}개는 앞서 제외됨',
        padCardScope: '아래 금액은 견적 전체 {{all}}개가 아니라 남은 {{n}}개 SKU 기준입니다.',
        padCardGone: '\u2014 \uc774 \uc635\uc158\uc740 \ud604\uc7ac \uacac\uc801\uc5d0 \uc5c6\uc2b5\ub2c8\ub2e4',
        padBilled: '\uccad\uad6c\ub418\ub294 \ucd94\uac00 \ube44\uc6a9',
        padRevenue: '\ud310\ub9e4 \ub9e4\ucd9c (\uc218\ub7c9 \u00d7 MSRP)',
        padAllIn: '\ud30c\ud2b8\ub108 \uc2e4\ubd80\ub2f4',
        padGross: '\uc8fc\ubb38 \ucd1d \ub9c8\uc9c4',
        padOh: 'Helinox O.H \u00b7 \uae30\ubcf8 \uc6d0\uac00\uc758 {{pct}}%',
        padNetProfit: 'Helinox \uc21c\ub9c8\uc9c4',
        padNetMargin: '\ud30c\ud2b8\ub108 \ud310\ub9e4\uac00 \ub300\ube44 \uc21c\ub9c8\uc9c4\ub960',
        padSelect: '{{key}} \uc120\ud0dd',
        padDecision: '\ub300\ud45c \uacb0\uc815 \u00b7 \uc2b9\uc778\uacfc \ud568\uaed8 \uae30\ub85d',
        padChosen: '\ucd94\uac00 \ube44\uc6a9 \ubd80\ub2f4\uc790',
        padUndecided: '\uc544\uc9c1 \uacb0\uc815\ub418\uc9c0 \uc54a\uc74c',
        padInstruction: '\uc9c0\uc2dc',
        padInstructionPh: '\uc608: A\ub85c \uac00\ub418 \ud30c\ud2b8\ub108 \ub2e8\uac00\ub294 $60\uc73c\ub85c \ubc18\uc62c\ub9bc, Extra\ub294 \uc77c\uad04 \uccad\uad6c.',
        padSaveInstruction: '\uc9c0\uc2dc \uc800\uc7a5',
        padDecisionSaved: '\uacb0\uc815\uc774 \uae30\ub85d\ub418\uc5c8\uc2b5\ub2c8\ub2e4.',
        padMemo: 'Sales OP \ucd5c\uc885 \uba54\ubaa8',
        padMemoSub: '\uc704 \uacb0\uc815\uc744 \ud655\uc778\ud55c \ub4a4 \ucd5c\uc885 \ud569\uc758\ub41c \uac00\uaca9\uacfc \uc870\uac74\uc785\ub2c8\ub2e4. \ud55c \ubc88\ub9cc \uc791\uc131\ud560 \uc218 \uc788\uc73c\uba70 \uc774\ud6c4 \uc218\uc815\ud560 \uc218 \uc5c6\uc2b5\ub2c8\ub2e4.',
        padMemoPh: '\ud569\uc758\ub41c \ucd5c\uc885 \uac00\uaca9\uacfc \uc870\uac74\u2026',
        padMemoWaitShort: '\uc544\uc9c1 \uae30\ub85d\ub418\uc9c0 \uc54a\uc74c',
        padMemoWait: '\uc544\uc9c1 \uc774 \uacac\uc801\uc5d0 \ub300\ud55c \uacb0\uc815\uc774 \uc5c6\uc2b5\ub2c8\ub2e4.',
        padMemoSave: '\uba54\ubaa8 \uae30\ub85d',
        padMemoSaved: '\uba54\ubaa8\uac00 \uae30\ub85d\ub418\uc5c8\uc2b5\ub2c8\ub2e4.',
        modeRules: '\uac00\uaca9 \uaddc\uce59',
        modeRulesSub: '\uad00\ub9ac\uc790 \u00b7 \ubaa8\ub4e0 \uacac\uc801\uc758 \uae30\uc900 \uc0c1\uc218',
        rulesTitle: '\uac00\uaca9 \uaddc\uce59',
        rulesSub: '\ubcc0\uacbd\uc740 \uc800\uc7a5 \uc774\ud6c4 \uc0dd\uc131\ub41c \uacac\uc801\ubd80\ud130 \uc801\uc6a9\ub429\ub2c8\ub2e4. \uc774\ubbf8 \uc791\uc131\ub41c \uacac\uc801\uc740 \ub2f9\uc2dc \uaddc\uce59\uc744 \uadf8\ub300\ub85c \uc720\uc9c0\ud569\ub2c8\ub2e4.',
        ruleName: '\uaddc\uce59',
        ruleNow: '\ud604\uc7ac \uac12',
        ruleNew: '\uc0c8 \uac12',
        ruleWhy: '\uc0ac\uc720',
        ruleWhyPh: '\uc65c \ubcc0\uacbd\ud569\ub2c8\uae4c?',
        ruleSave: '\uc800\uc7a5',
        ruleSaved: '\uc800\uc7a5\ub418\uc5c8\uc2b5\ub2c8\ub2e4. \uc774\ud6c4 \uc0dd\uc131\ub418\ub294 \uacac\uc801\uc5d0 \uc801\uc6a9\ub429\ub2c8\ub2e4.',
        ruleHistory: '\ubcc0\uacbd \uc774\ub825 \u00b7 \ucd5c\uc2e0\uc21c',
        ruleValue: '\uac12',
        ruleBy: '\ubcc0\uacbd\uc790',
        ruleFrom: '\uc801\uc6a9 \uc2dc\uc810',
        rule_partner_ratio_pct: '\ud30c\ud2b8\ub108 \ube0c\ub79c\ub4dc \uacf5\uae09\uac00',
        ruleHint_partner_ratio_pct: 'MSRP \ub300\ube44 \ud30c\ud2b8\ub108 \ube0c\ub79c\ub4dc \uacf5\uae09\uac00 \ube44\uc728\uc785\ub2c8\ub2e4.',
        rule_tier_oh_pct: 'Helinox O.H',
        ruleHint_tier_oh_pct: '\ud2f0\uc5b4 \ube44\uc728\uc758 \uac04\uc811\ube44 \ubd80\ubd84\uc785\ub2c8\ub2e4. \uc0c8 \uacac\uc801\uc758 \ucd08\uae43\uac12\uc774\uba70, \uc800\uc7a5\ub41c \uacac\uc801\uc740 \uc790\uccb4 \uac12\uc744 \uc720\uc9c0\ud569\ub2c8\ub2e4.',
        rule_frame_profit_pct: '\ud504\ub808\uc784 \ub9c8\uc9c4',
        ruleHint_frame_profit_pct: '\ud504\ub808\uc784 \uc6d0\uac00 \u00d7 \uc774 \ube44\uc728. \ubb34\uc0c1 \uc81c\uacf5 \uc2dc\uc5d0\ub294 0\uc785\ub2c8\ub2e4.',
        modeSaved: '저장된 프로젝트',
        modeSavedSub: '조회 · 불러오기 · 엑셀 내보내기',
        modeOpa: 'OPA / SCT',
        modeOpaSub: '공정 분석 · 표준 사이클 타임',
        projectPlaceholder: '프로젝트 이름 — 예: 26FW MICRO KICKBOARD x Chair One (re)',
        template: '가져오기 양식',
        importExcel: '엑셀 가져오기',
        importBom: '벤더 BOM 가져오기',
        bomRead: '{{vendor}} BOM으로 읽음 — SKU {{n}}개: {{names}}',
        bomFailed: '이 BOM을 읽을 수 없습니다',
        bomLookedFor: '포털이 아는 두 가지 벤더 양식에서 찾은 항목:',
        bomSheets: '이 파일의 시트: {{list}}',
        dismiss: '닫기',
        imported: '파일에서 {{n}}개 SKU를 불러왔습니다 — 숫자를 확인한 뒤 저장하세요.',
        save: '프로젝트 저장',
        saveChanges: '변경사항 저장',
        newProject: '새 프로젝트',
        exportExcel: '엑셀 내보내기',
        savedAs: '{{id}} (으)로 저장했습니다 — 다음 건을 입력할 수 있습니다.',
        loaded: '{{id}} 불러옴 — 저장하면 갱신된 뒤 화면이 초기화됩니다.',
        needName: '프로젝트 이름을 먼저 입력하세요.',
        needSave: '먼저 저장하세요 — 내보내기는 저장된 내용을 읽습니다.',
        deleteAsk: '{{id}} 을(를) 삭제할까요? 목록에서만 사라지고 코스팅 자체는 보관됩니다.',
        sku: 'SKU',
        addSku: 'SKU 추가',
        duplicate: '복제',
        del: '삭제',
        inputs: '입력',
        collapse: '접기',
        expand: '펼치기',
        results: '결과',
        skuName: 'SKU / 품목명',
        skuNameHint: '프로젝트 내에서 이 품목을 구분할 이름.',
        qty: '발주 수량 (EA)',
        qtyHint: 'MOQ 서차지와 HPAC 차지 금액을 결정합니다.',
        poDate: 'PO 발행일',
        poDateHint: '리드타임 시작일.',
        xfDate: '\uc694\uccad \ucd9c\uace0 \uc77c\uc790',
        xfDateHint: 'XF − PO = 리드타임.',
        moqLabel: 'MOQ',
        moqNone: '500pcs 이상 → 서차지 없음',
        moqApplied: '{{qty}}pcs → {{of}}의 {{rate}}',
        moqQtyPcs: '{{qty}}pcs →',
        moqOfBase: '/ {{of}} 기준',
        leadLabel: '리드타임',
        leadNone: '두 일자를 모두 입력하면 계산됩니다',
        leadDays: '{{days}}일 → 숏리드 {{rate}}',
        leadWarn: '{{days}}일은 60일 미만입니다 — 규정에 없는 구간입니다. 임시로 50%를 적용했으며 서차지는 별도 협의가 필요합니다.',
        baseTitle: 'Markup Base 원가',
        baseSub: '마진은 아래 항목과 FM Fee만을 기준으로 산정됩니다.',
        skin: '\uc2a4\ud0a8 + \ucf00\uc774\uc2a4 + \ud3ec\uc7a5',
        skinHint: '원단 스킨 · 케이스 · 포장 합계.',
        cmt: '\ubca4\ub354 \uacf5\uc784',
        cmtHint: '벤더 봉제 공임 (Cut · Make · Trim).',
        ohp: '\ubca4\ub354 OH & \ub9c8\uc9c4',
        ohpHint: '벤더의 관리비와 마진.',
        frame: '프레임 원가',
        frameHint: '모델을 선택하면 프레임 단가표에서 자동 입력됩니다.',
        framePick: '프레임 모델 선택…',
        frameSupply: '프레임 공급 방식',
        supplySell: '벤더에 판매',
        supplySellHint: '\ud504\ub808\uc784 \ub9c8\uc9c4 + \ud578\ub4e4\ub9c1 $0.50 + \uc2e4\ube44 \uc6b4\uc1a1\ube44 (p.19)',
        supplyFoc: '무상 지급 (FOC)',
        supplyFocHint: 'FM Fee $0.04만 부과 (p.13, p.20)',
        matSupply: '자재 공급 방식',
        matVendor: '벤더가 직접 구매',
        matVendorHint: '원단·케이스·포장재를 벤더가 자체 구매합니다.',
        matFf: '헬리녹스 무상 지급 (FF)',
        matFfHint: '자재 원가가 벤더에 없으므로 대신 FF CMT 수수료를 청구합니다.',
        frameProfitRate: '\ud504\ub808\uc784 \ub9c8\uc9c4\ub960',
        excludedTitle: '제외 항목',
        excludedSub: '인라인 생산에서는 발생하지 않는 비용입니다. T1 마진 기준에 포함되며, 동시에 HPAC 마케팅 비용으로 파트너에게 회수합니다. T2는 이 비용을 빼고 산정합니다. 값을 직접 입력하면 해당 항목만 수동으로 바뀝니다.',
        autoToggle: '규정값 자동 적용',
        autoTag: '자동',
        resetOverride: '수동 — 되돌리기',
        fprofit: '\ud504\ub808\uc784 \ub9c8\uc9c4',
        fprofitHint: '프레임 원가 x 이익률. 무상 지급이면 0.',
        handling: '핸들링',
        handlingHint: 'Helinox가 벤더에 프레임을 판매할 때 $0.50 (p.19).',
        fmfee: 'FM Fee',
        fmfeeHint: '프레임을 무상 지급할 때 $0.04 (p.13, p.20). 기준 원가에 포함됩니다.',
        transport: '운송비',
        transportHint: '실비 — 추정하지 않습니다.',
        moqCharge: 'MOQ 서차지',
        moqChargeHint: 'Markup Base x MOQ 요율 (p.15).',
        addMat: '추가 자재비',
        addMatHint: 'Markup Base에 포함되지 않은 추가 소요분.',
        ffcmt: 'FF CMT 수수료',
        ffcmtHint: '헬리녹스가 지급한 자재에 대한 벤더 가공비. 서차지 이후에 더해집니다.',
        ffcmtOff: '헬리녹스가 자재를 지급할 때만 적용 \u2014 자재 공급 방식 참조.',
        shortLead: '\ub2e8\ub0a9\uae30 \uc11c\ucc28\uc9c0',
        shortLeadHint: '벤더 CMT x (1 + MOQ 요율) x 숏리드 요율 (p.21).',
        moqBaseLabel: 'MOQ 서차지 부과 대상',
        moqBaseHint: '이 벤더가 서차지 뒤에 더하는 항목은 체크를 해제하세요.',
        part_skin: 'Skin + Case + Packaging',
        part_cmt: '벤더 CMT',
        part_ohp: 'OH & Profit',
        part_frame: '프레임',
        part_fmfee: 'FM fee',
        pricingTitle: '가격 산정',
        stepProfit: '\ub9c8\uc9c4 / MSRP \uc2dc\ubbac\ub808\uc774\ud130',
        stepTarget: '목표 MSRP',
        t1Ratio: 'HPAC \uc678\ubd80 \ub9c8\uc9c4 (T2 \ube44\uc728)',
        t1RatioHint: 'T2에 대한 마진이며, 원가에 더하는 마크업이 아닙니다. Case A에서는 필수입니다.',
        t0Ratio: 'HPAC \ub0b4\ubd80 \ub9c8\uc9c4 (T1 \ube44\uc728)',
        t0RatioHint: 'Helinox 법인 단계입니다. Case B에서는 필수입니다.',
        options: '옵션',
        optionsNote: '각 옵션은 이 견적에서 한두 가지 조건만 바꾼 것이며, 위의 모든 수치를 그대로 따라갑니다.',
        optionAdd: '옵션 추가',
        optionN: '옵션 {{n}}',
        optAsQuoted: '\uc774 \uacac\uc801',
        optAsQuotedSub: '위에 입력한 값',
        optNoCond: '아직 변경한 조건이 없습니다',
        optSameAsQuote: '견적과 동일합니다 — 왼쪽에서 값을 바꾸면 이 숫자가 움직입니다.',
        optEdit: '조건',
        optCancel: '취소',
        optDone: '완료',
        optRemove: '삭제',
        optQuoteReadOnly: '옵션과 대조해 보기 위한 견적 자체의 값입니다. 수정은 위쪽 블록에서 하세요.',
        optClose: '닫기',
        optCondNote: '여기서 값을 바꾸세요. 그대로 둔 항목은 견적을 따릅니다.',
        optOrderProfitT1: '\uc8fc\ubb38 \ucd1d\ub9c8\uc9c4 (T1 \uae30\uc900)',
        optOrderProfitT2: '\uc8fc\ubb38 \ucd1d\ub9c8\uc9c4 (T2 \uae30\uc900)',
        optQty: '주문 수량',
        optBasis: '마진 기준',
        optT1Profit: 'T1 \ub9c8\uc9c4',
        optT2Ratio: 'T2 비율',
        optCbdMargin: 'T2 마진',
        optT2Profit: 'T2 이익',
        optPremium: '콜라보 프리미엄',
        optShortLead: '단납기 할증',
        benchTitle: '인라인 비교',
        benchTag: '비교 전용',
        benchLine: '원가 항목',
        benchThis: '이 견적',
        benchInline: '인라인 제품',
        benchDiff: '차이',
        vsInline: '인라인 소비자가 대비',
        linesApplied: '이 옵션이 청구하는 기타 비용 1 항목',
        qtyWord: '주문 수량',
        targetHint: '인라인 소매가 × (1 + 콜라보 프리미엄)',
        lineDropped: '청구 안 함',
        inclTitle: '마진을 계산하는 금액에 포함',
        exclTitle: '그 밖 — HPAC 마케팅 debit으로 회수',
        inclBoth: '마진 기준 금액에도 포함되어 있어 이중으로 회수됩니다',
        inclTotal: '마진 기준',
        exclTotal: '파트너에게 청구',
        quotedIn: '마진 대상',
        quotedOut: '마진 제외',
        estIsCheck: '참고용 확인값이며 견적의 MSRP가 아닙니다 — 이 값을 읽는 계산은 없습니다. 견적이 사용하는 MSRP는 6번 블록에 있습니다.',
        msrpIsQuote: '견적 MSRP',
        factoryWord: '생산원가',
        frameWord: '프레임',
        marginAtQty: '{{n}} EA 기준 마진',
        gapWord: '차이',
        collabCol: '콜라보',
        benchCol: '인라인',
        benchTotal: '공장 원가',
        benchWhy: '차이가 나는 이유',
        benchWhyHint: '인라인 버전 대비 협업 제품에서 더해지거나 빠지는 부분 — 승인자가 차이 옆에서 읽는 문장입니다. 이 단계의 값은 견적에 반영되지 않습니다.',
        ohPart: '\uc624\ubc84\ud5e4\ub4dc (\uac04\uc811\ube44)',
        ohPartFixed: '간접비 (고정)',
        frameModelLabel: '프레임 모델',
        benchLinkedTo: '연결 대상',
        benchNoModel: '프레임 모델 미선택',
        benchFollowsFrame: '3번 블록에서 고른 프레임 모델을 따릅니다',
        benchFromCollab: 'Collab에서',
        benchFrameHint: '인라인 제품이 쓰는 프레임의 자체 리스트 가격입니다.',
        benchModelHint: '3번 블록을 따릅니다 — 같은 부품이므로 차이는 설계가 아니라 가격입니다.',
        benchSame: '동일',
        benchBase: '기준',
        blkExtra: '별도',
        fxBuying: '\ub9c8\uc9c4 \uc804 \uac1c\ub2f9 \uc6d0\uac00',
        fxBuyingRule: 'Base는 공장원가 + 프레임원가로, 티어 비율이 적용되는 기준 금액입니다. 별도 비용은 마진을 붙인 뒤에 더해지며 마크업 대상이 아닙니다. 둘을 합치면 매입원가(buying cost)입니다.',
        fxBenchBase: '\uc778\ub77c\uc778 \uc6d0\uac00 (\uc2a4\ud0a8 + CMT + \uac04\uc811\ube44\u00b7\ub9c8\uc9c4 + \ud504\ub808\uc784)',
        fxBenchBaseRule: '인라인 제품 자체의 공장원가에 해당 제품이 쓰는 프레임의 정가를 더한 값입니다. 이후 더해지는 항목은 없습니다 — 인라인 쪽에는 별도 비용이 설정되어 있지 않습니다.',
        benchFrameLabel: '프레임 원가',
        benchImport: '원가표에서 채우기',
        benchImportHint: '인라인 원가표에 보관된 원가표로 이 열을 채웁니다.',
        benchSrc: '출처',
        benchPickTitle: '이 견적은 어느 행과 비교합니까?',
        benchPickBody: '{{file}}에 {{n}}개 행의 가격이 있습니다. 이 견적을 비교할 행을 고르세요 — 각 행의 금액은 원가표가 직접 적어 둔 buying cost입니다.',
        benchVariant: '원가 버전',
        benchApply: '벤치마크 채우기',
        benchRefused: '{{n}}개 행은 읽지 못했습니다. 각 행의 항목 합계가 옆에 적힌 buying cost와 맞지 않습니다:',
        benchLibBody: '인라인 원가표에 보관된 원가표를 스타일, SKU, 컬러웨이, 파일명으로 찾으세요.',
        benchSearch: '스타일, SKU, 컬러웨이, 파일명 검색…',
        benchNewFile: '새 파일 읽기…',
        benchBackToLib: '보관함으로',
        benchLibEmpty: '아직 보관된 원가표가 없습니다. Sales Operation → 인라인 원가표에서 읽어 옵니다.',
        benchNoHit: '"{{q}}"와 일치하는 항목이 보관함에 없습니다.',
        benchLineCol: '항목',
        benchDiff: '차이',
        benchWhyCol: '설명',
        benchWhyPh: '차이가 나는 이유',
        benchNoGap: '차이 없음 — 설명할 것이 없습니다',
        benchAllLevel: '모든 항목이 인라인 제품과 같습니다 — 설명할 것이 없습니다.',
        benchNotFilled: 'Inline benchmark를 채우면 차이가 나는 항목이 표시됩니다.',
        benchElsePh: '그 외 항목…',
        benchBomSub: '콜라보 가격과 인라인 가격의 BOM 차이입니다.',
        stepExtra2: 'Extra cost 2',
        stepExtra2Sub: '(원가 비교)',
        addMatWhat: '어떤 항목인지…',
        addLine: '행 추가',
        removeLine: '이 행 삭제',
        ohLine: 'Helinox \uc624\ubc84\ud5e4\ub4dc (\uac04\uc811\ube44)',
        profitLine: 'Helinox \ub9c8\uc9c4',
        extraLine: '기타 비용',
        extraFromBlock: '4번 블록에서',
        inclExtra: 'O.H + 이익 + 기타 비용 — 파트너가 실제로 지불하는 금액',
        fxOpen: '{{what}} 계산 방법',
        fxFactory: '공장 원가',
        fxFrame: '프레임 원가',
        fxExtra: '기타 비용 1',
        t2Divide: '{{basis}} ÷ (1 − {{r}}%)',
        t2BasisFromOpt: '옵션에서 지정',
        t2ThisQuote: '이 견적',
        t2Both: '동일한 비율을 두 가지 원가 기준에 각각 적용한 값입니다. 견적은 표시된 쪽을 따르며, 그 선택은 옵션에서 합니다.',
        fxT2: 'T2 가격 산정 방식',
        fxT2Rule: '가격에 얹는 것이 아니라 가격에서 빼내는 마진입니다: 기준 금액을 (1 − T2 비율)로 나눕니다. 저장된 모든 견적이 따르는 방식이며, T1에 쓰는 누적식은 다른 값을 냅니다.',
        fxExtraAfter: '마진 계산 후에 더해지며 마크업 대상이 아님',
        fxT1Base: '공장원가 + 프레임원가 + 기타 비용 1',
        t1BaseLine: '공장원가 + 프레임원가 + 기타 비용 1',
        optExtraOff: '적용할 기타 비용 1 항목',
        optExtraOffLbl: '제외:',
        optExtraOffNone: '전부',
        optExtraOffSome: '{{n}}개 제외',
        t1Sum: '\uae30\uc900 + O.H + \ub9c8\uc9c4',
        fxT1On: '{{n}} 기준',
        fxOh: 'Helinox \uc624\ubc84\ud5e4\ub4dc (\uac04\uc811\ube44)',
        fxProfit: 'Helinox \ub9c8\uc9c4',
        fxTier: '단계 가격',
        fxMsrp: '목표 MSRP',
        fxLead: '리드타임',
        fxBenchProfit: '\uc778\ub77c\uc778 \uc81c\ud488\uc758 \ub9c8\uc9c4',
        fxMoq: 'MOQ 할증',
        fxSl: '단납기 할증',
        fxFprofit: '프레임 이익',
        byOption: '같은 견적, 옵션별 비교',
        optPick: '이 옵션이 바꾸는 항목:',
        optName: '옵션 이름',
        optNamePh: '옵션 {{n}} — 알아보기 쉬운 이름을 입력하세요',
        fxBenchRule: '\uc778\ub77c\uc778 \uc81c\ud488\uc758 \uc6d0\uac00\uc5d0 \ucf5c\ub77c\ubcf4\uc758 T1 \ub9c8\uc9c4\ub960\uc744 \uc801\uc6a9\ud569\ub2c8\ub2e4 \u2014 \ubca4\uce58\ub9c8\ud06c \uc804\uc6a9 \ub9c8\uc9c4\ub960\uc740 \uc5c6\uc2b5\ub2c8\ub2e4.',
        fxBenchBase: '\uc778\ub77c\uc778 \uc6d0\uac00 (\uc2a4\ud0a8 + CMT + \uac04\uc811\ube44\u00b7\ub9c8\uc9c4 + \ud504\ub808\uc784)',
        optDropCond: '이 항목을 옵션에서 빼기',
        tierSubAny: '해당 열의 요율로 계산한 O.H + 이익 + 기타 비용',
        tierSubOwn: 'O.H + 이익 + 기타 비용',
        fxTimesQty: '개당 금액 × 주문 수량 — 1개가 아니라 주문 전체입니다.',
        fxDebitRule: '마크업하지 않은 모든 항목을 단계 가격 위에 파트너에게 청구합니다.',
        fxDebitInPrice: '별도 청구 없음 — 이 방식에서는 이미 가격 안에 포함되어 있습니다.',
        profitSubOwn: '이 단계가 버는 O.H와 이익, 개당',
        ohShareAny: '이 중 간접비',
        profitShareAny: '이 중 이익',
        optSameShort: '견적과 동일',
        revenueSubAny: '단계 가격 × 주문 수량 — 1개가 아니라 주문 전체',
        totalProfitSubAny: '개당 이익 × 주문 수량 — 매출에서 주문 원가를 뺀 값',
        fxExtraRule: '제외 항목 전체의 합계입니다.',
        fxMoqRule: 'MOQ 기준액 × 주문 수량이 해당하는 요율입니다.',
        fxMoqBase: 'MOQ 기준액',
        fxSlRule: 'Vendor CMT에 리드타임이 해당하는 단납기 요율을 곱합니다.',
        extraInBase: '기준 금액에 포함',
        unit: '개',
        totalWord: '합계',
        estMsrp: '예상 MSRP: ',
        estTo: '~',
        estTimes: '배 →',
        targetMsrp: '목표 MSRP',
        targetMsrpHint: '인라인 소비자가 × (1 + 프리미엄).',
        msrpOverT1: 'MSRP ÷ T1',
        msrpOverT1Hint: '해당하는 배수',
        yTarget: 'Y 목표가',
        yTargetHint: '인라인 가격 대비 비율로 정한 목표가입니다. 선택 항목.',
        collabVsInline: '콜라보 vs 인라인',
        blockResult: '결과 / 옵션',
        blockDetail: '산출 근거',
        optConditions: '조건',
        colCollab: '콜라보',
        colInline: '인라인 비교',
        profitPart: '\ub9c8\uc9c4',
        ohShare: '이 중 간접비 ({{pct}})',
        profitShare: '이 중 이익 ({{pct}})',
        dealCase: 'Case',
        dealCaseHint: '어떤 협업 모델인지 — 어느 단계 비율이 필수인지 결정합니다.',
        dealCasePick: '\uc720\ud615\uc744 \uc120\ud0dd\ud558\uc138\uc694\u2026',
        dealCaseBoth: 'Both',
        dealCaseA: 'Case A: HPAC Direct Collaboration',
        dealCaseB: 'Case B: Subsidiary-led Collaboration',
        dealCasePath_both: '공장 → HPAC → 파트너   ·   공장 → HPAC → Helinox 법인 → 해당 시장',
        dealCasePath_a: '공장 → HPAC → 콜라보 파트너',
        dealCasePath_b: '공장 → HPAC → Helinox 법인 → 자국 시장',
        dealCasePathLbl: '누가 누구에게 파는가',
        dealCaseWhat_both: '두 경로 모두 산정합니다: HPAC이 매입 후 판매하고, Helinox 법인도 자국 시장에서 판매합니다. 두 단계 비율이 모두 필요합니다.',
        dealCaseWhat_a: 'HPAC이 공장에서 매입해 콜라보 파트너에게 직접 판매합니다. 중간에 Helinox 법인이 없으므로 HPAC 단계만 산정합니다.',
        dealCaseWhat_b: 'Helinox 법인(Helinox Inc., Helinox Japan 등)이 HPAC에서 매입해 자국 시장에서 판매합니다. 그 두 번째 단계를 법인 단계 비율이 산정합니다.',
        needPhoto: '저장하기 전에 사진을 최소 1장 추가하세요.',
        needCase: '{{sku}}: Case를 선택하세요.',
        needT1: '{{sku}}: 이 Case에서는 T1 비율이 필수입니다.',
        needT2: '{{sku}}: 이 Case에서는 T2 비율이 필수입니다.',
        needInlineRrp: '{{sku}}: Inline Retail price는 필수입니다.',
        needPremium: '{{sku}}: Collab premium은 필수입니다.',
        marginBasis: '마진 산정 기준',
        marginBasisBase: 'Markup Base (가이드라인)',
        marginBasisBuying: '총 매입원가 (Quote Sheet)',
        marginBasisBaseHint: '제외 항목은 T2 기준에서 빠지며 HPAC 비용으로 회수합니다.',
        marginBasisBuyingHint: '전 항목을 마크업하므로 별도 차지가 없습니다.',
        partnerRatio: '파트너 비율',
        partnerRatioHint: 'MSRP 대비 파트너 공급가 비율.',
        inlineRrp: '인라인 소비자가',
        productPage: '제품 페이지',
        folderPlaceholder: '폴더 — 예: 27SS',
        folderNone: '미분류',
        newFolder: '새 폴더',
        newFolderPlaceholder: '폴더 이름',
        renameFolder: '이름 변경',
        renameFolderTitle: '{{name}} 이름 변경',
        deleteFolderTitle: '{{name}} 폴더를 삭제할까요?',
        deleteFolderBody: '안에 있는 견적은 유지되며 미분류로 돌아갑니다.',
        dragHint: '행을 폴더로 끌어다 놓으면 분류됩니다.',
        approvePricing: '가격 승인',
        inlineRrpHint: '비교 대상 인라인 제품의 소비자가.',
        premium: '콜라보 프리미엄',
        premiumHint: '콜라보로 추가되는 비율 — 인라인 RRP 대비 %.',
        markupBase: 'Markup Base',
        markupBaseSub: '마진 산정의 기준이 되는 원가',
        composition: '벤더 FOB 구성',
        pricingResults: '가격 산정 결과',
        buyingCost: '총 사입가',
        buyingCostSub: 'Markup Base + 제외 항목',
        hpacUnit: 'HPAC 마케팅 비용',
        hpacUnitSub: '단위당',
        hpacTotal: 'HPAC 마케팅 비용',
        hpacTotalSub: '총 차지 · 단위당 × 발주 수량',
        t1: 'T2 (파트너 브랜드)',
        t1Sub: 'Markup Base ÷ (1 − T2 비율)',
        colStatus: '상태',
        stDraft: '작성 중',
        colNo: '번호',
        countAll_one: '견적 {{count}}건',
        countAll_other: '견적 {{count}}건',
        countOf: '{{total}}건 중 {{n}}건',
        allTeams: '전체 팀',
        approvalTitle: '가격 승인',
        approvalSub: '2단계: 영업 매니저, 그다음 CEO. 여기 있는 견적은 누군가의 결정을 기다리는 일입니다.',
        colTeam: 'Sales OP',
        colTrail: '이력',
        colSubmittedBy: '제출자',
        colNote: '메모',
        colCreatedBy: '생성자',
        colCreatedAt: '생성일',
        colModifiedBy: '수정자',
        colModifiedAt: '수정일',
        selected: '{{n}}건 선택',
        approveSelected: '선택 항목 승인',
        clearSelection: '선택 해제',
        submitTitle: '{{id}} 제출',
        submitBody: '{{who}}에게 전할 메모 — 이 가격의 이유나 변경 사항. 선택입니다.',
        submitPh: '예: 9월 9일 벤더 견적 반영해 프레임 원가 수정',
        recall: '회수',
        recallTitle: '{{id}} 회수',
        recallBody: '{{who}}의 대기 목록에서 빼고 Draft로 되돌립니다. 수정한 뒤 다시 올릴 수 있으며, 더 이상 승인 대기가 아니라는 메일이 그분께 발송됩니다.',
        recallPh: '회수 사유 (선택)',
        noteFrom: '메모',
        noteManager: '매니저',
        noteFinal: '최종 승인자',
        gateBy: '{{who}} 제출',
        openMissing: '{{id}}는 이 목록에 없습니다 — 이미 승인되었을 수 있습니다.',
        duplicate: '복제',
        deleted: '{{id}} 삭제됨.',
        deleteTitle: '이 견적을 삭제할까요?',
        deleteBody: '{{id}}이(가) 삭제됩니다. 초안 또는 반려된 견적만 삭제할 수 있습니다.',
        confirmDelete: '삭제',
        stagePending: '작성 중',
        stagePendingHint: '작성자에게 있는 견적 — 새 초안과 반려된 건입니다. 행의 상태로 구분됩니다.',
        stageManager: '영업 매니저 — 승인',
        stageManagerHint: '제출되어 Sales OP 팀 매니저의 결정을 기다립니다.',
        stageFinal: 'CEO — 승인',
        stageFinalHint: '매니저가 승인했습니다. 최종 결정을 기다립니다.',
        stageDone: '승인 완료',
        stageDoneHint: '승인이 끝난 견적입니다. 기다리는 사람이 없습니다.',
        stageApproval: '승인',
        approvedElsewhere: '승인된 견적은 완료된 작업입니다 — 저장된 프로젝트에서 복제할 수 있습니다.',
        queueMine: '내 결정 대기',
        queueMineHint: '승인하면 다음 단계로 넘어가고, 반려하려면 사유가 필요합니다.',
        queueBack: '반려됨',
        queueBackHint: '요청된 부분을 고친 뒤 다시 제출하세요.',
        queueDraft: '미제출',
        queueDraftHint: '아직 아무에게도 요청하지 않은 초안입니다.',
        queueWaiting: '다른 사람 대기 중',
        queueWaitingHint: '다른 사람의 결정을 기다리는 중 — "내 견적 어디 있지"를 묻지 않아도 되도록 보여 줍니다.',
        queueDone: '승인됨',
        queueDoneHint: '확정입니다. 복제하면 출처가 기록된 새 초안이 생깁니다.',
        queueEmpty: '없습니다.',
        trailSubmitted: '{{who}} 제출 · {{on}}',
        trailManager: '영업 매니저: {{who}} · {{on}}',
        trailFinal: 'CEO: {{who}} · {{on}}',
        trailNone: '미제출',
        resubmit: '다시 제출',
        gate: '승인',
        goApproval: '가격 승인 화면 열기',
        reload: '새로고침',
        loading: '불러오는 중…',
        stPendingmanager: '대기: 매니저',
        stPendingCEO: '대기: CEO',
        stApproved: '승인됨',
        stRejected: '반려됨',
        sentBackMgr: '영업 매니저 반려',
        sentBackCeo: 'CEO 반려',
        submit: '제출',
        reject: '반려',
        revise: '복제',
        view: '보기',
        rejectTitle: '{{id}} 반려',
        rejectBody: '무엇을 고쳐야 하는지 적어 주세요. 받는 사람에게는 이 내용뿐입니다.',
        nowStatus: '완료 — 현재 상태: {{status}}. {{to}}',
        revisedInto: '{{id}}(으)로 복사되어 새 초안이 되었습니다.',
        leadWorked: 'XF − PO = {{days}}일 → 단납기 {{rate}}',
        stepOrder: '주문',
        stepFactory: '공장 원가',
        stepFrame: '프레임 원가',
        stepOthers: '기타 비용 1',
        days: '일',
        unitCost: '단위당 원가',
        share: '비중',
        bothConventions: '두 가지 기준으로 본 같은 견적',
        whatTheyPay: '법인과 파트너가 지불하는 금액',
        marginTakenOn: '마진 기준 금액',
        profitAtT1: 'T1 이익',
        profitAtT2: 'T2 이익',
        profitSub: '해당 단계 가격에서 마진 기준 금액을 뺀 단위당 금액',
        totalProfitAtT1: 'T1 \ucd1d\ub9c8\uc9c4',
        totalProfitAtT2: 'T2 \ucd1d\ub9c8\uc9c4',
        totalProfitSub: '단위당 이익 × {{qty}} EA — 매출에서 주문 원가를 뺀 금액',
        revenueAtT1: 'T1 매출',
        revenueAtT2: 'T2 매출',
        revenueSub: '단계 가격 × {{qty}} EA — 단위가 아니라 주문 전체',
        gapNote: '두 기준은 {{tier}}에서 {{gap}} 차이가 납니다. 가이드라인에서는 단위당 기타 비용 {{unit}}을 파트너에게 별도 청구하며({{qty}} EA 기준 {{debit}}), 견적서 기준에서는 이미 가격에 포함되어 있습니다. 같은 금액이 다른 자리에 있을 뿐이라, 견적에는 어느 기준인지 반드시 적혀야 합니다.',
        basisLine: '{{name}} 기준으로 제출됩니다 — 강조된 열이 승인자가 승인하는 금액입니다.',
        t0: 'T1 (Helinox 법인)',
        t0Sub: 'Markup Base ÷ (1 − T1 비율)',
        t0SubBuying: '총 매입원가 ÷ (1 − T1 비율)',
        t1SubBuying: '총 매입원가 ÷ (1 − T2 비율)',
        marginOnBuying: '총 매입원가',
        hpacTotalInPrice: '이 기준에서는 이미 가격에 포함되어 있습니다',
        msrp: 'MSRP',
        msrpSub: '인라인 소비자가 × (1 + 프리미엄 %)',
        partnerPrice: '파트너 공급가',
        partnerPriceSub: 'MSRP × 파트너 비율',
        allSkus: '전체 SKU',
        globalInputs: '글로벌 입력값',
        licensedSub: '라이선스 모델은 파트너의 판매가에서 역산합니다. 로열티가 판매가에 부과되므로 중요한 것은 로열티 차감 후 남는 마진입니다.',
        globalMsrp: '글로벌 MSRP (USD)',
        t1Fob: 'T1 FOB',
        logistics: '물류비 %',
        logisticsHint: 'T1 FOB에 더해 랜디드 원가를 산출합니다.',
        wholesale: '도매 %',
        wholesaleHint: '현지 소비자가 대비 도매가 비율.',
        wsRoyalty: '도매 로열티 %',
        wsRoyaltyHint: '도매가에 부과되는 로열티.',
        dtcRoyalty: 'DTC 로열티 %',
        dtcRoyaltyHint: '소비자가에 부과되는 로열티.',
        landed: '랜디드 원가',
        landedSub: 'T1 FOB × (1 + 물류비 %)',
        entities: '법인',
        addEntity: '법인 추가',
        entity: '법인',
        fx: '환율 / USD',
        localSrp: '현지 소비자가',
        resultsByEntity: '법인별 결과',
        resultsByEntitySub: '로열티 차감 전후 마진을 함께 표시합니다. 두 값의 차이가 라이선스 비용입니다.',
        srpUsd: '소비자가 (USD)',
        wholesaleCol: '도매가',
        wsMargin: '도매 마진',
        dtcMargin: 'DTC 마진',
        prePost: '차감 전 → 후',
        fobMult: 'FOB 배수',
        addEntityFirst: '법인을 추가하세요',
        colProject: '프로젝트',
        colName: '이름',
        openQuote: '견적 {{id}} 열기',
        colMode: '유형',
        colSkus: 'SKU 수',
        colSavedBy: '저장자',
        colSaved: '저장일',
        general: '일반',
        licensed: '라이선스',
        nothingSaved: '저장된 프로젝트가 없습니다',
        skuField: 'SKU',
        skuFieldHint: '계산할 단일 품목.',
        globalMsrpHint: '글로벌 소비자가 (USD) 하나. 각 법인의 현지가는 여기서 환산됩니다.',
        t1FobHint: '벤더 협상 후 법인 이전가격이며 물류비 반영 전 금액입니다.',
        royaltyNote: '로열티는 원가가 아닙니다. 제품을 만드는 데 드는 비용이 아니라 판매 후 매출에서 지급하는 브랜드 수수료이므로, 원가에 쌓지 않고 매출 아래 별도 항목으로 계산합니다. 또한 법인 간 T1 이전(HPAC → 각 법인)에는 부과되지 않으며, 제품이 외부(도매처 또는 소비자)로 나갈 때만 발생합니다.',
        burdenNote: '도매가가 소비자가의 50%일 때 도매 로열티 11%는 DTC 로열티 5.5%와 실질 부담이 같습니다. 도매 비율을 바꾸면 이 균형도 달라지므로 두 요율을 함께 검토하세요.',
        autoFillSrp: '현지 소비자가 자동 입력',
        autoFillHint: '글로벌 MSRP × 각 법인 환율.',
        resetEntities: '초기화',
        wholesalePrice: '도매가',
        dtcRoyaltyAmt: 'DTC 로열티',
        wsRoyaltyAmt: '로열티',
        wsMarginLabel: '도매 마진',
        dtcMarginLabel: 'DTC 마진',
        pre: '차감 전',
        lowMargin: '마진 낮음',
        fobBadge: 'FOB',
        deleteTitle: '이 프로젝트를 삭제할까요?',
        deleteBody: '{{id}} 은(는) 저장 목록에서 사라집니다. 코스팅 자체는 보관되며 관리자가 되살릴 수 있습니다.',
        cancel: '취소',
        confirmDelete: '삭제',
        colAction: '작업',
        colBuying: '사입가',
        colT1: 'T2',
        edit: '편집',
        clearFilters: '정렬 · 필터 해제',
        discardTitle: '작성 중인 내용을 버릴까요?',
        discardBody: '화면의 내용은 아직 저장되지 않았습니다. 버리면 모두 지워지고 빈 프로젝트로 시작합니다.',
        keepEditing: '계속 편집',
        discard: '버리기',
      },
      ask: {
        launch: 'Catchy에게 묻기',
        role: 'AI 업무 도우미',
        note: '채팅 내용은 Catchy를 더 똑똑하게 만드는 데 사용됩니다.',
        hello: '안녕하세요, Catchy입니다. 프로젝트와 SKU 단계, 샘플, 티켓, OPA / SCT, NFC 토큰, 매출에 대해 물어보세요. 볼 수 있는 화면 범위 안에서 답해 드려요. 스크린샷을 붙여 넣어도 됩니다.',
        s1: '이번 주에 마감인 샘플은?',
        s2: '기한이 지난 티켓은?',
        s3: 'Team 2에서 Delayed인 SKU는 몇 개?',
        hideSugs: '추천 질문 숨기기', showSugs: '추천 질문 보기', otherSugs: '다른 질문',
        placeholder: '질문 또는 스크린샷 붙여 넣기…', send: '보내기', attach: '스크린샷 추가',
        screenshot: '스크린샷', screenshotReady: '스크린샷 첨부됨', remove: '삭제',
        lookingAt: '보고 있는 항목:', lookingAtHint: 'Catchy는 “이것”을 이 화면에 열린 항목으로 이해합니다', dropContext: '이 항목 없이 묻기',
        save: '대화 저장', newChat: '새 대화', expand: '크게 보기', shrink: '작게 보기', close: '닫기',
        you: '나',
        leftToday_other: '오늘 남은 질문 {{count}}개',
        noneLeft: '오늘 질문을 모두 사용했어요. 자정 이후에 다시 물어보세요.',
      },
      ps: {
        titleFor: '{{org}} 샘플 트래커',
        partner: '협력사',
        loading: '불러오는 중…',
        fills: '{{org}} 입력',
        requiresReview: '{{reviewer}} 검토 필요',
        reviewerDefault: '디자이너',
        reviewerNote: '{{reviewer}} 검토: 아래 Round Outcome에 Review Date와 결과를 입력하세요.',
        reviewDateHint: '{{reviewer}}가 검토한 날짜',
        internalOnly: '{{org}} 내부 검토만',
        internalNote: '{{org}} 내부 검토 기록은 Review Date와 Remarks를 사용하세요. 이 라운드는 디자이너 업무량이나 피드백 대기에 포함되지 않습니다.',
        internalReview: '{{org}} 내부 검토',
        location: '위치',
        allLocations: '모든 위치',
        locationPh: '위치를 선택하거나 입력...',
        createTitle: '새 샘플 — 스타일 / 품목명 입력',
        createSub: '새 스타일은 다음 Base ID({{prefix}}-00001, {{prefix}}-00002…)를 받으며 사진이 필요합니다. 이미 있는 스타일이면 이 라운드가 해당 Base ID에 추가됩니다.',
        styleTypedHint: '여기에 입력 — Base ID는 스타일명으로 부여됩니다',
        pickPh: '예: {{prefix}}-00001-P1 또는 {{prefix}}-00001...',
        pickNone: '일치하는 샘플이 없습니다. 새로 시작하려면 + Create Sample을 사용하세요.',
        pickCompany: '이 샘플의 회사를 선택하세요',
        allCompanies: '모든 회사',
        empty: '아직 {{org}} 샘플이 없습니다. + Create Sample을 눌러 첫 샘플을 만드세요.',
        tourCreateB: '스타일 / 품목명을 입력하고 사진을 추가하세요. 새 스타일은 다음 Base ID를 받고, 이미 있는 스타일이면 그 Base ID에 라운드가 추가됩니다. 그다음 Sample Plan을 채우세요.',
        tourFiltersB: '상태, 개발자, 위치로 거르거나 검색창에 입력하세요. Clear를 누르면 열 머리글의 깔때기까지 모두 초기화됩니다.',
        manual: '사용자 매뉴얼',
        manualTitle: '이 트래커의 매뉴얼을 새 탭에서 엽니다 — Tiếng Việt, English, 한국어',
        tourTabsT: '두 가지 보기',
        tourTabsB: 'All Samples는 Base ID마다 라운드를 보여 줍니다. Sample-level Summary는 샘플마다 한 줄로, R1부터 R5까지 라운드를 나란히 보여 줍니다.',
      },
      cr: {
        requestTitle: '쿠리어 발송 요청',
        requestSub: '헬리녹스 쿠리어 계정(FedEx, DHL, UPS 등)으로 보내기 전에 먼저 여기서 요청하세요. 포털이 계약 요율과 비교하고, 승인자가 출하 전에 결정합니다. 일반 해상·항공 운송은 해당되지 않습니다.',
        approvalTitle: '쿠리어 승인',
        approvalSub: '출하 전 결정을 기다리는 쿠리어 발송 건입니다. 승인자: {{who}}.',
        new: '새 요청',
        newTitle: '새 쿠리어 요청',
        close: '닫기',
        cancel: '취소',
        send: '승인 요청',
        pick: '선택…',
        loading: '불러오는 중…',
        clear: '초기화',
        sendNote: '승인 요청은 승인자 {{who}}에게 가며, 승인자가 여기서 승인 또는 반려합니다. 본인과 참조자도 메일을 받습니다.',
        b_requester: '요청자',
        b_carrier: '배송사와 청구 계정',
        b_route: '발송지와 도착지',
        b_weight: '중량과 박스',
        b_cost: '예상 비용',
        b_goods: '물품',
        requester: '요청자',
        department: '부서',
        ccEmails: '참조 (계정 보유 법인)',
        ccHint: '1번 조항: 계정 보유 법인을 참조로 넣습니다. @helinox.com 주소만, 쉼표로 구분하세요.',
        carrier: '배송사',
        carrierOther: '배송사 이름',
        service: '서비스 등급',
        accountHolder: '청구되는 헬리녹스 계정',
        accountHint: '쿠리어 계정을 가진 법인.',
        accountNo: '계정 번호',
        from: '발송지',
        to: '도착지',
        shipper: '발송인',
        receiver: '수취인',
        country: '국가',
        city: '도시',
        address: '주소',
        zipHint: '우편번호를 포함하세요.',
        toIndividual: '개인 주소로 배송 (직원 자택, 고객 현장)',
        importer: '수하인·수입자',
        importerHint: '5번 조항: 헬리녹스 법인으로, 개인은 c/o — 개인 단독은 안 됩니다.',
        shipmentTo: '받는 곳',
        to_internal: '헬리녹스 법인·팀',
        to_direct: '고객·제3자 (직송)',
        toDirect: '고객 또는 제3자에게 직접 발송 (헬리녹스 법인이 아님)',
        to_customer: '고객 (직송)',
        to_partner: '파트너·제3자 (직송)',
        freightPayer: '운임 부담자',
        payerHint: '6번 조항: 부킹 전에 누가 부담하는지 명시.',
        incoterm: '운송 조건 (인코텀)',
        cartons: '박스 수',
        actualKg: '실중량(kg)',
        dimKg: '부피중량(kg)',
        dimPh: '전체 박스 합계',
        useBoxTitle: '박스 한 개 크기(L × W × H ÷ 5,000) × 박스 수로 부피중량 계산',
        boxSize: '박스 크기 cm (가로 × 세로 × 높이)',
        useBox: '적용',
        estCost: '예상 운임',
        currency: '통화',
        duties: '예상 관세·세금',
        dutiesDdp: 'DDP: 헬리녹스가 부담합니다 — 예상 금액을 입력하세요 (없으면 0).',
        bulkyNote: '부피화물(부피중량이 실중량 초과 또는 100kg 초과): 익스프레스를 기본값으로 두지 마십시오. 포워더 견적을 최소 하나 넣어 부킹 전에 비교하세요(2번 조항). 폴·텐트·프레임·가구는 정의상 부피화물입니다.',
        fwdName: '포워더',
        fwdCost: '포워더 견적',
        goodsFor: '물건이 누구 앞인지',
        goodsForHint: '받는 사람, 팀 또는 고객.',
        contents: '내용물',
        purpose: '목적',
        notes: '메모',
        checkTitle: '비용 확인',
        checkWhat: '양식을 채우는 동안 포털이 예상 비용을 HINC의 FedEx 계약 요율과 비교합니다.',
        checkNeed: '아직 필요한 항목: {{list}}.',
        need_courier: '배송사', need_dest: '도착 국가', need_weight: '실중량', need_cost: '예상 비용', need_currency: '통화',
        chargeable: '청구중량',
        bulky: '부피화물',
        quoted: '예상 운임',
        usdPerKg: 'kg당',
        diff: '계약 요율 대비',
        fxWeek: '환율 (NetSuite)',
        v_reasonable: '적정',
        v_review: '검토 권장',
        v_high: '높을 수 있음',
        v_nocontract: '계약 요율 없음',
        v_manual: '직접 확인',
        v_undefined: '—',
        s_waiting: '대기',
        s_approved: '승인',
        s_rejected: '반려',
        s_withdrawn: '철회',
        tab_waiting: '결정 대기',
        tab_approved: '승인',
        tab_rejected: '반려',
        tab_all: '전체',
        tab_rates: '요율',
        empty_waiting: '결정을 기다리는 건이 없습니다.',
        empty_approved: '아직 승인된 건이 없습니다.',
        empty_rejected: '반려된 건이 없습니다.',
        empty_all: '쿠리어 요청이 아직 없습니다.',
        emptyMine: '아직 보낸 쿠리어 요청이 없습니다.',
        countRows: '{{total}}건 중 {{n}}건',
        c_no: '요청',
        c_created: '보낸 날',
        c_requester: '요청자',
        c_entity: '법인',
        c_route: '경로',
        c_carrier: '배송사',
        c_originCountry: '출발 국가',
        c_originCity: '출발 도시',
        c_destCountry: '도착 국가',
        c_destCity: '도착 도시',
        c_service: '서비스',
        c_account: '계정',
        c_kg: '청구중량(kg)',
        c_cost: '예상 운임',
        c_check: '확인',
        c_status: '상태',
        c_decidedBy: '결정자',
        c_decidedAt: '결정일',
        f_route: '경로',
        f_carrier: '배송사·서비스',
        f_account: '청구 계정',
        f_weight: '중량·박스',
        f_cost: '예상 운임',
        f_fwd: '포워더 견적',
        f_goodsFor: '물건이 누구 앞인지',
        f_purpose: '목적',
        f_contents: '내용물',
        f_shipper: '발송인',
        f_receiver: '수취인',
        f_importer: '수입자',
        f_direct: '직송 (운임 부담자)',
        f_incoterm: '운송 조건',
        ddpShort: '관세·세금은 헬리녹스 부담',
        f_requester: '요청자',
        f_cc: '참조',
        f_notes: '메모',
        actualShort: '실중량',
        dimShort: '부피중량',
        chargeableShort: '청구중량',
        cartonsShort: '박스',
        dutiesShort: '관세·세금',
        raised: '{{at}} · {{by}} 요청.',
        approvedBy: '{{by}} 승인 · {{at}}',
        rejectedBy: '{{by}} 반려 · {{at}}',
        withdrawnAt: '{{at}} 철회.',
        rule4: '부킹 시 운송사 견적이 이 예상보다 높게 나오면 출하 전에 멈추고 말씀해 주십시오(4번 조항).',
        approve: '승인',
        reject: '반려',
        withdraw: '철회',
        withdrawAsk: '{{no}}를 철회할까요? 승인자에게 결정할 건이 없다고 알립니다.',
        didApprove: '{{no}} 승인 — 요청자에게 알렸습니다.',
        didReject: '{{no}} 반려 — 요청자에게 사유를 알렸습니다.',
        didWithdraw: '{{no}} 철회.',
        s_draft: '초안', notSent: '미발송', saveDraft: '임시 저장', c_createdBy: '작성자', c_createdAt: '작성일',
        checkSource: 'HINC FedEx 계약 요율 파일로 계산 — AI 검토가 아닙니다.', noContract: '이 구간에 계약 요율 없음',
        contractBase: '유류할증 전 계약 요율', h_shipment: '화물', h_source: '수치 출처', h_findings: '검토 결과',
        editTitle: '{{no}} 수정', copyTitle: '새 쿠리어 요청 — {{no}}에서 복사',
        savedDraft: '{{no}}을(를) 초안으로 저장했습니다 — 준비되면 보내세요.', didDelete: '{{no}} 삭제됨.',
        didDeleteMailed: '{{no}} 삭제됨 — 승인자에게 결정할 것이 없다고 알렸습니다.',
        deleteAsk: '{{no}}을(를) 삭제할까요? 되돌릴 수 없습니다.',
        deleteAskWaiting: '{{no}}을(를) 삭제할까요? 승인자에게 가 있으며, 결정할 것이 없다고 알립니다. 되돌릴 수 없습니다.',
        saveWaiting: '저장',
        changedByYou: '{{no}} 수정됨 — 요청자에게 변경 내용을 알렸습니다. 계속 결정 대기 중입니다.',
        changedBy: '최종 수정 {{by}} · {{at}}.',
        why_notWaiting: '결정 대기 중인 요청만',
        c_managerNote: '매니저 메모',
        c_action: '작업', a_view: '보기', a_edit: '수정', a_submit: '제출', a_copy: '복제', a_delete: '삭제', a_withdraw: '철회',
        why_waiting: '승인자에게 가 있습니다 — 바꾸려면 먼저 철회하세요',
        why_approved: '승인됨 — 다른 발송은 복제해서 만드세요',
        why_sent: '이미 승인자에게 가 있습니다', why_done: '이미 결정되었습니다',
        why_record: '승인됨 — 승인 기록으로 남습니다', why_notMine: '본인 요청이 아닙니다',
        sent: '{{no}} 승인 요청을 보냈습니다.',
        sentMailed: '{{no}} 승인 요청을 보냈습니다 — 승인자에게 알렸습니다.',
        billed: '청구 중량',
        lane: '계약 구간',
        dir_export: '한국발 수출',
        dir_import: '한국행 수입',
        'dir_sg-export': '싱가포르발 수출',
        'dir_sg-import': '싱가포르행 수입',
        dhlFreightOnly: 'DHL 요율표: 운임만 — 유류할증·관세·DHL 부대비용 별도',
        estSurcharge: '예상 할증료', estSurchargeHint: '운임 외 유류할증·원격지 등 할증료 — 계약 요율은 운임만 포함',
        dhlSettingsTitle: 'HPAC DHL (싱가포르) — 요율표는 운임만 포함',
        dhlFuelPct: 'DHL 유류할증 %',
        dhlRemarkLabel: 'DHL 비교마다 표시되는 비고',
        ratesTitleDhl: 'HPAC DHL Express 계약 요율 (SGD)',
        ratesNoteDhl: 'HPAC의 DHL Express Singapore 요율표 기준 DHL Express Worldwide입니다. 30kg까지는 0.5kg 단위의 건당 요금, 30.1kg부터는 1kg 단위의 kg당 요금입니다. 운임만 포함: 유류할증, 관세, 세금, DHL 부대비용은 별도입니다. 서류 전용 요금과 제3국 DHL은 반영하지 않았습니다. 내년 요율표(.pdf)도 같은 방법으로 업로드합니다.',
        contract: '계약 요율',
        withFuel: '유류할증 {{pct}}% 포함',
        prioRate: 'Priority: Economy 요율 × {{f}}', econRate: '파일의 Economy 요율',
        economyBase: '파일의 Economy 요율', h_remark: '비고',
        priorityFactor: 'Priority = Economy 요율 ×', remarkLabel: '모든 계약 요율 비교 아래에 표시되는 비고',
        rateFile: '요율 파일',
        depPick: '부서를 선택하거나 입력하세요',
        depNone: '“{{name}}”과(와) 일치하는 부서가 없습니다', depAddTo: '+ “{{name}}”을(를) 부서 목록에 추가',
        depAdded: '"{{name}}"을(를) 목록에 추가했습니다.',
        rateVersion: '계약 요율: {{file}} · {{by}} 등록 · {{at}}',
        rateNone: '등록된 계약 요율이 없습니다.',
        settingsTitle: '견적 판정 기준',
        settingsNote: '요율 파일은 FedEx Economy 요율이며, Priority 서비스는 여기에 계수를 곱합니다. 계약에는 유류할증이 없으므로 바뀌지 않는 한 0으로 둡니다. 계약 요율 대비 "적정 상한" 이내면 적정, "검토 상한" 이내면 검토 권장, 그 이상은 높을 수 있음입니다.',
        fuelPct: '유류할증 %',
        okPct: '적정 상한 (%)',
        reviewPct: '검토 상한 (%)',
        saveSettings: '저장',
        settingsSaved: '저장했습니다.',
        ratesTitle: 'HINC FedEx 계약 요율 (KRW)',
        ratesNote: '20.5kg까지는 0.5kg 단위의 건당 요금, 21kg부터는 1kg 단위의 kg당 요금입니다. 내년 파일은 업로드로 넣습니다: 먼저 읽어 보여 준 뒤 이 목록을 교체합니다.',
        upload: '요율 파일 업로드 (FedEx .xlsx / DHL .pdf)',
        previewCounts: '수출: {{el}}개 열, 도착지 {{ec}}곳. 수입: 지역코드 {{il}}개, 국가 {{ic}}곳. 요율 {{rows}}개.',
        replaceRates: '요율 교체',
        ratesLoaded: '{{file}}에서 요율을 불러왔습니다.',
        weightCol: '중량',
        perKgShort: '(kg당)',
        laneNoCountry: '이 열에 연결된 국가가 없습니다',
        countriesTitle: '열별 국가',
      },
      spl: {
        title: '세일즈 플래닝', loading: '불러오는 중…', cycle: '오더 사이클', season: '시즌', next: '다음', orderDue: '오더', exf: '출고',
        tab_format: '오더 포맷', tab_sheets: '오더 시트', tab_stock: '재고', tab_production: '생산', tab_overview: '개요', tab_setup: '설정',
        fmtNone: '미업로드', fmtCount: 'SKU {{n}}', sheetsCount: '{{done}}/{{n}} 완료', stockServed: '재고 {{n}} pcs', prodCount: '공장 지정 {{set}}/{{n}}',
        st_open: '진행 중', st_closed: '마감', closedNote: '마감된 오더입니다. 수정하려면 오더 포맷(수정 › 상태)에서 다시 여세요.',
        newOrder: '오더 생성', newOrderTitle: '오더 생성', open: '열기', openFormat: '이 오더의 포맷 열기', delOrder: '오더 삭제',
        col_id: 'ID', view: '보기', viewOrder: '이 오더 보기 — 읽기 전용', editOrder: '이 오더와 포맷 수정',
        modeView: '보기 전용', modeEdit: '수정 중', formatHead: '오더 포맷 · {{n}} SKU',
        col_file: '오더 포맷 파일', fileBy: '{{by}} · {{at}}', fileNone: '아직 오더 포맷 파일이 없습니다.',
        col_format: '포맷 SKU', col_sheets: '완료 시트', col_qty: '주문 수량', col_createdBy: '생성자', col_createdAt: '생성 일시', col_action: '작업',
        ordersCount: '오더 {{total}}건 중 {{n}}건', clearFilters: '필터 해제',
        noOrderYetMgr: '아직 오더가 없습니다 — 오더 생성을 누르고 오더 포맷을 가져오세요.', noOrderYet: '아직 오더가 없습니다 — Sales Planning 매니저가 오더 포맷에서 생성합니다.',
        fmtSource: '오더 포맷', src_file: 'Master 오더 포맷 가져오기 (.xlsx)', src_copy: '이전 오더의 포맷 복사', src_later: '나중에 추가',
        copyOption: '{{date}} · 출고 {{exf}} · SKU {{n}}개', datesFirst: '오더 마감일과 출고일을 입력하세요 — 둘 다 입력되면 파일을 바로 읽습니다.',
        pvFile: '시트 "{{sheet}}": SKU {{rows}}개가 이 오더의 포맷이 됩니다.', pvCopy: '{{date}} 오더에서 SKU {{n}}개를 복사합니다.',
        orderCreated: '{{date}} 오더를 생성했습니다 — 포맷 SKU {{n}}개.', orderCreatedNoFmt: '{{date}} 오더를 생성했습니다 — 준비되면 오더 포맷을 가져오세요.',
        orderDeleted: '{{date}} 오더를 삭제했습니다.', delAsk: '{{date}} 오더(출고 {{exf}})와 포맷 SKU {{n}}개를 삭제할까요? 아직 아무 작업도 없습니다.',
        formatOf: '오더 포맷 · {{date}} ({{season}}) · 출고 {{exf}}', seasonAuto: '시즌 {{s}} — 오더 마감일 기준 (10월~9월).',
        seasonAutoTip: '오더 마감일로 정해집니다: 시즌은 10월부터 9월까지입니다.',
        sh_none: '시작 전', sh_open: '작성 중', sh_done: '완료', sh_checked: '확인됨',
        st_done_done: '{{cust}}: 완료 처리 — 매니저가 확인할 수 있습니다.', st_check_done: '{{cust}}: 확인했습니다.', st_reopen_done: '{{cust}}: 다시 열었습니다.',
        cancel: '취소', close: '닫기', save: '저장', saveN: '저장 ({{n}})', saved: '저장했습니다.', savedCells: '{{n}}건 저장했습니다.',
        edit: '수정', remove: '삭제', removed: '{{sap}}을(를) 이번 사이클에서 삭제했습니다.', read: '파일 읽기', write: '반영', writeN: '{{n}}건 반영',
        search: 'SAP 코드, 제품, 색상…', allCategories: '전체 카테고리', allLineups: '전체 라인업', noRows: '해당 항목이 없습니다.', noOrders: '이번 사이클에 아직 오더가 없습니다.',
        sap: 'SAP 코드', product: '제품', qty: '오더 수량', amountUsd: '금액 (USD)', total: '합계', buyer: '바이어', pic: '담당자', status: '상태',
        fmtEmpty: '이번 사이클의 오더 포맷이 아직 없습니다.',
        fmtEmptyHint: '이번 달 Master order format(.xlsx)을 업로드하세요. SKU와 가격이 이번 사이클의 목록이 되고, 오더 시트는 이 SKU만 제공합니다. SKU는 언제든 추가·수정할 수 있습니다.',
        fmtWaiting: '세일즈 플래닝 매니저가 업로드합니다.', upload: '오더 포맷 업로드', uploadNew: '새 버전 업로드', copyFrom: '{{cycle}} 포맷 복사',
        addSku: 'SKU 추가', fmtDownload: '포맷 다운로드', fmtDownloadTip: '이번 사이클 오더 포맷을 Master order format 형식의 .xlsx로 — 수정 후 다시 업로드할 수 있습니다.', portalOnly: '포털에서만 관리 — Master order format에는 없는 열이며, 해당 SKU가 있던 지난 사이클에서 가져옵니다.', fmtNote: 'SKU {{n}} / {{total}} · 가격 USD',
        fmtUploadTitle: 'Master order format 업로드',
        fmtUploadBody: '파일을 먼저 읽고, 반영을 눌러야 저장됩니다. 이미 있는 SKU는 파일의 속성과 가격으로 바뀌고, 파일에 없는 SKU는 아래를 체크하지 않는 한 유지됩니다.',
        fmtSummary: '시트 "{{sheet}}": SKU {{rows}}개 — 신규 {{added}}, 변경 {{changed}}, 파일에 없음 {{missing}}.',
        fmtAdded: '신규', fmtChanged: '변경', fmtMissing: '파일에 없음', fmtUsed: '체크·오더 있음, 유지',
        fmtRemove: '파일에 없고 체크·오더도 없는 {{n}}개 삭제', fmtNoChange: '파일이 이번 사이클 포맷과 같습니다.',
        fmtDone: '오더 포맷 반영: 신규 {{added}}, 변경 {{changed}}, 삭제 {{removed}}.', fmtCopied: 'SKU {{n}}개 복사했습니다.',
        skuNew: 'SKU 추가', skuEdit: '{{sap}} 수정', skuRemoveAsk: '{{sap}} ({{product}})을(를) 이번 사이클 오더 포맷에서 삭제할까요?',
        noFormatYet: '먼저 오더 포맷을 업로드하세요 — 모든 시트가 그 SKU를 제공합니다.',
        priceCol: '적용 가격', pf_price_t1: 'T1 FOB', pf_price_t2: 'T2 FOB', pf_price_hema_dt: 'HEMA DT 가격', pf_null: '—',
        ticked: '체크 SKU', ordered: '오더', updated: '최근 저장', myBuyers: '내 바이어만', allStatus: '전체 상태',
        sheetsNote: '시트 {{n}}개 중 {{done}}개 완료', sapAll: 'SAP 업로드 (전체 바이어)', sapOne: '이 바이어의 SAP SO 업로드',
        sheetTitle: '오더 시트 {{date}}', customerRef: 'Customer Reference (바이어 PO#)', showTicked: '체크됨 ({{n}})', showAll: '전체 SKU ({{n}})',
        tick: '체크', tickShown: '표시된 항목 모두 체크', untickShown: '표시된 항목 체크 해제', fromLast: '지난 사이클과 같은 SKU',
        fromLastDone: '{{date}}과 같이 SKU {{n}}개를 체크했습니다.', nothingTicked: '아직 체크된 SKU가 없습니다 — 이 바이어가 오더할 SKU를 체크하거나(전체 SKU), 지난 사이클에서 시작하세요.',
        tickedN: '{{n}}개 체크', exportForm: '바이어용 오더폼', exportTip: '체크된 SKU와 이 바이어의 가격만 담은 오더 포맷 — 바이어는 오더 수량만 입력합니다.',
        tickFirst: '먼저 이 바이어가 오더할 SKU를 체크하세요', saveFirst: '먼저 변경 사항을 저장하세요', importForm: '바이어 오더폼 가져오기',
        markDone: '완료', markChecked: '확인 완료', reopen: '다시 열기', doneBy: '{{who}} 완료, {{at}}', checkedBy: '{{who}} 확인, {{at}}',
        unsaved: '저장하지 않은 변경 {{n}}건.', keepEditing: '계속 편집', discard: '버리기',
        sheetLocked: '이 시트는 제출되었습니다 — 세일즈 플래닝 매니저는 수정하거나 다시 열 수 있습니다.',
        notYours: '이 바이어의 담당자는 {{pic}}입니다 — 담당자 또는 매니저만 작성할 수 있습니다.',
        flagCycle: '오더 포맷: {{rule}} — 이번 사이클 대상 아님', flagPrice: '이 바이어 가격 없음',
        formTitle: '{{cust}} — 바이어 오더폼 가져오기',
        formBody: '보낸 오더폼을 바이어가 작성한 파일(.xlsx)에서 SAP Code와 Order Q\'ty 열을 읽습니다. 폼에 있는 SKU는 그 수량으로 바뀌고(빈칸 = 오더 없음), 폼에 없는 SKU는 그대로 둡니다. 반영을 누르기 전에는 저장되지 않습니다.',
        formSummary: '시트 "{{sheet}}": SKU {{rows}}행, 오더 {{total}} pcs — 변경 {{n}}건.',
        formUnknown: '이번 사이클 오더 포맷에 없음 (가져오지 않음): {{list}}', formDup: '중복 (첫 행 사용): {{list}}',
        formNoChange: '오더폼이 시트와 같습니다.', formWritten: '{{cust}}: 오더폼에서 SKU {{n}}개 반영했습니다.', was: '현재', now: '오더폼',
        stockNote: '재고 세 풀을 입력한 뒤 배정하세요: 바이어는 재고 우선순위(설정 › 바이어) 순으로 KR 국내, EUSU, KR SOP 순서로 배정받습니다.',
        notAllDone: '아직 완료되지 않은 시트 {{n}}개 — 모두 들어오면 다시 배정하세요.', countOver: '오더보다 재고 배정이 많은 바이어 라인 {{n}}건.',
        pool_kr_dom: 'KR 국내', pool_eusu: 'EUSU (SOP)', pool_kr_sop: 'KR (SOP)', avail: '가용', fromStock: '재고 사용', left: '남음',
        allocation: '배정', onlyActive: '오더 또는 재고가 있는 SKU만', stockTpl: '재고 파일', stockImport: '재고 파일 가져오기', allocate: '재고 배정',
        allocateTitle: '재고 배정', allocateGo: '배정',
        allocateBody: '재고가 있는 모든 SKU를 다시 배정합니다. 아래 순서의 바이어가 KR 국내, EUSU, KR SOP 순으로 오더 수량만큼, 남은 만큼 가져갑니다. 직접 수정한 수량은 대체됩니다.',
        allocateOrder: '배정 순서 (설정 › 바이어 › 재고 우선순위):', allocated: 'SKU {{skus}}개 재고 배정: 재고 사용 {{pcs}} pcs.',
        allocEdit: '{{sap}} 배정', allocTotal: '가용 {{avail}} 중 {{served}} 배정',
        stockImportBody: '파일에 있는 SKU는 세 수치로 바뀌고(빈칸 = 재고 없음), 파일에 없는 SKU는 그대로 둡니다. 형식은 재고 파일을 내려받아 확인하세요.',
        stockSummary: '시트 "{{sheet}}": {{rows}}행 — 변경 SKU {{n}}개.', stockWritten: 'SKU {{n}}개 재고를 반영했습니다.',
        prodNote: '필요 생산 = 바이어별 오더 − 재고 사용의 합. 각 SKU에 공장과 추가 생산을 지정하세요. 카드는 공장별 계획 수량입니다.',
        required: '필요', overbuild: '추가 생산', planned: '계획', factory: '공장', comment: '코멘트', needsOnly: '생산할 SKU만',
        allFactories: '전체 공장', unassigned: '미지정', byBuyer: '바이어별',
        m_order: '오더', m_stock: '재고 사용', m_prod: '생산', total_order: '총 오더', total_stock: '총 재고 사용', total_prod: '필요',
        mNote_order: '노란색: 이번 사이클 대상이 아닌 SKU 오더, 또는 해당 바이어 가격 없음.', mNote_stock: '빨간색: 오더보다 많은 재고 배정.',
        mNote_prod: '바이어별 오더 − 재고 사용.',
        c_orders: '총 오더', c_stock: '재고 사용', c_prod: '필요 생산', c_ob: '추가 생산', c_planned: '계획 생산', c_amount: '오더 금액',
        customerReport: '고객 리포트 — 제품 카테고리별',
        priceRule: '금액 = 수량 × 바이어 가격: T2 바이어는 T2 FOB, HEMA 대리점은 HEMA DT 가격, 그 외는 T1 FOB.',
        sub_customers: '바이어', sub_cycles: '일정', addCustomer: '바이어 추가', active: '활성', discontinued: '거래 중단',
        custNote: '담당자 = 바이어 시트를 작성하는 Sale OP 담당자. 가격 등급과 법인이 가격 열을 정하고, 오더북 열이 있으면 시트 대상 바이어가 되며, 재고 우선순위는 재고 배정 순서입니다.',
        cycleNote: '오더 & 출고 일정 (Monthly order program 2026). 마감된 사이클은 수정할 수 없습니다.',
        etas: 'ETA KR/JP/SIN · AUS · EU · USA', etaLine: '{{a}} · {{au}} · {{eu}} · {{us}}',
        edit_customer: '바이어 수정', edit_customer_new: '바이어 추가', edit_cycle: '{{date}} 오더 수정',
        f_customer_key: '바이어', f_pic_user_id: '담당자 (Sale OP)', f_country: '국가', f_pricing: '가격 등급', f_entity: 'Helinox 법인', f_status: '상태',
        f_sold_to: 'Sold-To (SAP)', f_ship_to: 'Ship-To (SAP)', f_grid_order: '오더북 열', f_alloc_priority: '재고 우선순위', f_remark: '비고',
        f_sap_code: 'SAP 코드', f_product: '제품', f_skin_color: '스킨 색상', f_frame_color: '프레임 색상', f_selling_sku: '판매 SKU#', f_upc: 'UPC',
        f_line_category: '카테고리', f_lineup: '라인업', f_order_cycle: '오더 주기', f_noos: 'NOOS', f_product_category: '제품 카테고리',
        f_ex_factory_loc: '출고 위치', f_carton_qty: '카톤 입수', f_hts_usa: 'HTS (미국)', f_hts_eu: 'HTS (유럽)', f_ctn_size: '카톤 사이즈/타입',
        f_frame_category: '프레임 카테고리', f_price_t1: 'T1 FOB (글로벌 법인·아시아)', f_price_hema_dt: 'T1 FOB (HEMA DT)', f_price_t2: 'T2 FOB (비대리점)',
        f_season: '시즌', f_fc_due: 'FC 마감', f_order_due: '오더 마감', f_ex_factory: '출고', f_items_note: '품목', f_eta_asia: 'ETA KR/JP/SIN',
        f_eta_aus: 'ETA 호주', f_eta_eu: 'ETA 유럽', f_eta_usa: 'ETA 미국',
      },
      nav: {
        aiPowered: 'Claude(Anthropic의 AI)가 이 포털의 요약과 답변을 작성합니다',
        report: '리포트',
        salesReport: '세일즈 리포트',
        saleOperation: '기타 도구',
        development: '개발',
        salesOperation: '세일즈 오퍼레이션',
        production: '생산',
        productSalesDashboard: '상품 매출 대시보드',
        salesReportDashboard: '비즈니스 성과',
        marketingDashboard: '마케팅 대시보드',
        dataExplorer: '데이터 탐색',
        salesDataLoad: '판매 데이터 업데이트',
        inventoryReport: 'Global Inventory Report',
        poleInventory: '폴 재고 월마감',
        eusuStock: 'EUSU 비매 재고',
        inventoryDashboard: '재고 대시보드',
        poleDashboard: '폴 재고 대시보드',
        demandForecastDashboard: '수요 예측 대시보드',
        autoJobs: 'Sales Op. Python',
        dashboard: '대시보드',
        projectTracker: '프로젝트 트래커',
        documents: 'E-Docs',
        documentLibrary: '라이브러리',
        sampleTracker: '샘플 트래커',
        partnerSamples: 'DAC 샘플 트래커',
        ticketStation: '티켓 스테이션',
        userManual: '사용자 매뉴얼',
        projectApproval: '프로젝트 승인',
        nfcTokenPortal: 'NFC 토큰 포털',
        pricingCalculator: '콜라보 가격 계산기',
        ceoOffice: 'CEO Office',
        gtm: 'GTM',
        finance: '재무',
        cashSetup: '설정',
        cashImport: '제출',
        cashApproval: '승인',
        courierRequests: '쿠리어 발송 요청',
        courierApproval: '쿠리어 승인',
        courier: '쿠리어',
        collabPricing: '콜라보 가격',
        arrangeOpen: '메뉴 순서 변경',
        arrangeTitle: '내 메뉴 순서',
        arrangeHint: '폴더나 메뉴를 끌어 놓거나 화살표를 사용하세요. 이 순서는 나에게만 보입니다.',
        arrangeUp: '{{name}} 위로',
        arrangeDown: '{{name}} 아래로',
        arrangeDone: '완료',
        arrangeCancel: '취소',
        arrangeReset: '초기화',
        arrangeResetHint: '포털 기본 순서로 되돌립니다',
        arrangeIsDefault: '이미 포털 기본 순서입니다',
        arrangeFailed: '메뉴 순서를 저장하지 못했습니다',
        arrangeUnfold: '{{name}} 메뉴 보기',
        arrangeFold: '{{name}} 메뉴 숨기기',
        hevnAdmin: 'HEVN 관리',
        driverLog: '운행 일지',
        salesPlanning: '세일즈 플래닝',
        splFormat: '오더 포맷', splOrders: '오더 시트', splStock: '재고 배정', splProduction: '공장 배정', splOverview: '오더 개요', splSetup: '플래닝 설정',
        cashWeekly: '주간 현금 보고',
        cashPlan: '월간 현금 계획',
        cashMonthEnd: '월말 대사',
        cashReport: '현금 보고서',
        gtmProgrammes: 'GTM 프로그램',
        gtmApproval: 'GTM 승인',
        gtmTracker: 'GTM 진행 현황',
        ceoFolder: 'CEO',
        benchmarkLibrary: '인라인 원가표',
        pricingApproval: '가격 승인',
        costing: '원가',
        opaSct: 'OPA / SCT',
        sctSummary: 'SCT 요약',
      },
      cash: {
        title: '설정',
        sub: '모든 현금 보고가 읽는 목록입니다: 법인, 은행 계좌, 주차, 환율, 비용 분류, 그리고 작성·검토·승인 담당자. 변경은 Group CFO가 승인해야 적용됩니다.',
        loading: '불러오는 중…',
        tab_entity: '법인', tab_account: '계좌', tab_user: '사용자', tab_period: '기간', tab_fx: '환율',
        tab_expense: '비용 분류', tab_account_type: '계좌 유형', tab_role: '역할', tab_notice: '알림',
        tab_flow: '흐름', tab_changes: '대기 중인 변경', tab_history: '이력', tab_setting: '설정', tab_currency: '통화',
        f_code: '코드', f_name: '이름', f_tax_code: '세금 코드 / ID', f_legal_address: '법적 주소', f_country: '국가',
        f_ho_address: '본사 주소', f_acc_mail: '관리 / 회계 메일', f_min_balance_sgd: '최소 잔액 (SGD)', f_status: '상태',
        f_seq: '순서', f_account_id: '계좌 ID', f_entity_code: '법인', f_account_no: '계좌 번호', f_currency: '통화',
        f_bank_name: '은행', f_account_type: '계좌 유형', f_restricted: '제한', f_fixed_deposit: '정기예금',
        f_remarks: '비고', f_user_code: '코드', f_mail: '메일', f_role_key: '역할', f_year: '연도', f_week: '주',
        f_week_ending: '주 마감 (금요일)', f_submit_deadline: '제출 마감', f_category_type: '유형', f_describe: '설명',
        f_type_name: '유형', f_default_restricted: '기본: 제한', f_default_fd: '기본: 정기예금',
        f_action: '작업', f_right_scope: '권한', f_restriction: '제한 (직무 분리)',
        f_notice_id: '알림 ID', f_header: '제목', f_content: '내용', f_report: '보고서', f_send_to_role: '수신 (역할)',
        f_send_day: '발송 요일', f_send_time: '발송 시간', f_setting_value: '값', f_rate: '환율', f_week_code: '주',
        f_trigger_key: '트리거', tab_log: '알림 기록',
        trg_report_open: '주간 보고 시작 (예약)', trg_due_soon: '마감 알림 (예약)', trg_missing: '미제출 (예약)',
        trg_plan_update: '13주 계획 갱신 (예약)', trg_month_open: '월말 대사 시작 (예약)',
        trg_upload_errors: '업로드 오류', trg_submitted: '검토 요청', trg_reviewed: '검토 완료', trg_approved: 'Group CFO 승인',
        trg_closed: 'CEO 마감', trg_returned: '반려', trg_below_min: '최소 잔액 미달', trg_setup_submitted: 'Set up 승인 요청',
        mailHeld: '메일 보류', mailLive: '메일 발송 중',
        mailHeldLine: '모듈 개발 중이라 현금 보고 메일은 보류됩니다: 아래 알림은 모두 작성·기록만 되었고 아무에게도 발송되지 않았습니다.',
        mailLiveLine: '현금 보고 메일이 발송 중입니다: 아래 알림은 발송되었습니다.',
        logRun: '예약 알림 지금 실행', logRunAt: '이 시점 기준으로 실행', logAtWhy: '비워 두면 지금 기준, 시점을 고르면 그때 무엇이 나갈지 확인합니다 (메일 보류 중에만). 서버 시간.',
        logRan_one: '{{at}} 기준 실행: 알림 {{count}}건 작성.', logRan_other: '{{at}} 기준 실행: 알림 {{count}}건 작성.',
        logEmpty: '아직 작성된 현금 보고 메일이 없습니다.', logWhen: '시각', logStatus: '상태', logWhat: '알림', logFor: '법인 · 기간',
        logTo: '받는 사람', logSubject: '제목', log_held: '보류', log_sent: '발송', log_failed: '실패', log_claimed: '발송 중…', log_tester: '테스터에게 발송',
        testAs: '테스트 역할', testOff: '끔 — 내 역할',
        testOnNote: '{{role}}(으)로 테스트 중: 모든 단계가 "(test as {{role}})"로 기록됩니다. Cash 메일이 보류된 동안에만 가능합니다.',
        testOffNote: '구축 중: Preparer 또는 Reviewer 역할로 보고서의 두 단계를 직접 진행해 볼 수 있습니다.',
        logSim: '모의 실행', logSimWhy: '특정 시점 기준 실행으로 작성됨. 그날의 실제 알림을 소진하지 않습니다.',
        s_base_currency: '기준 통화', s_rate_source: '환율 출처', s_rate_date: '환율 기준일',
        state_clean: '적용 중', state_draft: '초안 — 미발송', state_submitted: 'Group CFO 승인 대기',
        bandClean: '이 탭의 모든 내용은 승인되어 적용 중입니다.',
        bandEmpty: '아직 승인된 내용이 없습니다. 설정 워크북을 가져온 뒤 Group CFO에게 보내세요.',
        bandCounts: '신규 {{insert}} · 변경 {{update}} · 삭제 {{delete}} — Group CFO 승인 전까지 적용되지 않습니다',
        sentBy: '{{by}}님이 {{at}}에 발송',
        returned: '{{by}}님이 {{at}}에 반려:',
        bootstrap: '사용자 목록에 승인된 행이 아직 없어 포털 관리자인 귀하가 현금 보고 관리자로 동작합니다. 첫 목록이 승인되면 종료됩니다.',
        provisional: '아직 승인된 내용이 없어, 제안된 사용자 목록의 역할({{roles}})로 보고 있습니다. Group CFO가 목록을 승인하면 확정됩니다.',
        import: 'Excel 가져오기', send: 'Group CFO에게 보내기', approve: '승인',
        notePh: 'Group CFO에게 남길 메모 (선택)',
        fixFirst: '데이터 검사 오류가 있는 행을 먼저 고쳐야 보낼 수 있습니다.',
        sent: 'Group CFO에게 보냈습니다. 승인 전까지 아무것도 바뀌지 않습니다.',
        approved: '승인되었습니다 — 변경이 지금 적용됩니다.',
        rejected: '사유와 함께 관리자에게 반려했습니다.',
        imported: '가져오기: 신규 {{insert}}, 변경 {{update}}, 동일 {{same}}. 초안에 있으며 아직 적용되지 않았습니다.',
        sumErrors_one: '데이터 검사 오류 {{count}}행', sumErrors_other: '데이터 검사 오류 {{count}}행',
        sumWarns_one: '경고 {{count}}행', sumWarns_other: '경고 {{count}}행',
        onlyIssues: '데이터 검사 메시지가 있는 행만', onlyChanged: '변경된 행만',
        rows_one: '{{count}}행', rows_other: '{{count}}행',
        search: '검색…', clear: '지우기', addRow: '행 추가', save: '저장', cancel: '취소', edit: '수정',
        remove: '삭제', undo: '이 변경 취소', was: '승인된 값', choose: '선택…', auto: '저장 시 발급',
        autoHint: '새 행의 코드는 포털이 발급하며 (Entity-01, EC-0001, N-001…) 다시 사용되지 않습니다.',
        dataCheck: '데이터 검사', chkOk: 'OK', chkError: '오류', chkWarn: '경고',
        op_insert: '신규', op_update: '변경', op_delete: '삭제 예정',
        noRows: '행이 없습니다.', yes: '예', no: '아니오',
        week: '주', allWeeks: '모든 주', year: '연도', addYear: '다음 연도의 모든 주 추가', addWeeks: '주 추가', weeksAdded: '{{year}}년 {{count}}주가 초안에 추가되었습니다.',
        fxHint: '환율 = 해당 통화 1단위당 기준 통화 금액, 즉 SGD 금액 = 외화 금액 × 환율. 주마다 하나의 환율; Template 1은 해당 주의 환율을 읽습니다.',
        newCurrency: '예: CHF', addCurrency: '통화 추가', editRates: '환율 수정', saveRates: '{{count}}칸 저장',
        fxSync: 'NetSuite에서 동기화', fxSyncTitle: '매주 금요일 환율을 NetSuite에서 읽어옵니다. Group CFO 승인 없이 바로 반영되며, 여기서 직접 입력한 환율은 바꾸지 않습니다.',
        fxLastSync: 'NetSuite 최종 환율: {{when}}', fxNeverSynced: '아직 NetSuite에서 동기화하지 않음',
        fxSynced: 'NetSuite: {{weeks}}주 동안 환율 {{written}}개 반영, {{unchanged}}개는 이미 최신.',
        fxKeptTyped: 'Set up에서 입력한 환율 {{n}}개는 그대로 두었습니다 ({{list}}).',
        fxMissing: 'NetSuite에 환율이 없는 항목 {{n}}개 ({{list}}).',
        fxFromNetsuite: 'NetSuite — {{day}} 환율', fxBase: '기준 통화', fxTyped: 'Set up에서 입력·승인됨 — NetSuite 동기화가 바꾸지 않습니다',
        flowReport: '보고서', step: '{{n}}단계',
        rep_weekly: '주간 현금 보고', rep_13w: '13주 현금 계획', rep_month: '월말 대사', rep_setup: '설정',
        flowPrep: 'Preparer: 작성', flowReview: 'Reviewer: 검토 및 동의', flowCfo: 'Group CFO: 승인', flowCeo: 'CEO: 승인 및 마감',
        flowAdmin: 'Admin: 설정',
        flowNote: '세 보고서 모두 이 흐름을 따릅니다 (Finance › 현금 보고서: 주간 현금 보고, 13주 현금 계획, 월말 대사) — Reviewer의 동의가 마지막 단계이며, 동의되면 현금 보고서에 표시됩니다. 알림 탭은 무엇을 누구에게 언제 보낼지 정합니다 — 예약 알림은 발송 요일·시간, 단계에서 생기는 알림은 Event — 그리고 알림 기록에서 작성된 모든 메일을 볼 수 있습니다.',
        noChanges: '대기 중인 변경이 없습니다.', list: '목록', code: '코드', change: '변경', what: '변경 내용', by: '작성자',
        noHistory: '승인된 변경이 아직 없습니다.',
        histLine: '변경 {{n}}건 · {{by}} {{at}} 발송 · {{cfo}} {{on}} 승인',
        histBuild: '변경 {{n}}건 · {{by}} {{on}} 개발용으로 적용 — Group CFO 승인 아님',
      },
      cashr: {
        title: '현금 보고서',
        reviewedOnly: 'Reviewer가 동의한 보고서만 금액이 표시됩니다.',
      },
      cashw: {
        h_balance: "잔액 ({{ccy}})",
        h_balanceOrig: "잔액 (계좌 통화)",
        h_amountIn: "금액 ({{ccy}})",
        baseLabel: "표시 통화",
        baseWhy: "모든 금액은 Set up의 해당 주 환율(NetSuite, 금요일)로 SGD에서 환산됩니다. 계좌 잔액은 계좌 통화 그대로 표시됩니다.",
        baseRate: "Set up의 해당 주 환율 적용: 1 {{ccy}} = {{rate}} SGD.",
        noBaseRate: "이번 주 {{ccy}} 환율이 아직 Set up에 없어 SGD로 표시합니다.",
        balanceWhy: "은행 계좌별 현금 보기",
        balanceNote: "잔액 = 주말 기준 은행 보유 현금. 가용 현금 = 잔액 − 사용 제한 − 정기예금. 계좌 잔액은 은행 잔고증명과 같이 계좌 통화로, 그 외 금액은 Set up의 해당 주 환율(NetSuite)로 환산됩니다. 전주 대비는 두 주 모두 보고한 법인 기준입니다.",
        tot_balance: "잔액",
        title: '현금 보고 · 주간',
        sub: '각 법인이 제출 화면에서 해당 주의 Template 1을 가져오면, 보고서는 Preparer → Reviewer 순으로 진행되며 Reviewer가 동의하면 여기에 표시됩니다. 모든 금액은 SGD이며 입력된 내용으로 포털이 계산합니다.',
        prevWeek: '이전 주', nextWeek: '다음 주',
        weekLine: '주 마감 {{end}} · 제출 기한 {{due}}',
        closedWeek: '마감됨',
        noRates: 'Set up에 이 주의 환율이 아직 없습니다 — 금요일 환율이 나오면 NetSuite에서 자동으로 들어옵니다. 그전까지는 SGD로 표시할 수 없고 보고서를 제출할 수 없습니다.',
        h_entity: '법인', h_opening: '기초', h_inflow: '입금', h_outflow: '출금', h_closing: '기말',
        h_restricted: '사용 제한', h_fd: '정기예금', h_available: '가용 현금', h_change: '전주 대비',
        h_belowMin: '최소 잔액 미달', h_status: '상태',
        h_no: 'No.', h_category: '분류', h_memo: '메모', h_amount: '금액 (SGD)',
        h_account: '계좌', h_ccy: '통화', h_closingStmt: '기말 (명세서)', h_diff: '차이', h_rate: 'SGD 환율',
        h_closingSgd: '기말 (SGD)', h_files: '파일', h_kind: '구분', h_other: '상대 법인', h_ref: '참조',
        groupNote: '법인 간 거래 제외',
        groupCount: '{{of}}개 법인 중 {{n}}개 검토 완료',
        unmatched: '관계사 간 거래 미상계: {{amt}} SGD',
        unmatchedWhy: '법인들이 서로 받았다고 보고한 금액과 지급했다고 보고한 금액의 합이 0이 아닙니다 — 한쪽이 아직 보고하지 않았거나 이동 중입니다. 기말은 은행에 있는 현금이므로, 정확히 이 금액만큼 기초 + 입금 − 출금과 다릅니다.',
        nothingYet: '이번 주에 업로드한 법인이 아직 없습니다.',
        yearTitle: '{{year}}년 전체 주', yearPick: '연도', h_weekEntity: '법인 / 계좌', h_bank: '은행', h_acctNo: '계좌번호', h_acctType: '계좌 유형', h_acctFlag: '사용 제한 / 정기예금',
        weekTitle: '{{code}} 주 · {{end}} 마감',
        weekOpenWhy: '이 주의 법인 열기/닫기', weekPickWhy: '이 주를 화면 기준 주로 — 차트, Top 3, 보고서',
        weekNothing: '보고 없음', yearLoading: '나머지 주를 불러오는 중…',
        yearNote: '연도의 주마다 GROUP 한 행; ▸ 를 눌러 주의 법인을, 법인의 ▸ 로 계좌를 열고, 주 코드를 눌러 화면 기준 주로 바꿉니다.',
        origClosing: '{{ccy}} 기준 기말: {{amt}}',
        inCcy: '{{ccy}} 기준',
        origWhy: '계좌 통화({{ccy}}) 기준, 은행 잔고증명 금액입니다.',
        notUploaded: '아직 업로드되지 않았습니다.',
        notReviewed: '아직 검토 전 — Reviewer가 동의하면 금액이 표시됩니다.',
        expandWhy: '은행 계좌별 현금 보기',
        errorsN_one: 'Data Check 오류 {{count}}건', errorsN_other: 'Data Check 오류 {{count}}건',
        below: '미달', belowWhy: '가용 현금이 이 법인의 최소 잔액 {{min}} SGD보다 적습니다.',
        noMinWhy: 'Set up에 이 법인의 최소 잔액이 없습니다.',
        interGroup: '관계사 간 거래', interGroupWhy: '다른 Helinox 법인과의 거래 — GROUP 행에서 제외됩니다.',
        restrictedTag: '사용 제한', fdTag: '정기예금', noTxn: '거래 없음',
        sgdNote: 'SGD = 계좌 통화 금액 × Set up의 해당 주 환율 (NetSuite). 계좌 통화 기준 기말 잔액은 각 계좌 행에 표시됩니다. 입금·출금은 법인 내 계좌 간 이체를 제외합니다. 가용 현금 = 기말 − 사용 제한 − 정기예금.',
        topTitle: 'Top 3', top_spend: '이번 주 주요 지출 Top 3', top_payable: '다음 주 지급 예정 Top 3', noTops: '이번 주에 보고한 법인이 아직 없습니다',
        expandAll: '모두 펼치기', collapseAll: '모두 접기',
        st_none: '미업로드', st_open: '작성됨 — 미발송', st_returned: '반려됨', st_submitted: 'Reviewer 검토 중',
        st_reviewed: '동의 완료', st_approved: '승인', st_closed: '마감',
        by_submitted: '{{by}} {{at}} 발송', by_reviewed: '{{by}} {{at}} 검토', by_approved: '{{by}} {{at}} 승인',
        by_closed: '{{by}} {{at}} 마감', by_returned: '{{by}} {{at}} 반려',
        weekEnding: '주 마감 {{d}}',
        step_prep: 'Preparer', step_review: 'Reviewer', step_cfo: 'Group CFO', step_ceo: 'CEO',
        notYet: '아직',
        returnedBy: '{{by}}님이 {{at}}에 반려:',
        weekClosed: 'Set up에서 마감된 주입니다 — 조회만 가능하고 업로드할 수 없습니다.',
        weekClosedReturned: 'Set up에서 마감된 주이지만, 반려된 보고서는 수정해서 다시 발송할 수 있습니다.',
        selfBlocked: '이 버전을 업로드했거나 이전 단계를 처리하셨으므로 “{{step}}”은(는) 다른 분이 처리해야 합니다.',
        tooBig: '파일은 최대 3.5 MB입니다. 은행 명세서를 더 작은 PDF로 저장하거나 나누어 첨부하세요.',
        changeOver_one: '두 주 모두 보고한 {{count}}개 법인 기준', changeOver_other: '두 주 모두 보고한 {{count}}개 법인 기준',
        template: '양식 다운로드', upload: '파일 가져오기', uploadAgain: '다시 가져오기',
        notePh: 'Reviewer에게 메모 (선택)', submit: 'Reviewer에게 발송',
        fixFirst: '발송 전에 Data Check 오류를 수정하세요.',
        btn_review: '검토·동의', btn_approve: '승인', btn_close: '승인·마감', sendBack: '반려',
        reopen: '수정을 위해 다시 열기', reopenWhy: '동의 완료. 다시 열면 사유와 함께 Preparer에게 반려되며, 다시 동의될 때까지 현금 보고서에서 수치가 빠집니다.',
        done_submit: 'Reviewer에게 발송했습니다.', done_review: '검토·동의 완료 — 이제 현금 보고서에 표시됩니다.',
        done_approve: '승인 완료 — CEO 승인 대기.', done_close: '승인·마감했습니다.', done_return: '사유와 함께 Preparer에게 반려했습니다.',
        mailHeld: '(메일은 발송되지 않았습니다: 모듈 개발 중에는 현금 보고 메일을 보류합니다.)',
        uploadedOk: '버전 {{v}} 가져옴.',
        replacedOk: '버전 {{v}} 가져옴 — Data Check를 통과해 Reviewer가 보고 있던 버전 {{old}}을(를) 대체했습니다.',
        correctWhileReview: 'Reviewer 검토 중 (버전 {{v}}). 아래에서 바로 고치거나 고친 파일을 가져와 수정할 수 있습니다: 저장 후 Data Check를 통과하면 Reviewer가 보는 버전을 대체합니다.',
        uploadedErrors_one: '버전 {{v}} 업로드 — 발송 전에 수정할 Data Check 오류 {{count}}건.',
        uploadedErrors_other: '버전 {{v}} 업로드 — 발송 전에 수정할 Data Check 오류 {{count}}건.',
        nothingUploaded: '아직 입력된 내용이 없습니다.',
        sumErrors_one: 'Data Check 오류 {{count}}건', sumErrors_other: 'Data Check 오류 {{count}}건',
        sumWarns_one: '경고 {{count}}건', sumWarns_other: '경고 {{count}}건',
        versionLine: '버전 {{v}} 표시 중',
        accountsTitle: '계좌', transfersTitle: '이체', filesTitle: '은행 명세서와 잔액 화면',
        versionsTitle: '버전 — 가져오기 또는 입력', historyTitle: '이력과 코멘트', current: '진행 중',
        rateFrom_setup: 'Set up 환율', rateFrom_base: '기준 통화',
        tot_opening: '기초', tot_inflow: '입금', tot_outflow: '출금', tot_closing: '기말', tot_restricted: '사용 제한',
        tot_fixed_deposit: '정기예금', tot_available: '가용 현금',
        tr_internal: '법인 내 이체', tr_group: '관계사 간 거래',
        kind_statement: '명세서', kind_balance: '잔액 화면', kind_other: '기타',
        wholeEntity: '법인 전체', attach: '파일 첨부', attached: '파일을 첨부했습니다.', removeFile: '이 파일 삭제',
        noFiles: '첨부된 파일이 없습니다.', commentPh: '코멘트 입력…', comment: '코멘트', noEvents: '아직 이력이 없습니다.',
        waitingYou: '내 결정 대기',
        waitingOpen: '승인 화면에서 열어 결정하기',
        ev_upload: '파일에서 버전 {{v}} 가져옴', ev_entry: '포털에서 수정해 버전 {{v}} 저장',
        changesAgainst: '버전 {{v}} 대비 변경 — {{n}}건', noChange: '버전 {{v}} 대비 바뀐 수치가 없습니다.', moreChanges: '{{n}}건 더 보기', fewerChanges: '접기', changesCut: '…외 {{n}}건', ev_discard: '버전 {{v}} 취소', cancelled: '취소됨', ev_submit: '버전 {{v}}을(를) Reviewer에게 발송', ev_review: '검토·동의',
        ev_approve: '승인', ev_close: '승인·마감', ev_return: '반려', ev_comment: '코멘트',
        ev_file: '파일 첨부', ev_unfile: '파일 삭제',
      },
      cashp: {
        title: '현금 보고 · 13주 현금 계획',
        sub: '각 법인이 13주 앞을 SGD로 계획하고 (Template 2), 그 계획을 주간 현금 보고의 실제 수치와 비교합니다. 매주 새로 작성하며 주간 보고와 같은 2단계를 거칩니다.',
        startsLine: '{{week}} 시작 계획 (주 마감 {{end}}), {{last}}까지',
        h_week: 'Week No.', h_end: '주 마감일', h_planOpen: '기초 (직전 기말)', h_planIn: '계획 입금', h_planOut: '계획 출금',
        h_planClose: '계획 기말 (예상)', h_actIn: '실제 입금', h_actOut: '실제 출금', h_actClose: '실제 기말',
        h_variance: '계획 대비 차이 (기말)', h_weekly: '주간 보고', h_reported: '보고', h_comment: '코멘트',
        h_planClose13: '13주차 계획 기말',
        h_planOpen1: '1주차 기초', h_planIn13: '13주 계획 입금', h_planOut13: '13주 계획 출금',
        noReviewed: '검토된 계획이 아직 없습니다',
        reportedOf: '{{n}}개 보고',
        noPlan: '이번 주에 시작하는 계획을 업로드한 법인이 아직 없습니다.',
        openWhy: '연간 계획 열기/닫기', groupPlanned: '검토된 계획: {{of}}개 법인 중 {{n}}개',
        listHint: '각 법인의 이번 주 계획 진행 상황입니다. 위의 차트와 표는 선택한 대상에 따라 바뀝니다.',
        pickLabel: '표시', pickGroup: 'GROUP (전체 법인)', pickGroupWhy: '전체 법인 합계',
        filtersTitle: '계획 필터', filterEntities: '법인',
        filtersHint: 'GROUP, 법인 하나 또는 여러 법인(합계)을 고른 뒤 연도를 고르세요. 아래 차트와 53주 표가 둘 다 따라갑니다.',
        oneYear: '아직 계획이 한 해만 있습니다',
        weeksShown: '{{of}}주 중 {{count}}주', h_planFrom: '기준 계획', planFromWhy: '{{week}} 시작 계획에서 가져옴 — 이 주를 포함하는 가장 최신 계획',
        plannedBy_one: '법인 {{count}}개', plannedBy_other: '법인 {{count}}개', yearOf: '{{year}}년 전체 주',
        yearNote: '연도의 모든 주. 각 주는 그 주를 포함하는 가장 최신 계획에서 가져옵니다 — 지난 주는 그 주에 제출된 계획, 앞으로의 주는 최신 예측 — "기준 계획"이 그 계획입니다.',
        groupTitle_one: 'GROUP — {{count}}개 법인 계획', groupTitle_other: 'GROUP — 이번 주 계획을 낸 {{n}}개 법인의 합계',
        groupNote: 'GROUP은 이번 주 계획을 낸 법인의 합계입니다. 실제 수치는 해당 법인이 모두 그 주를 보고한 뒤에만 합산되므로, 일부 법인의 실제와 전체 계획을 비교하지 않습니다.',
        entityNote: '실제 수치는 주간 현금 보고의 SGD 수치입니다 (법인 내 계좌 간 이체 제외 입출금, 기말 = 은행 잔액 합계). 차이 = 실제 기말 − 계획 기말.',
        panelHeading: '{{week}} 시작 계획', opening: '기초 현금 (SGD)', closing13: '13주차 계획 기말 (SGD)',
      },
      cashm: {
        title: '현금 보고 · 월말 대사',
        sub: '각 법인이 월말에 모든 계좌를 대사합니다 (Template 3): 월 은행 명세서의 잔액과 주간 현금 보고의 주말 잔액 (월말 당일 또는 그 이전의 마지막 주 마감일), 그리고 두 날짜 사이의 입출금. Reconcile = Variance − Inflow + Outflow, 반드시 0. 주간 보고와 같은 2단계를 거칩니다.',
        monthLine: '{{month}} · 월말 {{end}}', open: '대사 가능', notEnded: '아직 끝나지 않음',
        notEndedLine: '아직 끝나지 않은 달입니다 — 월이 끝나면 대사를 시작할 수 있습니다.',
        ruleHead: '주말 기준: {{week}} ({{end}}).',
        h_reconciled: '대사 완료 계좌', h_totalSgd: '월말 잔액 (SGD)',
        h_monthEnd: '월말 잔액', h_weekEnd: '주말 잔액 ({{week}})', h_variance: '차이', netOff: 'Net-off (상계)', statusGroup: '상태', h_netIn: '상계 입금',
        h_netOut: '상계 출금', h_reconcile: '대사',
        accountsTitle: '계좌', reconciled: '대사 완료 계좌', totalSgd: '월말 잔액 (SGD)',
        unconvertedWhy: '해당 주의 Set up 환율이 없는 계좌는 SGD 합계에서 빠집니다.',
        monthEndOn: '월말 {{d}}',
        st_submitted: '작성', st_reviewed: '검토', st_approved: '승인', st_closed: '마감',
        unconverted: '환율 없음 {{n}}개', allReconciled: '{{n}}개 모두 대사 완료',
        reportNote: '각 법인 행에는 월말 잔액(SGD)과 작성·검토한 사람과 시각이 있고, 그 아래에 계좌별 금액(계좌 통화)이 이어집니다. 법인을 누르면 대사가 열립니다.',
      },
      cashq: {
        title: '승인',
        sub: 'Preparer가 발송한 Weekly Cash Report, 13 Weeks Cash Plan, Month-End Reconcile 보고서입니다. 보고서를 선택해 확인하고 동의하거나 반려하세요. Reviewer가 동의하면 현금 보고서에 표시됩니다.',
        tab_waiting: '내 결정 대기', tab_progress: '진행 중', tab_closed: '동의 완료',
        rows_one: '보고서 {{count}}건', rows_other: '보고서 {{count}}건', clear: '정렬·필터 초기화',
        colReport: '보고서', colEntity: '법인', colPeriod: '기간', colStatus: '상태',
        colSentBy: '발송자', colSentAt: '발송일', colUpdated: '최종 변경',
        empty_waiting: '결정을 기다리는 보고서가 없습니다.', empty_progress: '진행 중이거나 Preparer에게 반려된 보고서가 없습니다.',
        empty_closed: '동의된 보고서가 아직 없습니다.',
        notListed: '이 보고서는 대기 목록에 없습니다 — 아직 발송되지 않았거나 조회 권한이 없는 보고서입니다.',
      },
      cashi: {
        title: '제출',
        sub: '내 법인의 템플릿을 다운로드해 작성하고 가져와 확인한 뒤 Reviewer에게 보냅니다.',
        entity: '법인',
        yoursOne: '설정의 User 목록에 등록된 내 법인입니다.',
        yoursMany: '{{n}}개 법인의 Preparer입니다. 입력할 법인을 선택하세요.',
        notPreparer: '현금 보고 User 목록에서 어느 법인의 Preparer도 아니므로, 이곳에서 조회와 다운로드만 할 수 있고 가져오기는 할 수 없습니다. Preparer는 설정 › User에서 지정합니다.',
        noEntity: '표시할 법인이 없습니다.',
        checkPassed: 'Data Check 통과',
        saveOrCancel: '먼저 변경 사항을 저장하거나 취소한 뒤 가져오세요.',
        openReport: '{{report}} 열기',
        pointer: '이 보고서는 제출 화면({{tpl}})에서 가져오고 발송합니다.',
        pointerReturned: 'Preparer에게 반려되었습니다. 제출 화면에서 수정 후 다시 발송합니다.',
        goImport: '제출 열기',
        pointerDecide: '이 보고서는 회원님의 결정을 기다리고 있습니다 — 승인 화면에서 동의하거나 반려하세요.',
        nothingYet: '아직 입력된 내용이 없습니다. 해당 법인의 Preparer가 제출 화면에서 {{tpl}}을(를) 가져옵니다.',
      },
      cashc: {
        byEntity: '법인별 현금 ({{ccy}})', byEntitySub: '잔액을 가용 / 사용 제한 / 정기예금으로 나눔 — 이번 주 {{of}}개 법인 중 {{n}}개 검토 완료.',
        trend: 'GROUP 현금, 최근 12주 ({{ccy}})', trendSub: '매주 보고한 법인의 잔액과 가용 현금 — 빈 구간은 보고가 없는 주입니다.',
        reported: '{{of}}개 중 {{n}}개 보고', noneYet: '아직 보고한 법인이 없습니다.', noTrend: '이 기간에는 아직 보고가 없습니다.',
        planTitle: '계획 — {{who}} (SGD)', planSub: '계획 입금은 위, 계획 출금은 아래; 점선은 계획 기말, 실선은 주간 현금 보고의 실제 기말입니다.',
        monthCash: '법인별 월말 현금 (SGD)', monthCashSub: '이번 달 {{of}}개 법인 중 {{n}}개 보고.',
        noRateAll: '설정에 {{week}} 환율이 입력되면 SGD로 표시됩니다 — 보고된 계좌를 아직 환산할 수 없습니다.',
        noRateSome: '표시되지 않음 — {{week}} 환율 없음: {{list}}.',
        recon: '대사된 계좌', reconSub: '법인별: Reconcile이 0인 계좌와 차이가 있는 계좌.',
        reconciled: '대사 완료', difference: '차이 있음', accounts_one: '{{count}}개 계좌', accounts_other: '{{count}}개 계좌',
      },
      cashf: {
        inputTitle: '입력 — {{tpl}}', enterHere: '포털에서 입력', editHere: '포털에서 수정',
        inputClosed: '해당 법인의 Preparer만, 보고서가 열려 있거나 반려되었을 때 가져올 수 있습니다.',
        field1: 'Field 1 — 법인 실제 현금 흐름 (SGD)', field2: 'Field 2 — 법인 계좌: 전체 거래 합계',
        field3: 'Field 3 — 법인 내 계좌 간 이체 (Intra-entity transfers)',
        field4: 'Field 4 — 관계사 간 거래 (Intercompany transactions, 다른 Helinox 법인과)',
        field5: 'Field 5 — 이번 주 주요 지출 Top 3', field6: 'Field 6 — 다음 주 지급 예정 Top 3',
        f1Entity: '법인', f1Overall: '전체', f1Group: '관계사 간 거래',
        f1NoRate: 'Field 1은 모든 계좌 통화에 Set up › Exchange rates의 이번 주 환율이 있을 때 SGD로 계산됩니다.',
        h_rate: '환율', h_noTxn: '거래 없음 확인', h_restrictedAcct: '사용 제한 계좌', h_fdAcct: '정기예금',
        h_upStatement: '명세서 업로드', h_upBalance: '잔액 화면 업로드', h_entity: '법인',
        h_no: 'No.', h_category: '카테고리', h_memo: '메모', h_amountSgd: '금액 (SGD)',
        fromSetup: '설정의 계좌 유형에서 가져옴', rateFromSetup: '설정의 이번 주 환율',
        rateMissing: 'Set up › Exchange rates(NetSuite 동기화)에 이번 주 {{ccy}} 환율이 없습니다 — 여기서는 입력할 수 없습니다', closingCalc: '기말 = 기초 + 입금 − 출금',
        closingFiled: '가져온 파일의 기말은 {{v}}입니다 — 기초 + 입금 − 출금은 표시된 값입니다. 여기서 저장하면 계산된 값이 저장됩니다.',
        closingDiff: '기초 + 입금 − 출금이 이 기말과 {{d}}만큼 다릅니다',
        memoPh: '내용 — 필요한 만큼 길게', f4None: '이번 주 관계사 간 거래가 없습니다.',
        rateOpt: 'SGD 환율 (선택)', noTxn: '거래 없음', check: '확인',
        ok: 'OK', diff: '차이 {{d}}', notNumber: '숫자가 아님', noTxnAmounts: '거래 없음인데 금액이 있음',
        upStatement: '명세서', upBalance: '잔액 화면',
        addRow: '행 추가', removeRow: '이 행 삭제', save: '새 버전으로 저장', cancel: '편집 취소', cancelImport: '가져오기 취소',
        cancelBack: '버전 {{v}}을(를) 빼고 보고서를 버전 {{back}}(으)로 되돌립니다.', cancelEmpty: '버전 {{v}}을(를) 빼고 보고서를 빈 상태로 되돌립니다. 다시 가져올 수 있습니다.',
        discardedBack: '버전 {{v}} 취소됨 — 버전 {{back}}(으)로 돌아갔습니다.', discardedEmpty: '버전 {{v}} 취소됨 — 보고서가 다시 비었습니다.', noChanges: '아직 변경 사항이 없습니다. 금액을 수정하거나 파일을 가져오세요.',
        saveHint: '저장하면 새 버전이 만들어지고 Data Check가 다시 실행됩니다.',
        payrollRule: '급여는 총액 하나만 입력하세요. 직원 이름이나 개인 급여는 절대 입력하지 마세요.',
        savedOk: '버전 {{v}} 저장됨.', savedReplaced: '버전 {{v}} 저장 — Data Check를 통과해 Reviewer가 보던 버전 {{old}}을(를) 대체했습니다.',
        savedErrors_one: '버전 {{v}} 저장 — 발송 전에 수정할 Data Check 오류 {{count}}건.',
        savedErrors_other: '버전 {{v}} 저장 — 발송 전에 수정할 Data Check 오류 {{count}}건.',
        openingFrom: '첫 주의 직전 기말은 {{week}} 주간 현금 보고의 기말입니다 — 틀린 경우에만 고치세요.',
        prepareDate: '작성일', entityGroup: '법인 / 그룹', entityLabel: '법인', currency: '통화', currencySgd: 'SGD — 이 템플릿의 모든 금액',
        planCloseExp: '계획 기말 (예상)', prevWeek: '직전 주', thisWeek: '이번 주',
        openingNone: '이전 주 주간 현금 보고가 없습니다 — 기초 현금을 입력하세요.',
      },
      gtm: {
        title: 'GTM 프로그램',
        subtitle: '7가지 질문으로 프로그램을 작성하고 검토를 요청합니다. 승인 결정은 GTM › 승인 화면에서 합니다.',
        newProgramme: '새 프로그램',
        createTitle: '프로그램 시작',
        nameLabel: '제목',
        namePlaceholder: '예: 선셋체어(re) 홈 · 북리딩 에디션',
        create: '생성',
        cancel: '취소',
        back: '전체 프로그램',
        none: '아직 프로그램이 없습니다.',
        openDecisions: '결정 필요',
        openVerifies: '확인 필요',
        regionsWaiting: '회신 대기 리전',
        committed: '커밋 수량',
        ofTarget: '/ {{n}}',
        editProgramme: '프로그램 편집',
        headline: '한 줄 설명',
        openItem: '미결 항목',
        openItems: '결정 · 할 일 · 확인',
        noItems: '이 질문에는 아직 없습니다.',
        lineTitle: '내용',
        lineDetail: '상세',
        inThisProgrammeHint: '왜 포함되는지, 또는 왜 아닌지 — 표에 함께 표시되는 문장입니다.',
        discardAsk: '입력한 내용을 버릴까요?',
        needed: '아직 필요: {{fields}}',
        editTable: '표 편집',
        editTableWhy: '표를 열고 여러 줄을 고친 뒤 한 번에 저장합니다',
        editTableHint: '자유롭게 입력하세요 — 저장하기 전에는 아무것도 기록되지 않고, 저장은 한 번에 됩니다: 전부 반영되거나 아무것도 바뀌지 않습니다.',
        saveTable: '표 저장',
        saveTableN: '표 저장 ({{n}})',
        layerHead: '판매 채널 위에서 움직이는 것',
        stage: '단계',
        role: '역할',
        condition: '조건',
        addStage: '단계 추가',
        orderHead: '순서',
        orderLabel: '진행 순서',
        step: '순서',
        stepDetail: '내용',
        addStep: '순서 추가',
        savedN: '{{n}}건 저장했습니다.',
        savedNothing: '바뀐 내용이 없습니다.',
        regionsHint: '이 프로그램이 다루는 리전입니다. 순서가 우선순위이며, 행을 끌거나 화살표로 바꿉니다.',
        sheetLocked: '편집하려면 Edit를 누르세요.',
        sheetOpen: '편집 중입니다 — 자유롭게 입력하세요. 저장을 누르기 전에는 기록되지 않습니다. 줄을 옮기는 것은 즉시 저장됩니다.',
        acrossProgramme: '프로그램 전체',
        qOpenCount: '미결 {{n}}건',
        removeYes: '삭제',
        waitingOnYou: '내 결정 대기 중',
        rev_draft: '검토 요청 전',
        rev_in_review: '승인자 검토 중',
        rev_approved: '승인됨',
        rev_rejected: '반려됨',
        approver: '승인자',
        sentForReview: '검토 요청일',
        approvedOn: '승인일',
        rejectedOn: '반려일',
        sendForReview: '검토 요청',
        reviewAgain: '재검토 요청',
        approve: '승인',
        resubmit: '재요청',
        sent: '발송됨',
        view: '보기',
        kpiAll: '전체 프로그램',
        kpiMine: '내 프로그램',
        scopeMine: '내 프로그램',
        scopeAll: '전체 프로그램',
        noneMine: '아직 내 프로그램이 없습니다 — 작성·요청·담당한 프로그램이 없습니다.',
        showAll_other: '전체 {{count}}건 보기',
        backApproval: 'GTM 승인',
        approvalTitle: 'GTM 승인',
        approvalSub: '나에게 결정을 요청한 프로그램입니다. 작성 중인 초안은 이곳에 표시되지 않습니다.',
        approvalSubAdmin: '결정을 요청받은 프로그램입니다. 관리자는 모든 승인자의 목록을 봅니다. 초안은 표시되지 않습니다.',
        tabWaiting: '결정 대기',
        tabApproved: '승인됨',
        tabRejected: '반려됨',
        emptyQueue_in_review: '결정을 기다리는 프로그램이 없습니다.',
        emptyQueue_approved: '승인된 프로그램이 없습니다.',
        emptyQueue_rejected: '반려된 프로그램이 없습니다.',
        colSentBy: '요청자',
        colSentOn: '요청일',
        colDecidedOn: '결정일',
        colCreatedBy: '작성자',
        colCreatedAt: '작성일',
        colUpdatedBy: '수정자',
        colUpdatedAt: '수정일',
        colManagerNote: '매니저 메모',
        colSkus: 'Project SKU',
        skuHead: '연결된 Project SKU',
        skuHint: '이 프로그램으로 출시되는 Project SKU입니다. Project Tracker의 각 SKU에 있는 GTM 항목과 같은 연결입니다.',
        skuNone: '아직 연결된 Project SKU가 없습니다.',
        skuId: 'Project SKU ID',
        skuName: 'SKU 이름',
        skuGroup: '프로젝트 그룹',
        skuStatus: '상태',
        skuPh: 'Project SKU 선택 또는 번호 입력 (예: 9048 또는 SKU-09048)',
        skuAdd: 'SKU 연결',
        skuUnlink: '이 SKU 연결 해제',
        deleteLockedSent: '검토 요청 중 — 승인자가 검토하는 동안에는 삭제할 수 없습니다.',
        deleteLockedApproved: '승인됨 — 승인된 프로그램은 삭제할 수 없습니다.',
        countAll_other: '프로그램 {{count}}건',
        countOf: '프로그램 {{total}}건 중 {{n}}건',
        searchPh: '코드, 제목, 담당 검색…',
        clear: '초기화',
        noMatch: '조건에 맞는 프로그램이 없습니다.',
        loading: '불러오는 중…',
        colNo: '번호',
        colCode: 'GTM ID',
        colTitle: '프로그램',
        colStatus: '상태',
        colOwner: '담당',
        colApprover: '승인자',
        colLaunch: '출시',
        colDecisions: '결정 필요',
        colVerify: '확인 필요',
        colRegions: '리전',
        colAction: '작업',
        reviewDone_submit: '검토 요청을 보냈습니다.',
        reviewDone_approve: '승인했습니다.',
        reviewDone_reject: '반려했습니다.',
        coverEyebrow: 'GTM 프로그램',
        printedOn: '출력일',
        coverStatus: '검토',
        coverDecisionNote: '결정 메모',
        exportPdf: 'PDF 내보내기',
        delete: '삭제',
        deleteProgramme: '이 프로그램 삭제',
        deleteBody: '하위 {{n}}행과 사진 {{photos}}장이 함께 삭제됩니다.',
        deleteWarn: '되돌릴 수 없습니다.',
        exporting: '내보내는 중…',
        pdf: 'PDF',
        addStop: '중단 조건 추가',
        stopKind_manual: '사람이 직접 확인',
        stopKind_commit_min: '리전 커밋 합계가 기준 미만',
        stopKind_anchor_consent: '앵커 리전 인물이 동의하지 않음',
        stopKindAsk: '포털이 무엇을 읽어야 하나요?',
        deleteYes: '삭제',
        measureHead: '성공을 어떻게 재는가',
        measureHint: '각 항목이 무엇이고, 무엇을 좋다고 보는지.',
        measureItem: '지표',
        measureWhat: '무엇이며 어떻게 판단하는지',
        measurePh: '방어, 에디션, 인라인…',
        timelineHead: '타임라인 · 오너',
        timelineHint: '이 프로그램을 어떻게 굴리는지, 각 단계를 누가 맡는지.',
        timelineStep: '단계',
        timelineStage: '구분',
        timelineWho: '무엇을 하는지',
        timelinePh: '오너 지정, 결정, 출시…',
        timelineWhen: '예정일',
        timelineNote: '받는 사람',
        stopWhere: '현재 상황',
        stopThreshold: '기준값',
        removeDoc: '그래도 삭제',
        removeDocWhy: '마스터플랜에서 온 항목입니다. 임포트를 다시 돌리면 복구됩니다.',
        keepIt: '유지',
        specHead: '항목과 내용',
        specHint: '런치 전에 있어야 할 것: 각 항목과 그 내용. 그 밖의 것은 줄을 추가하세요.',
        specItem: '항목',
        specItemPh: '프레임, 포켓, 가격…',
        specA: '인라인',
        specB: '에디션',
        photosHead: '사진',
        photosHint: '이 프로그램이 가리키는 사진. 설명은 그 사진이 무엇을 짚는지 말합니다.',
        discoverHead: '어떻게 발견되는가',
        discoverHint: '런치 전, 당일, 그 이후에 무엇이 일어나는지.',
        discoverPh: '콘텐츠, 시딩, 초대…',
        noLines: '아직 없습니다.',
        collapseAll: '모두 접기',
        expandAll: '모두 펼치기',
        fieldTitle: '제목',
        stopHead: '정지 조건 (제안)',
        stopNone: '설정된 정지 조건이 없습니다.',
        tripped: '중단 조건 발동',
        holding: '유지 중',
        addLine: '항목 추가',
        lineKind: '종류',
        persona: '페르소나',
        channel: '채널',
        inThisProgramme: '이 프로그램에서',
        remarks: '비고', select: '선택', selectHint: '행을 체크하거나 아래 추가 칸에서 고르면 선택됩니다. 선택된 행의 순서가 우선순위이며, 행을 끌거나 화살표로 바꿉니다.',
        regionsCount: '이 프로그램의 리전 수', specContent: '내용', timelineOwner: '담당자',
        discoverItem: '항목', discoverDetail: '무엇을 하는지',
        trkTitle: 'GTM 진행 현황', trkSub: '승인된 프로그램이 Timeline · Owners의 단계별로 어디까지 왔는지 보여 줍니다. 일이 진행되면 단계를 진행 중 또는 완료로 바꾸세요. 예정일이 지났는데 완료되지 않은 단계는 지연으로 표시됩니다.',
        trkApproved: '승인된 프로그램', trkStepsDone: '완료된 단계', trkOverdue: '지연', trkOnlyOverdue: '지연된 단계가 있는 프로그램만 보기',
        trkSearch: '프로그램, 단계, 담당자 검색…', trkToday: '오늘: {{day}}', trkNone: '아직 승인된 프로그램이 없습니다.',
        trkApprovedBy: '승인자', trkStepsOf: '{{total}}단계 중 {{done}}단계 완료', trkNoSteps: '타임라인 단계가 아직 없습니다',
        trkOverdueN_one: '{{count}}건 지연', trkOverdueN_other: '{{count}}건 지연', trkNext: '다음', trkAllDone: '모든 단계 완료',
        trkStatus: '상태', trkDoneOn: '완료일', trkNoStepsHint: '이 프로그램에는 아직 타임라인 단계가 없습니다. 프로그램의 Timeline · Owners에서 추가하세요.',
        st_not_started: '시작 전', st_in_progress: '진행 중', st_done: '완료',
        trkProgress: '진행률', trkNextDue: '다음 단계 예정일', trkApprovedOn: '승인',
        listNew: '새 항목: 다음에도 쓸 수 있게 목록에 추가됩니다.', listAllIn: '목록의 항목이 모두 이 프로그램에 있습니다. 새로 입력하세요.', listDup: '이미 이 프로그램에서 선택되어 있습니다.', listHave: '이미 이 프로그램에 있습니다.', listPick: '목록에서 고르거나 새로 입력하세요',
        addPersona: '페르소나 추가',
        addChannel: '채널 추가',
        removeRow: '삭제',
        removeAsk: '"{{name}}"을(를) 삭제할까요? 되돌릴 수 없습니다.',
        add: '추가',
        noteOnTable: '이 표에 대한 메모',
        noteOnTableHint: '위 표가 담지 못하는 것 — 단서나 빠진 부분.',
        moveUp: '위로',
        moveDown: '아래로',
        targetAudience: '선택한 페르소나 — 우선순위 순',
        notATarget: '선택 안 함',
        channelsInUse: '선택한 채널 — 우선순위 순',
        channelsOut: '선택 안 함',
        chStatus_green: '전사 채널 상태: 양호',
        chStatus_amber: '전사 채널 상태: 주의',
        chStatus_red: '전사 채널 상태: 취약',
        notYet: '아직 판단 불가',
        unmeasured: '수동 확인',
        stopNote: '프로그램 담당자가 각 조건의 현재 상황을 기록합니다.',
        question: '질문 {{n}}',
        emptySection: '아직 작성되지 않았습니다.',
        oneChange: '섹션 1개 변경됨',
        nChanges: '섹션 {{count}}개 변경됨',
        discard: '되돌리기',
        edit: '편집',
        save: '저장',
        decision: '결정 필요',
        verify: '확인 필요',
        todo: '할 일',
        addTodo: '할 일 추가',
        phaseFilter: '출시 단계',
        allPhases: '전체 단계',
        phaseHintOne: '{{phase}} 단계만 보고 있습니다 — 남은 {{all}}건 중 {{hidden}}건이 가려져 있습니다. 다시 누르거나 전체 단계를 누르면 모두 보입니다.',
        phaseWhy_all: '계획에서 시점이 정해진 모든 일',
        phaseWhy_before: 'Q5 — 시작 전에 갖춰야 할 것과 아직 남은 결정',
        phaseWhy_pre: 'Q6 — PRE-LAUNCH: 출시일 이전',
        phaseWhy_launch: 'Q6 — LAUNCH: 출시 당일',
        phaseWhy_post: 'Q6 — POST-LAUNCH: 출시 이후',
        workNone: '아직 없습니다.',
        phase_before: '시작 전',
        phase_pre: '출시 전',
        phase_launch: '출시',
        phase_post: '출시 후',
        noPhase: '단계 없음',
        addDecision: '결정 항목 추가',
        addVerify: '확인 항목 추가',
        owner: '담당',
        noOwner: '담당 미지정',
        due: '기한',
        noDue: '기한 없음',
        answer: '답변',
        answered: '완료',
        dropped: '취소',
        open: '열림',
        remove: '삭제',
        regionsHead: '리전',
        region: '리전',
        person: '인물',
        replyStatus: '회신',
        consent: '합의',
        commitQty: '커밋',
        replyDue: '회신 기한',
        anchor: '앵커',
        note: '비고',
        addRegion: '리전 추가',
        metricsHead: '성공 측정',
        metric: '지표',
        baseline: '기준선',
        baselineWindow: '기간',
        live: '이후',
        takeBaseline: '기준선 기록',
        retakeBaseline: '다시 기록',
        baselineTaken: '{{when}} 기록',
        baselineNotTaken: '미기록',
        manualMetric: '수기 입력',
        baselineWhy: '기록한 날짜로 고정됩니다. Shopify 주문은 몇 달 뒤에도 환불될 수 있어, 매번 다시 계산하면 비교의 기준 자체가 움직입니다.',
        units: '개',
        sourceDoc: '원본 문서',
        status: '상태',
        launchTarget: '출시',
        notSet: '미정',
        saved: '저장됨',
        saveFailed: '저장 실패',
      },
      account: {
        profile: '프로필',
        listUsers: '사용자 목록',
        createUser: '사용자 생성',
        accessRequests: '액세스 요청',
        rolesPermissions: '역할 및 권한',
        portalInsights: '포털 인사이트',
        masterData: '기준 데이터 관리',
        activityLog: '활동 로그',
        logout: '로그아웃',
      },
      quote: {
        label: '오늘의 한마디',
      },
      doc: {
        title: '라이브러리',
        folders: '폴더',
        allDocs: '전체 문서',
        unfiled: '미분류',
        newFolder: '새 폴더',
        newFolderIn: '{{name}} 안에 새 폴더',
        folderName: '폴더 이름',
        renameFolder: '이름 변경',
        deleteFolder: '삭제',
        deleteFolderAsk: '폴더 "{{name}}"을(를) 삭제할까요? 안에 있는 문서는 삭제되지 않습니다.',
        folderField: '폴더',
        folderNone: '미분류',
        folderSaved: '폴더를 저장했습니다.',
        subtitle: '회사 문서를 여기서 읽거나, 버전이 표시된 사본을 내려받으세요. 최신본은 항상 포털에 있습니다.',
        search: '제목 또는 코드 검색…',
        upload: '업로드',
        refresh: '새로고침',
        none: '공유된 문서가 아직 없습니다.',
        noMatch: '검색 결과가 없습니다.',
        unread: '읽지 않음',
        acknowledged: '읽음',
        code: '코드',
        docTitle: '제목',
        version: '버전',
        updated: '업데이트',
        status: '상태',
        changeNote: '변경 내용',
        uploadedBy: '올린 사람',
        author: '작성자',
        authorNone: '— 미지정 —',
        versionAuthor: '이 버전의 작성자', authorSame: '— 이전 버전과 같음 —', byAuthor: '작성자 {{name}}',
        authorHint: '문서를 쓴 사람. 올린 사람과 다릅니다.',
        newDoc: '새 문서',
        newVersion: '새 버전 업로드',
        addingVersionTo: '새 버전을 추가할 문서',
        findDocPh: '코드 또는 제목 입력 — HB-0001, handbook…',
        findDocNone: '"{{q}}"와 일치하는 문서가 없습니다.',
        currentlyAt: '현재 {{version}} — 새 버전이 추가되고 이전 버전은 아래 이력에서 계속 열람할 수 있습니다',
        kind: '종류',
        codeAuto: '포털이 자동 부여',
        summary: '간단한 설명',
        everyone: '문서함을 열 수 있는 모든 사람이 읽을 수 있음',
        narrowHint: '지정하지 않으면 공유하기 전까지 본인과 문서함 관리자만 읽을 수 있습니다.',
        file: '파일',
        fileKinds: 'HTML, PDF, PNG, JPEG, Word, PowerPoint, Excel(.xlsx) 모두 여기서 바로 읽습니다. 다운로드는 항상 원본 파일입니다.',
        noPreview: '이 형식은 브라우저가 화면에 표시할 수 없습니다. 버전과 함께 보관되며, 업로드한 이름 그대로 다운로드됩니다.',
        download: '다운로드',
        imgBroken: '이 이미지를 열 수 없습니다 — 파일이 손상되었거나 브라우저가 읽을 수 없는 형식입니다. 파일은 그대로 보관되어 있으며, 업로드한 이름으로 다운로드됩니다.',
        versionLabel: '버전',
        versionAuto: '비워두면 이전 버전에서 이어집니다',
        versionFound: '문서에서 읽은 버전: {{version}}',
        versionNotFound: '문서 표지에서 버전을 찾지 못했습니다 — 비워 두면 v1.0, 또는 직접 입력하세요.',
        changeNoteLabel: '변경 내용 (한 줄)',
        uploading: '업로드 중…',
        allDocuments: '전체 문서',
        history: '버전 이력',
        latest: '최신',
        view: '열기',
        readingOld: '이전 버전을 보고 있습니다 —',
        backToLatest: '최신 버전으로',
        iHaveRead: '읽었습니다',
        recording: '기록 중…',
        youAcknowledged: '{{version}} 읽음을 확인했습니다',
        noVersion: '아직 업로드된 버전이 없습니다.',
        readers: '읽은 사람',
        readersCount: '{{notified}}명 중 {{opened}}명 열람 · {{acked}}명 확인',
        notified: '통보',
        opened: '열람',
        confirmed: '확인',
        nobody: '아직 통보한 사람이 없습니다.',
        share: '공유',
        shareLink: '링크 복사',
        linkCopied: '링크 복사됨',
        shareByEmail: '메일로 보내기',
        emails: '메일 주소',
        emailsHint: '여러 개는 쉼표나 공백으로 구분하세요.',
        groups: '그룹',
        person: '수신자',
        groupsNone: '그룹 선택…',
        groupsClear: '선택 해제',
        addGroup: '그룹 추가',
        addGroupName: '그룹 이름',
        addGroupEmail: '그룹 메일 주소',
        addGroupSave: '목록에 추가',
        addGroupHint: '앞으로 문서를 공유하는 모든 사람에게 이 그룹이 보입니다.',
        groupAdded: '"{{name}}" 을(를) 목록에 넣었습니다.',
        groupRemoved: '"{{name}}" 을(를) 목록에서 뺐습니다.',
        removeGroupConfirm: '"{{name}}" 을(를) 모든 사람의 그룹 목록에서 빼시겠습니까?',
        sharers: '공유 권한',
        sharersIntro: '{{code}} 을(를) 올린 사람은 본인이므로 보내는 권한도 본인에게 있습니다. 여기에 지정한 사람도 보낼 수 있습니다.',
        sharersIntroAdmin: '{{code}} 은(는) {{owner}} 이(가) 올렸습니다. 관리자인 본인과 여기에 지정한 사람이 보낼 수 있습니다.',
        sharersNone: '지정된 사람이 없습니다. {{owner}} 와(과) 관리자만 이 문서를 보낼 수 있습니다.',
        sharersAdd: '사람 추가',
        sharersPick: '사람 선택…',
        sharersFind: '이름이나 메일 주소를 입력하세요…',
        sharersNoMatch: '해당하는 사람이 없습니다.',
        sharersAddN: '공유 권한 주기 ({{count}})',
        sharersSaved: '공유 권한을 저장했습니다.',
        sharersUploader: '올린 사람',
        cannotShare: '{{owner}} 와(과) 관리자만 이 문서를 보낼 수 있습니다.',
        sendNote: '메모 (선택 — 비우면 변경 내용이 들어갑니다)',
        send: '알림 보내기',
        sending: '보내는 중…',
        sentTo: '{{count}}명에게 보냈습니다.',
        remove: '삭제',
        edit: '수정',
        save: '저장',
        saving: '저장 중…',
        savedMsg: '저장했습니다.',
        noteOfVersion: '변경 메모는 현재 목록에 보이는 {{version}} 의 것입니다.',
        comments: '댓글',
        noComments: '아직 댓글이 없습니다. 첫 질문은 대개 다른 사람도 궁금해하는 질문입니다.',
        commentPlaceholder: '이 문서에 대해 묻거나 답해 주세요…',
        commentHint: '이 문서를 열 수 있는 모든 사람에게 보입니다. Ctrl+Enter 로 등록.',
        post: '등록',
        posting: '등록 중…',
        commentGone: '삭제된 댓글입니다.',
        deleteComment: '삭제',
        deleteCommentTitle: '이 댓글을 삭제할까요?',
        deleteCommentBody: '모든 사람에게서 사라집니다. 대화의 자리는 삭제됨으로 남습니다.',
        removeTitle: '이 문서를 내릴까요?',
        removeConfirm: '모든 사람의 목록에서 사라지고 링크도 열리지 않습니다. 읽음 기록은 남고, 이 코드는 다른 문서에 다시 쓰이지 않습니다.',
        removedMsg: '{{code}} 를 문서함에서 내렸습니다.',
        cancel: '취소',
        removed: '삭제했습니다.',
      },
      home: {
        morning: '좋은 아침입니다',
        afternoon: '좋은 오후입니다',
        evening: '좋은 저녁입니다',
        myWork: '내 업무 — {{role}}',
        notLinkedToEmployee: '이 포털 계정이 직원 기록과 연결되지 않아 담당 업무를 찾을 수 없습니다. 관리자에게 연결을 요청하세요.',
        notLinkedToSalesRep: '이 계정이 Sales Rep과 연결되지 않아 담당 프로젝트를 찾을 수 없습니다. 관리자에게 연결을 요청하세요.',
        targetLaunch: '목표 출시 월',
        ack: '확인',
        ackTitle: '이 반려 건을 확인했다고 표시합니다 — 이 카드에서 더 이상 보이지 않습니다',
        lateThreshold: '지연 기준 (일)',
        saved: '저장됨',
        lateThresholdRange: '1-365',
        save: '저장',
        ackUndo: '되돌리기',
        seenCount: '확인함 {{n}}건 — 보기',
        viewAs: '보기',
        allOf: '전체 — {{role}}',
        viewAll: '전체',
        repNoAccount: '계정 미연결',
        openThisList: '샘플 트래커에서 이 목록 열기',
        andMore: '+{{n}}건 더',
        workload: {
            title: '주간 업무량',
            sub: '매주 월요일 스냅샷 — 그 시점에 담당자별로 진행 중이던 건수',
            rounds: '진행 중 라운드',
            projects: '진행 중 프로젝트',
            pic: '담당자',
            change: '지난주 대비',
            total: '합계',
            reconstructed: '이 주차는 해당 월요일 아침에 집계한 값이 아니라 사후에 채운 값입니다.',
            cellRounds: '생산 중 {{prod}} · 결과 대기 {{pending}} · 자재 대기 {{wait}}',
            note: '진행 중 라운드 = In Production + Pending Outcome. * 표시된 주차는 그 월요일에 집계한 값이 아니라 사후에 채운 값입니다.',
        },
        noModules: '아직 계정에 허용된 모듈이 없습니다. 관리자에게 접근 권한을 요청해 주세요.',
      },
      tc: {
        colNo: '번호',
        colAction: '작업',
        stateActive: '사용',
        stateInactive: '미사용',
        title: '티켓 코드',
        subtitle: '티켓 코드 드롭다운의 기준 목록입니다. 총 {{n}}개.',
        readOnly: '— 현재 역할에서는 조회만 가능합니다.',
        newCode: '코드 추가',
        exportExcel: '엑셀 내보내기',
        searchPh: '코드, 이름, 단계, 팀 검색…',
        showInactive: '미사용 포함',
        clearTools: '정렬·필터 초기화',
        countOf: '{{total}}개 중 {{n}}개',
        loading: '불러오는 중…',
        saving: '저장 중…',
        btnEdit: '수정',
        btnView: '보기',
        btnViewHint: '이 코드 보기 (읽기 전용)',
        btnClose: '닫기',
        formView: '티켓 코드 {{code}}',
        viewOnly: '보기 전용',
        btnSave: '저장',
        btnCancel: '취소',
        formNew: '새 티켓 코드',
        formEdit: '{{code}} 수정',
        fCode: '코드',
        fTeam: '요청 팀',
        fCase: '케이스',
        fCategory: '카테고리',
        fName: '티켓명',
        fPhase: '단계',
        fAssignedTo: '담당',
        fGroupEmail: '그룹 메일',
        fNote: '메모',
        fActive: '사용 — 미사용으로 바꿔도 이미 발행된 티켓에는 그대로 남고, 새 티켓 화면에만 나타나지 않습니다',
        phCase: 'A / B / 공란',
        phName: '샘플 개발 요청',
        phTeamSearch: '팀 검색…',
        everyCategory: '전체 카테고리',
        created: '{{code}} 코드를 추가했습니다.',
        saved: '{{code}} 코드를 저장했습니다.',
        nothingToExport: '내보낼 항목이 없습니다',
        preparingExport: '엑셀 파일을 준비하는 중…',
        exported: '코드 {{n}}개를 내보냈습니다',
        exportFailed: '내보내기에 실패했습니다',
        exportInclInactive: '미사용 코드 포함',
        exportActiveOnly: '사용 중인 코드만',
      },
      pa: {
        title: '프로젝트 승인',
        subtitle: '새 프로젝트는 여기서 승인되기 전까지 Pending 상태입니다. 승인되지 않은 프로젝트에는 티켓을 발행할 수 없습니다.',
        tabPending: '승인 대기',
        tabApproved: '승인 완료',
        tabRejected: '반려',
        tabSettings: '설정',
        loading: '불러오는 중…',
        saving: '저장 중…',
        working: '처리 중…',
        countAll: '프로젝트 {{n}}건',
        countOf: '전체 {{total}}건 중 {{n}}건',
        chartPick: '차트: {{label}} · 프로젝트 {{n}}건',
        chartPickClear: '모든 프로젝트 다시 보기',
        allTeams: '전체 팀',
        scopeHint: '어느 팀의 프로젝트를 볼지 선택',
        clearTools: '정렬·필터 초기화',
        searchPh: '프로젝트, SKU, 팀, 담당자 검색…',
        rejectedChip: '반려 · SKU {{n}}개',
        rejectedChipHint: '승인자가 반려한 SKU {{n}}개 — 수정 후 Resubmit을 누르세요.',
        selected: '{{n}}건 선택됨',
        decideSelected: '선택 건 결정',
        clearSelection: '선택 해제',
        emptyFiltered: '열 필터에 맞는 프로젝트가 없습니다.',
        emptyPending: '승인 대기 중인 프로젝트가 없습니다.',
        emptyTab: '{{tab}} 프로젝트가 없습니다.',
        colNo: '번호',
        colProjectId: '프로젝트 그룹 ID',
        colProjectName: '프로젝트명',
        colTeam: '팀',
        colManager: '매니저',
        colOwner: '오너',
        colSalesRep: '영업 담당',
        colCategory: '카테고리',
        colSeason: '시즌',
        colPartner: '파트너 / 라인',
        colTargetLaunch: '목표 출시',
        colSkus: 'SKU 수',
        colRequestedBy: '요청자',
        colRequestedAt: '요청 일시',
        colManagerNote: '매니저 메모',
        colApprovedBy: '승인자',
        colApprovedAt: '승인 일시',
        colApprovedOpinion: '승인 의견',
        colRejectedBy: '반려자',
        colRejectedAt: '반려 일시',
        colReason: '사유',
        colAction: '작업',
        btnView: '보기',
        btnApprove: '승인',
        btnEdit: '수정',
        btnResubmit: '재요청',
        btnCancel: '취소',
        btnClose: '닫기',
        btnSaveChanges: '변경 사항 저장',
        btnApproveN: '{{n}}건 승인',
        btnRejectN: '{{n}}건 반려',
        notYourTeam: '다른 팀의 프로젝트입니다 — 해당 프로젝트의 오너, 그 팀의 매니저, 또는 관리자만 변경할 수 있습니다.',
        dlgResubmitTitle: '{{id}} 재요청',
        dlgDecisionOne: '{{id}} 결정',
        dlgDecisionBulk: '프로젝트 {{n}}건 결정',
        dlgStatus: '승인 상태',
        dlgAgree: '승인',
        dlgReject: '반려',
        skuToggle: '이 프로젝트의 SKU {{n}}개 보기',
        skuNone: '아직 SKU가 없습니다.',
        skuId: 'SKU',
        skuItem: '아이템',
        skuCategory: '카테고리',
        skuQty: '수량',
        skuPrice: '목표가',
        skuExf: 'EXF',
        skuPic: '개발자 / 디자이너',
        skuStatus: '단계',
        skuTba: '미정',
        skuState: '승인',
        skuNote: '승인자 메모',
        skuSt_unsent: '미제출',
        skuSt_waiting: '대기',
        skuSt_approved: '승인',
        skuSt_rejected: '미승인',
        skuPicked: 'SKU {{n}}개 선택됨',
        skuWithApprover: '이미 제출됨 — 승인자가 결정할 항목입니다.',
        skuNotYours: '미제출 — 요청 팀이 보낼 항목입니다.',
        skuDoneAlready: '이미 결정되었습니다.',
        skuNoneYours: '여기에는 처리할 항목이 없습니다.',
        submitPickFirst: '보낼 SKU를 선택하세요. 프로젝트를 선택하면 전체가 선택됩니다.',
        skuNotRefused: '반려된 항목이 아니므로 재제출할 것이 없습니다.',
        resubmitSome: '재제출 {{n}}건',
        dlgBackHead: 'SKU별 변경 내용 ({{n}}건 재제출)',
        dlgBackWas: '미승인',
        dlgBackWhy: '이 SKU에서 무엇이 바뀌었는지',
        skuMgrNote: '매니저 메모',
        submitSome: '{{n}}건 제출',
        submitSomeHint: '아래에서 선택한 SKU {{n}}건만 보냅니다. 나머지는 그대로 둡니다.',
        approveSome: '{{n}}건 결정',
        approveSomeHint: '아래에서 선택한 SKU {{n}}건만 결정합니다.',
        dlgSkuHead: 'SKU별로 결정하세요 (대기 {{n}}건)',
        dlgAllApprove: '전체 승인',
        dlgAllReject: '전체 반려',
        dlgSkuWhy: '이 SKU를 승인하지 않는 이유',
        dlgSkuDefault: '✕ 표시를 하지 않으면 모두 승인됩니다.',
        dlgSkuYes: '승인',
        dlgSkuNo: '미승인',
        dlgSkuUndo: '이 SKU를 다시 승인으로',
        dlgWasApproved: '이전에 승인됨',
        dlgWasRejected: '이전에 반려됨',
        btnApproveSkus: 'SKU {{n}}건 승인',
        btnRejectSkus: 'SKU {{n}}건 반려',
        btnPartial: '{{yes}}건 승인, {{no}}건 반려',
        stPartial: '부분 승인',
        dlgUnchangedTitle: '반려 이후 수정된 내용이 없습니다.',
        dlgUnchangedBody: '승인자가 요청한 부분을 수정하거나(행의 Edit), 수정 없이 승인되어야 하는 이유를 아래에 적어 주세요 — 그대로 재요청하려면 메모가 필요합니다.',
        dlgManagerOpinion: '매니저 의견',
        dlgApproverOpinion: '승인자 의견',
        dlgPhReject: '왜 승인할 수 없는지 적어 주세요. 담당 팀이 이 내용을 봅니다.',
        dlgPhResubmitRequired: '필수 — 수정 없이 승인되어야 하는 이유는 무엇입니까?',
        dlgPhResubmit: '반려 이후 무엇이 바뀌었나요? (선택)',
        dlgPhOptional: '내용 입력 (선택)',
        secProject: '프로젝트 정보',
        secSku: 'SKU 정보',
        fTeam: '팀',
        fOwner: '프로젝트 오너 (Sales OP)',
        fCategory: '카테고리',
        fSeason: '시즌',
        fPartner: '파트너 / 라인',
        fKeyword: '키워드',
        fTargetLaunch: '목표 출시 (월)',
        fCase: '케이스',
        fEntity: '법인',
        selectTeam: '팀 선택…',
        selectOwner: '오너 선택…',
        pickTeamFirst: '먼저 팀을 선택하세요',
        noMember: '이 팀에 구성원이 없습니다 — 사용자 목록에서 지정하세요',
        addSku: '+ SKU 추가',
        noSkuYet: '아직 SKU가 없습니다 — 괜찮습니다. SKU는 언제든 추가할 수 있습니다.',
        skuSaveHint: '새 SKU는 저장할 때 생성됩니다. 기존 SKU 삭제는 저장과 무관하게 즉시 적용됩니다.',
        fillRequired: '필수 항목을 먼저 입력하세요:',
        couldNotSave: '저장할 수 없습니다:',
        rejectedBy: '{{who}} 반려:',
        managerNoteLabel: '매니저 메모:',
        submit: '제출',
        submitSelected: '선택 항목 승인 요청',
        readyToSubmit: '{{total}}건 중 {{n}}건 제출 가능',
        nothingToSubmit: '제출할 수 있는 항목이 없습니다 — 이미 보냈거나 다른 팀입니다.',
        // A STATE, not a button — the request has already gone (Jay, 2026-09-16: 발송 --> 발송됨).
        sent: '발송됨',
        submittedOn: '{{when}} 승인 요청 발송',
        remind: '리마인드',
        remindHint: '{{when}} 발송 · {{n}}개 SKU가 아직 승인 대기 중입니다. 리마인드를 보냅니다.',
        remindSent: '{{to}}에게 리마인드를 보냈습니다 — {{n}}개 SKU 대기 중',
        lockedSubmitted: '이미 승인 요청이 발송된 프로젝트입니다 — 승인자가 받은 메일에는 발송 당시의 값이 그대로 남아 있습니다.',
        drop: '삭제',
        dropTitle: '이 프로젝트 삭제',
        dropSubmitted: '이미 승인 요청을 보냈으므로 삭제할 수 없습니다.',
        dropHead: '{{id}} 을(를) 삭제할까요?',
        dropChecking: '이 프로젝트에 속한 항목을 확인하는 중…',
        dropWarnOne: 'SKU 1건이 삭제됩니다. 샘플 라운드는 남습니다 — 프로젝트 없이 Sample Tracker에 그대로 있습니다. 되돌릴 수 없습니다.',
        dropWarnMany: 'SKU {{n}}건이 삭제됩니다. 샘플 라운드는 남습니다 — 프로젝트 없이 Sample Tracker에 그대로 있습니다. 되돌릴 수 없습니다.',
        dropTicketsOne: '이 프로젝트의 티켓 1건이 취소되며, 수신 팀에 통보됩니다:',
        dropTicketsMany: '이 프로젝트의 티켓 {{n}}건이 취소되며, 수신 팀에 통보됩니다:',
        dropEmailed: '메일 발송됨',
        dropReason: '취소 사유는 무엇입니까? (메일에 포함됩니다)',
        dropConfirm: '프로젝트 삭제',
        dropBusy: '삭제 중…',
        approverNoteLabel: '승인자 메모:',
        setTitle: '승인 메일',
        setNote: '“프로젝트 승인 요청” 메일이 가는 주소입니다. 여기서 바꾸면 즉시 반영되며 재배포가 필요 없습니다.',
        setApprover: '승인자 (수신)',
        setCc: '참조 (쉼표로 구분)',
        setGate: '승인 게이트 사용 (새 프로젝트는 Pending으로 시작하고 티켓이 차단됩니다)',
        setSave: '승인 설정 저장',
        setSaved: '승인 설정을 저장했습니다.',
      },
      tour: {
        title: '화면 안내',
        ciTabsT: '템플릿별 탭',
        ciTabsB: 'Template 1은 Weekly Cash Report, Template 2는 13 Weeks Cash Plan, Template 3은 Month-End Reconcile입니다. 제출할 템플릿을 선택하세요.',
        ciPeriodT: '법인과 기간',
        ciPeriodB: '법인은 현금 보고 User 목록의 Preparer 지정에 따라 자동으로 정해지며, 두 개 이상을 담당할 때만 선택 목록이 나타납니다. 화살표로 이전·다음 주(또는 월)로 이동하고, 옆의 문구에 보고 기한이 표시됩니다.',
        ciStepsT: '보고서 진행 상황',
        ciStepsB: 'Preparer → Reviewer 순으로 진행됩니다. 완료된 단계는 표시가 켜지며, Reviewer가 동의하면 보고서 수치가 현금 보고서에 표시됩니다.',
        ciTemplateT: '템플릿 다운로드',
        ciTemplateB: '이 법인과 기간의 Excel 템플릿으로, 계좌가 미리 입력되어 있습니다. 작성한 뒤 가져오세요. 포털에서 금액을 직접 입력할 수는 없습니다.',
        ciImportT: '파일 가져오기',
        ciImportB: '작성한 템플릿을 가져오면 하나의 버전이 되고, Data Check가 실행되며 금액이 아래에 표시됩니다. 잘못된 파일은 취소하고 수정한 파일을 다시 가져올 수 있습니다.', ciFormT: '여기서 수정', ciFormB: '양식을 가져오면 그 수치가 여기에 열립니다. 수치를 고치고 저장하면 같은 Data Check를 거쳐 다음 버전이 됩니다 — 파일을 다시 가져올 필요가 없습니다.',
        ciCheckT: 'Data Check',
        ciCheckB: '모든 버전은 자동으로 점검됩니다. 오류는 발송 전에 반드시 수정해야 하며, 경고는 Reviewer가 볼 수 있도록 함께 전달됩니다.',
        ciSendT: 'Reviewer에게 발송',
        ciSendB: 'Reviewer에게 전할 내용이 있으면 메모를 남기고 발송하세요. 발송된 보고서는 수정할 수 없으며, 반려되면 이곳에서 수정해 다시 발송합니다.',
        ciFilesT: '은행 거래내역서와 스크린샷',
        ciFilesB: '계좌별 거래내역서나 잔액 스크린샷을 첨부하세요. 보고서를 검토하거나 승인하는 모든 사람이 볼 수 있습니다.',
        ciHistoryT: '이력과 코멘트',
        ciHistoryB: '가져오기, 입력, 발송, 결정이 모두 누가 언제 했는지와 함께 기록됩니다. 이 보고서에 대한 질문이나 답변은 코멘트로 남기세요.',
        ciTestAsT: 'Test as',
        ciTestAsB: '테스트 전용: 현금 보고 메일이 보류된 동안 각 역할로 전체 흐름을 시험해 볼 수 있습니다. 진행한 모든 단계는 "test as …"로 기록됩니다.',
        cqTabsT: '세 개의 탭',
        cqTabsB: '내 결정 대기에는 내 검토를 기다리는 보고서가 오래된 순으로 있습니다. 진행 중에는 다른 Reviewer에게 있는 보고서와 사유와 함께 Preparer에게 반려된 보고서가, 동의 완료에는 Reviewer가 동의한 보고서(현금 보고서에 표시되는 보고서)가 있습니다.',
        cqListT: '발송된 모든 보고서',
        cqListB: '주간 현금 보고, 13주 현금 계획, 월말 대사를 모든 법인과 기간에 걸쳐 한 목록으로 보여 줍니다. 각 머리글은 Excel처럼 정렬·필터할 수 있습니다. 행을 누르면 아래에 보고서가 열리고, 다시 누르면 닫힙니다.',
        cqDecideT: '나의 결정',
        cqDecideB: 'Review & agree가 마지막 단계입니다: 동의하면 보고서 수치가 현금 보고서에 표시됩니다. 동의한 보고서도 사유와 함께 여기서 다시 열어 수정할 수 있습니다. 누르면 동의하거나 사유와 함께 Preparer에게 반려하는 창이 열립니다. 반려된 보고서는 다시 발송될 때까지 진행 중에 남습니다.',
        cqCheckT: 'Data Check',
        cqCheckB: '오류가 있으면 Preparer가 발송할 수 없으므로 발송된 보고서에는 오류가 없습니다. 경고는 결정 전에 읽어 볼 수 있도록 함께 전달됩니다.',
        cqBodyT: '가져온 내용',
        cqBodyB: 'Preparer가 템플릿에서 가져온 그대로의 금액입니다 — 주간 보고의 Field 1~6, 계획의 13주, 또는 계좌별 대사. 여기서는 수정할 수 없으며, 잘못된 금액은 반려합니다.',
        cqFilesT: '은행 거래내역서와 스크린샷',
        cqFilesB: 'Preparer가 계좌별로 첨부한 파일입니다. 열어서 은행 자료와 금액을 대조하세요.',
        cqHistoryT: '이력과 코멘트',
        cqHistoryB: '누가 언제 가져오고, 발송하고, 결정했는지 기록됩니다. 반려하지 않고 Preparer에게 질문하려면 코멘트를 남기세요.',
        crNewT: '요청 시작',
        crNewB: '물품 수령 대상, 배송사와 청구되는 Helinox 계정, 출발지와 도착지, 중량, 예상 운임, 무역 조건을 입력합니다. 옆의 비용 확인이 입력하는 대로 예상 운임을 HINC의 FedEx 계약 요율과 비교합니다. 임시 저장은 작성 중인 양식을 보관하고, 승인 요청은 승인자에게 보냅니다.',
        crListT: '내 요청',
        crListB: '요청 하나가 한 줄입니다. 앞의 다섯 열은 고정되고 나머지는 옆으로 스크롤됩니다. 줄을 누르면 보낸 양식 그대로 요청을 볼 수 있습니다.',
        crCheckT: '비용 확인',
        crCheckB: '예상 운임과 계약 요율 비교: 적정(15% 이내), 검토(35% 이내), 높을 수 있음. 계약에 없는 노선이나 배송사는 수동으로 확인합니다.',
        crStatusT: '진행 상태',
        crStatusB: '초안(미발송), 대기(승인자 검토 중), 승인, 반려, 철회.',
        crNoteT: '매니저 메모',
        crNoteB: '승인자가 결정과 함께 남긴 메모입니다. 누르면 전체를 볼 수 있습니다.',
        crActT: '할 수 있는 작업',
        crActB: '보기 · 수정(초안, 반려 또는 철회된 요청, 대기 중인 요청 — 저장하면 승인자에게 변경 내용을 알립니다) · 초안 제출 · 새 요청으로 복제 · 승인되지 않은 요청 삭제. 회색 버튼은 마우스를 올리면 이유가 나옵니다.',
        crqTabsT: '세 개의 탭',
        crqTabsB: '결정 대기가 처리할 목록이고, 승인과 반려에는 결정된 요청이 남습니다.',
        crqListT: '요청 목록',
        crqListB: '요청자, 법인, 경로, 배송사. 앞의 다섯 열은 고정됩니다. 줄을 누르면 비용 확인과 함께 요청을 볼 수 있습니다.',
        crqNoteT: '매니저 메모',
        crqNoteB: '결정과 함께 남긴 메모이며, 요청자 목록에도 보입니다.',
        crqActT: '보기 · 수정 · 승인',
        crqActB: '수정은 대기 중인 요청을 고칩니다 — 계속 대기 상태이며 요청자에게 변경 내용이 메일로 갑니다. 승인을 누르면 결정 창이 열리고, 반려도 그 안에 있으며 사유가 필요합니다.',
        crqRatesT: '요율',
        crqRatesB: '비용 확인이 읽는 FedEx 계약 요율 파일, 업로드, 유류할증료와 두 기준값.',
        crTabsT: '세 가지 보고서',
        crTabsB: '주간 현금 보고, 13주 현금 계획, 월말 대사. Reviewer가 동의한 수치만 표시되며, Preparer나 Reviewer에게 있는 보고서는 어디에 있는지만 한 줄로 표시됩니다.',
        crPeriodT: '기간',
        crPeriodB: '주(월말 탭에서는 월)를 고르세요. 화살표로 이전·다음으로 이동하며, 옆 줄에 보고 기한이 표시됩니다.',
        crChartsT: '차트',
        crChartsWB: '법인별 현금(가용·사용 제한·정기예금)과 최근 12주 GROUP 현금입니다. 막대나 점에 마우스를 올리면 수치가 보입니다.',
        crChartsPB: '계획 입금은 위로, 계획 출금은 아래로 표시됩니다. 점선은 계획 기말, 실선은 주간 현금 보고의 실제 기말입니다.',
        crChartsMB: '법인별 월말 현금과, 대사가 맞는 계좌 수와 차이가 있는 계좌 수입니다.',
        crWeekT: 'GROUP, 그다음 각 법인',
        crWeekB: '첫 행은 GROUP이고 각 법인이 SGD로 이어집니다. 법인 행을 클릭하면 관계사 행과 계좌가 열리며, 이때 은행·계좌번호·계좌 유형·사용 제한/정기예금 열이 나타나고 각 계좌는 업로드한 그대로 계좌 통화로 표시됩니다.',
        crExpandT: '모든 법인 열기·접기',
        crExpandB: '모두 펼치기는 여기서 모든 법인의 계좌와 아래 Top 3를 열고, 모두 접기는 다시 접습니다.',
        crTopT: 'Top 3',
        crTopB: '각 법인의 이번 주 주요 지출 3건과 다음 주 지급 예정 3건(SGD)입니다. 업로드하지 않은 법인도 표시되어 누락을 바로 알 수 있습니다.',
        crFiltersT: '두 가지 필터',
        crFiltersB: '법인: GROUP, 법인 하나, 또는 여러 법인(합계). 연도: 표시할 연도. 아래 차트와 표가 둘 다 따라갑니다.',
        crPlanTableT: '연간 전체 주',
        crPlanTableB: '주마다 한 행입니다. 지난 주는 그 주에 제출된 계획을, 다가올 주는 최신 계획을 읽으며 From plan 열에 그 계획이 표시됩니다. 실제 수치는 주간 현금 보고가 동의된 뒤 들어옵니다. 화면의 주가 강조되며 모든 헤더는 Excel처럼 정렬·필터됩니다.',
        crPlanListT: '이번 주 계획 현황',
        crPlanListB: '화면에 있는 주에 대한 각 법인의 계획: 13주차 계획 기말과 계획의 진행 상태입니다.',
        crMonthT: '대사',
        crMonthB: '각 법인 행에는 월말 잔액(SGD)과 작성·검토한 사람이 있고, 그 아래에 계좌별 금액(계좌 통화)이 이어집니다. 모든 계좌의 Reconcile은 0이어야 합니다.',
        crWaitingT: '내 결정 대기',
        crWaitingB: '다음 단계가 내 차례인 보고서에는 이 표시가 붙습니다. 누르면 승인 화면에서 열려 동의하거나 반려할 수 있습니다.',
        step: '{{of}}단계 중 {{n}}',
        next: '다음',
        back: '이전',
        done: '완료',
        skip: '건너뛰기',
        stCreateT: '새 샘플은 여기서 시작합니다',
        stCreateB: '샘플이 속할 프로젝트 SKU를 고르세요. SKU 하나에 샘플은 하나이므로 아직 샘플이 없는 SKU만 나옵니다. 이제 Project Tracker로 갈 필요가 없습니다.',
        stRoundT: '이미 있는 샘플에 라운드 추가',
        stRoundB: 'P1, P2, P3 … 먼저 Sample ID, Base ID 또는 스타일명으로 샘플을 찾으세요. 샘플을 고르면 나머지 항목이 열립니다.',
        stTrialT: '프로젝트가 없는 샘플',
        stTrialB: '아직 프로젝트 SKU가 되지 않은 작업을 위한 예외 경로입니다. SMP 대신 TSMP 번호를 받습니다.',
        stCardsT: '카드가 곧 필터입니다',
        stCardsB: '카드를 누르면 아래 표가 해당 항목만 보여 줍니다. 다시 누르면 해제됩니다. 윗줄은 샘플 수, 아랫줄은 라운드 수입니다.',
        stFiltersT: '더 좁히기',
        stFiltersB: '개발자, 디자이너, R&D 위치로 거르거나 검색창에 입력하세요. Clear를 누르면 열 머리글의 깔때기까지 모두 초기화됩니다.',
        stRowT: '샘플을 펼치면 라운드가 보입니다',
        stRowB: '행의 화살표를 누르면 그 Base ID의 모든 라운드가 나옵니다. Expand All은 한 번에 펼칩니다.',
        stExportT: '엑셀로 내보내기',
        stExportB: '지금 표에 보이는 그대로, 필터까지 반영해 내보냅니다.',
      },
      st: {
        tabAll: '전체 샘플',
        tabSummary: '샘플별 요약',
        tagTrial: '시험',
        pillProject: '프로젝트 샘플 (SMP)',
        pillTrial: '시험 샘플 (TSMP)',
        pillBoth: '둘 다',
        pillCtrlHint: 'Ctrl / \u2318 + 클릭하면 다른 유형과 함께 표시됩니다',
        hintTrial: '프로젝트 SKU가 없는 일회성 샘플입니다. Base ID는 SMP가 아니라 TSMP입니다.',
        titleTracker: '샘플 트래커',
        titleSummary: '샘플별 요약',
        hintTracker: '카드를 클릭하면 표가 갱신됩니다 - 샘플 행을 클릭하면 라운드가 펼쳐집니다',
        hintSummary: 'Base ID / 제품별 1행, 최신 일정과 라운드 상태',
        groupSample: '샘플 / Base ID',
        groupRounds: '라운드 / 워크플로',
        totalSamples: '전체 샘플',
        totalSamplesSub: '모든 상태의 샘플 기록',
        inProgressSamples: '진행 중 샘플',
        confirmedSamples: '확정 샘플',
        reworkSamples: '재작업 샘플',
        droppedSamples: '드롭된 샘플',
        onHoldSamples: '보류 샘플',
        totalRounds: '전체 라운드',
        totalRoundsSub: '모든 상태의 라운드 기록',
        inProgressRounds: '진행 중 라운드',
        confirmedRounds: '확정 라운드',
        reworkRounds: '재작업 라운드',
        droppedRounds: '드롭된 샘플 라운드',
        onHoldRounds: '보류 라운드',
        pendingOutcome: '결과 대기',
        pendingOutcomeSub: '완료일 있음, 결과 없음',
        rmWaiting: '원자재 대기',
        rmWaitingSub: '원자재 미준비',
        inProduction: '생산 중',
        inProductionSub: '원자재 준비, 미완료',
        allRounds: '전체 라운드',
        allDevelopers: '전체 개발자',
        allDesigners: '전체 디자이너',
        allLocations: '전체 R&D 위치',
        allStatus: '전체 상태',
        searchPlaceholder: 'ID 또는 품목 검색...',
        clear: '초기화',
        newRound: '+ 라운드 추가',
        csButton: '+ 샘플 생성',
        tourButton: '안내',
        tourReopen: '화면 사용법 보기',
        csTitle: '샘플 생성',
        csIntro: '이 샘플이 속할 프로젝트 SKU를 고르세요. 샘플이 아직 없는 SKU만 나옵니다 — SKU 하나에 샘플은 하나입니다.',
        csSearch: '프로젝트, SKU 코드, 스타일 검색…',
        csCount: '프로젝트 {{projects}}개 · SKU {{skus}}개',
        csNone: '진행 중인 SKU는 모두 샘플이 있습니다.',
        csNoMatch: '검색 결과가 없습니다.',
        csMore: '외 {{n}}개 — 검색어를 좁혀 주세요',
        trButton: '+ 시험 샘플 (프로젝트 없음)',
        trTitleNew: '신규 시험 샘플',
        trAddNewSample: '신규 샘플 추가',
        trTitle: '시험 샘플 — 프로젝트 없음',
        trSub: '프로젝트 SKU가 없는 일회성 샘플용입니다. Base ID는 SMP가 아니라 TSMP로 발급됩니다.',
        trStylePh: '스타일 / 아이템명을 입력하세요',
        trStyleHint: '위에서 입력 — 시험 샘플은 가져올 프로젝트 SKU가 없습니다',
        expandAll: '모두 펼치기',
        collapseAll: '모두 접기',
        exportExcel: '엑셀 내보내기',
        backToBase: 'Base ID 보기로',
        colBaseId: 'Base ID',
        colStyleItem: '스타일 / 품목',
        colRound: '라운드',
        colSeason: '시즌',
        colLocation: 'R&D 위치',
        colDeveloper: '개발자',
        colDesigner: '디자이너',
        colProjectId: '프로젝트 SKU ID',
        colRequestDate: '요청일',
        colDevelopmentDue: '개발 기한',
        colRmReady: '원자재 준비',
        colRoundDue: '라운드 기한',
        colSampleFinish: '샘플 완료일',
        colReceived: '입고일',
        colComment: '코멘트일',
        colQty: '수량',
        colRounds: '라운드 수',
        colStatus: '상태',
        colDday: 'D-day',
        colDdayHint: 'D-DAY = 오늘 − 개발 기한(Development Due). 양수는 기한을 그만큼 초과했다는 뜻이고, 음수는 그만큼 남았다는 뜻입니다.',
        colDaysWaiting: '대기 일수',
        goToProject: 'Project Tracker에서 이 SKU 열기',
        colRemarks: '비고',
        colActions: '작업',
        colRework: '재작업',
        ptNo: '번호',
        ptId: 'ID',
        ptProjectItem: '프로젝트 / 품목',
        ptNotYours: '프로젝트 오너, 팀 매니저, 관리자만 이 프로젝트를 변경할 수 있습니다.',
        ptNotYourSku: '이 SKU의 담당 개발자, 프로젝트 오너, 팀 매니저만 변경할 수 있습니다.',
        ptDeleteNotYours: 'SKU 삭제는 프로젝트 오너, 팀 매니저, 관리자만 가능합니다.',
        ptCompletedLocked: '이 SKU는 Complete 상태이며 생산팀에 통보되었습니다 — 더 이상 수정하거나 삭제할 수 없습니다.',
        ptTicketNotYours: '이 프로젝트의 티켓 발행은 프로젝트 오너, 팀 매니저, 관리자만 가능합니다.',
        ptTeam: '팀',
        ptOwner: '프로젝트 오너',
        ptCategory: '카테고리',
        ptCase: '케이스',
        ptEntity: '법인',
        ptItemCategory: '품목 분류',
        ptFrameColor: '프레임 컬러',
        ptQty: '수량',
        ptAssigned: '담당자',
        ptAllocation: '배정',
        ptStatus: '상태',
        ptSalesRep: 'Sales Rep',
        ptWhere: '프로젝트 상태',
        ptWhereComplete: '완료',
        ptWhereDropped: '드롭',
        ptWhereActive: '진행',
        ptAiHead: 'Claude 요약',
        ptAiWriting: '요약 중…',
        ptStart: '시작일',
        ptExfDate: 'EXF 일자',
        ptTargetPrice: '목표 가격 (USD)',
        ptLaunchDate: '출시일',
        ptWeeksLeft: '남은 주',
        ptProgress: '진행률',
        ptRemark: '비고',
        ptSampleId: '샘플 ID',
        ptAction: '작업',
        ptTitle: '프로젝트 트래커',
        ptHint: '모든 개발 프로젝트 관리 및 추적',
        ptGroups: '프로젝트 그룹',
        ptProjectComplete: '완료 프로젝트',
        ptProjectCompleteSub: '모든 SKU가 Complete (또는 Dropped)',
        ptProjectDropped: '드롭 프로젝트',
        ptProjectDroppedSub: '모든 SKU가 Dropped',
        ptProjectActive: '진행 프로젝트',
        ptProjectActiveSub: '미완료 SKU가 남음',
        ptSkus: '프로젝트 SKU',
        ptComplete: 'SKU 완료',
        ptDrop: '중단',
        ptActive: 'SKU 진행',
        ptPriority: 'SKU 우선순위',
        ptViewProject: '프로젝트 보기 (읽기 전용)',
        ptViewSku: 'SKU 보기 (읽기 전용)',
        ptViewTag: '보기 전용',
        rmTitleView: '샘플 라운드',
        viewRound: '라운드 보기 (읽기 전용)',
        viewBase: 'Base ID 보기 (읽기 전용)',
        tkView: '티켓 보기 (읽기 전용)',
        tkTitleView: '티켓',
        viewClose: '닫기',
        ptViewHint: '읽기 전용 — 이 화면에서는 변경하거나 저장할 수 없습니다.',
        ptViewClose: '닫기',
        ptOnTrack: 'SKU 정상 진행',
        ptConcern: '주의',
        ptDelayed: '지연',
        ptOnHold: '보류',
        ptShipFinished: '선적 완료',
        ptAllTeams: '전체 팀',
        ptAllOwners: '전체 오너',
        ptByMember: '담당자별',
        ptByMemberTitle: '프로젝트 오너별 업무량',
        ptByMemberHint: '행을 클릭하면 해당 오너로 트래커가 필터링됩니다. 수치는 위 필터를 따릅니다.',
        ptOverdueExf: 'EXF 지연',
        ptAllCategories: '전체 카테고리',
        ptAllAssignees: '전체 담당자',
        ptAllAllocation: '전체 배정',
        ptSearch: '프로젝트 / 품목 검색...',
        ptExpandAll: '모두 펼치기',
        ptCollapseAll: '모두 접기',
        ptColumns: '열 선택',
        ptReport: '리포트',
        ptNewProject: '+ 프로젝트 추가',
        ptExpand: '펼치기',
        ptCollapse: '접기',
        ptNoProjects: '프로젝트가 없습니다.',
        // 공정 단계 열 제목 — 프로젝트 그리드, 열 선택, 프로젝트 폼의 단계 입력에서 공용으로 사용
        stgLdSoff: '랩딥 승인',
        stgProto: '프로토',
        stgCounter: '카운터',
        stgCosting: '원가 산출',
        stgHandOffMeeting: '핸드오프 미팅',
        stgPpsPpm: 'PPS/PPM',
        stgProduction: '생산',
        stgFinalInspection: '최종 검사',
        stgExfShipment: '선적 상태',
        stgPayment: '결제',
        stgPoStatus: 'PO 상태',
        // 프로젝트 신규 / 편집 폼
        pfNewProject: '신규 프로젝트',
        pfProjectInfo: '프로젝트 정보',
        pfProjectInfoHint: '기본 정보, 일정, 상태, 단계, 연결된 샘플',
        pfCategory: '카테고리',
        pfSelectCategory: '카테고리 선택...',
        dgpTitle: '프로젝트 {{id}} 을(를) 삭제할까요?',
        dgpChecking: '이 프로젝트에 딸린 항목을 확인하는 중…',
        dgpSkus: 'SKU {{n}}개가 삭제됩니다.',
        dgpRounds: '샘플 라운드는 삭제되지 않습니다 — 프로젝트 연결만 끊긴 채 Sample Tracker에 남습니다.',
        dgpTickets: '티켓 {{n}}건이 취소되고, 받은 팀에 안내 메일이 갑니다.',
        dgpUndo: '포털에서는 되돌릴 수 없습니다.',
        dgpConfirm: '프로젝트 삭제',
        dt1Title: '티켓 {{id}} 을(를) 삭제할까요?',
        dt1Emailed: '이 티켓은 이미 {{to}} 로 메일 발송되었습니다. 삭제하면 요청이 취소되고, 아래 이유와 함께 안내 메일이 갑니다.',
        dt1NotEmailed: '이 티켓은 메일로 발송된 적이 없어 포털 밖에서는 아무도 모릅니다. 삭제해도 알림은 가지 않습니다.',
        dt1ReasonHint: '받는 팀이 요청이 사라진 이유를 아는 유일한 문장입니다.',
        dt1Confirm: '티켓 삭제',
        dt1ConfirmMail: '삭제하고 알림 보내기',
        dtTitle: '{{id}} 삭제 — 티켓이 있습니다',
        dtLead: '이 프로젝트에 티켓 {{n}}건이 있습니다. 삭제하면 티켓이 취소되고, 받은 팀에 안내 메일이 갑니다:',
        dtNeverEmailed: '메일 발송 안 됨',
        dtReason: '삭제 이유',
        dtReasonPh: '예: 파트너가 협업을 취소함',
        dtReasonHint: '이 문장이 취소 안내 메일에 들어갑니다 — 받는 팀이 이유를 아는 유일한 문장입니다.',
        dtConfirm: '삭제하고 티켓 취소',
        gmSkuOnlyHint: '이 팀은 SKU 영역만 수정할 수 있습니다 — 위쪽 프로젝트 정보는 Sales OP 소관입니다.',
        pfReadOnlyHere: '여기서는 조회만 가능합니다 — 수정은 프로젝트 그룹 수정에서 하세요',
        bmHint: '라운드가 아니라 Base ID 자체에 속한 것들입니다: 사진, 그리고 모든 라운드가 공유하는 스타일명 (현재 {{rounds}}개 라운드).',
        bmStyleHint: '이름 변경은 이 Base ID 의 모든 라운드에 한 번에 적용됩니다.',
        bmRename: '스타일명 변경',
        rmBasePhotos: '샘플 사진 (Base ID)',
        rmPhotoRequired: '필수',
        rmPhotoNewBase: '새 스타일이므로 저장하면 새 Base ID가 만들어집니다 — 샘플 사진이 최소 1장 필요합니다. 사진은 Base ID에 속하므로 이후 라운드에서는 다시 묻지 않습니다.',
        rmPhotoExistingBase: '이 스타일은 이미 {{baseId}} 로 존재하며 사진도 함께 있습니다. 여기서 추가할 것은 없습니다.',
        rmPhotoNoStyle: '먼저 Style / Item 을 입력하세요. 새 스타일은 새 Base ID 를 만들고, 사진이 필요합니다.',
        pfPurpose: '프로젝트 목적',
        pfPurposePh: '왜 하는지, 그리고 어디에 판매할 것인지.',
        pfPurposeHint: 'Sales OP 매니저가 세일즈 담당자와 확인해 주세요. 대표가 이 한 줄을 읽고 진행 여부를 결정합니다.',
        pfNote: '메모',
        pfTeam: '팀',
        pfOwner: '프로젝트 오너 (Sales OP)',
        pfHandover: '이 프로젝트를 {{who}}님께 넘깁니다. 저장하면 더 이상 수정할 수 없으며, {{who}}님·{{mgr}}·관리자만 수정할 수 있습니다.',
        pfHandoverMgr: '팀 매니저',
        pfYear: '연도',
        pfYearPick: '연도 선택…',
        pfSeasonParts: '시즌',
        pfSeasonMax2: '선택 사항 — 최대 2개',
        pfSeasonCode: '프로젝트 코드: {{code}}',
        seasonSpring: '봄 (Spring)',
        seasonSummer: '여름 (Summer)',
        seasonFall: '가을 (Fall)',
        seasonWinter: '겨울 (Winter)',
        pfSeason: '시즌',
        pfPartner: '파트너 / 라인',
        pfKeyword: '키워드',
        pfTargetLaunch: '목표 출시 (월)',
        pfNameHint: '시스템이 자동으로 만듭니다. PRJ-ID가 고정 키이며, 사람들이 읽는 것은 이 이름입니다.',
        pfSelectTeam: '팀 선택…',
        pfSelectOwner: '오너 선택…',
        pfPickTeamFirst: '먼저 팀을 선택하세요',
        gmTitle: '프로젝트 그룹 수정',
        gmSaveGroup: '그룹 저장',
        gmSaveTickets: '티켓 저장',
        pfProjectName: '프로젝트명',
        pfProjectNamePh: '예: 25 PARTICULIER',
        pfCase: '케이스',
        pfSelectCase: '케이스 선택...',
        pfEntity: '법인',
        pfSelectEntity: '법인 선택...',
        pfSkuInfo: 'SKU 정보',
        pfAddSku: '+ SKU 추가',
        mvBtn: '다른 프로젝트에서 SKU 이동',
        mvBtnTitle: '같은 팀의 다른 프로젝트에서 승인된 SKU를 가져옵니다 — 승인은 유지됩니다',
        mvNeedTeam: '먼저 프로젝트의 팀을 선택하세요',
        mvNotManager: '관리자 또는 이 팀의 매니저만 SKU를 이동할 수 있습니다',
        mvHead: '승인된 SKU를 이 프로젝트로 이동',
        mvIntro: '같은 팀의 프로젝트를 선택하고 승인된 SKU를 체크하세요. 이 양식을 저장할 때 이동되며 SKU ID, 승인, 샘플, 단계가 그대로 유지되어 CEO 승인을 다시 받지 않습니다. 이미 발행된 티켓은 원래 프로젝트에 남습니다. 개발자, 디자이너, 프로젝트 오너에게 메일이 발송됩니다.',
        mvSource: '가져올 프로젝트',
        mvSearch: '프로젝트 또는 SKU 검색',
        mvSearchPh: 'PRJ 번호, 프로젝트명, SKU 번호 또는 아이템명…',
        mvNoMatch: '일치하는 항목이 없습니다 — 더 짧은 단어로 검색해 보세요.',
        mvPicked: '{{n}}개 선택됨',
        mvChoose: '프로젝트 선택…',
        mvLoading: '불러오는 중…',
        mvApprovedN: '승인 {{n}}개',
        mvNone: '이 팀의 다른 프로젝트에 승인된 SKU가 없습니다.',
        mvAllTaken: '이 프로젝트의 승인된 SKU는 모두 이미 선택되었습니다.',
        mvAll: '전체 선택',
        mvColItem: '아이템', mvColApproved: '승인', mvColDeveloper: '개발자', mvColDesigner: '디자이너',
        mvCancel: '취소',
        mvAdd: 'SKU {{n}}개 추가',
        mvListHead: '저장 시 이동 — SKU {{n}}개',
        mvFrom: '{{id}}에서',
        mvApprovedBy: '{{who}} 승인',
        mvRemove: '이 SKU는 이동하지 않음',
        mvSaveHint: '아직 이동되지 않았습니다. 양식을 저장하면 이 SKU들이 이 프로젝트로 이동합니다.',
        mvTeamChanged: '위에서 선택한 팀과 다른 팀의 프로젝트에서 가져온 SKU입니다 — 제거하거나 팀을 다시 바꾸세요.',
        pfSkuHint: '각 SKU는 하나의 PG 프로젝트 아래 개별 PRJ 항목으로 저장됩니다. SKU를 펼쳐 정보, 초기 단계, 연결 샘플을 편집하세요.',
        pfUnassigned: '미배정',
        pfNoCategory: '카테고리 없음',
        pfQty: '수량',
        pfStageChip: '단계',
        pfRemoveSku: 'SKU 삭제',
        pfDuplicateSku: '이 SKU 복제',
        pfApprovalNoteHead: '승인 대기 상태로 저장되며, 아직 승인자에게 발송되지 않습니다.',
        pfApprovalNoteBody: 'Project Approval 화면에서 Submit을 눌러 요청을 보내세요. 승인 후 티켓을 발행할 수 있습니다.',
        pfDeveloper: '개발자',
        pfDesigner: '디자이너',
        pfNoDesigner: '디자이너 없음',
        ptDesigner: '디자이너',
        ptAllSalesReps: '전체 Sales Rep',
        ptAllDesigners: '전체 디자이너',
        pfAssignedTo: '담당자',
        pfAllocation: '배정',
        pfProductCategory: '제품 카테고리',
        pfFrameColor: '프레임 컬러',
        pfFrameColorPh: '검색하거나 새로 추가…',
        pfItemName: '아이템 / SKU 명',
        pfItemNamePh: '예: Backpack 17L',
        pfEstQty: '예상 수량',
        pfStartDate: '시작일',
        pfExfShipDate: 'EXF 선적일',
        pfTargetPrice: '목표 가격 (USD)',
        pfLaunchDate: '출시일',
        pfProjectStatus: '프로젝트 상태',
        pfRemark: '비고',
        pfInitialStages: '초기 공정 단계',
        pfPctComplete: '완료',
        pfStageHint: '목록에서 상태를 선택하거나 직접 입력하세요 — 날짜를 입력할 경우 dd/mm/yyyy 형식을 사용하세요. 비워 두면 Pending으로 저장됩니다.',
        pfRepeatOrder: '리오더',
        pfRepeatOrderHint: '이미 개발된 스타일의 재주문 — 프로토·카운터 샘플, L/D 승인, 핸드오프 미팅이 없으므로 네 단계 모두 Complete로 설정됩니다.',
        pfStagePh: 'Pending 또는 dd/mm/yyyy',
        pfStageAuto: '자동 — 연결된 샘플 기준',
        pfLinkSample: '샘플 연결 (선택)',
        pfLinkSampleHint: '샘플/베이스 ID를 쉼표로 구분해 하나 이상 입력합니다. 샘플을 선택하면 이 PRJ 항목에 연결됩니다.',
        pfLinkSamplePh: '샘플/베이스 ID 선택 또는 입력',
        pfGtm: 'GTM 프로그램 (선택)',
        pfGtmHint: '이 SKU가 출시되는 GTM 프로그램입니다. GTM 시트에도 표시됩니다',
        pfGtmPh: 'GTM 코드 선택 또는 입력 (예: GTM-26-001)',
        pfTickets: '티켓',
        pfTicketsAdded: '티켓 {{count}}건 추가됨',
        pfNoTicketYet: '추가된 티켓 없음',
        pfAllReceivingTeams: '전체 수신 팀',
        pfAddTicket: '＋ 티켓 추가…',
        pfTicketHint: '이 프로젝트의 팀·케이스·카테고리에 맞는 코드만 단계별로 표시됩니다. 수신 팀을 고른 뒤 “＋ 티켓 추가”를 누르세요. 새 티켓은 그룹을 저장할 때 생성됩니다.',
        pfClearTickets: '티켓 비우기',
        pfPhotosOnSku: '사진은 위의 각 SKU에서 첨부합니다 — SKU를 열고 Add photos를 누르세요.',
        pfTicketEmpty: '추가된 티켓이 없습니다 — “＋ 티켓 추가”에서 선택하세요.',
        pfCancel: '취소',
        pfSaving: '저장 중...',
        pfAddProject: '프로젝트 추가',
        pfDiscardTitle: '작성 중인 프로젝트를 취소할까요?',
        pfDiscardBody: '입력한 내용은 아직 저장되지 않았습니다. 창을 닫으면 사라집니다.',
        pfDiscardKeep: '계속 작성',
        pfDiscardConfirm: '취소하기',
        dgEditTitle: '변경 사항을 취소할까요?',
        dgEditBody: '여기서 수정한 내용은 아직 저장되지 않았습니다. 창을 닫으면 사라집니다.',
        pfSaveChanges: '변경 저장',
        pfViewSample: '연결된 샘플 보기',
        pfSampleBtn: '샘플',
        pfNewSku: '신규 SKU',
        pfEditPrefix: '편집',
        pfGroupLevelHint: '그룹 단위 정보 — 프로젝트 그룹에서 편집하세요',
        pfNewTag: '신규',
        pfSaveAddSku: 'SKU 추가',
        thSortFilter: '정렬 및 필터',
        thSortAZ: '오름차순 정렬',
        thSortOldNew: '오래된 날짜순 정렬',
        thSortZA: '내림차순 정렬',
        thSortNewOld: '최신 날짜순 정렬',
        thSearch: '검색...',
        thSelectAll: '(전체 선택)',
        thNoMatches: '일치하는 항목 없음',
        thOk: '확인',
        thCancel: '취소',
        thBlanks: '(빈 값)',
        thFiltered: '필터 적용 — {{count}}개 값 표시',
        pgSharedHint: '이 그룹의 모든 SKU가 공유하는 기본 정보',
        pgPrjItems: 'PRJ 항목',
        pgSkuHint: '각 SKU는 이 PG 프로젝트 아래 개별 PRJ 항목입니다. SKU를 클릭해 정보, 단계, 연결 샘플을 편집하세요.',
        pgNoSku: '이 그룹에는 아직 SKU가 없습니다.',
        pgRemoveSku: '이 SKU를 그룹에서 제거',
        // 라운드 신규 / 편집 모달
        rmTitleNew: '신규 라운드',
        rmTitleNewSample: '신규 샘플',
        rmTitleEdit: '기존 라운드 편집',
        rmTitleAdvance: '다음 라운드 (Advance 필수)',
        tkFiles: '첨부 파일',
        tfPick: '클릭하여 파일 첨부',
        tfHint: 'PDF, Excel, CSV, Word, 이미지 — 파일당 최대 {{max}} MB, 티켓 메일에 첨부되어 발송됩니다',
        tfUploading: '업로드 중…',
        tfRemove: '이 파일 삭제',
        tfTotal: '파일 {{n}}개, {{size}} — 티켓 메일에 첨부되어 발송됩니다',
        tfSaveFirst: '이 티켓에는 파일을 첨부할 수 없습니다.',
        tfDraftNote: '티켓을 저장할 때 첨부됩니다',
        tfTooBig: '{{name}}은(는) {{mb}} MB입니다. 파일당 제한은 {{max}} MB입니다.',
        rmPickSample: '라운드를 추가할 기존 샘플 선택',
        rmPickSampleSub: '— Sample ID, Base ID 또는 스타일명 입력',
        rmPickPh: '예: SMP-2601-P1 또는 SMP-2601...',
        rmPickRequired: '이 라운드가 속할 샘플을 선택하세요 — 라운드는 항상 기존 샘플에 추가됩니다.',
        rmPickFirst: '여기서 시작하세요. Sample ID를 입력하면 아래 양식이 열립니다.',
        rmStyleFromSample: '위에서 선택한 샘플에서 가져옵니다',
        rmStyleFromSku: '이 샘플이 속한 프로젝트 SKU에서 가져옵니다',
        rmStylePlaceholder: '위에서 샘플을 선택하세요',
        rmSalesTeam: '영업팀',
        rmProjectOwner: '프로젝트 담당자',
        rmProjectName: '프로젝트',
        rmNoLinkedSku: '연결된 SKU 없음',
        rmClear: '지우기',
        rmAddingTo: '라운드 추가 대상',
        rmChange: '변경',
        rmAdvanceNote: '을(를) Advance로 설정하고 있습니다. 개발이 다음 라운드로 넘어간다는 의미입니다. 해당 라운드를 입력하고 저장하세요 — 두 건이 함께 저장됩니다.',
        rmAdvanceNoteSub: '취소하면 Advance 결과도 저장되지 않고 라운드는 그대로 유지됩니다.',
        rmDupWarn: '중복 가능: {{sampleId}}에 이미 이 스타일의 "{{round}}" 라운드가 있습니다 (결과: {{outcome}}).',
        rmDupOpen: '미정',
        rmOwnership: '입력 담당:',
        rmHevnFills: 'HEVN 입력',
        rmHincFills: 'HINC 디자인 입력',
        rmBothTeams: '양 팀',
        rmAutoGen: '자동 생성',
        rmMandatory: '= 필수 항목',
        rmSampleIdent: '샘플 식별 정보',
        rmStyleItem: '스타일 / 품목',
        rmStyleHint: '기존 항목을 선택하거나 새로 입력 — Base ID는 자동 부여됩니다',
        rmStylePh: '예: Chair Zero LT',
        rmBaseId: 'Base ID',
        rmAutoAssigned: '자동 부여',
        rmSampleId: 'Sample ID',
        rmSampleIdHint: 'Base ID + 라운드 + 옵션 (자동)',
        rmRound: '라운드',
        rmOption: '옵션',
        rmOptionHint: '구조가 다를 때만 입력',
        rmOptionPh: '원단 / 메시 (색상 변형은 비워 두세요)',
        rmCategory: '카테고리',
        rmSeason: '시즌',
        rmDevelopmentDue: '개발 기한',
        rmDevDueHint: '필수 — 시트에서 노란색',
        rmTargetPrice: '목표 가격 (USD)',
        rmLinkedSku: '연결된 프로젝트 SKU',
        rmOptional: '선택',
        rmLinkedSkuHint: '이 샘플을 프로젝트 SKU에 연결',
        rmSearchProject: '프로젝트 검색...',
        rmReviewType: '검토 유형',
        rmHevn: 'HEVN',
        rmRequiresDesigner: '디자이너 검토 필요',
        rmInternalOnly: 'HEVN 내부 검토만',
        rmInternalNote: 'HEVN 내부 검토 기록에는 검토일과 비고를 사용하세요. 이 라운드는 디자이너 업무량이나 대기 피드백에 집계되지 않습니다.',
        rmDesignerNote: '디자이너 검토는 기존 HINC 검토 및 결과 흐름을 그대로 따릅니다.',
        rmInternalReview: 'HEVN 내부 검토',
        rmRequestDate: '요청일',
        rmInternalReqHint: 'HINC 디자인 검토의 요청일과 같은 항목입니다 — 여기서는 내부 검토일입니다',
        rmRemarks: '비고',
        rmInternalRemarksPh: '내부 검토 관련 메모...',
        rmSamplePlan: '샘플 계획',
        rmDeveloper: '개발자',
        rmDeveloperPh: '이름 선택 또는 입력...',
        rmEitherName: '개발자 또는 디자이너 중 한 명이면 됩니다.',
        rmLocation: 'R&D 위치',
        rmRmReady: '원자재 준비일',
        rmRmReadyHint: '예정일인 경우 “예정일”을 체크하고, 자재/스펙이 준비되면 체크를 해제하세요.',
        pfNeedOwner: '이 프로젝트에는 아직 Project Owner가 없어 티켓을 발행할 수 없습니다 — 메일이 "Project Owner: -" 상태로 나가면 수신 부서가 누구에게 회신해야 할지 알 수 없습니다. 프로젝트 행의 연필 버튼으로 프로젝트를 열어 Project Owner를 지정하고 저장한 뒤 다시 오세요.',
        pfNeedOwnerShort: '먼저 프로젝트에 Project Owner를 지정하세요',
        rmMaterials: '자재 목록',
        rmMaterialsHint: '자재 목록의 모든 열은 SKU 소유이며 SKU에서 수정합니다 — 복사본이 아니라 하나의 목록이라 SKU에 자재를 추가하면 모든 라운드에 나타납니다. RM ready date만 예외로, 이 라운드의 값이며 라운드와 함께 저장됩니다.',
        rmMaterialsNoSku: '자재 목록은 프로젝트 SKU에 속하는데 이 라운드는 아직 연결되어 있지 않습니다. 위의 Linked Project SKU를 채우면 해당 자재가 여기에 표시됩니다.',
        rmExpectedDate: '예정일',
        rmSampleFinish: '샘플 완성일',
        rmQty: '수량',
        rmActualCost: '실제 원가 (USD)',
        rmAutoCalc: '자동 계산 (읽기 전용):',
        rmMakeDays: '제작 일수',
        rmShipDays: '배송 일수',
        rmRoundCycle: '라운드 사이클',
        rmDday: 'D-day',
        rmHincReview: 'HINC 디자인 검토',
        rmHincDesigner: 'HINC 디자이너',
        rmHincReqHint: 'HINC가 이 샘플을 요청한 날짜',
        rmRoundDue: '라운드 기한',
        rmRoundDueHint: 'HINC의 샘플 완성 목표일',
        rmReceivedDate: '수령일',
        rmReceivedHint: 'HINC가 샘플을 수령한 날짜',
        rmReviewDate: '검토일 (코멘트일)',
        rmReviewDateHint: 'HINC가 피드백을 제공한 날짜',
        rmRoundOutcome: '라운드 결과',
        rmOutcomeHint: '주간 코멘트 미팅 후 설정',
        rmNotSetYet: '— 미설정 —',
        rmOutcomePick: '결과를 선택하면 설명이 표시됩니다.',
        rmReworkTitle: '재작업 — 사유 필수',
        rmReworkReason: '재작업 사유',
        rmSelectAll: '(해당 항목 모두 선택)',
        rmRoundRemarksPh: '이 라운드 관련 메모...',
        rmSaving: '저장 중...',
        rmSaveChanges: '변경 저장',
        rmSaveAdvance: 'Advance + 신규 라운드 저장',
        rmAddNewRound: '신규 라운드 추가',
        rmCancel: '취소',
        rmCancelAdvance: '취소 (Advance도 함께 취소)',
        // 티켓 스테이션 + 티켓 폼
        tkTitle: '티켓 스테이션',
        tkHint: '프로젝트 티켓, 담당자 팔로업, 기한, 상태 업데이트를 추적합니다.',
        tkTabTickets: '티켓 스테이션',
        tkManageCodes: '티켓 코드',
        tkAll: '전체 티켓',
        tkDone: '종료',
        tkCancelled: '취소됨',
        tkPending: '열림',
        tkOverdue: '기한 초과',
        tkAllRequesters: '전체 요청자',
        tkAllTeams: '전체 수신 팀',
        tkAllSopTeams: '전체 Sales OP 팀',
        tkAllPriority: '전체 우선순위',
        tkSearch: '티켓 / 프로젝트 검색...',
        tkNewTicket: '+ 티켓 추가',
        tkColTicketId: '티켓 ID',
        tkColTicket: '티켓',
        tkColProject: '프로젝트',
        tkColProjectName: '프로젝트명',
        tkColTeam: '수신 팀',
        tkColRequester: '요청자',
        tkColType: '유형',
        tkColPriority: '우선순위',
        tkColDue: '기한',
        tkColCreatedBy: '생성자',
        tkColCreatedAt: '생성일시',
        tkColUpdatedBy: '수정자',
        tkColUpdatedAt: '수정일시',
        tkColNote: '메모',
        tkColAssigneeReply: '담당자 회신',
        tkLinkNotSent: '먼저 티켓 메일을 발송하세요 — 아직 담당 팀은 이 요청이 있다는 것을 모르므로 이 링크로 회신할 사람이 없습니다.',
        tkColDescription: '상세 내용',
        tkColAssigneeNote: '담당자 메모',
        tkColEmailSent: '메일 발송일',
        tkExportEmpty: '내보낼 항목이 없습니다 — 현재 필터에 해당하는 티켓이 없습니다.',
        tkManagerStatus: '매니저 상태',
        tkRemind: '알림',
        tkRemindHint: '이 티켓이 아직 열려 있음을 수신 팀에 메일로 알립니다',
        tkSend: '발송',
        tkSendHint: '이 티켓은 아직 메일로 발송된 적이 없습니다 — 수신 팀에 요청을 보냅니다',
        tkSendDone: '티켓 {{id}}을(를) {{to}}(으)로 발송했습니다.',
        tkSendFailed: '발송 실패: {{err}}',
        tkDdayHint: 'D-DAY = 오늘 − 마감일. 양수는 그만큼 기한을 넘겼다는 뜻이며, Done/Cancelled가 되면 마감된 날짜에서 멈춥니다.',
        tkNotYourTeam: '이 티켓은 생성자, 프로젝트 오너, 해당 팀 매니저, 관리자만 변경할 수 있습니다.',
        tkExpandAll: '전체 펼치기',
        tkCollapseAll: '전체 접기',
        tkTicketsSingular: '건',
        tkTicketsPlural: '건',
        tkNoProject: '(프로젝트 없음)',
        tkEdit: '티켓 편집',
        tkDuplicate: '티켓 복제',
        tkDelete: '티켓 삭제',
        tkOpenLink: '공개 상태 링크 열기',
        tkLink: '링크',
        tkNoTickets: '티켓이 없습니다.',
        tkTitleNew: '신규 티켓',
        tkTitleEdit: '티켓 편집',
        tkTitleDup: '티켓 복제',
        tkAuto: '자동',
        tkProjectGroup: '프로젝트 그룹',
        tkSearchGroup: '프로젝트 그룹 검색...',
        tkAutoFromGroup: '프로젝트 그룹에서 자동',
        tkSelectTeam: '수신 팀 선택...',
        tkTicketCode: '티켓 코드',
        tkSelectCode: '합의된 티켓 유형 선택...',
        tkTicketName: '티켓명',
        tkSelectTypeFirst: '티켓 유형을 선택하세요',
        tkDescription: '설명',
        tkDescriptionPh: '티켓 상세 / 기대 결과',
        tkRequesterPh: '로그인 사용자로 자동 입력',
        tkGroupEmail: '그룹 이메일',
        tkGroupEmailPh: '수신 팀으로 자동 입력',
        tkDueDate: '기한일',
        tkSentAt: '메일 발송일',
        tkAttachment: '첨부 링크',
        tkAttachmentPh: 'Drive/파일 링크 붙여넣기',
        tkNotePh: '내부 메모 또는 최신 업데이트',
        tkSendAfterSave: '저장 후 이메일 발송',
        tkSent: '발송됨',
        tkNotSent: '미발송',
        tkEmailSubject: '이메일 제목',
        tkSystemGenerated: '시스템 생성',
        tkEmailCc: '이메일 CC',
        tkEmailMessage: '이메일 본문',
        tkReadOnly: '읽기 전용',
        tkEmailMessagePh: '이메일 본문',
        tkGeneratedNote: '이메일 제목과 본문은 자동 생성되며 편집할 수 없습니다.',
        tkSendEmail: '이메일 발송',
        tkDeleteTicket: '티켓 삭제',
        tkSaveTicket: '티켓 저장',
        tkNoDueDate: '기한 없음',
        tkRemoveTicket: '티켓 제거',
        tkOptionalNote: '선택 메모',
        tkWillSend: '저장 시 발송',
        tkQueued: '발송 대기',
        tkQueuedCancelTitle: '발송 대기 — 클릭하면 취소',
        tkSendBlocked: '위의 필수 항목을 먼저 입력하세요 — 입력 전에는 이메일을 보낼 수 없습니다',
        tkSendTitle: '티켓은 저장 시 생성되므로 이메일은 그 직후 발송됩니다',
      },
      rt: {
        bold: '굵게 (Ctrl+B)',
        italic: '기울임 (Ctrl+I)',
        underline: '밑줄 (Ctrl+U)',
        strike: '취소선',
        colour: '글자 색',
        bullets: '글머리 기호 목록',
        numbers: '번호 매기기 목록',
        clear: '서식 지우기',
        cDefault: '기본',
        cRed: '빨강',
        cOrange: '주황',
        cGreen: '초록',
        cBlue: '파랑',
        cPurple: '보라',
        cGrey: '회색',
      },
      nfc: {
        stDraft: '미제출',
        stWaiting: '승인 대기',
        stApproved: '승인됨',
        stRejected: '반려됨',
        replOk: '정상',
        replNotFound: '찾을 수 없음',
        replReplaced: '이미 교체됨',
        replDuplicate: '중복',
        notSignedIn: '(로그인되지 않음)',
        role: '역할',
        manager: '매니저',
        coordinator: '코디네이터',
        unassigned: '미지정',
        loading: '불러오는 중…',
        noAccessTitle: '접근 권한 없음',
        noAccessBody: '계정에 NFC 포털의 코디네이터/매니저 역할이 부여되지 않았습니다. Special Project 팀에 등록을 요청하세요.',
        accountWord: '계정',
        createTitle: '토큰 발행 요청 생성',
        tabIssuance: '토큰 발행',
        tabReplacement: '불량 NFC 교체',
        labelType: '라벨 유형',
        labelNfc: 'NFC 라벨',
        labelQr: 'QR 열전사 라벨',
        projectName: '프로젝트명',
        batchName: '배치명',
        qty: '수량 (QTY)',
        qtyPh: '품목 가져오기를 하지 않으면 필수',
        style: '스타일 / 제품',
        stylePh: '예: Chair One (re)',
        color: '색상',
        vendor: '벤더',
        vendorPh: '예: Metric / TheSang',
        code: '코드',
        codePh: '예: 10001555',
        importTitle: '여러 품목 가져오기 (선택)',
        importReplTitle: '불량 NFC 토큰 가져오기',
        downloadTemplate: 'CSV 템플릿 다운로드',
        chooseCsv: 'CSV 선택',
        noFile: '선택된 파일 없음',
        importSummary: '{{count}}개 품목, 총 수량 {{qty}}을 불러왔습니다. 제출 전에 확인하고 수정하세요.',
        importInvalid: '일부 행이 유효하지 않습니다 (프로젝트 + 배치 + 수량 필요).',
        importEmpty: '가져온 파일이 없습니다 — 비워 두면 위의 단일 품목 항목을 사용합니다.',
        replSummary: '불량 토큰 {{count}}건을 불러왔습니다. 제출 전에 확인하세요.',
        replChecking: '원장과 대조 중…',
        warning: '경고:',
        oldToken: '기존 토큰 / NFC URL',
        reason: '사유',
        notes: '메모',
        status: '상태',
        checking: '확인 중…',
        notChecked: '미확인',
        replEmpty: '불러온 불량 토큰이 없습니다 — 템플릿을 다운로드한 뒤 CSV를 업로드하세요.',
        optional: '선택',
        saving: '저장 중…',
        save: '저장',
        cancel: '취소',
        myRequests: '내 요청',
        reload: '새로 고침',
        colId: 'ID',
        colProject: '프로젝트',
        colBatch: '배치',
        colQty: '수량',
        colItems: '품목',
        colLabel: '라벨',
        colVendor: '벤더',
        colCode: '코드',
        colStatus: '상태',
        colUpdated: '업데이트',
        colFile: '파일',
        colAction: '작업',
        colOld: '이전',
        colNew: '현재',
        colOutput: '출력',
        colRequestedBy: '요청자',
        colEmail: '이메일',
        colAddedBy: '등록자',
        downloadOrder: '발주 파일 다운로드',
        viewDetail: '상세 보기',
        edit: '편집',
        submitApproval: '승인 요청',
        delete: '삭제',
        reject: '반려',
        noRequests: '요청이 없습니다.',
        reviewTitle: '요청 검토',
        accessTitle: '포털 접근 관리',
        accessHint: '이 NFC 포털의 코디네이터 / 매니저 접근 권한을 부여하거나 회수합니다. 변경 사항은 해당 사용자가 포털을 다시 열 때 적용됩니다.',
        grantAccess: '권한 부여',
        noUsers: '지정된 사용자가 없습니다.',
        detailTitle: '요청 상세',
        lblNotes: '메모',
        lblOutputFile: '출력 파일',
        openOutput: '출력 파일 열기',
        lblDecisionNote: '결정 메모',
        replItems: '교체 품목',
        items: '품목',
        close: '닫기',
        genTitle: '토큰 생성 중',
        genLead: '요청을 승인하는 중입니다. 이 탭을 열어 둔 상태로 유지하고',
        genLeadBold: '승인을 다시 클릭하지 마세요',
        requestWord: '요청',
        working: '처리 중...',
        rejectTitle: '요청 반려',
        rejectReason: '사유 (요청자에게 전달)',
        rejectReasonPh: '반려 사유는 무엇입니까?',
        rejectReasonReq: '반려 사유를 입력하세요.',
        rejectBtn: '요청 반려',
        deleteTitle: '요청 삭제',
        deleteBody: '{{id}} 요청을 삭제하시겠습니까? 미제출 또는 반려된 요청만 삭제할 수 있습니다.',
        nReplacements: '교체 {{count}}건',
        nItems: '품목 {{count}}건',
        single: '단일',
        nCodes: '코드 {{count}}건',
        flashCleared: '요청 양식을 초기화했습니다.',
        flashSubmitted: '{{id}} 요청을 승인 요청했습니다.',
        flashDraft: '{{id}} 요청을 임시 저장했습니다.',
        flashLoadedEdit: '{{id}}을(를) 편집용으로 불러왔습니다.',
        flashDeleted: '{{id}} 요청을 삭제했습니다.',
        flashApproved: '{{id}} 요청을 승인했습니다 — 토큰 {{qty}}개 생성.',
        flashGranted: '{{email}}에게 {{role}} 권한을 부여했습니다.',
        flashRevoked: '{{email}}의 {{role}} 권한을 회수했습니다.',
        flashEmailReq: '이메일을 입력하세요.',
        flashLoadedFile: '{{name}}을(를) 불러왔습니다.',
        flashRejected: '{{id}} 요청을 반려했습니다.',
        progStarting: '승인을 시작합니다...',
        progCompleted: '완료',
        progGenerated: '토큰 {{qty}}개 생성',
        progSavedImport: '저장된 가져오기 내용을 불러왔습니다',
      },
      dd: {
        title: '대시보드',
        scopeNote: '프로젝트 샘플만 집계합니다 — 시험 샘플(TSMP)은 이 페이지의 모든 수치에서 제외됩니다. 샘플 트래커에는 둘 다 표시됩니다.',
        loading: '대시보드 불러오는 중…',
        retry: '다시 시도',
        noData: '데이터 없음',
        totalRounds: '전체 라운드',
        acrossSamples: '샘플 {{count}}건 기준',
        avgRoundsSample: '샘플당 평균 라운드',
        higherSpinning: '높을수록 반복이 많음',
        reworkRatio: '재작업 비율',
        mainKpi: '핵심 지표',
        pastDue: '개발 기한 초과',
        pastDueSub: 'D-day ≥ 0 인 {{unit}}',
        reworkByRoom: '개발실별 재작업 비율',
        reworkAttribution: '재작업 귀속 (양방향)',
        hevnCaused: 'HEVN 원인',
        hincCaused: 'HINC 원인',
        external: '외부 요인',
        hevnRatio: 'HEVN 귀속 비율',
        stuckTime: '정체 시간 지표',
        avgCommentDiffers: "평균 코멘트 일수 — 'differs' 라운드",
        avgCommentAdvance: '평균 코멘트 일수 — Advance 라운드',
        avgCommentAll: '평균 코멘트 일수 — 전체',
        spinning: '반복 샘플 (4라운드 이상)',
        sampleSpeed: '샘플 속도',
        sampleSpeedNote: '원자재 준비일 = 원단·자재·스펙이 준비되어 샘플실이 제작을 시작할 수 있는 날짜입니다. 샘플 속도는 원자재 준비일부터 샘플 완료일까지를 계산하며, 실제 준비일이 없거나 예정 준비일이 미래인 라운드는 원자재 대기 상태로 제외됩니다.',
        avgMakeHevn: '평균 제작 일수 (HEVN R&D)',
        avgMakeTs: '평균 제작 일수 (TS)',
        avgMakeMs: '평균 제작 일수 (MS)',
        avgMakeTotal: '평균 제작 일수 (전체)',
        byDeveloper: '개발자별 — 전체 내역',
        colDeveloper: '개발자',
        colSamples: '샘플 수',
        colRounds: '라운드 수',
        colRework: '재작업',
        colReworkRate: '재작업 비율',
        colAvgRounds: '평균 라운드',
        colAvgMakeDays: '평균 제작 일수',
        colPending: '대기',
      },
    },
  },
};

// The chosen language is remembered per browser. Deliberately NOT stored per user in the database: reading it
// would add a round trip before the first paint, and the whole point of this feature is that it costs nothing
// at load time.
// Each language in its own name, with the country code the picker prints beside it (LanguagePicker.jsx).
// `htmlLang` goes on <html lang>, which is what makes a browser pick Simplified rather than Japanese glyphs.
export const LANGUAGES = [
  { code: 'en', label: 'English', flag: 'US', htmlLang: 'en' },
  { code: 'ko', label: '한국어', flag: 'KR', htmlLang: 'ko' },
  { code: 'vi', label: 'Tiếng Việt', flag: 'VN', htmlLang: 'vi' },
  { code: 'zh', label: '简体中文', flag: 'CN', htmlLang: 'zh-CN' },
];
const htmlLangOf = (code) => (LANGUAGES.find((l) => l.code === code) || LANGUAGES[0]).htmlLang;
const STORAGE_KEY = 'portalLanguage';

const savedLanguage = () => {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return LANGUAGES.some((l) => l.code === saved) ? saved : 'en';
  } catch (error) {
    // Private mode / storage disabled — English is a safe default.
    return 'en';
  }
};

export const setPortalLanguage = (code) => {
  const next = LANGUAGES.some((l) => l.code === code) ? code : 'en';
  try { window.localStorage.setItem(STORAGE_KEY, next); } catch (error) { /* not fatal */ }
  i18n.changeLanguage(next);
  if (typeof document !== 'undefined') document.documentElement.lang = htmlLangOf(next);
};

// Screens put into the dictionary from 2026-10-21 on (Max: "nhiều chỗ vẫn chưa chuyển ngữ") keep their strings in
// src/locales/<ns>.js, one file per namespace carrying all four languages side by side — so a string and its
// translations are added in one place, and adding a screen means adding a file, not editing this one. Each file
// exports { ns, en, ko, vi, zh }; it is laid under `common.<ns>` of each language. A namespace may not repeat one
// above (scripts/i18n_locales_check.cjs refuses it), so nothing here can be overwritten by a file.
const localeFiles = require.context('./locales', false, /\.js$/);
localeFiles.keys().forEach((file) => {
  const mod = localeFiles(file).default;
  if (!mod || !mod.ns) return;
  [['en', resources.en], ['ko', resources.ko], ['vi', vi], ['zh', zh]].forEach(([lng, target]) => {
    if (!target.common) target.common = {};
    if (mod[lng]) target.common[mod.ns] = mod[lng];
  });
});

i18n
  .use(initReactI18next)
  .init({
    // `resources` is bundled with the app, so switching language never issues a network request and never
    // touches the API. Data loading is completely unaffected.
    resources: { ...resources, vi, zh },
    lng: savedLanguage(),
    fallbackLng: 'en',
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    // A key missing from Korean, Vietnamese or Chinese falls back to English rather than rendering the raw key name.
    returnEmptyString: false,
  });

if (typeof document !== 'undefined') document.documentElement.lang = htmlLangOf(i18n.language);

export default i18n;
